/*!
 * Copyright (c) 2022 WEBLIFE
 *
 * Permission is granted to use this file for the period specified in the contract
 * between WEBLIFE and the client. Usage of this file beyond the contract period
 * is strictly prohibited unless an extended license or written permission is obtained.
 *
 * Modifying or editing this file without prior written consent from WEBLIFE
 * is not permitted.
 *
 * THE FILE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT.
 */
var __defProp = Object.defineProperty,
  __defNormalProp = (obj, key, value) =>
    key in obj
      ? __defProp(obj, key, {
        enumerable: !0,
        configurable: !0,
        writable: !0,
        value: value,
      })
      : (obj[key] = value),
  __publicField = (obj, key, value) =>
    __defNormalProp(obj, "symbol" != typeof key ? key + "" : key, value);
/* empty css                  */ class CartCache {
  constructor() {
    (__publicField(this, "cache", null),
      __publicField(this, "cacheTime", 0),
      __publicField(this, "CACHE_DURATION", 2e3),
      __publicField(this, "pendingRequest", null),
      __publicField(this, "fetchInterceptInstalled", !1));
  }
  async get(force = !1) {
    const now = Date.now();
    return !force && this.cache && now - this.cacheTime < this.CACHE_DURATION
      ? this.cache
      : (this.pendingRequest ||
        (this.pendingRequest = fetch("/cart.js")
          .then((response) => response.json())
          .then(
            (cart2) => (
              (this.cache = cart2),
              (this.cacheTime = Date.now()),
              (this.pendingRequest = null),
              cart2
            ),
          )
          .catch((error) => {
            throw ((this.pendingRequest = null), error);
          })),
        this.pendingRequest);
  }
  invalidate() {
    ((this.cache = null), (this.cacheTime = 0));
  }
  async refresh() {
    return (this.invalidate(), this.get(!0));
  }
  getCached() {
    const now = Date.now();
    return this.cache && now - this.cacheTime < this.CACHE_DURATION
      ? this.cache
      : null;
  }
  isValid() {
    const now = Date.now();
    return null !== this.cache && now - this.cacheTime < this.CACHE_DURATION;
  }
  installFetchInterceptor() {
    if (this.fetchInterceptInstalled) return;
    const originalFetch = window.fetch,
      self = this;
    ((window.fetch = function (...args) {
      var _a;
      const url =
        "string" == typeof args[0]
          ? args[0]
          : null == (_a = args[0])
            ? void 0
            : _a.url,
        isCartApi = undefined;
      return "string" == typeof url &&
        (url.includes("/cart/change.js") ||
          url.includes("/cart/update.js") ||
          url.includes("/cart/add.js") ||
          url.includes("/cart/clear.js"))
        ? (url.includes("/cart/change.js"),
          originalFetch.apply(this, args).then((response) => {
            const clonedResponse = undefined;
            return (response.clone().ok && self.invalidate(), response);
          }))
        : originalFetch.apply(this, args);
    }),
      (this.fetchInterceptInstalled = !0));
  }
}
const cartCache = new CartCache();
"undefined" != typeof window && cartCache.installFetchInterceptor();
class CartUpdateError extends Error {
  constructor(params) {
    (super(
      "OUT_OF_STOCK" === params.code
        ? "在庫不足によりカート更新に失敗しました"
        : "カート更新に失敗しました",
    ),
      __publicField(this, "code"),
      __publicField(this, "status"),
      __publicField(this, "messageRaw"),
      __publicField(this, "itemId"),
      __publicField(this, "variantId"),
      __publicField(this, "productTitle"),
      (this.name = "CartUpdateError"),
      (this.code = params.code),
      (this.status = params.status),
      (this.messageRaw = params.messageRaw),
      (this.itemId = params.itemId ?? null),
      (this.variantId = params.variantId ?? null),
      (this.productTitle = params.productTitle ?? null));
  }
}
function isCartUpdateError(error) {
  return Boolean(
    error &&
    "object" == typeof error &&
    "name" in error &&
    "CartUpdateError" === error.name &&
    "code" in error,
  );
}
function formatCartUpdateErrorForAlert(error, options) {
  var _a;
  if (isCartUpdateError(error) && "OUT_OF_STOCK" === error.code) {
    const extraTitles =
      (null == (_a = null == options ? void 0 : options.productTitles)
        ? void 0
        : _a.map((t) => String(t).trim()).filter((t) => "" !== t)) ?? [],
      mergedTitles = Array.from(
        new Set([
          ...(error.productTitle && "" !== String(error.productTitle).trim()
            ? [String(error.productTitle).trim()]
            : []),
          ...extraTitles,
        ]),
      );
    return mergedTitles.length > 0
      ? `在庫が不足しています（商品名: ${mergedTitles.join("、")}）。商品数を変更してください。`
      : "在庫が不足しています。商品数を変更してください。";
  }
  return "カートの更新に失敗しました。在庫が不足している可能性があります。商品の数量を見直してください。";
}
function looksLikeOutOfStock(status, bodyRaw) {
  if (422 === status) return !0;
  const haystack = String(bodyRaw || "");
  return /(在庫|不足|inventory|stock|available|sold\s*out|not\s+enough|quantity)/i.test(
    haystack,
  );
}
async function readResponseBodyAsText(response) {
  try {
    return await response.text();
  } catch (e) {
    return "";
  }
}
async function tryResolveCartItemInfoByKey(itemKey) {
  var _a;
  const cached = undefined,
    cart2 = cartCache.getCached() ?? (await cartCache.get(!0)),
    item =
      (null == (_a = null == cart2 ? void 0 : cart2.items)
        ? void 0
        : _a.find((i) => (null == i ? void 0 : i.key) === itemKey)) ?? null,
    productTitle =
      (null == item ? void 0 : item.product_title) ??
      (null == item ? void 0 : item.title) ??
      null,
    variantId = undefined;
  return {
    variantId:
      null != (null == item ? void 0 : item.variant_id)
        ? String(item.variant_id)
        : null,
    productTitle: productTitle,
  };
}
async function buildCartUpdateErrorFromResponse(params) {
  const bodyRaw = await readResponseBodyAsText(params.response),
    status = params.response.status,
    code = looksLikeOutOfStock(status, bodyRaw) ? "OUT_OF_STOCK" : "UNKNOWN";
  let variantId = null,
    productTitle = null;
  if (params.itemId)
    try {
      const resolved = await tryResolveCartItemInfoByKey(params.itemId);
      ((variantId = resolved.variantId),
        (productTitle = resolved.productTitle));
    } catch (e) { }
  return new CartUpdateError({
    code: code,
    status: status,
    messageRaw: bodyRaw,
    itemId: params.itemId ?? null,
    variantId: variantId,
    productTitle: productTitle,
  });
}
function getCartSectionIds() {
  const sectionIds = [],
    cartItemsElement = document.querySelector("cart-items");
  if (cartItemsElement) {
    const cartItemsSection =
      cartItemsElement.closest(".shopify-section") ||
      cartItemsElement.closest('[id^="shopify-section-"]');
    if (cartItemsSection && cartItemsSection.id) {
      const cartSectionId = cartItemsSection.id.replace("shopify-section-", "");
      cartSectionId && sectionIds.push(cartSectionId);
    }
  }
  const cartFooterElement = document.querySelector(".wl-main-cart-footer");
  if (cartFooterElement) {
    const footerSection =
      cartFooterElement.closest(".shopify-section") ||
      cartFooterElement.closest('[id^="shopify-section-"]');
    if (footerSection && footerSection.id) {
      const footerSectionId = footerSection.id.replace("shopify-section-", "");
      footerSectionId &&
        !sectionIds.includes(footerSectionId) &&
        sectionIds.push(footerSectionId);
    }
  }
  return sectionIds;
}
function updateSectionHtml(sections, sectionIds) {
  let updatedCount = 0;
  for (const sectionId of sectionIds) {
    const sectionHtml = sections[sectionId];
    if (!sectionHtml) continue;
    const parser = undefined,
      doc = new DOMParser().parseFromString(sectionHtml, "text/html"),
      oldSection = document.getElementById(`shopify-section-${sectionId}`);
    if (!oldSection) continue;
    const newSection =
      doc.querySelector(".shopify-section") ||
      doc.querySelector(`[id*="${sectionId}"]`);
    newSection && (oldSection.replaceWith(newSection), updatedCount++);
  }
  for (const sectionId of sectionIds)
    document.dispatchEvent(
      new CustomEvent("shopify:section:load", {
        detail: { sectionId: sectionId },
      }),
    );
}
async function updateCartItem(
  itemKey,
  quantity,
  properties,
  updateSections = !0,
) {
  const sectionIds = updateSections ? getCartSectionIds() : [],
    requestBody = { id: itemKey, quantity: quantity };
  (properties && (requestBody.properties = properties),
    sectionIds.length > 0 &&
    ((requestBody.sections = sectionIds.join(",")),
      (requestBody.sections_url = window.location.pathname)));
  const response = await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });
  if (!response.ok)
    throw await buildCartUpdateErrorFromResponse({
      response: response,
      itemId: itemKey,
    });
  const responseData = await response.json();
  return (
    cartCache.invalidate(),
    updateSections &&
    responseData.sections &&
    Object.keys(responseData.sections).length > 0 &&
    updateSectionHtml(responseData.sections, sectionIds),
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: { cart: responseData, source: "cart-helper" },
      }),
    ),
    responseData
  );
}
async function updateCartAttributes(attributes, updateSections = !0) {
  const sectionIds = updateSections ? getCartSectionIds() : [],
    requestBody = { attributes: attributes };
  sectionIds.length > 0 &&
    ((requestBody.sections = sectionIds.join(",")),
      (requestBody.sections_url = window.location.pathname));
  const response = await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });
  if (!response.ok)
    throw await buildCartUpdateErrorFromResponse({
      response: response,
      itemId: null,
    });
  const responseData = await response.json();
  return (
    cartCache.invalidate(),
    updateSections &&
    responseData.sections &&
    Object.keys(responseData.sections).length > 0 &&
    updateSectionHtml(responseData.sections, sectionIds),
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: { cart: responseData, source: "cart-helper" },
      }),
    ),
    responseData
  );
}
async function updateCartItemsBatch(updates, updateSections = !0, onProgress) {
  const sectionIds = updateSections ? getCartSectionIds() : [];
  let lastResponse = null;
  for (let i = 0; i < updates.length; i++) {
    const update = updates[i],
      isLastItem = i === updates.length - 1,
      requestBody = { id: update.id, quantity: update.quantity };
    (update.properties && (requestBody.properties = update.properties),
      isLastItem &&
      sectionIds.length > 0 &&
      ((requestBody.sections = sectionIds.join(",")),
        (requestBody.sections_url = window.location.pathname)));
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok)
      throw await buildCartUpdateErrorFromResponse({
        response: response,
        itemId: update.id,
      });
    ((lastResponse = await response.json()),
      cartCache.invalidate(),
      onProgress && onProgress(i + 1, updates.length),
      isLastItem &&
      updateSections &&
      lastResponse.sections &&
      Object.keys(lastResponse.sections).length > 0 &&
      updateSectionHtml(lastResponse.sections, sectionIds),
      isLastItem &&
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart: lastResponse, source: "cart-helper" },
        }),
      ));
  }
  return lastResponse;
}
function applyCartLevelDiscounts(cart2) {
  const items = cart2.items || [],
    cartLevelDiscounts = cart2.cart_level_discount_applications || [];
  if (0 === cartLevelDiscounts.length) return items;
  const lineItemDiscounts = cartLevelDiscounts.filter(
    (discount) => !discount.target_type || "line_item" === discount.target_type,
  );
  if (0 === lineItemDiscounts.length) return items;
  const toDiscountValue = (v) =>
    "number" != typeof v || isNaN(v) ? parseFloat(String(v)) || 0 : v,
    totalCartDiscount = lineItemDiscounts.reduce(
      (sum, discount) => sum + toDiscountValue(discount.value),
      0,
    ),
    totalOriginalLinePrice = items.reduce((sum, item) => {
      const originalLinePrice = undefined;
      return sum + (item.original_line_price || item.price * item.quantity);
    }, 0);
  if (0 === totalOriginalLinePrice) return items;
  const itemsWithAllocatedDiscount = undefined;
  return items.map((item) => {
    const originalPrice = item.original_price || item.price,
      originalLinePrice =
        item.original_line_price || item.price * item.quantity,
      proportion = originalLinePrice / totalOriginalLinePrice,
      allocatedCartDiscount = Math.round(totalCartDiscount * proportion),
      itemLevelDiscount = undefined,
      totalItemDiscount = (item.total_discount || 0) + allocatedCartDiscount,
      finalLinePrice = originalLinePrice - totalItemDiscount,
      finalPrice = Math.round(finalLinePrice / item.quantity);
    return {
      ...item,
      original_price: originalPrice,
      original_line_price: originalLinePrice,
      final_price: finalPrice,
      final_line_price: finalLinePrice,
      total_discount: totalItemDiscount,
      allocated_cart_discount: allocatedCartDiscount,
    };
  });
}
function hasCartLevelDiscounts(cart2) {
  const cartLevelDiscounts = undefined;
  return (cart2.cart_level_discount_applications || []).length > 0;
}
class PurposeService {
  static getCommonPurposes(cartItems, masterPurposes) {
    if (0 === cartItems.length) return [];
    const productPurposeTitles = cartItems.map((item) => {
      const purposes = undefined,
        titles = (item.purposes || [])
          .map((p) => p.name || p._title)
          .filter((title) => Boolean(title));
      return new Set(titles);
    }),
      firstProductPurposes = productPurposeTitles[0],
      commonPurposeTitles = new Set();
    firstProductPurposes.forEach((title) => {
      const isCommon = undefined;
      productPurposeTitles.every((purposeSet) => purposeSet.has(title)) &&
        commonPurposeTitles.add(title);
    });
    const commonPurposes = undefined;
    return masterPurposes.filter((purpose) => {
      const purposeName = purpose.name || purpose._title;
      return purposeName && commonPurposeTitles.has(purposeName);
    });
  }
  static getPurposes(cartItems, masterPurposes) {
    if (0 === cartItems.length) return [];
    const productPurposeTitles = cartItems.map((item) => {
      const purposes2 = undefined,
        titles = (item.purposes || [])
          .map((p) => p.name || p._title)
          .filter((title) => Boolean(title));
      return new Set(titles);
    }),
      purposes = undefined;
    return masterPurposes.filter((purpose) => {
      const purposeName = purpose.name || purpose._title;
      return (
        purposeName &&
        productPurposeTitles.some((titles) => titles.has(purposeName))
      );
    });
  }
  static normalizeDateString(dateStr, currentYear) {
    return /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
      ? dateStr
      : /^\d{2}-\d{2}$/.test(dateStr)
        ? `${currentYear}-${dateStr}`
        : dateStr;
  }
  static getActivePurposes(purposes) {
    const now = new Date(),
      currentYear = now.getFullYear(),
      activePurposes = undefined;
    return purposes
      .filter((purpose) => {
        const purposeName = purpose.name || purpose._title,
          isPublished = undefined;
        if (
          !(void 0 !== purpose.is_published
            ? purpose.is_published
            : purpose._isActive) ||
          "自宅用" === purposeName
        )
          return !1;
        const orderEndDate =
          purpose.order_acceptance_ends_at || purpose._order_end_date;
        if (orderEndDate) {
          const hasTime = undefined;
          if (/T\d{2}:\d{2}/.test(orderEndDate)) {
            const orderEndDateTime = new Date(orderEndDate);
            if (now > orderEndDateTime) return !1;
          } else {
            const normalizedOrderEndDate = this.normalizeDateString(
              orderEndDate,
              currentYear,
            ),
              orderEndDateTime = new Date(normalizedOrderEndDate);
            if (
              (orderEndDateTime.setHours(23, 59, 59, 999),
                now > orderEndDateTime)
            )
              return !1;
          }
        }
        return !0;
      })
      .sort((a, b) => {
        const orderA = a.display_order,
          orderB = b.display_order;
        return null != orderA && null != orderB
          ? orderA - orderB
          : null != orderA && null == orderB
            ? -1
            : null == orderA && null != orderB
              ? 1
              : 0;
      });
  }
  static isPurposeValid(purpose) {
    var _a;
    const now = new Date(),
      currentYear = now.getFullYear(),
      w = window,
      purposes =
        w._PURPOSE_MASTER ??
        (null == (_a = w.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.purposeMaster);
    if (!purposes || 0 === purposes.length) return !1;
    const targetPurpose = purposes.find(
      (p) => (p.name || p._title) === purpose,
    );
    if (!targetPurpose) return !1;
    const purposeName = targetPurpose.name || targetPurpose._title,
      isPublished = undefined;
    if (
      !(void 0 !== targetPurpose.is_published
        ? targetPurpose.is_published
        : targetPurpose._isActive)
    )
      return !1;
    const orderEndDate =
      targetPurpose.order_acceptance_ends_at || targetPurpose._order_end_date;
    if (orderEndDate) {
      const hasTime = undefined;
      if (/T\d{2}:\d{2}/.test(orderEndDate)) {
        const orderEndDateTime = undefined;
        if (now > new Date(orderEndDate)) return !1;
      } else {
        const normalizedOrderEndDate = this.normalizeDateString(
          orderEndDate,
          currentYear,
        ),
          orderEndDateTime = new Date(normalizedOrderEndDate);
        if (
          (orderEndDateTime.setHours(23, 59, 59, 999), now > orderEndDateTime)
        )
          return !1;
      }
    }
    return !0;
  }
  static isWithinShippingPeriod(purpose, date = new Date()) {
    const startDate =
      purpose.desired_datetime_start_at ||
      purpose._datetime_selection_start ||
      purpose._start_date,
      endDate =
        purpose.desired_datetime_end_at ||
        purpose._datetime_selection_end ||
        purpose._end_date;
    if (!startDate || !endDate) return !0;
    const start = new Date(startDate),
      end = new Date(endDate);
    return date >= start && date <= end;
  }
  static productHasPurpose(productPurposes, selectedPurpose) {
    const selectedName = selectedPurpose.name || selectedPurpose._title;
    return productPurposes.some((p) => (p.name || p._title) === selectedName);
  }
  static validateCartPurposes(cartItems, selectedPurpose) {
    const invalidProducts = [];
    return (
      cartItems.forEach((item) => {
        var _a, _b, _c, _d, _e;
        const productPurposes =
          item.purposes ||
          (null ==
            (_d =
              null ==
                (_c =
                  null ==
                    (_b = null == (_a = item.product) ? void 0 : _a.metafields)
                    ? void 0
                    : _b.itfor)
                ? void 0
                : _c.purpose)
            ? void 0
            : _d.value) ||
          [],
          productTitle =
            item.product_title ||
            (null == (_e = item.product) ? void 0 : _e.title) ||
            item.title,
          hasPurpose = undefined;
        this.productHasPurpose(productPurposes, selectedPurpose) ||
          invalidProducts.push(productTitle);
      }),
      {
        isValid: 0 === invalidProducts.length,
        invalidProducts: invalidProducts,
      }
    );
  }
  static getProductsWithoutPurpose(cartItems, purposeTitle = "自宅用") {
    return cartItems.filter((item) => {
      var _a, _b, _c, _d;
      const productPurposes = undefined,
        hasPurpose = undefined;
      return !(
        item.purposes ||
        (null ==
          (_d =
            null ==
              (_c =
                null == (_b = null == (_a = item.product) ? void 0 : _a.metafields)
                  ? void 0
                  : _b.itfor)
              ? void 0
              : _c.purpose)
          ? void 0
          : _d.value) ||
        []
      ).some((p) => (p.name || p._title) === purposeTitle);
    });
  }
  static getDeliveryPeriodOptions(purpose) {
    const periods = purpose.desired_periods || purpose._deliveryperiod;
    if (!periods) return [];
    if (Array.isArray(periods)) return periods;
    const periodsValue = periods;
    return "string" == typeof periods
      ? periods
        .split(/[,\n]/)
        .map((period) => period.trim())
        .filter((period) => period.length > 0)
      : [];
  }
}
function formatPhoneNumber(phone) {
  if (!phone) return "";
  let cleaned = phone.trim();
  if (/^0[\d]+-[\d]+-[\d]+$/.test(cleaned)) return cleaned;
  cleaned.startsWith("+81") && (cleaned = cleaned.substring(3));
  const digitsOnly = cleaned.replace(/[^\d]/g, "");
  if (!digitsOnly) return "";
  const normalized = digitsOnly.startsWith("0") ? digitsOnly : "0" + digitsOnly;
  return 11 === normalized.length
    ? `${normalized.substring(0, 3)}-${normalized.substring(3, 7)}-${normalized.substring(7, 11)}`
    : 10 === normalized.length
      ? normalized.startsWith("03") ||
        normalized.startsWith("04") ||
        normalized.startsWith("06") ||
        normalized.startsWith("07")
        ? `${normalized.substring(0, 2)}-${normalized.substring(2, 6)}-${normalized.substring(6, 10)}`
        : `${normalized.substring(0, 3)}-${normalized.substring(3, 6)}-${normalized.substring(6, 10)}`
      : normalized;
}
function getDisplayPhone(data) {
  var _a, _b, _c;
  return data
    ? (null == (_a = data.phone_1) ? void 0 : _a.trim()) &&
      (null == (_b = data.phone_2) ? void 0 : _b.trim()) &&
      (null == (_c = data.phone_3) ? void 0 : _c.trim())
      ? `${data.phone_1.trim()}-${data.phone_2.trim()}-${data.phone_3.trim()}`
      : formatPhoneNumber(data.phone)
    : "";
}
if (!customElements.get("wl-tabs")) {
  const tabSelectors = {
    button: "button",
    contentContainer: ".content-container",
  };
  class WlTabs extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "buttons"),
        __publicField(this, "contentContainers"),
        __publicField(this, "__clickListener"),
        __publicField(this, "pendingOrderType", null),
        (this.buttons = this.querySelectorAll(tabSelectors.button)),
        (this.contentContainers = document.querySelectorAll(
          tabSelectors.contentContainer,
        )));
    }
    connectedCallback() {
      ((this.__clickListener = this.handleClick.bind(this)),
        this.buttons.forEach((button) => {
          button.addEventListener("click", this.__clickListener);
        }));
    }
    disconnectedCallback() {
      this.buttons.forEach((button) => {
        button.removeEventListener("click", this.__clickListener);
      });
    }
    handleClick(event) {
      (event.preventDefault(), event.stopPropagation());
      const target = undefined,
        orderType = event.target.dataset.orderType,
        currentActiveButton = Array.from(this.buttons).find((btn) =>
          btn.classList.contains("active"),
        ),
        currentOrderType = undefined;
      if (
        (null == currentActiveButton
          ? void 0
          : currentActiveButton.dataset.orderType) !== orderType
      )
        if (this.hasConfiguredValues()) {
          this.pendingOrderType = orderType;
          const modal = document.querySelector("wl-tab-switch-confirm-modal");
          modal && modal.open(orderType);
        } else this.switchTab(orderType);
    }
    hasConfiguredValues() {
      var _a;
      const multiShippingSettings = document.querySelector(
        "multi-shipping-settings",
      );
      if (!multiShippingSettings) return !1;
      const settings =
        null == (_a = multiShippingSettings.getSettings)
          ? void 0
          : _a.call(multiShippingSettings);
      if (!settings) return !1;
      const hasDeliveryGroups =
        settings.deliveryGroups && settings.deliveryGroups.length > 0,
        hasHomeDestinations =
          settings.homeDestinations && settings.homeDestinations.length > 0;
      if (hasDeliveryGroups) {
        const hasItems = undefined;
        if (
          settings.deliveryGroups.some((group) => {
            var _a2;
            return null == (_a2 = group.destinations)
              ? void 0
              : _a2.some((dest) => dest.items && dest.items.length > 0);
          })
        )
          return !0;
      }
      if (hasHomeDestinations) {
        const hasItems = undefined;
        if (
          settings.homeDestinations.some(
            (dest) => dest.items && dest.items.length > 0,
          )
        )
          return !0;
      }
      return !1;
    }
    confirmTabSwitch() {
      if (this.pendingOrderType) {
        const multiShippingSettings = document.querySelector(
          "multi-shipping-settings",
        );
        (multiShippingSettings &&
          multiShippingSettings.resetAllSettings &&
          multiShippingSettings.resetAllSettings(),
          this.switchTab(this.pendingOrderType),
          (this.pendingOrderType = null));
      }
    }
    cancelTabSwitch() {
      this.pendingOrderType = null;
    }
    switchTab(orderType, skipValidation = !1) {
      var _a, _b, _c;
      if (orderType) {
        if ("home" === orderType && !skipValidation) {
          const allCartItems =
            (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
              ? void 0
              : _a.cartItemsWithPurposes) || [],
            currentCartItems =
              (null ==
                (_c =
                  null == (_b = window.BINDEC_SHIPPING_SETTINGS)
                    ? void 0
                    : _b.cart)
                ? void 0
                : _c.items) || [],
            currentVariantIds = new Set(
              currentCartItems.map((item) => String(item.variant_id)),
            ),
            activeCartItems = allCartItems.filter((item) => {
              const variantId = String(item.variant_id || item.id);
              return currentVariantIds.has(variantId);
            }),
            productsWithoutPurpose = PurposeService.getProductsWithoutPurpose(
              activeCartItems,
              "自宅用",
            );
          if (productsWithoutPurpose.length > 0) {
            const purposeHomeAlertModal = document.querySelector(
              "wl-purpose-home-alert-modal",
            );
            return void (
              purposeHomeAlertModal &&
              purposeHomeAlertModal.open(productsWithoutPurpose)
            );
          }
        }
        (this.buttons.forEach((button) => {
          (button.classList.remove("active"),
            button.classList.add("inactive"),
            button.dataset.orderType === orderType &&
            (button.classList.remove("inactive"),
              button.classList.add("active")));
        }),
          this.contentContainers.forEach((container) => {
            container.style.display =
              orderType === container.dataset.tab ? "block" : "none";
          }),
          this.dispatchEvent(
            new CustomEvent("tab-switched", {
              detail: { orderType: orderType },
              bubbles: !0,
              composed: !0,
            }),
          ));
      }
    }
    resetTabs() {
      (this.buttons.forEach((button) => {
        (button.classList.remove("active"),
          button.classList.remove("inactive"));
      }),
        this.contentContainers.forEach((container) => {
          container.style.display = "none";
        }),
        (this.pendingOrderType = null));
    }
  }
  customElements.define("wl-tabs", WlTabs);
}
if (!customElements.get("wl-purpose-home-alert-modal")) {
  class WlPurposeHomeAlertModal extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "modal"),
        __publicField(this, "productNameElement"),
        __publicField(this, "confirmButton"),
        __publicField(this, "__closeListener"),
        (this.modal = this.querySelector("dialog")),
        (this.productNameElement = this.querySelector(".js-product-title")),
        (this.confirmButton = this.querySelector(".modal-confirm-btn")));
    }
    connectedCallback() {
      ((this.__closeListener = this.close.bind(this)),
        this.confirmButton.addEventListener("click", this.__closeListener));
    }
    disconnectedCallback() {
      this.confirmButton.removeEventListener("click", this.__closeListener);
    }
    open(productsWithoutPurpose) {
      ((this.productNameElement.textContent = productsWithoutPurpose
        .map((product) => product.product_title)
        .join("、")),
        this.modal.showModal());
    }
    close() {
      this.modal.close();
    }
  }
  customElements.define("wl-purpose-home-alert-modal", WlPurposeHomeAlertModal);
}
if (!customElements.get("wl-tab-switch-confirm-modal")) {
  class WlTabSwitchConfirmModal extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "modal"),
        __publicField(this, "confirmButton"),
        __publicField(this, "cancelButton"),
        __publicField(this, "__confirmListener"),
        __publicField(this, "__cancelListener"),
        (this.modal = this.querySelector("dialog")),
        (this.confirmButton = this.querySelector(".modal-confirm-btn")),
        (this.cancelButton = this.querySelector(".modal-cancel-btn")));
    }
    connectedCallback() {
      ((this.__confirmListener = this.confirm.bind(this)),
        (this.__cancelListener = this.cancel.bind(this)),
        this.confirmButton.addEventListener("click", this.__confirmListener),
        this.cancelButton.addEventListener("click", this.__cancelListener));
    }
    disconnectedCallback() {
      (this.confirmButton.removeEventListener("click", this.__confirmListener),
        this.cancelButton.removeEventListener("click", this.__cancelListener));
    }
    open(orderType) {
      this.modal.showModal();
    }
    confirm() {
      this.modal.close();
      const tabsElement = document.querySelector("wl-tabs");
      tabsElement && tabsElement.confirmTabSwitch();
    }
    cancel() {
      this.modal.close();
      const tabsElement = document.querySelector("wl-tabs");
      tabsElement && tabsElement.cancelTabSwitch();
    }
  }
  customElements.define("wl-tab-switch-confirm-modal", WlTabSwitchConfirmModal);
}
function normalizePrefectureName(value) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}
function convertProvinceToJapanese(province) {
  const provinceMapping = undefined;
  return (
    {
      Tokyo: "東京都",
      Hokkaidō: "北海道",
      Hokkaido: "北海道",
      Ōsaka: "大阪府",
      Osaka: "大阪府",
      Kyōto: "京都府",
      Kyoto: "京都府",
      Aichi: "愛知県",
      Akita: "秋田県",
      Aomori: "青森県",
      Chiba: "千葉県",
      Ehime: "愛媛県",
      Fukui: "福井県",
      Fukuoka: "福岡県",
      Fukushima: "福島県",
      Gifu: "岐阜県",
      Gunma: "群馬県",
      Hiroshima: "広島県",
      Hyōgo: "兵庫県",
      Hyogo: "兵庫県",
      Ibaraki: "茨城県",
      Ishikawa: "石川県",
      Iwate: "岩手県",
      Kagawa: "香川県",
      Kagoshima: "鹿児島県",
      Kanagawa: "神奈川県",
      Kōchi: "高知県",
      Kochi: "高知県",
      Kumamoto: "熊本県",
      Mie: "三重県",
      Miyagi: "宮城県",
      Miyazaki: "宮崎県",
      Nagano: "長野県",
      Nagasaki: "長崎県",
      Nara: "奈良県",
      Niigata: "新潟県",
      Ōita: "大分県",
      Oita: "大分県",
      Okayama: "岡山県",
      Okinawa: "沖縄県",
      Saga: "佐賀県",
      Saitama: "埼玉県",
      Shiga: "滋賀県",
      Shimane: "島根県",
      Shizuoka: "静岡県",
      Tochigi: "栃木県",
      Tokushima: "徳島県",
      Tottori: "鳥取県",
      Toyama: "富山県",
      Wakayama: "和歌山県",
      Yamagata: "山形県",
      Yamaguchi: "山口県",
      Yamanashi: "山梨県",
    }[province] || province
  );
}
function convertJapanesePrefectureToCode(prefecture) {
  const japaneseToISO = {
    北海道: "JP-01",
    青森県: "JP-02",
    岩手県: "JP-03",
    宮城県: "JP-04",
    秋田県: "JP-05",
    山形県: "JP-06",
    福島県: "JP-07",
    茨城県: "JP-08",
    栃木県: "JP-09",
    群馬県: "JP-10",
    埼玉県: "JP-11",
    千葉県: "JP-12",
    東京都: "JP-13",
    神奈川県: "JP-14",
    新潟県: "JP-15",
    富山県: "JP-16",
    石川県: "JP-17",
    福井県: "JP-18",
    山梨県: "JP-19",
    長野県: "JP-20",
    岐阜県: "JP-21",
    静岡県: "JP-22",
    愛知県: "JP-23",
    三重県: "JP-24",
    滋賀県: "JP-25",
    京都府: "JP-26",
    大阪府: "JP-27",
    兵庫県: "JP-28",
    奈良県: "JP-29",
    和歌山県: "JP-30",
    鳥取県: "JP-31",
    島根県: "JP-32",
    岡山県: "JP-33",
    広島県: "JP-34",
    山口県: "JP-35",
    徳島県: "JP-36",
    香川県: "JP-37",
    愛媛県: "JP-38",
    高知県: "JP-39",
    福岡県: "JP-40",
    佐賀県: "JP-41",
    長崎県: "JP-42",
    熊本県: "JP-43",
    大分県: "JP-44",
    宮崎県: "JP-45",
    鹿児島県: "JP-46",
    沖縄県: "JP-47",
  },
    englishToISO = {
      Tokyo: "JP-13",
      Hokkaidō: "JP-01",
      Hokkaido: "JP-01",
      Ōsaka: "JP-27",
      Osaka: "JP-27",
      Kyōto: "JP-26",
      Kyoto: "JP-26",
      Aichi: "JP-23",
      Akita: "JP-05",
      Aomori: "JP-02",
      Chiba: "JP-12",
      Ehime: "JP-38",
      Fukui: "JP-18",
      Fukuoka: "JP-40",
      Fukushima: "JP-07",
      Gifu: "JP-21",
      Gunma: "JP-10",
      Hiroshima: "JP-34",
      Hyōgo: "JP-28",
      Hyogo: "JP-28",
      Ibaraki: "JP-08",
      Ishikawa: "JP-17",
      Iwate: "JP-03",
      Kagawa: "JP-37",
      Kagoshima: "JP-46",
      Kanagawa: "JP-14",
      Kōchi: "JP-39",
      Kochi: "JP-39",
      Kumamoto: "JP-43",
      Mie: "JP-24",
      Miyagi: "JP-04",
      Miyazaki: "JP-45",
      Nagano: "JP-20",
      Nagasaki: "JP-42",
      Nara: "JP-29",
      Niigata: "JP-15",
      Ōita: "JP-44",
      Oita: "JP-44",
      Okayama: "JP-33",
      Okinawa: "JP-47",
      Saga: "JP-41",
      Saitama: "JP-11",
      Shiga: "JP-25",
      Shimane: "JP-32",
      Shizuoka: "JP-22",
      Tochigi: "JP-09",
      Tokushima: "JP-36",
      Tottori: "JP-31",
      Toyama: "JP-16",
      Wakayama: "JP-30",
      Yamagata: "JP-06",
      Yamaguchi: "JP-35",
      Yamanashi: "JP-19",
    },
    normalizedEnglishToISO = {};
  if (
    (Object.entries(englishToISO).forEach(([key, value]) => {
      normalizedEnglishToISO[normalizePrefectureName(key)] = value;
    }),
      japaneseToISO[prefecture])
  )
    return japaneseToISO[prefecture];
  if (englishToISO[prefecture]) return englishToISO[prefecture];
  const normalizedPrefecture = normalizePrefectureName(prefecture);
  return normalizedEnglishToISO[normalizedPrefecture]
    ? normalizedEnglishToISO[normalizedPrefecture]
    : prefecture;
}
function getStreetPartForDisplay(addr) {
  var _a, _b, _c;
  const town = (null == (_a = addr.town) ? void 0 : _a.trim()) ?? "",
    line1 = (null == (_b = addr.address_line1) ? void 0 : _b.trim()) ?? "";
  return town || line1
    ? `${town} ${line1}`.trim()
    : ((null == (_c = addr.address1) ? void 0 : _c.trim()) ?? "");
}
class CustomerService {
  static async getCustomerFromCart() {
    var _a;
    try {
      const cart2 = await cartCache.get();
      return (
        null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.customer
      )
        ? window.BINDEC_SHIPPING_SETTINGS.customer
        : null;
    } catch (error) {
      return null;
    }
  }
  static getCustomerFromGlobal() {
    var _a, _b;
    return (
      null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.customer
    )
      ? window.BINDEC_SHIPPING_SETTINGS.customer
      : (null == (_b = window.Shopify) ? void 0 : _b.customer)
        ? window.Shopify.customer
        : null;
  }
  static getCustomerId() {
    var _a;
    const customer = this.getCustomerFromGlobal();
    return (
      (null == (_a = null == customer ? void 0 : customer.id)
        ? void 0
        : _a.toString()) || null
    );
  }
  static getShopifyCustomerId() {
    const customer = this.getCustomerFromGlobal();
    return (null == customer ? void 0 : customer.id)
      ? "string" == typeof customer.id &&
        customer.id.startsWith("gid://shopify/Customer/")
        ? customer.id
        : `gid://shopify/Customer/${customer.id}`
      : null;
  }
  static getCustomerBasicInfo() {
    var _a, _b;
    const customer = this.getCustomerFromGlobal();
    return customer
      ? {
        customerId:
          (null == (_a = customer.id) ? void 0 : _a.toString()) || null,
        shopifyCustomerId: this.getShopifyCustomerId(),
        email: customer.email || null,
        firstName: customer.first_name || null,
        lastName: customer.last_name || null,
        name:
          customer.first_name && customer.last_name
            ? `${customer.last_name} ${customer.first_name}`
            : null,
        phone:
          customer.phone ||
          (null == (_b = customer.default_address) ? void 0 : _b.phone) ||
          null,
      }
      : {
        customerId: null,
        shopifyCustomerId: null,
        email: null,
        firstName: null,
        lastName: null,
        name: null,
        phone: null,
      };
  }
  static getDefaultAddress() {
    const customer = this.getCustomerFromGlobal();
    return (null == customer ? void 0 : customer.default_address) || null;
  }
  static isAuthenticated() {
    const customer = this.getCustomerFromGlobal();
    return !!(null == customer ? void 0 : customer.id);
  }
}
if (!customElements.get("sender-selector")) {
  const selectors = {
    openButton: ".btn-edit",
    addressModal: "address-modal",
    selectedDepartment: ".js-selected-department",
    selectedSenderName: ".js-selected-sender",
    selectedSenderNameKana: ".js-selected-sender-kana",
    selectedSenderHonorific: ".js-selected-sender-honorific",
    selectedSenderAddress: ".js-selected-sender-address",
    selectedSenderPhone: ".js-selected-sender-phone",
  };
  class SenderSelector extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "openButton"),
        __publicField(this, "addressModal"),
        __publicField(this, "selectedDepartment"),
        __publicField(this, "selectedSenderName"),
        __publicField(this, "selectedSenderNameKana"),
        __publicField(this, "selectedSenderHonorific"),
        __publicField(this, "selectedSenderAddress"),
        __publicField(this, "selectedSenderPhone"),
        __publicField(this, "__openListener"),
        __publicField(this, "currentSender", null),
        __publicField(this, "restoredAddress", null),
        (this.openButton = this.querySelector(selectors.openButton)),
        (this.selectedSenderName = this.querySelector(
          selectors.selectedSenderName,
        )),
        (this.selectedSenderNameKana = this.querySelector(
          selectors.selectedSenderNameKana,
        )),
        (this.selectedDepartment = this.querySelector(
          selectors.selectedDepartment,
        )),
        (this.selectedSenderHonorific = this.querySelector(
          selectors.selectedSenderHonorific,
        )),
        (this.selectedSenderAddress = this.querySelector(
          selectors.selectedSenderAddress,
        )),
        (this.selectedSenderPhone = this.querySelector(
          selectors.selectedSenderPhone,
        )));
    }
    connectedCallback() {
      (this.createAddressModal(),
        this.initializeSender(),
        (this.__openListener = this.open.bind(this)),
        this.openButton.addEventListener("click", this.__openListener));
    }
    disconnectedCallback() {
      this.openButton.removeEventListener("click", this.__openListener);
    }
    refreshElementReferences() {
      (this.openButton ||
        (this.openButton = this.querySelector(selectors.openButton)),
        this.selectedSenderName ||
        (this.selectedSenderName = this.querySelector(
          selectors.selectedSenderName,
        )),
        this.selectedSenderNameKana ||
        (this.selectedSenderNameKana = this.querySelector(
          selectors.selectedSenderNameKana,
        )),
        this.selectedSenderHonorific ||
        (this.selectedSenderHonorific = this.querySelector(
          selectors.selectedSenderHonorific,
        )),
        this.selectedDepartment ||
        (this.selectedDepartment = this.querySelector(
          selectors.selectedDepartment,
        )),
        this.selectedSenderAddress ||
        (this.selectedSenderAddress = this.querySelector(
          selectors.selectedSenderAddress,
        )),
        this.selectedSenderPhone ||
        (this.selectedSenderPhone = this.querySelector(
          selectors.selectedSenderPhone,
        )));
    }
    createAddressModal() {
      ((this.addressModal = document.createElement("address-modal")),
        document.body.appendChild(this.addressModal));
    }
    open(event) {
      var _a, _b, _c, _d, _e, _f;
      const allAddresses =
        (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
          ? void 0
          : _a.addresses) || [],
        customDataAddresses =
          null ==
            (_c =
              null == (_b = window.BINDEC_SHIPPING_SETTINGS)
                ? void 0
                : _b.addressCustomData)
            ? void 0
            : _c.addresses,
        senderAddressIds = new Set();
      Array.isArray(customDataAddresses) &&
        customDataAddresses.forEach((entry) => {
          var _a2, _b2;
          const isSender = undefined;
          (!0 ===
            (null == (_a2 = null == entry ? void 0 : entry.data)
              ? void 0
              : _a2.sender) ||
            "true" ===
            (null == (_b2 = null == entry ? void 0 : entry.data)
              ? void 0
              : _b2.sender)) &&
            null != (null == entry ? void 0 : entry.id) &&
            senderAddressIds.add(String(entry.id));
        });
      const addresses = undefined,
        currentAddressId = undefined,
        config = {
          title: "配送設定",
          mode: "sender",
          addresses: allAddresses.filter((addr) =>
            senderAddressIds.has(String(addr.id)),
          ),
          selectedAddressId:
            null ==
              (_f =
                null ==
                  (_e =
                    null == (_d = this.currentSender)
                      ? void 0
                      : _d.shopifyAddressId)
                  ? void 0
                  : _e.match(/\/(\d+)$/))
              ? void 0
              : _f[1],
          restoredAddress: this.restoredAddress || void 0,
          onSelect: (address) => {
            this.handleAddressSelected(address);
          },
        };
      this.addressModal.open(config);
    }
    handleAddressSelected(address) {
      var _a, _b, _c, _d, _e;
      this.refreshElementReferences();
      const addressCustomData =
        (null ==
          (_d =
            null ==
              (_c =
                null ==
                  (_b =
                    null == (_a = window.BINDEC_SHIPPING_SETTINGS)
                      ? void 0
                      : _a.addressCustomData)
                  ? void 0
                  : _b.addresses)
              ? void 0
              : _c.find((item) => item.id === address.id))
          ? void 0
          : _d.data) || {};
      address = { ...address, ...(addressCustomData || {}) };
      const customEvent = new CustomEvent("settings-change", {
        bubbles: !0,
        detail: {
          type: "sender-selected",
          data: {
            groupId:
              null == (_e = this.closest("delivery-group"))
                ? void 0
                : _e.getAttribute("data-delivery-group-id"),
            sender: {
              name: `${address.last_name} ${address.first_name}`,
              firstName: address.first_name,
              lastName: address.last_name,
              firstNameKana: address.first_name_kana || "",
              lastNameKana: address.last_name_kana || "",
              honorific: address.honorific,
              shopifyAddressId: `gid://shopify/MailingAddress/${address.id}`,
              company: address.company,
              department: address.department || address.job_title || "",
              email: address.email,
              phone: getDisplayPhone(address),
              address: {
                zipcode: address.zip,
                prefecture: address.province,
                city: address.city,
                town: address.town ?? void 0,
                address_line1: address.address_line1 ?? void 0,
                address1: address.address1,
                address2: address.address2,
              },
            },
          },
        },
      });
      if (
        (this.dispatchEvent(customEvent),
          this.selectedSenderName &&
          (this.selectedSenderName.textContent = `${address.last_name} ${address.first_name}`),
          this.selectedSenderNameKana)
      ) {
        const kana =
          `${address.last_name_kana || ""} ${address.first_name_kana || ""}`.trim();
        this.selectedSenderNameKana.textContent = kana || "";
      }
      if (this.selectedSenderHonorific) {
        const honorificDisplay =
          address.honorific && String(address.honorific).trim()
            ? ` ${String(address.honorific).trim()}`
            : " 様";
        this.selectedSenderHonorific.textContent = honorificDisplay;
      }
      if (
        (this.selectedDepartment &&
          (this.selectedDepartment.textContent = `${address.company || ""} ${address.department || address.job_title || ""}`),
          this.selectedSenderAddress)
      ) {
        const streetPart = getStreetPartForDisplay(address);
        this.selectedSenderAddress.textContent =
          `住所：〒 ${address.zip} ${convertProvinceToJapanese(address.province)} ${address.city} ${streetPart} ${address.address2 || ""}`.trim();
      }
      (this.selectedSenderPhone &&
        (this.selectedSenderPhone.textContent = `電話番号：${getDisplayPhone(address)}`),
        (this.currentSender = {
          name: `${address.last_name} ${address.first_name}`,
          firstName: address.first_name,
          lastName: address.last_name,
          firstNameKana: address.first_name_kana || "",
          lastNameKana: address.last_name_kana || "",
          honorific: address.honorific,
          shopifyAddressId: `gid://shopify/MailingAddress/${address.id}`,
          company: address.company,
          department: address.department || address.job_title || "",
          email: address.email,
          phone: getDisplayPhone(address),
          address: {
            zipcode: address.zip,
            prefecture: address.province,
            city: address.city,
            town: address.town ?? void 0,
            address_line1: address.address_line1 ?? void 0,
            address1: address.address1,
            address2: address.address2,
          },
        }));
    }
    restoreSender(sender) {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o;
      if (!sender) return;
      let resolvedSender = sender;
      if (
        (!sender.honorific || "" === sender.honorific) &&
        sender.shopifyAddressId
      ) {
        const addressId =
          (null == (_a = sender.shopifyAddressId.match(/(\d+)$/))
            ? void 0
            : _a[1]) ?? sender.shopifyAddressId,
          addresses =
            null ==
              (_c =
                null == (_b = window.BINDEC_SHIPPING_SETTINGS)
                  ? void 0
                  : _b.addressCustomData)
              ? void 0
              : _c.addresses;
        if (Array.isArray(addresses)) {
          const item = addresses.find(
            (a) => null != a.id && String(a.id) === String(addressId),
          ),
            honorific =
              null == (_d = null == item ? void 0 : item.data)
                ? void 0
                : _d.honorific;
          null != honorific &&
            "" !== honorific &&
            (resolvedSender = { ...sender, honorific: String(honorific) });
        }
      }
      if (
        (this.refreshElementReferences(),
          (this.currentSender = {
            ...resolvedSender,
            phone: getDisplayPhone(resolvedSender) || resolvedSender.phone || "",
          }),
          this.selectedSenderName &&
          (this.selectedSenderName.textContent = `${resolvedSender.lastName || ""} ${resolvedSender.firstName || ""}`),
          this.selectedSenderNameKana)
      ) {
        const kana =
          `${resolvedSender.lastNameKana || ""} ${resolvedSender.firstNameKana || ""}`.trim();
        this.selectedSenderNameKana.textContent = kana || "";
      }
      if (this.selectedSenderHonorific) {
        const honorificDisplay =
          resolvedSender.honorific && String(resolvedSender.honorific).trim()
            ? ` ${String(resolvedSender.honorific).trim()}`
            : " 様";
        this.selectedSenderHonorific.textContent = honorificDisplay;
      }
      if (
        (this.selectedDepartment &&
          (this.selectedDepartment.textContent = `${resolvedSender.company || ""} ${resolvedSender.department || ""}`),
          this.selectedSenderAddress)
      ) {
        const streetPart = resolvedSender.address
          ? getStreetPartForDisplay(resolvedSender.address)
          : "";
        this.selectedSenderAddress.textContent =
          `住所：〒 ${(null == (_e = resolvedSender.address) ? void 0 : _e.zipcode) || ""} ${convertProvinceToJapanese((null == (_f = resolvedSender.address) ? void 0 : _f.prefecture) || "")} ${(null == (_g = resolvedSender.address) ? void 0 : _g.city) || ""} ${streetPart} ${(null == (_h = resolvedSender.address) ? void 0 : _h.address2) || ""}`.trim();
      }
      this.selectedSenderPhone &&
        (this.selectedSenderPhone.textContent = `電話番号：${getDisplayPhone(resolvedSender)}`);
      const isAuthenticated = CustomerService.isAuthenticated();
      this.restoredAddress =
        !isAuthenticated && resolvedSender
          ? {
            id: `temp_${Date.now()}`,
            first_name: resolvedSender.firstName || "",
            last_name: resolvedSender.lastName || "",
            first_name_kana: resolvedSender.firstNameKana || "",
            last_name_kana: resolvedSender.lastNameKana || "",
            honorific: resolvedSender.honorific || "",
            company: resolvedSender.company || "",
            department: resolvedSender.department || "",
            email: resolvedSender.email || "",
            phone: resolvedSender.phone || "",
            zip:
              (null == (_i = resolvedSender.address) ? void 0 : _i.zipcode) ||
              "",
            province:
              (null == (_j = resolvedSender.address)
                ? void 0
                : _j.prefecture) || "",
            city:
              (null == (_k = resolvedSender.address) ? void 0 : _k.city) ||
              "",
            town: null == (_l = resolvedSender.address) ? void 0 : _l.town,
            address_line1:
              null == (_m = resolvedSender.address)
                ? void 0
                : _m.address_line1,
            address1:
              (null == (_n = resolvedSender.address)
                ? void 0
                : _n.address1) || "",
            address2:
              (null == (_o = resolvedSender.address)
                ? void 0
                : _o.address2) || "",
          }
          : null;
    }
    getCurrentSender() {
      return this.currentSender;
    }
    initializeSender() {
      var _a, _b, _c, _d, _e, _f;
      const profileAddressId =
        null ==
          (_b =
            null == (_a = window.BINDEC_SHIPPING_SETTINGS)
              ? void 0
              : _a.profileAddress)
          ? void 0
          : _b.id,
        defaultAddressId =
          null == (_c = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _c.defaultAddressId,
        targetAddressId = profileAddressId ?? defaultAddressId,
        addresses = undefined,
        selectedAddress = (
          (null == (_d = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _d.addresses) || []
        ).find((address) => address.id === targetAddressId);
      if (selectedAddress) {
        const addressCustomData =
          null ==
            (_f =
              null == (_e = window.BINDEC_SHIPPING_SETTINGS)
                ? void 0
                : _e.addressCustomData)
            ? void 0
            : _f.addresses;
        if (Array.isArray(addressCustomData)) {
          const customData = addressCustomData.find(
            (a) => null != a.id && String(a.id) === String(targetAddressId),
          );
          (null == customData ? void 0 : customData.data) &&
            (customData.data.first_name_kana &&
              (selectedAddress.first_name_kana =
                customData.data.first_name_kana),
              customData.data.last_name_kana &&
              (selectedAddress.last_name_kana = customData.data.last_name_kana),
              customData.data.honorific &&
              (selectedAddress.honorific = customData.data.honorific),
              customData.data.job_title &&
              (selectedAddress.job_title = customData.data.job_title));
        }
        this.handleAddressSelected(selectedAddress);
      }
    }
  }
  customElements.define("sender-selector", SenderSelector);
}
const shippingClassificationLabels = (value, isList = !1) => {
  const classificationLabels = [
    { label: "通常配送", value: "regular" },
    { label: "定額配送", value: "flat_rate" },
    { label: "送料無料", value: "free" },
    { label: "送料込", value: "included" },
  ];
  if (isList) return classificationLabels;
  const target = classificationLabels.find(
    (classificationLabel) => classificationLabel.value == value,
  );
  return (null == target ? void 0 : target.label) || value;
},
  shippingMethodLabels = (value, isList = !1) => {
    const methodLabels = [
      { label: "常温", value: "normal" },
      { label: "冷蔵", value: "cool" },
      { label: "冷凍", value: "cold" },
      { label: "同梱不可", value: "noBundle" },
    ];
    if (isList) return methodLabels;
    const target = methodLabels.find(
      (methodLabel) => methodLabel.value == value,
    );
    return (null == target ? void 0 : target.label) || value;
  },
  shippingCompanyLabels = (value, isList = !1) => {
    const companyLabels = [
      { label: "ヤマト運輸", value: "yamato" },
      { label: "佐川急便", value: "sagawa" },
      { label: "日本郵便", value: "japan_post" },
      { label: "西濃運輸", value: "seino" },
    ];
    if (isList) return companyLabels;
    const target = companyLabels.find(
      (companyLabel) => companyLabel.value == value,
    );
    return (null == target ? void 0 : target.label) || value;
  },
  shippingRuleStatusLabels = (value, isList = !1) => {
    const statusLabels = [
      { label: "有効", value: !0 },
      { label: "無効", value: !1 },
    ];
    return isList ? statusLabels : statusLabels[value] || value;
  },
  shippingRuleDefaultLabels = (value, isList = !1) => {
    const defaultLabels = [
      { label: "有効", value: !0 },
      { label: "無効", value: !1 },
    ];
    return isList ? defaultLabels : defaultLabels[value] || value;
  },
  FREE_SHIPPING_OPTIONS = [
    { label: "送料無料なし", value: "" },
    { label: "注文合計金額", value: "order" },
    { label: "注文合計送料金額", value: "shipping_cost" },
  ],
  MULTIPLE_POLICY_OPTIONS = [
    { label: "送料積上げ", value: "sum" },
    { label: "一番送料が高い", value: "highest" },
    { label: "一番送料が安い", value: "cheapest" },
  ],
  SHIPPING_CLASSIFICATION_OPTIONS = [
    { label: "一般", value: "regular" },
    { label: "一律", value: "flat_rate" },
    { label: "送料無料", value: "free" },
    { label: "送料込", value: "included" },
  ],
  shippingMethodOptions = () => [
    { label: "常温", value: "normal" },
    { label: "冷蔵", value: "cool" },
    { label: "冷凍", value: "cold" },
    { label: "特殊", value: "noBundle" },
  ],
  SHIPPING_METHOD_OPTIONS = [
    { label: "常温", value: "normal" },
    { label: "冷蔵", value: "cool" },
    { label: "冷凍", value: "cold" },
    { label: "特殊", value: "noBundle" },
  ],
  shippingCompanyOptions = () => [
    { label: "ヤマト運輸", value: "yamato" },
    { label: "佐川急便", value: "sagawa" },
    { label: "日本郵便", value: "japanpost" },
    { label: "西濃運輸", value: "seino" },
    { label: "その他", value: "other" },
  ],
  SHIPPING_COMPANY_OPTIONS = [
    { label: "ヤマト運輸", value: "yamato" },
    { label: "佐川急便", value: "sagawa" },
    { label: "日本郵便", value: "japanpost" },
    { label: "西濃運輸", value: "seino" },
    { label: "その他", value: "other" },
  ],
  DELIVERY_SLIP_TYPE_OPTIONS = [
    { label: "ヤマトB2（宅急便）", value: "yamato-b2-takkyubin" },
    { label: "ヤマトB2（クール）", value: "yamato-b2-cool" },
    { label: "ヤマトB2（冷凍）", value: "yamato-b2-cold" },
    { label: "ヤマトWeb出荷（宅急便）", value: "yamato_web_takkyubin" },
    { label: "ヤマトWeb出荷（クール）", value: "yamato_web_cool" },
    { label: "ヤマトWeb出荷（冷凍）", value: "yamato_web_cold" },
  ],
  deliverySlipTypeLabels = (value, isList = !1) => {
    const slipTypeLabels = [
      { label: "ヤマトB2（宅急便）", value: "yamato-b2-takkyubin" },
      { label: "ヤマトB2（クール）", value: "yamato-b2-cool" },
      { label: "ヤマトB2（冷凍）", value: "yamato-b2-cold" },
      { label: "ヤマトWeb出荷（宅急便）", value: "yamato_web_takkyubin" },
      { label: "ヤマトWeb出荷（クール）", value: "yamato_web_cool" },
      { label: "ヤマトWeb出荷（冷凍）", value: "yamato_web_cold" },
    ];
    if (isList) return slipTypeLabels;
    const target = slipTypeLabels.find((slipType) => slipType.value == value);
    return (null == target ? void 0 : target.label) || value;
  },
  DELIVERY_SLIP_TYPE_TO_COMPANY = {
    "yamato-b2-takkyubin": "yamato",
    "yamato-b2-cool": "yamato",
    "yamato-b2-cold": "yamato",
    yamato_web_takkyubin: "yamato",
    yamato_web_cool: "yamato",
    yamato_web_cold: "yamato",
  };
function getDeliverySlipTypes(rule) {
  return rule.delivery_slip_types &&
    Array.isArray(rule.delivery_slip_types) &&
    rule.delivery_slip_types.length > 0
    ? rule.delivery_slip_types
    : rule.delivery_slip_type
      ? [rule.delivery_slip_type]
      : [];
}
function getMainDeliverySlipType(rule) {
  const slipTypes = undefined;
  return getDeliverySlipTypes(rule)[0] || null;
}
function getCompanyFromSlipType(slipType) {
  return DELIVERY_SLIP_TYPE_TO_COMPANY[slipType] || null;
}
function compareSlipTypePriority(slipType1, slipType2, priorityList) {
  const index1 = priorityList.indexOf(slipType1),
    index2 = priorityList.indexOf(slipType2);
  return -1 === index1 && -1 === index2
    ? 0
    : -1 === index1
      ? 1
      : -1 === index2
        ? -1
        : index1 - index2;
}
function mapDeliveryClsfToShippingMethod(deliveryClsf) {
  switch (deliveryClsf) {
    case "常温":
    default:
      return "normal";
    case "冷蔵":
      return "cool";
    case "冷凍":
      return "cold";
    case "同梱不可":
      return "noBundle";
  }
}
function getCompanyFromSlipTypeMaster(slipTypeHandle, slipTypeMaster) {
  var _a;
  return (
    null ==
      (_a = null == slipTypeMaster ? void 0 : slipTypeMaster[slipTypeHandle])
      ? void 0
      : _a.carrier_name
  )
    ? slipTypeMaster[slipTypeHandle].carrier_name
    : DELIVERY_SLIP_TYPE_TO_COMPANY[slipTypeHandle] || null;
}
function normalizeSlipTypeIds(slipTypeId) {
  return slipTypeId
    ? Array.isArray(slipTypeId)
      ? slipTypeId
      : [slipTypeId]
    : [];
}
function selectPrimarySlipType(slipTypeId) {
  if (slipTypeId)
    return Array.isArray(slipTypeId) ? slipTypeId[0] || void 0 : slipTypeId;
}
const ISO_TO_ENGLISH_CODE_MAPPING = {
  "JP-01": "hokkaido",
  "JP-02": "aomori",
  "JP-03": "iwate",
  "JP-04": "miyagi",
  "JP-05": "akita",
  "JP-06": "yamagata",
  "JP-07": "fukushima",
  "JP-08": "ibaraki",
  "JP-09": "tochigi",
  "JP-10": "gunma",
  "JP-11": "saitama",
  "JP-12": "chiba",
  "JP-13": "tokyo",
  "JP-14": "kanagawa",
  "JP-15": "niigata",
  "JP-16": "toyama",
  "JP-17": "ishikawa",
  "JP-18": "fukui",
  "JP-19": "yamanashi",
  "JP-20": "nagano",
  "JP-21": "gifu",
  "JP-22": "shizuoka",
  "JP-23": "aichi",
  "JP-24": "mie",
  "JP-25": "shiga",
  "JP-26": "kyoto",
  "JP-27": "osaka",
  "JP-28": "hyogo",
  "JP-29": "nara",
  "JP-30": "wakayama",
  "JP-31": "tottori",
  "JP-32": "shimane",
  "JP-33": "okayama",
  "JP-34": "hiroshima",
  "JP-35": "yamaguchi",
  "JP-36": "tokushima",
  "JP-37": "kagawa",
  "JP-38": "ehime",
  "JP-39": "kochi",
  "JP-40": "fukuoka",
  "JP-41": "saga",
  "JP-42": "nagasaki",
  "JP-43": "kumamoto",
  "JP-44": "oita",
  "JP-45": "miyazaki",
  "JP-46": "kagoshima",
  "JP-47": "okinawa",
},
  ENGLISH_TO_ISO_CODE_MAPPING = Object.fromEntries(
    Object.entries(ISO_TO_ENGLISH_CODE_MAPPING).map(([iso, eng]) => [eng, iso]),
  ),
  PREFECTURES_hokkaido = "北海道",
  PREFECTURES_aomori = "青森県",
  PREFECTURES_iwate = "岩手県",
  PREFECTURES_miyagi = "宮城県",
  PREFECTURES_akita = "秋田県",
  PREFECTURES_yamagata = "山形県",
  PREFECTURES_fukushima = "福島県",
  PREFECTURES_ibaraki = "茨城県",
  PREFECTURES_tochigi = "栃木県",
  PREFECTURES_gunma = "群馬県",
  PREFECTURES_saitama = "埼玉県",
  PREFECTURES_chiba = "千葉県",
  PREFECTURES_tokyo = "東京都",
  PREFECTURES_kanagawa = "神奈川県",
  PREFECTURES_niigata = "新潟県",
  PREFECTURES_toyama = "富山県",
  PREFECTURES_ishikawa = "石川県",
  PREFECTURES_fukui = "福井県",
  PREFECTURES_yamanashi = "山梨県",
  PREFECTURES_nagano = "長野県",
  PREFECTURES_gifu = "岐阜県",
  PREFECTURES_shizuoka = "静岡県",
  PREFECTURES_aichi = "愛知県",
  PREFECTURES_mie = "三重県",
  PREFECTURES_shiga = "滋賀県",
  PREFECTURES_kyoto = "京都府",
  PREFECTURES_osaka = "大阪府",
  PREFECTURES_hyogo = "兵庫県",
  PREFECTURES_nara = "奈良県",
  PREFECTURES_wakayama = "和歌山県",
  PREFECTURES_tottori = "鳥取県",
  PREFECTURES_shimane = "島根県",
  PREFECTURES_okayama = "岡山県",
  PREFECTURES_hiroshima = "広島県",
  PREFECTURES_yamaguchi = "山口県",
  PREFECTURES_tokushima = "徳島県",
  PREFECTURES_kagawa = "香川県",
  PREFECTURES_ehime = "愛媛県",
  PREFECTURES_kochi = "高知県",
  PREFECTURES_fukuoka = "福岡県",
  PREFECTURES_saga = "佐賀県",
  PREFECTURES_nagasaki = "長崎県",
  PREFECTURES_kumamoto = "熊本県",
  PREFECTURES_oita = "大分県",
  PREFECTURES_miyazaki = "宮崎県",
  PREFECTURES_kagoshima = "鹿児島県",
  PREFECTURES_okinawa = "沖縄県",
  DEFAULT_DELIVERY_SCHEDULE_SETTINGS = undefined,
  DEFAULT_SHIPPING_SETTING_id = "",
  DEFAULT_SHIPPING_SETTING_shipping_status = !0,
  DEFAULT_SHIPPING_SETTING_shipping_classification = "regular",
  DEFAULT_SHIPPING_SETTING_shipping_method = "normal",
  DEFAULT_SHIPPING_SETTING_shipping_company = "yamato",
  DEFAULT_SHIPPING_SETTING_shipping_table = [],
  DEFAULT_SHIPPING_SETTING_default_flg = !1,
  DEFAULT_SHIPPING_SETTING_title = "",
  DEFAULT_SHIPPING_SETTING_excluded_area = "",
  DEFAULT_SHIPPING_SETTING_cod_excluded_area = "",
  DEFAULT_SHIPPING_SETTING_flat_rate_shipping_amount = "",
  DEFAULT_SHIPPING_SETTING_delivery_slip_type = "yamato-b2-takkyubin",
  DEFAULT_SHIPPING_SETTING_multiple_shipping_policy = "sum",
  DEFAULT_SHIPPING_SETTING_deliveryScheduleSettings = {
    defaultLeadDays: 2,
    prefectureLeadTimes: [],
    islandLeadTimes: [],
    deliveryTimeSlots: [],
    regularHolidays: [],
    annualHolidays: [],
    specificHolidays: [],
  },
  PREFECTURE_TO_PROVINCE_CODE_MAPPING = {
    北海道: "JP-01",
    Hokkaidō: "JP-01",
    青森県: "JP-02",
    Aomori: "JP-02",
    岩手県: "JP-03",
    Iwate: "JP-03",
    宮城県: "JP-04",
    Miyagi: "JP-04",
    秋田県: "JP-05",
    Akita: "JP-05",
    山形県: "JP-06",
    Yamagata: "JP-06",
    福島県: "JP-07",
    Fukushima: "JP-07",
    茨城県: "JP-08",
    Ibaraki: "JP-08",
    栃木県: "JP-09",
    Tochigi: "JP-09",
    群馬県: "JP-10",
    Gunma: "JP-10",
    埼玉県: "JP-11",
    Saitama: "JP-11",
    千葉県: "JP-12",
    Chiba: "JP-12",
    東京都: "JP-13",
    Tokyo: "JP-13",
    Tōkyō: "JP-13",
    神奈川県: "JP-14",
    Kanagawa: "JP-14",
    新潟県: "JP-15",
    Niigata: "JP-15",
    富山県: "JP-16",
    Toyama: "JP-16",
    石川県: "JP-17",
    Ishikawa: "JP-17",
    福井県: "JP-18",
    Fukui: "JP-18",
    山梨県: "JP-19",
    Yamanashi: "JP-19",
    長野県: "JP-20",
    Nagano: "JP-20",
    岐阜県: "JP-21",
    Gifu: "JP-21",
    静岡県: "JP-22",
    Shizuoka: "JP-22",
    愛知県: "JP-23",
    Aichi: "JP-23",
    三重県: "JP-24",
    Mie: "JP-24",
    滋賀県: "JP-25",
    Shiga: "JP-25",
    京都府: "JP-26",
    Kyoto: "JP-26",
    大阪府: "JP-27",
    Osaka: "JP-27",
    兵庫県: "JP-28",
    Hyogo: "JP-28",
    奈良県: "JP-29",
    Nara: "JP-29",
    和歌山県: "JP-30",
    Wakayama: "JP-30",
    鳥取県: "JP-31",
    Tottori: "JP-31",
    島根県: "JP-32",
    Shimane: "JP-32",
    岡山県: "JP-33",
    Okayama: "JP-33",
    広島県: "JP-34",
    Hiroshima: "JP-34",
    山口県: "JP-35",
    Yamaguchi: "JP-35",
    徳島県: "JP-36",
    Tokushima: "JP-36",
    香川県: "JP-37",
    Kagawa: "JP-37",
    愛媛県: "JP-38",
    Ehime: "JP-38",
    高知県: "JP-39",
    Kochi: "JP-39",
    福岡県: "JP-40",
    Fukuoka: "JP-40",
    佐賀県: "JP-41",
    Saga: "JP-41",
    長崎県: "JP-42",
    Nagasaki: "JP-42",
    熊本県: "JP-43",
    Kumamoto: "JP-43",
    大分県: "JP-44",
    Oita: "JP-44",
    宮崎県: "JP-45",
    Miyazaki: "JP-45",
    鹿児島県: "JP-46",
    Kagoshima: "JP-46",
    沖縄県: "JP-47",
    Okinawa: "JP-47",
  };
class ShippingUnitSeparator {
  constructor() { }
  hasMultipleDeliverySlipTypes(shippingData) {
    return shippingData.some((rule) => {
      const slipTypes = undefined;
      return getDeliverySlipTypes(rule).length > 1;
    });
  }
  separateWithMultipleSlipTypes(items, shippingData, variantShippingRuleIds) {
    const shippingUnits = {};
    let unitIndex = 0;
    const slipTypeGroups = this._groupByCommonSlipTypeFromShippingData(
      items,
      shippingData,
      variantShippingRuleIds,
    );
    for (const { slipType: slipType, items: slipTypeItems } of slipTypeGroups) {
      const company = getCompanyFromSlipType(slipType),
        groupedByMethod = this._groupByShippingMethod(
          slipTypeItems,
          shippingData,
          variantShippingRuleIds,
        );
      for (const [methodKey, methodItems] of Object.entries(groupedByMethod)) {
        const [shippingMethod, shippingClassification] = methodKey.split("_"),
          bundleGroups = this._groupByCommonBundle(
            methodItems,
            variantShippingRuleIds,
          );
        for (const {
          bundleName: bundleName,
          items: bundleItems,
        } of bundleGroups) {
          const applicableRules = shippingData.filter((rule) => {
            const ruleSlipTypes = undefined;
            return (
              getDeliverySlipTypes(rule).includes(slipType) &&
              rule.shipping_method === shippingMethod &&
              rule.shipping_classification === shippingClassification
            );
          }),
            unitKey = `unit_${slipType}_${shippingMethod}_${shippingClassification}_${bundleName}_${unitIndex}`;
          ((shippingUnits[unitKey] = {
            id: unitKey,
            ruleLevel: "variant",
            items: bundleItems,
            company: company,
            deliverySlipType: slipType,
            shippingMethod: shippingMethod,
            shippingClassification: shippingClassification,
            expandedItems: [],
            shippingData: applicableRules,
          }),
            unitIndex++);
        }
      }
    }
    return shippingUnits;
  }
  _groupByCommonSlipTypeFromShippingData(
    items,
    shippingData,
    variantShippingRuleIds,
  ) {
    if (0 === items.length) return [];
    const getItemSlipTypes = (item) => {
      const ruleIds = variantShippingRuleIds[item.variant_id],
        variantRuleIds = this._normalizeRuleIds(
          null == ruleIds ? void 0 : ruleIds.variant,
        ),
        rules = shippingData.filter((rule) => variantRuleIds.includes(rule.id)),
        slipTypes = [];
      return (
        rules.forEach((rule) => {
          getDeliverySlipTypes(rule).forEach((t) => {
            slipTypes.includes(t) || slipTypes.push(t);
          });
        }),
        slipTypes
      );
    };
    if (1 === items.length) {
      const slipTypes = getItemSlipTypes(items[0]);
      if (0 === slipTypes.length)
        throw new Error(
          `[配送単位分離エラー] 商品 (variant_id: ${items[0].variant_id}) に伝票種別が設定されていません。配送設定を確認してください。`,
        );
      return [{ slipType: slipTypes[0], items: items }];
    }
    const itemSlipTypeSets = items.map((item) => {
      const slipTypes = getItemSlipTypes(item),
        isSingle = slipTypes.length <= 1;
      return {
        item: item,
        slipTypes: new Set(slipTypes),
        slipTypesArray: slipTypes,
        isSingle: isSingle,
      };
    }),
      singleGroups = new Map(),
      multiItems = [];
    itemSlipTypeSets.forEach((set) => {
      if (0 === set.slipTypesArray.length)
        throw new Error(
          `[配送単位分離エラー] 商品 (variant_id: ${set.item.variant_id}) に伝票種別が設定されていません。配送設定を確認してください。`,
        );
      if (set.isSingle && 1 === set.slipTypesArray.length) {
        const slipType = set.slipTypesArray[0];
        (singleGroups.has(slipType) || singleGroups.set(slipType, []),
          singleGroups.get(slipType).push(set.item));
      } else multiItems.push(set);
    });
    const remainingMultiItems = [];
    multiItems.forEach((set) => {
      let merged = !1;
      for (const slipType of set.slipTypesArray)
        if (singleGroups.has(slipType)) {
          (singleGroups.get(slipType).push(set.item), (merged = !0));
          break;
        }
      merged || remainingMultiItems.push(set);
    });
    const results = [];
    if (
      (singleGroups.forEach((groupItems, slipType) => {
        results.push({ slipType: slipType, items: groupItems });
      }),
        0 === remainingMultiItems.length)
    )
      return results;
    let commonSlipTypes = new Set(remainingMultiItems[0].slipTypes);
    for (let i = 1; i < remainingMultiItems.length; i++)
      commonSlipTypes = new Set(
        [...commonSlipTypes].filter((st) =>
          remainingMultiItems[i].slipTypes.has(st),
        ),
      );
    if (commonSlipTypes.size > 0) {
      const basePriority = undefined,
        selectedSlipType2 =
          remainingMultiItems[0].slipTypesArray.find((st) =>
            commonSlipTypes.has(st),
          ) || [...commonSlipTypes][0];
      return (
        results.push({
          slipType: selectedSlipType2,
          items: remainingMultiItems.map((s) => s.item),
        }),
        results
      );
    }
    const selectedSlipType = remainingMultiItems[0].slipTypesArray[0],
      matched = [],
      unmatched = [];
    if (
      (remainingMultiItems.forEach((set) => {
        set.slipTypes.has(selectedSlipType)
          ? matched.push(set.item)
          : unmatched.push(set.item);
      }),
        matched.length > 0 &&
        results.push({ slipType: selectedSlipType, items: matched }),
        unmatched.length > 0)
    ) {
      const rest = this._groupByCommonSlipTypeFromShippingData(
        unmatched,
        shippingData,
        variantShippingRuleIds,
      );
      results.push(...rest);
    }
    return results;
  }
  _groupBySlipTypeFromShippingData(
    items,
    shippingData,
    variantShippingRuleIds,
  ) {
    const grouped = {};
    return (
      items.forEach((item) => {
        const ruleIds = variantShippingRuleIds[item.variant_id];
        if (!ruleIds) return;
        const variantRuleIds = this._normalizeRuleIds(ruleIds.variant),
          rules = shippingData.filter((rule) =>
            variantRuleIds.includes(rule.id),
          ),
          slipTypes = [];
        if (
          (rules.forEach((rule) => {
            getDeliverySlipTypes(rule).forEach((t) => {
              slipTypes.includes(t) || slipTypes.push(t);
            });
          }),
            0 === slipTypes.length)
        )
          throw new Error(
            `[配送単位分離エラー] 商品 (variant_id: ${item.variant_id}) に伝票種別が設定されていません。配送設定を確認してください。`,
          );
        const slipType = slipTypes[0];
        (grouped[slipType] || (grouped[slipType] = []),
          grouped[slipType].push(item));
      }),
      grouped
    );
  }
  _groupByShippingMethod(items, shippingData, variantShippingRuleIds) {
    const grouped = {};
    return (
      items.forEach((item) => {
        const ruleIds = variantShippingRuleIds[item.variant_id];
        if (!ruleIds) return;
        const variantRuleIds = this._normalizeRuleIds(ruleIds.variant),
          rules = shippingData.filter((rule2) =>
            variantRuleIds.includes(rule2.id),
          );
        if (0 === rules.length) return;
        const rule = rules[0],
          methodKey = `${rule.shipping_method}_${rule.shipping_classification}`;
        (grouped[methodKey] || (grouped[methodKey] = []),
          grouped[methodKey].push(item));
      }),
      grouped
    );
  }
  _selectCommonDeliverySlipTypeForGroup(
    items,
    shippingData,
    variantShippingRuleIds,
    targetShippingMethod,
    targetShippingClassification,
  ) {
    var _a;
    const itemSlipTypeSets = items.map((item) => {
      const ruleIds = variantShippingRuleIds[item.variant_id];
      if (!ruleIds)
        return {
          itemId: item.id,
          variantId: item.variant_id,
          slipTypes: new Set(),
          slipTypesOrder: [],
        };
      const variantRuleIds = this._normalizeRuleIds(ruleIds.variant),
        rules = shippingData.filter(
          (rule) =>
            variantRuleIds.includes(rule.id) &&
            rule.shipping_method === targetShippingMethod &&
            rule.shipping_classification === targetShippingClassification,
        ),
        slipTypesWithOrder = [];
      return (
        rules.forEach((rule) => {
          const types = undefined;
          getDeliverySlipTypes(rule).forEach((t) => {
            slipTypesWithOrder.includes(t) || slipTypesWithOrder.push(t);
          });
        }),
        {
          itemId: item.id,
          variantId: item.variant_id,
          slipTypes: new Set(slipTypesWithOrder),
          slipTypesOrder: slipTypesWithOrder,
        }
      );
    }),
      basePriorityOrder =
        (null == (_a = itemSlipTypeSets[0]) ? void 0 : _a.slipTypesOrder) || [];
    if (0 === itemSlipTypeSets.length)
      return {
        selectedSlipType: null,
        selectedCompany: null,
        expandedItems: new Map(),
      };
    let commonSlipTypes = new Set(itemSlipTypeSets[0].slipTypes);
    for (let i = 1; i < itemSlipTypeSets.length; i++)
      commonSlipTypes = new Set(
        [...commonSlipTypes].filter((st) =>
          itemSlipTypeSets[i].slipTypes.has(st),
        ),
      );
    if (commonSlipTypes.size > 0) {
      const sortedSlipTypes = undefined,
        selectedSlipType = [...commonSlipTypes].sort((a, b) =>
          compareSlipTypePriority(a, b, basePriorityOrder),
        )[0],
        selectedCompany = undefined;
      return {
        selectedSlipType: selectedSlipType,
        selectedCompany: getCompanyFromSlipType(selectedSlipType),
        expandedItems: new Map(),
      };
    }
    return this._expandAndSelectCommonSlipType(
      itemSlipTypeSets,
      basePriorityOrder,
    );
  }
  _expandAndSelectCommonSlipType(itemSlipTypeSets, basePriorityOrder) {
    const slipTypeFrequency = new Map();
    itemSlipTypeSets.forEach((set) => {
      set.slipTypes.forEach((slipType) => {
        slipTypeFrequency.set(
          slipType,
          (slipTypeFrequency.get(slipType) || 0) + 1,
        );
      });
    });
    const sortedSlipTypes = Array.from(slipTypeFrequency.entries()).sort(
      (a, b) =>
        b[1] !== a[1]
          ? b[1] - a[1]
          : compareSlipTypePriority(a[0], b[0], basePriorityOrder),
    );
    if (0 === sortedSlipTypes.length)
      return {
        selectedSlipType: null,
        selectedCompany: null,
        expandedItems: new Map(),
      };
    const selectedSlipType = sortedSlipTypes[0][0],
      selectedCompany = getCompanyFromSlipType(selectedSlipType),
      expandedItems = new Map();
    return (
      itemSlipTypeSets.forEach((set) => {
        set.slipTypes.has(selectedSlipType) ||
          expandedItems.set(set.itemId, [selectedSlipType]);
      }),
      {
        selectedSlipType: selectedSlipType,
        selectedCompany: selectedCompany,
        expandedItems: expandedItems,
      }
    );
  }
  _normalizeRuleIds(value) {
    return value ? (Array.isArray(value) ? value : [value]) : [];
  }
  separateByMetafields(
    items,
    productMetafields,
    variantShippingRuleIds,
    slipTypeMaster,
  ) {
    const shippingUnits = {};
    let unitIndex = 0;
    const slipTypeGroups = this._groupByCommonSlipType(
      items,
      productMetafields,
    );
    for (const { slipType: slipType, items: slipTypeItems } of slipTypeGroups) {
      const company = getCompanyFromSlipTypeMaster(slipType, slipTypeMaster),
        groupedByDeliveryClsf = this._groupByDeliveryClsf(
          slipTypeItems,
          productMetafields,
        );
      for (const [methodKey, methodItems] of Object.entries(
        groupedByDeliveryClsf,
      )) {
        const bundleGroups = this._groupByCommonBundle(
          methodItems,
          variantShippingRuleIds,
        );
        for (const {
          bundleName: bundleName,
          items: bundleItems,
        } of bundleGroups) {
          const unitKey = `unit_${slipType}_${methodKey}_${bundleName}_${unitIndex}`;
          ((shippingUnits[unitKey] = {
            id: unitKey,
            ruleLevel: "variant",
            items: bundleItems,
            company: company || void 0,
            deliverySlipType: slipType,
            shippingMethod: methodKey,
            expandedItems: [],
          }),
            unitIndex++);
        }
      }
    }
    return shippingUnits;
  }
  _groupByCommonSlipType(items, productMetafields) {
    var _a;
    if (0 === items.length) return [];
    if (1 === items.length) {
      const variantId =
        null == (_a = items[0].variant_id) ? void 0 : _a.toString(),
        meta = productMetafields[variantId],
        slipTypes = normalizeSlipTypeIds(
          null == meta ? void 0 : meta.slip_type_id,
        );
      if (0 === slipTypes.length)
        throw new Error(
          `[配送単位分離エラー] 商品 (variant_id: ${items[0].variant_id}) に伝票種別が設定されていません。配送設定を確認してください。`,
        );
      return [{ slipType: slipTypes[0], items: items }];
    }
    const itemSlipTypeSets = items.map((item) => {
      var _a2;
      const variantId =
        null == (_a2 = item.variant_id) ? void 0 : _a2.toString(),
        meta = productMetafields[variantId],
        rawSlipTypeId = null == meta ? void 0 : meta.slip_type_id,
        isSingle = "string" == typeof rawSlipTypeId,
        slipTypes = normalizeSlipTypeIds(rawSlipTypeId);
      return {
        item: item,
        slipTypes: new Set(slipTypes),
        slipTypesArray: slipTypes,
        isSingle: isSingle,
      };
    }),
      singleGroups = new Map(),
      multiItems = [];
    itemSlipTypeSets.forEach((set) => {
      if (0 === set.slipTypesArray.length)
        throw new Error(
          `[配送単位分離エラー] 商品 (variant_id: ${set.item.variant_id}) に伝票種別が設定されていません。配送設定を確認してください。`,
        );
      if (set.isSingle && 1 === set.slipTypesArray.length) {
        const slipType = set.slipTypesArray[0];
        (singleGroups.has(slipType) || singleGroups.set(slipType, []),
          singleGroups.get(slipType).push(set.item));
      } else multiItems.push(set);
    });
    const remainingMultiItems = [];
    multiItems.forEach((set) => {
      let merged = !1;
      for (const slipType of set.slipTypesArray)
        if (singleGroups.has(slipType)) {
          (singleGroups.get(slipType).push(set.item), (merged = !0));
          break;
        }
      merged || remainingMultiItems.push(set);
    });
    const results = [];
    if (
      (singleGroups.forEach((groupItems, slipType) => {
        results.push({ slipType: slipType, items: groupItems });
      }),
        0 === remainingMultiItems.length)
    )
      return results;
    let commonSlipTypes = new Set(remainingMultiItems[0].slipTypes);
    for (let i = 1; i < remainingMultiItems.length; i++)
      commonSlipTypes = new Set(
        [...commonSlipTypes].filter((st) =>
          remainingMultiItems[i].slipTypes.has(st),
        ),
      );
    if (commonSlipTypes.size > 0) {
      const basePriority = undefined,
        selectedSlipType2 =
          remainingMultiItems[0].slipTypesArray.find((st) =>
            commonSlipTypes.has(st),
          ) || [...commonSlipTypes][0];
      return (
        results.push({
          slipType: selectedSlipType2,
          items: remainingMultiItems.map((s) => s.item),
        }),
        results
      );
    }
    const selectedSlipType = remainingMultiItems[0].slipTypesArray[0],
      matched = [],
      unmatched = [];
    if (
      (remainingMultiItems.forEach((set) => {
        set.slipTypes.has(selectedSlipType)
          ? matched.push(set.item)
          : unmatched.push(set.item);
      }),
        matched.length > 0 &&
        results.push({ slipType: selectedSlipType, items: matched }),
        unmatched.length > 0)
    ) {
      const rest = this._groupByCommonSlipType(unmatched, productMetafields);
      results.push(...rest);
    }
    return results;
  }
  _groupByCommonBundle(items, variantShippingRuleIds) {
    var _a, _b;
    if (0 === items.length) return [];
    if (1 === items.length) {
      const ruleIds = variantShippingRuleIds[items[0].variant_id],
        bundleName = undefined;
      return [
        {
          bundleName:
            (null ==
              (_b =
                null == (_a = null == ruleIds ? void 0 : ruleIds.bundle)
                  ? void 0
                  : _a[0])
              ? void 0
              : _b.name) || "no_bundle",
          items: items,
        },
      ];
    }
    const itemBundleSets = items.map((item) => {
      var _a2;
      const ruleIds = variantShippingRuleIds[item.variant_id],
        bundles =
          (null == (_a2 = null == ruleIds ? void 0 : ruleIds.bundle)
            ? void 0
            : _a2.map((b) => b.name)) || [];
      return { item: item, bundles: new Set(bundles), bundlesArray: bundles };
    }),
      hasNoBundles = undefined;
    if (itemBundleSets.some((set) => 0 === set.bundles.size)) {
      const noBundleItems = [],
        withBundleItems = [];
      itemBundleSets.forEach((set) => {
        0 === set.bundles.size
          ? noBundleItems.push(set.item)
          : withBundleItems.push(set.item);
      });
      const results2 = [];
      if (
        (noBundleItems.length > 0 &&
          results2.push({ bundleName: "no_bundle", items: noBundleItems }),
          withBundleItems.length > 0)
      ) {
        const rest = this._groupByCommonBundle(
          withBundleItems,
          variantShippingRuleIds,
        );
        results2.push(...rest);
      }
      return results2;
    }
    let commonBundles = new Set(itemBundleSets[0].bundles);
    for (let i = 1; i < itemBundleSets.length; i++)
      commonBundles = new Set(
        [...commonBundles].filter((b) => itemBundleSets[i].bundles.has(b)),
      );
    if (commonBundles.size > 0) {
      const selectedBundle2 = undefined;
      return [{ bundleName: [...commonBundles][0], items: items }];
    }
    const bundleFrequency = new Map();
    itemBundleSets.forEach((set) => {
      set.bundles.forEach((b) => {
        bundleFrequency.set(b, (bundleFrequency.get(b) || 0) + 1);
      });
    });
    const sortedByFrequency = undefined,
      selectedBundle = [...bundleFrequency.entries()].sort(
        (a, b) => b[1] - a[1],
      )[0][0],
      matched = [],
      unmatched = [];
    itemBundleSets.forEach((set) => {
      set.bundles.has(selectedBundle)
        ? matched.push(set.item)
        : unmatched.push(set.item);
    });
    const results = [];
    if (
      (matched.length > 0 &&
        results.push({ bundleName: selectedBundle, items: matched }),
        unmatched.length > 0)
    ) {
      const rest = this._groupByCommonBundle(unmatched, variantShippingRuleIds);
      results.push(...rest);
    }
    return results;
  }
  _groupBySlipType(items, productMetafields) {
    const grouped = {};
    return (
      items.forEach((item) => {
        var _a;
        const variantId =
          null == (_a = item.variant_id) ? void 0 : _a.toString(),
          meta = productMetafields[variantId],
          slipTypes = normalizeSlipTypeIds(
            null == meta ? void 0 : meta.slip_type_id,
          );
        if (0 === slipTypes.length)
          throw new Error(
            `[配送単位分離エラー] 商品 (variant_id: ${item.variant_id}) に伝票種別が設定されていません。配送設定を確認してください。`,
          );
        const slipType = slipTypes[0];
        (grouped[slipType] || (grouped[slipType] = []),
          grouped[slipType].push(item));
      }),
      grouped
    );
  }
  _groupByDeliveryClsf(items, productMetafields) {
    const grouped = {};
    return (
      items.forEach((item) => {
        var _a;
        const variantId =
          null == (_a = item.variant_id) ? void 0 : _a.toString(),
          meta = productMetafields[variantId],
          deliveryClsf = undefined,
          methodKey = mapDeliveryClsfToShippingMethod(
            (null == meta ? void 0 : meta.delivery_clsf) || null,
          );
        (grouped[methodKey] || (grouped[methodKey] = []),
          grouped[methodKey].push(item));
      }),
      grouped
    );
  }
  _selectCommonSlipTypeFromMetafields(
    items,
    productMetafields,
    slipTypeMaster,
  ) {
    var _a;
    const itemSlipTypeSets = items.map((item) => {
      var _a2;
      const variantId =
        null == (_a2 = item.variant_id) ? void 0 : _a2.toString(),
        meta = productMetafields[variantId],
        slipTypes = normalizeSlipTypeIds(
          null == meta ? void 0 : meta.slip_type_id,
        );
      return {
        itemId: String(item.id),
        variantId: variantId,
        slipTypes: new Set(slipTypes),
        slipTypesOrder: slipTypes,
      };
    });
    if (0 === itemSlipTypeSets.length)
      return {
        selectedSlipType: null,
        selectedCompany: null,
        expandedItems: new Map(),
      };
    const basePriorityOrder =
      (null == (_a = itemSlipTypeSets[0]) ? void 0 : _a.slipTypesOrder) || [];
    let commonSlipTypes = new Set(itemSlipTypeSets[0].slipTypes);
    for (let i = 1; i < itemSlipTypeSets.length; i++)
      commonSlipTypes = new Set(
        [...commonSlipTypes].filter((st) =>
          itemSlipTypeSets[i].slipTypes.has(st),
        ),
      );
    if (commonSlipTypes.size > 0) {
      const sortedSlipTypes = undefined,
        selectedSlipType = [...commonSlipTypes].sort((a, b) =>
          compareSlipTypePriority(a, b, basePriorityOrder),
        )[0],
        selectedCompany = undefined;
      return {
        selectedSlipType: selectedSlipType,
        selectedCompany: getCompanyFromSlipTypeMaster(
          selectedSlipType,
          slipTypeMaster,
        ),
        expandedItems: new Map(),
      };
    }
    const result = this._expandAndSelectCommonSlipType(
      itemSlipTypeSets,
      basePriorityOrder,
    );
    return (
      result.selectedSlipType &&
      (result.selectedCompany = getCompanyFromSlipTypeMaster(
        result.selectedSlipType,
        slipTypeMaster,
      )),
      result
    );
  }
  _groupByBundle(items, variantShippingRuleIds) {
    if (items.length <= 1) return { default: items };
    const itemBundles = items.map((item) => {
      var _a;
      const ruleIds = variantShippingRuleIds[item.variant_id];
      return (
        (null == (_a = null == ruleIds ? void 0 : ruleIds.bundle)
          ? void 0
          : _a.map((b) => b.name)) || []
      );
    });
    if (itemBundles.every((b) => 0 === b.length)) return { default: items };
    const firstBundles = itemBundles[0];
    if (firstBundles.length > 0)
      for (const bundleName of firstBundles) {
        const isCommon = undefined;
        if (
          itemBundles
            .slice(1)
            .every(
              (bundles) => 0 === bundles.length || bundles.includes(bundleName),
            )
        )
          return { [bundleName]: items };
      }
    const grouped = {};
    return (
      items.forEach((item, idx) => {
        const bundleKey =
          itemBundles[idx].length > 0 ? itemBundles[idx][0] : "no_bundle";
        (grouped[bundleKey] || (grouped[bundleKey] = []),
          grouped[bundleKey].push(item));
      }),
      grouped
    );
  }
}
const _SettingsService = class _SettingsService {
  constructor() {
    (__publicField(this, "unitSeparator"),
      (this.unitSeparator = new ShippingUnitSeparator()));
  }
  calculateShippingCostForAllDestinations(destinations) {
    return destinations.reduce(
      (sum, destination) => sum + (destination.shippingFee || 0),
      0,
    );
  }
  separateByShippingUnit(items, shippingData, variantShippingRuleIds) {
    var _a, _b;
    if (
      ((variantShippingRuleIds = variantShippingRuleIds || {}),
        this._shouldUseMetafieldPath(shippingData))
    ) {
      const productMetafields =
        (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
          ? void 0
          : _a.productMetafields) || {},
        slipTypeMaster =
          null == (_b = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _b.slipTypeMaster;
      return this.unitSeparator.separateByMetafields(
        items,
        productMetafields,
        variantShippingRuleIds,
        slipTypeMaster,
      );
    }
    const hasMultipleSlipTypes = undefined;
    if (this.unitSeparator.hasMultipleDeliverySlipTypes(shippingData))
      return this.unitSeparator.separateWithMultipleSlipTypes(
        items,
        shippingData,
        variantShippingRuleIds,
      );
    const commonCarriers = this._selectCommonCarriers(
      items,
      shippingData,
      variantShippingRuleIds,
    ),
      hasCommonCarriers = Object.keys(commonCarriers).length > 0,
      groupedItems = items.reduce((acc, item, index) => {
        const shippingRuleIds = variantShippingRuleIds[item.variant_id];
        let shippingRule,
          ruleLevel = "variant",
          groupKey;
        if (!shippingRuleIds) {
          const fallbackRule = {
            id: "default",
            title: "通常配送",
            shipping_status: !0,
            shipping_classification: "regular",
            shipping_method: "normal",
            shipping_company: "default",
            shipping_table: [],
            flat_rate_shipping_amount: "0",
            excluded_area: "",
            default_flg: !0,
          };
          return (
            (groupKey = "variant_default"),
            acc[groupKey] ||
            (acc[groupKey] = {
              id: `shipping_unit_default_${Date.now() + index}`,
              ruleLevel: "variant",
              items: [],
            }),
            acc[groupKey].items.push(item),
            acc
          );
        }
        if (hasCommonCarriers) {
          const variantRuleIds = undefined,
            allRuleIds = [...this._normalizeRuleIds(shippingRuleIds.variant)];
          for (const [groupKey2, carriers] of Object.entries(commonCarriers)) {
            const matchingCarrier = carriers.find((carrier) =>
              allRuleIds.includes(carrier.id),
            );
            if (matchingCarrier) {
              ((shippingRule = matchingCarrier), (ruleLevel = "variant"));
              break;
            }
          }
        }
        if (!shippingRule && shippingRuleIds.variant) {
          const variantIds = this._normalizeRuleIds(shippingRuleIds.variant);
          ((shippingRule =
            shippingData.length > 0
              ? shippingData.find((rule) => variantIds.includes(rule.id))
              : {
                id: variantIds[0] || "default",
                title: "配送ルール",
                shipping_status: !0,
                shipping_classification: "regular",
                shipping_method: "normal",
                shipping_company: "default",
                shipping_table: [],
                flat_rate_shipping_amount: "0",
                excluded_area: "",
                default_flg: !1,
              }),
            (ruleLevel = "variant"));
        }
        if (!shippingRule) {
          const fallbackRule = {
            id: "default",
            title: "通常配送",
            shipping_status: !0,
            shipping_classification: "regular",
            shipping_method: "normal",
            shipping_company: "default",
            shipping_table: [],
            flat_rate_shipping_amount: "0",
            excluded_area: "",
            default_flg: !0,
          };
          return (
            (groupKey = "variant_default"),
            acc[groupKey] ||
            (acc[groupKey] = {
              id: `shipping_unit_default_${Date.now() + index}`,
              ruleLevel: "variant",
              items: [],
            }),
            acc[groupKey].items.push(item),
            acc
          );
        }
        const bundleInfo = shippingRuleIds.bundle || [];
        return (
          (groupKey = `${shippingRule.id}_${shippingRule.shipping_classification}_${shippingRule.shipping_method}_${shippingRule.shipping_company}`),
          acc[groupKey] ||
          (acc[groupKey] = {
            id: `shipping_unit_${Date.now() + index}`,
            ruleLevel: ruleLevel,
            items: [],
          }),
          acc[groupKey].items.push(item),
          acc
        );
      }, {}),
      finalGroupedItems = {};
    for (const [tempKey, tempUnit] of Object.entries(groupedItems)) {
      const commonBundle = this._findCommonBundleGroup(
        tempUnit.items,
        variantShippingRuleIds,
      );
      if (commonBundle) {
        const finalKey = `${tempKey}_${commonBundle}`;
        finalGroupedItems[finalKey] = {
          ...tempUnit,
          id: `shipping_unit_${finalKey}_${Date.now()}`,
        };
      } else
        tempUnit.items.forEach((item, itemIndex) => {
          var _a2;
          const itemBundleInfo =
            (null == (_a2 = variantShippingRuleIds[item.variant_id])
              ? void 0
              : _a2.bundle) || [],
            itemFirstBundle =
              itemBundleInfo.length > 0 ? itemBundleInfo[0].name : "no_bundle",
            finalKey = `${tempKey}_${itemFirstBundle}`;
          (finalGroupedItems[finalKey] ||
            (finalGroupedItems[finalKey] = {
              id: `shipping_unit_${finalKey}_${Date.now()}_${itemIndex}`,
              ruleLevel: tempUnit.ruleLevel,
              items: [],
            }),
            finalGroupedItems[finalKey].items.push(item));
        });
    }
    return finalGroupedItems;
  }
  _normalizeRuleIds(value) {
    return value ? (Array.isArray(value) ? value : [value]) : [];
  }
  _selectCommonCarriers(items, shippingData, variantShippingRuleIds) {
    const itemCarrierSets = items.map((item, idx) => {
      const ruleIds = variantShippingRuleIds[item.variant_id];
      if (!ruleIds) return [];
      const variantRuleIds = undefined,
        allRuleIds = [...this._normalizeRuleIds(ruleIds.variant)],
        carriers = undefined;
      return shippingData.filter((rule) => allRuleIds.includes(rule.id));
    });
    if (
      0 === itemCarrierSets.length ||
      itemCarrierSets.some((set) => 0 === set.length)
    )
      return {};
    let commonCarriers = itemCarrierSets[0];
    for (let i = 1; i < itemCarrierSets.length; i++) {
      const currentSet = itemCarrierSets[i];
      commonCarriers = commonCarriers.filter((carrier) =>
        currentSet.some(
          (c) =>
            c.shipping_company === carrier.shipping_company &&
            c.shipping_classification === carrier.shipping_classification &&
            c.shipping_method === carrier.shipping_method,
        ),
      );
    }
    const groupedCarriers = undefined;
    return commonCarriers.reduce((acc, carrier) => {
      const key = `${carrier.id}_${carrier.shipping_classification}_${carrier.shipping_method}`;
      return (acc[key] || (acc[key] = []), acc[key].push(carrier), acc);
    }, {});
  }
  _shouldUseMetafieldPath(shippingData) {
    var _a;
    if (shippingData && shippingData.length > 0) return !1;
    const productMetafields =
      null == (_a = window.BINDEC_SHIPPING_SETTINGS)
        ? void 0
        : _a.productMetafields;
    return (
      !!productMetafields &&
      Object.values(productMetafields).some((meta) => null != meta.slip_type_id)
    );
  }
  _findCommonBundleGroup(items, variantShippingRuleIds) {
    var _a;
    if (0 === items.length) return null;
    if (1 === items.length) {
      const bundleInfo =
        (null == (_a = variantShippingRuleIds[items[0].variant_id])
          ? void 0
          : _a.bundle) || [];
      return bundleInfo.length > 0 ? bundleInfo[0].name : null;
    }
    const itemBundleSets = items.map((item) => {
      const ruleIds = variantShippingRuleIds[item.variant_id];
      return ruleIds && ruleIds.bundle ? ruleIds.bundle.map((b) => b.name) : [];
    });
    if (itemBundleSets.some((set) => 0 === set.length)) return null;
    const firstItemBundles = itemBundleSets[0];
    for (const bundleName of firstItemBundles) {
      const isCommon = undefined;
      if (
        itemBundleSets.slice(1).every((bundles) => bundles.includes(bundleName))
      )
        return bundleName;
    }
    return null;
  }
  isDaibikiUnavailable(deliveryGroups, orderType) {
    var _a;
    const _isDaibikiUnavailable = undefined;
    if (
      null == (_a = window.BINDEC_SHIPPING_SETTINGS)
        ? void 0
        : _a.isDaibikiUnavailable
    )
      return !0;
    const isGiftFlow = undefined;
    return !("gift" !== orderType);
  }
  isInStorePayment(deliveryGroups) {
    if (!deliveryGroups || 0 === deliveryGroups.length) return !1;
    const hasStorePickup = deliveryGroups.some((group) => {
      var _a;
      return null == (_a = group.destinations)
        ? void 0
        : _a.some((dest) => "pickup" === dest.deliveryMethod);
    });
    return hasStorePickup;
  }
  isAllItemsAreGift(deliveryGroups) {
    var _a;
    const _isAllItemsAreGift = undefined;
    return !!(null == (_a = window.BINDEC_SHIPPING_SETTINGS)
      ? void 0
      : _a.isAllItemsAreGift);
  }
  async withInterceptorPaused(fn) {
    if (_SettingsService.isInterceptorPaused) return fn();
    try {
      return ((_SettingsService.isInterceptorPaused = !0), await fn());
    } finally {
      _SettingsService.isInterceptorPaused = !1;
    }
  }
  static isCartInterceptorPaused() {
    return _SettingsService.isInterceptorPaused;
  }
};
__publicField(_SettingsService, "isInterceptorPaused", !1);
let SettingsService = _SettingsService;
function getAppProxyUrl() {
  var _a;
  if ("undefined" != typeof window) {
    const isDev = undefined,
      subpath = undefined;
    return `/apps/${"development" === (null == (_a = window.ENV) ? void 0 : _a.NODE_ENV) ? "bindshipping-dev" : "bindshipping"}`;
  }
  return "/apps/bindshipping";
}
const DEFAULT_OPTIONS = { timeout: 1e4, maxRetries: 2, retryDelay: 1e3 };
function getShopDomain() {
  var _a;
  if (
    "undefined" != typeof window &&
    (null == (_a = window.Shopify) ? void 0 : _a.shop)
  )
    return window.Shopify.shop;
  if ("undefined" != typeof window) {
    const hostname = window.location.hostname;
    if (hostname.endsWith(".myshopify.com")) return hostname;
  }
  return "";
}
async function fetchWithTimeout(url, options, timeout) {
  const controller = new AbortController(),
    timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return (clearTimeout(timeoutId), response);
  } catch (error) {
    if (
      (clearTimeout(timeoutId),
        error instanceof Error && "AbortError" === error.name)
    )
      throw new Error(`リクエストがタイムアウトしました (${timeout}ms)`);
    throw error;
  }
}
async function fetchWithRetry(url, body, options) {
  var _a;
  let lastError = null;
  for (let attempt = 0; attempt <= options.maxRetries; attempt++)
    try {
      const response = await fetchWithTimeout(
        url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        options.timeout,
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `API Error: ${response.status} ${response.statusText} - ${errorText}`,
        );
      }
      const data = undefined;
      return await response.json();
    } catch (error) {
      ((lastError = error instanceof Error ? error : new Error(String(error))),
        attempt < options.maxRetries &&
        (await new Promise((resolve) =>
          setTimeout(resolve, options.retryDelay),
        )));
    }
  return {
    success: "error",
    total_shipping: 0,
    calculations: [],
    error:
      (null == lastError ? void 0 : lastError.message) ||
      "送料計算APIの呼び出しに失敗しました",
  };
}
async function calculateShippingForDestination(
  address,
  items,
  shippingRuleId,
  options = {},
) {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options },
    shopDomain = getShopDomain(),
    prefecture = address.prefecture
      ? convertJapanesePrefectureToCode(address.prefecture)
      : void 0,
    lineItems = items.map((item) => ({
      variant_id: item.variant_id,
      quantity: item.quantity,
      price: item.price,
      grams: item.grams || 0,
    })),
    requestBody = {
      shipping_addresses: [
        {
          province_code: prefecture,
          zip: address.zipcode || void 0,
          line_items: lineItems,
          shipping_rule_id: shippingRuleId,
        },
      ],
      shop_domain: shopDomain,
      authentication: "auto",
    };
  try {
    const response = await fetchWithRetry(
      `${getAppProxyUrl()}/api/shipping-calculator`,
      requestBody,
      mergedOptions,
    );
    if (
      "error" === response.success ||
      !response.calculations ||
      0 === response.calculations.length
    )
      return {
        success: !1,
        shipping_cost: 0,
        error: response.error || "送料計算に失敗しました",
      };
    const calculation = response.calculations[0],
      shippingCost = calculation.shipping_cost ?? 0;
    return (
      calculation.shipping_cost,
      {
        success: !0,
        shipping_cost: shippingCost,
        shipping_rule_id: calculation.shipping_rule_id,
        shipperCode: calculation.shipper_code,
        company: calculation.company,
        delivery_method: calculation.delivery_method,
        shipping_method: calculation.shipping_method,
        deliverySlipType: calculation.delivery_slip_type,
        documentTypeHandle: calculation.document_type_handle,
        is_deliverable: calculation.is_deliverable,
        is_cod_available: calculation.is_cod_available,
        earliest_delivery_date: calculation.earliest_delivery_date,
        latest_delivery_date: calculation.latest_delivery_date,
        scheduled_ship_date: calculation.scheduled_ship_date,
        product_lead_time_days: calculation.product_lead_time_days,
        delivery_lead_time_days: calculation.delivery_lead_time_days,
        total_lead_time_days: calculation.total_lead_time_days,
        is_delivery_date_available: calculation.is_delivery_date_available,
        availableDeliveryPeriods: calculation.available_delivery_periods,
        error: calculation.error,
      }
    );
  } catch (error) {
    const errorMessage = undefined;
    return {
      success: !1,
      shipping_cost: 0,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
async function calculateShippingForMultipleDestinations(
  destinations,
  options = {},
) {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options },
    shopDomain = getShopDomain();
  let discountCodes = [];
  try {
    const cart2 = await cartCache.get(!0);
    if (cart2) {
      const codes = undefined;
      ((discountCodes = (cart2.discount_codes || [])
        .map((codeEntry) =>
          "string" == typeof codeEntry
            ? codeEntry
            : "object" == typeof codeEntry && codeEntry.code
              ? codeEntry.code
              : null,
        )
        .filter(Boolean)),
        discountCodes.length);
    }
  } catch (error) { }
  const shippingAddresses = undefined,
    requestBody = {
      shipping_addresses: destinations.map((dest) => {
        const prefecture = undefined;
        return {
          province_code: dest.address.prefecture
            ? convertJapanesePrefectureToCode(dest.address.prefecture)
            : void 0,
          zip: dest.address.zipcode || void 0,
          line_items: dest.items.map((item) => ({
            variant_id: item.variant_id,
            quantity: item.quantity,
            price: item.price,
            grams: item.grams || 0,
          })),
          shipping_rule_id: dest.shippingRuleId,
          document_type_id: dest.documentTypeId,
          delivery_control_id: dest.deliveryControlId,
          purpose: dest.purpose,
          shipping_start_date: dest.shippingStartDate,
          destination_id: dest.destinationId,
          desired_delivery_date: dest.desiredDeliveryDate,
        };
      }),
      shop_domain: shopDomain,
      authentication: "auto",
      discount_codes: discountCodes,
    },
    datetimeButtons = document.querySelectorAll(".btn-select-datetime");
  try {
    datetimeButtons.length > 0 &&
      datetimeButtons.forEach((button) => {
        button.disabled = !window._IS_DATE_TIME_SELECTED;
      });
    const response = await fetchWithRetry(
      `${getAppProxyUrl()}/api/shipping-calculator`,
      requestBody,
      mergedOptions,
    ),
      calculations = (response.calculations || []).map((calc, index) => {
        const shippingCost = calc.shipping_cost ?? 0;
        return (
          calc.shipping_cost,
          {
            success: !calc.error,
            shipping_cost: shippingCost,
            original_shipping_cost: calc.original_shipping_cost ?? shippingCost,
            shipping_rule_id: calc.shipping_rule_id,
            shipperCode: calc.shipper_code,
            company: calc.company,
            delivery_method: calc.delivery_method,
            shipping_method: calc.shipping_method,
            deliverySlipType: calc.delivery_slip_type,
            documentTypeId: calc.document_type_id,
            documentTypeHandle: calc.document_type_handle,
            is_deliverable: calc.is_deliverable,
            is_cod_available: calc.is_cod_available,
            earliest_delivery_date: calc.earliest_delivery_date,
            latest_delivery_date: calc.latest_delivery_date,
            scheduled_ship_date: calc.scheduled_ship_date,
            product_lead_time_days: calc.product_lead_time_days,
            delivery_lead_time_days: calc.delivery_lead_time_days,
            total_lead_time_days: calc.total_lead_time_days,
            is_delivery_date_available: calc.is_delivery_date_available,
            availableDeliveryPeriods: calc.available_delivery_periods,
            availableDeliveryTime: calc.available_delivery_time,
            error: calc.error,
            desired_delivery_date_error: calc.desired_delivery_date_error,
          }
        );
      });
    return (
      (window._IS_DATE_TIME_SELECTED = !1),
      datetimeButtons.length > 0 &&
      datetimeButtons.forEach((button) => {
        button.disabled = !1;
      }),
      {
        success: response.success,
        total_shipping: response.total_shipping,
        original_total_shipping: response.original_total_shipping,
        has_free_shipping_discount: response.has_free_shipping_discount,
        calculations: calculations,
        error: response.error,
      }
    );
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    window._IS_DATE_TIME_SELECTED = !1;
    const datetimeButtons2 = document.querySelectorAll(".btn-select-datetime");
    return (
      datetimeButtons2.length > 0 &&
      datetimeButtons2.forEach((button) => {
        button.disabled = !1;
      }),
      {
        success: "error",
        total_shipping: 0,
        calculations: [],
        error: errorMessage,
      }
    );
  }
}
function shouldCheckInventory(info) {
  return (
    !!info &&
    "shopify" === info.inventory_management &&
    "continue" !== info.inventory_policy
  );
}
function getInventoryInfo(variantId) {
  var _a;
  const productMetafields = undefined,
    meta = ((null == (_a = window.BINDEC_SHIPPING_SETTINGS)
      ? void 0
      : _a.productMetafields) || {})[String(variantId)];
  if (!meta) return null;
  const hasInventoryFields = undefined;
  return "inventory_quantity" in meta ||
    "inventory_management" in meta ||
    "inventory_policy" in meta
    ? {
      inventory_quantity: Number(meta.inventory_quantity) || 0,
      inventory_management: meta.inventory_management ?? null,
      inventory_policy: meta.inventory_policy ?? null,
    }
    : null;
}
function normalizeVariantId(variantId) {
  const s = String(variantId);
  return (s.includes("/") && s.split("/").pop()) || s;
}
function isWithinInventory(variantId, requiredQty) {
  const info = getInventoryInfo(normalizeVariantId(variantId));
  return !shouldCheckInventory(info) || requiredQty <= info.inventory_quantity;
}
function findInventoryShortages(requiredByVariant) {
  var _a, _b;
  const entries =
    requiredByVariant instanceof Map
      ? Array.from(requiredByVariant.entries())
      : Object.entries(requiredByVariant),
    shortages = [],
    productMetafields =
      (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
        ? void 0
        : _a.productMetafields) || {};
  for (const [rawVariantId, requiredQty] of entries) {
    if (!requiredQty || requiredQty <= 0) continue;
    const variantId = normalizeVariantId(rawVariantId),
      info = getInventoryInfo(variantId);
    shouldCheckInventory(info) &&
      requiredQty > info.inventory_quantity &&
      shortages.push({
        variantId: variantId,
        productTitle:
          (null == (_b = productMetafields[variantId])
            ? void 0
            : _b.product_title) || "",
        required: requiredQty,
        available: info.inventory_quantity,
      });
  }
  return shortages;
}
function getVariantUsageInOtherDestinations(variantId, excludeDestinationId) {
  const target = normalizeVariantId(variantId),
    unitSettings = window.BINDEC_SHIPPING_UNIT_SETTINGS;
  if (!unitSettings) return 0;
  let total = 0;
  const excludePrefix = excludeDestinationId
    ? `${excludeDestinationId}_`
    : null;
  return (
    Object.entries(unitSettings).forEach(([unitKey, unit]) => {
      if (excludePrefix && unitKey.startsWith(excludePrefix)) return;
      const items = null == unit ? void 0 : unit.items;
      Array.isArray(items) &&
        items.forEach((item) => {
          const rawId = item.variantId || item.variant_id;
          if (!rawId) return;
          if (normalizeVariantId(rawId) !== target) return;
          const qty = parseInt(item.quantity, 10) || 0;
          qty > 0 && (total += qty);
        });
    }),
    total
  );
}
function getMaxQuantityForDestination(variantId, excludeDestinationId) {
  const info = getInventoryInfo(normalizeVariantId(variantId));
  if (!shouldCheckInventory(info)) return null;
  const usedElsewhere = getVariantUsageInOtherDestinations(
    variantId,
    excludeDestinationId,
  ),
    max = info.inventory_quantity - usedElsewhere;
  return Math.max(0, max);
}
function formatShortageMessage(shortages) {
  return shortages.map((s) => {
    const title = undefined;
    return `商品${s.productTitle ? `「${s.productTitle}」` : ""}の在庫が不足しています。数量を見直してください。`;
  });
}
function getCurrentTotalsByVariant() {
  var _a;
  const totals = {},
    multiShippingSettings = document.querySelector("multi-shipping-settings"),
    settings =
      (null == multiShippingSettings
        ? void 0
        : multiShippingSettings.settings) ??
      (null ==
        (_a =
          null == multiShippingSettings
            ? void 0
            : multiShippingSettings.getSettings)
        ? void 0
        : _a.call(multiShippingSettings));
  if (!settings) return totals;
  const accumulate = (items) => {
    for (const item of items || []) {
      const vid =
        (null == item ? void 0 : item.variantId) ??
        (null == item ? void 0 : item.variant_id);
      if (null == vid) continue;
      const id = normalizeVariantId(vid),
        qty = Number(item.quantity) || 0;
      qty <= 0 || (totals[id] = (totals[id] || 0) + qty);
    }
  };
  for (const group of settings.deliveryGroups || [])
    for (const dest of group.destinations || []) accumulate(dest.items);
  for (const dest of settings.homeDestinations || []) accumulate(dest.items);
  return totals;
}
if (!customElements.get("destination-selector")) {
  const selectors = {
    deleteButton: ".js-btn-delete-destination",
    products: {
      section: ".products-section",
      warning: ".products-warning",
      shippingUnits: ".js-product-groups",
      actions: {
        selectProductsButton: "button.btn-select-products",
        copyProductsButton: "button.btn-copy-products",
      },
      modal: {
        modal: "dialog#product-selector-modal",
        cancelButton: "dialog#product-selector-modal .modal-cancel-btn",
        confirmButton: "dialog#product-selector-modal .modal-confirm-btn",
        productCheckboxes: "dialog#product-selector-modal .js-product-checkbox",
        productQuantityInputs:
          "dialog#product-selector-modal .js-product-quantity",
      },
    },
    deliveryMethod: {
      section: ".delivery-method-section",
      radios: "input[name='delivery-method']",
    },
    deliveryAddress: {
      section: ".delivery-address-section",
      title: ".delivery-address-title",
      selectedDestinationData: ".js-delivery-address-info",
      selectedDestinationDepartment: ".js-delivery-department",
      selectedDestinationName: ".js-delivery-address-name",
      selectedDestinationNameKana: ".js-delivery-address-name-kana",
      selectedDestinationHonorific: ".js-delivery-address-honorific",
      selectedDestinationAddress: ".js-delivery-address-address",
      selectedDestinationPhone: ".js-delivery-address-phone",
      actions: {
        selectAddressButton: "button.btn-select-address",
        editAddressButton: "button.btn-edit-address",
        copyAddressButton: "button.btn-copy-address",
      },
      modal: {
        modal: "dialog#delivery-address-selector-modal",
        cancelButton:
          "dialog#delivery-address-selector-modal .modal-cancel-btn",
        confirmButton:
          "dialog#delivery-address-selector-modal .modal-confirm-btn",
        deliveryDestinationSelect: ".js-delivery-address-select",
      },
      storePickupModal: {
        modal: "dialog#store-pickup-selector-modal",
        cancelButton: "dialog#store-pickup-selector-modal .modal-cancel-btn",
        confirmButton: "dialog#store-pickup-selector-modal .modal-confirm-btn",
        storePickupSelect: ".js-store-pickup-select",
      },
    },
    storeSelection: {
      section: ".store-selection-section",
      noCommonStore: ".store-selection-no-common-store",
      header: ".store-selection-header",
      label: ".store-selection-label",
      empty: ".store-selection-empty",
      emptyText: ".js-store-empty-text",
      selectedStoreData: ".js-store-selection-info",
      selectedStoreName: ".js-store-name",
      selectedStoreId: ".js-store-id",
      info: ".store-selection-info",
      details: ".store-selection-details",
      actions: {
        selectStoreButton: "button.btn-select-store",
        editStoreButton: "button.btn-edit-store",
      },
    },
  };
  class DestinationSelector extends HTMLElement {
    constructor() {
      var _a;
      (super(),
        __publicField(this, "deliveryGroupId"),
        __publicField(this, "destinationId"),
        __publicField(this, "restoredAddress", null),
        __publicField(this, "shippingFeeDebounceTimer", null),
        __publicField(this, "SHIPPING_FEE_DEBOUNCE_MS", 500),
        __publicField(this, "products"),
        __publicField(this, "deliveryMethod"),
        __publicField(this, "deliveryAddress"),
        __publicField(this, "storeSelection"),
        __publicField(this, "addressModal"),
        __publicField(this, "storeModal"),
        __publicField(this, "availableStores", []),
        __publicField(this, "__changeDeliveryMethodListener"),
        __publicField(this, "__datetimeSelectedListener"),
        __publicField(this, "__cartUpdatedListener"),
        __publicField(this, "__listeners"),
        (this.deliveryGroupId =
          (null == (_a = this.closest("delivery-group"))
            ? void 0
            : _a.getAttribute("data-delivery-group-id")) || ""),
        (this.destinationId = this.getAttribute("data-destination-id") || ""),
        (this.products = {
          section: this.querySelector(selectors.products.section),
          warning: this.querySelector(selectors.products.warning),
          shippingUnits: this.querySelector(selectors.products.shippingUnits),
          actions: {
            selectProductsButton: this.querySelector(
              selectors.products.actions.selectProductsButton,
            ),
            copyProductsButton: this.querySelector(
              selectors.products.actions.copyProductsButton,
            ),
          },
          modal: {
            dialog: this.querySelector(selectors.products.modal.modal),
            cancelButton: this.querySelector(
              selectors.products.modal.cancelButton,
            ),
            confirmButton: this.querySelector(
              selectors.products.modal.confirmButton,
            ),
            productCheckboxes: this.querySelectorAll(
              selectors.products.modal.productCheckboxes,
            ),
            productQuantityInputs: this.querySelectorAll(
              selectors.products.modal.productQuantityInputs,
            ),
          },
        }),
        (this.deliveryMethod = {
          section: this.querySelector(selectors.deliveryMethod.section),
          radios: this.querySelectorAll(selectors.deliveryMethod.radios),
        }),
        (this.deliveryAddress = {
          section: this.querySelector(selectors.deliveryAddress.section),
          title: this.querySelector(selectors.deliveryAddress.title),
          selectedDestinationData: this.querySelector(
            selectors.deliveryAddress.selectedDestinationData,
          ),
          selectedDestinationDepartment: this.querySelector(
            selectors.deliveryAddress.selectedDestinationDepartment,
          ),
          selectedDestinationName: this.querySelector(
            selectors.deliveryAddress.selectedDestinationName,
          ),
          selectedDestinationNameKana: this.querySelector(
            selectors.deliveryAddress.selectedDestinationNameKana,
          ),
          selectedDestinationHonorific: this.querySelector(
            selectors.deliveryAddress.selectedDestinationHonorific,
          ),
          selectedDestinationAddress: this.querySelector(
            selectors.deliveryAddress.selectedDestinationAddress,
          ),
          selectedDestinationPhone: this.querySelector(
            selectors.deliveryAddress.selectedDestinationPhone,
          ),
          actions: {
            selectAddressButton: this.querySelector(
              selectors.deliveryAddress.actions.selectAddressButton,
            ),
            editAddressButton: this.querySelector(
              selectors.deliveryAddress.actions.editAddressButton,
            ),
            copyAddressButton: this.querySelector(
              selectors.deliveryAddress.actions.copyAddressButton,
            ),
          },
          modal: {
            dialog: this.querySelector(selectors.deliveryAddress.modal.modal),
            cancelButton: this.querySelector(
              selectors.deliveryAddress.modal.cancelButton,
            ),
            confirmButton: this.querySelector(
              selectors.deliveryAddress.modal.confirmButton,
            ),
            deliveryDestinationSelect: this.querySelector(
              selectors.deliveryAddress.modal.deliveryDestinationSelect,
            ),
          },
          storePickupModal: {
            dialog: this.querySelector(
              selectors.deliveryAddress.storePickupModal.modal,
            ),
            cancelButton: this.querySelector(
              selectors.deliveryAddress.storePickupModal.cancelButton,
            ),
            confirmButton: this.querySelector(
              selectors.deliveryAddress.storePickupModal.confirmButton,
            ),
            storePickupSelect: this.querySelector(
              selectors.deliveryAddress.storePickupModal.storePickupSelect,
            ),
          },
        }),
        (this.storeSelection = {
          section: this.querySelector(selectors.storeSelection.section),
          noCommonStore: this.querySelector(
            selectors.storeSelection.noCommonStore,
          ),
          header: this.querySelector(selectors.storeSelection.header),
          label: this.querySelector(selectors.storeSelection.label),
          empty: this.querySelector(selectors.storeSelection.empty),
          emptyText: this.querySelector(selectors.storeSelection.emptyText),
          selectedStoreData: this.querySelector(
            selectors.storeSelection.selectedStoreData,
          ),
          selectedStoreName: this.querySelector(
            selectors.storeSelection.selectedStoreName,
          ),
          selectedStoreId: this.querySelector(
            selectors.storeSelection.selectedStoreId,
          ),
          info: this.querySelector(selectors.storeSelection.info),
          details: this.querySelector(selectors.storeSelection.details),
          actions: {
            selectStoreButton: this.querySelector(
              selectors.storeSelection.actions.selectStoreButton,
            ),
            editStoreButton: this.querySelector(
              selectors.storeSelection.actions.editStoreButton,
            ),
          },
        }));
    }
    refreshElementReferences() {
      (this.products.actions.selectProductsButton ||
        (this.products.actions.selectProductsButton = this.querySelector(
          selectors.products.actions.selectProductsButton,
        )),
        this.products.actions.copyProductsButton ||
        (this.products.actions.copyProductsButton = this.querySelector(
          selectors.products.actions.copyProductsButton,
        )),
        this.products.modal.dialog ||
        (this.products.modal.dialog = this.querySelector(
          selectors.products.modal.modal,
        )),
        this.products.modal.cancelButton ||
        (this.products.modal.cancelButton = this.querySelector(
          selectors.products.modal.cancelButton,
        )),
        this.products.modal.confirmButton ||
        (this.products.modal.confirmButton = this.querySelector(
          selectors.products.modal.confirmButton,
        )),
        (this.deliveryMethod.radios &&
          0 !== this.deliveryMethod.radios.length) ||
        (this.deliveryMethod.radios = this.querySelectorAll(
          selectors.deliveryMethod.radios,
        )),
        this.deliveryAddress.actions.selectAddressButton ||
        (this.deliveryAddress.actions.selectAddressButton =
          this.querySelector(
            selectors.deliveryAddress.actions.selectAddressButton,
          )),
        this.deliveryAddress.actions.editAddressButton ||
        (this.deliveryAddress.actions.editAddressButton = this.querySelector(
          selectors.deliveryAddress.actions.editAddressButton,
        )),
        this.deliveryAddress.actions.copyAddressButton ||
        (this.deliveryAddress.actions.copyAddressButton = this.querySelector(
          selectors.deliveryAddress.actions.copyAddressButton,
        )),
        this.storeSelection.actions.selectStoreButton ||
        (this.storeSelection.actions.selectStoreButton = this.querySelector(
          selectors.storeSelection.actions.selectStoreButton,
        )),
        this.storeSelection.actions.editStoreButton ||
        (this.storeSelection.actions.editStoreButton = this.querySelector(
          selectors.storeSelection.actions.editStoreButton,
        )));
    }
    connectedCallback() {
      var _a;
      (this.refreshElementReferences(),
        this.createAddressModal(),
        this.createStoreModal(),
        this.populateStoreOptions(),
        null == (_a = this.deliveryMethod.radios) ||
        _a.forEach((radio) => {
          radio && (radio.name = `delivery-method-${this.destinationId}`);
        }),
        (this.__listeners = {
          deliveryAddress: {
            openModal: this.openModal.bind(this, "address"),
            copyAddress: this.handleCopyAddressClick.bind(this),
          },
          storePickup: {
            closeModal: this.closeModal.bind(this, "address"),
            submitModal: this.submitModal.bind(this, "address"),
          },
          products: {
            openModal: this.openModal.bind(this, "products"),
            closeModal: this.closeModal.bind(this, "products"),
            submitModal: this.submitModal.bind(this, "products"),
            copyProducts: this.handleCopyProductsClick.bind(this),
          },
          store: { openModal: this.openStoreModal.bind(this) },
        }),
        this.deliveryAddress.actions.selectAddressButton &&
        this.deliveryAddress.actions.selectAddressButton.addEventListener(
          "click",
          this.__listeners.deliveryAddress.openModal,
        ),
        this.deliveryAddress.actions.editAddressButton &&
        this.deliveryAddress.actions.editAddressButton.addEventListener(
          "click",
          this.__listeners.deliveryAddress.openModal,
        ),
        this.deliveryAddress.actions.copyAddressButton &&
        this.deliveryAddress.actions.copyAddressButton.addEventListener(
          "click",
          this.__listeners.deliveryAddress.copyAddress,
        ),
        this.deliveryAddress.modal.cancelButton &&
        this.deliveryAddress.modal.cancelButton.addEventListener(
          "click",
          this.__listeners.deliveryAddress.closeModal,
        ),
        this.deliveryAddress.modal.confirmButton &&
        this.deliveryAddress.modal.confirmButton.addEventListener(
          "click",
          this.__listeners.deliveryAddress.submitModal,
        ),
        this.deliveryAddress.storePickupModal.cancelButton &&
        this.deliveryAddress.storePickupModal.cancelButton.addEventListener(
          "click",
          this.__listeners.storePickup.closeModal,
        ),
        this.deliveryAddress.storePickupModal.confirmButton &&
        this.deliveryAddress.storePickupModal.confirmButton.addEventListener(
          "click",
          this.__listeners.storePickup.submitModal,
        ),
        this.products.actions.selectProductsButton &&
        this.products.actions.selectProductsButton.addEventListener(
          "click",
          this.__listeners.products.openModal,
        ),
        this.products.actions.copyProductsButton &&
        this.products.actions.copyProductsButton.addEventListener(
          "click",
          this.__listeners.products.copyProducts,
        ),
        this.products.modal.cancelButton &&
        this.products.modal.cancelButton.addEventListener(
          "click",
          this.__listeners.products.closeModal,
        ),
        this.products.modal.confirmButton &&
        this.products.modal.confirmButton.addEventListener(
          "click",
          this.__listeners.products.submitModal,
        ),
        (this.__changeDeliveryMethodListener =
          this.changeDeliveryMethod.bind(this)),
        this.deliveryMethod.radios.forEach((radio) => {
          radio &&
            radio.addEventListener(
              "change",
              this.__changeDeliveryMethodListener,
            );
        }),
        this.storeSelection.actions.selectStoreButton &&
        this.storeSelection.actions.selectStoreButton.addEventListener(
          "click",
          this.__listeners.store.openModal,
        ),
        this.storeSelection.actions.editStoreButton &&
        this.storeSelection.actions.editStoreButton.addEventListener(
          "click",
          this.__listeners.store.openModal,
        ),
        (this.__datetimeSelectedListener =
          this.handleDatetimeSelected.bind(this)),
        this.addEventListener(
          "settings-change",
          this.__datetimeSelectedListener,
        ),
        (this.__cartUpdatedListener = this.handleCartUpdated.bind(this)),
        document.addEventListener("cart:updated", this.__cartUpdatedListener));
    }
    createAddressModal() {
      ((this.addressModal = document.createElement("address-modal")),
        document.body.appendChild(this.addressModal));
    }
    createStoreModal() {
      ((this.storeModal = document.createElement("store-modal")),
        document.body.appendChild(this.storeModal));
    }
    checkSelectedStoreIsCommonStore(selectedStoreData, selectedProducts) {
      if (
        !selectedStoreData ||
        !selectedProducts ||
        0 === selectedProducts.length
      )
        return { isCommonStore: !1, unavailableProducts: [] };
      const selectedStoreId =
        selectedStoreData.storeId || selectedStoreData.store_id;
      if (!selectedStoreId)
        return { isCommonStore: !1, unavailableProducts: [] };
      const productStoresMap = new Map(),
        unavailableProducts = [];
      selectedProducts.forEach((product) => {
        var _a, _b;
        const variantId = product.variant_id.toString(),
          variantInfo =
            null ==
              (_b =
                null == (_a = window.BINDEC_SHIPPING_SETTINGS)
                  ? void 0
                  : _a.variantShippingRuleIds)
              ? void 0
              : _b[variantId],
          receiveStoreNames =
            (null == variantInfo ? void 0 : variantInfo.receiveStoreNames) ||
            [];
        productStoresMap.set(variantId, receiveStoreNames);
        const isAvailableAtStore = undefined;
        receiveStoreNames.some(
          (store) => store.pickup_location_id === selectedStoreId,
        ) || unavailableProducts.push(product);
      });
      const productStores = Array.from(productStoresMap.values());
      let commonStores = [];
      if (productStores.length > 0) {
        commonStores = productStores[0] || [];
        for (let i = 1; i < productStores.length; i++) {
          const currentStores = productStores[i];
          if (!currentStores || 0 === currentStores.length) {
            commonStores = [];
            break;
          }
          if (
            ((commonStores = commonStores.filter((commonStore) =>
              currentStores.some(
                (store) =>
                  store.pickup_location_id === commonStore.pickup_location_id,
              ),
            )),
              0 === commonStores.length)
          )
            break;
        }
      }
      const isCommonStore = undefined;
      return {
        isCommonStore: commonStores.some(
          (store) => store.pickup_location_id === selectedStoreId,
        ),
        unavailableProducts: unavailableProducts,
      };
    }
    populateStoreOptions(selectedProducts, selectedDestinationAddress) {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
      const stores =
        (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
          ? void 0
          : _a.storeMaster) || [];
      let availableStores = stores,
        hasCommonStores = !0;
      if (selectedProducts && selectedProducts.length > 0) {
        const productStores = selectedProducts.map((product) => {
          var _a2, _b2;
          const variantId = product.variant_id.toString(),
            variantInfo =
              null ==
                (_b2 =
                  null == (_a2 = window.BINDEC_SHIPPING_SETTINGS)
                    ? void 0
                    : _a2.variantShippingRuleIds)
                ? void 0
                : _b2[variantId],
            receiveStoreNames = undefined;
          return (
            (null == variantInfo ? void 0 : variantInfo.receiveStoreNames) || []
          );
        });
        let commonStores = [];
        if (productStores.length > 0) {
          commonStores = productStores[0] || [];
          for (let i = 1; i < productStores.length; i++) {
            const currentStores = productStores[i];
            if (!currentStores || 0 === currentStores.length) {
              commonStores = [];
              break;
            }
            if (
              ((commonStores = commonStores.filter((commonStore) =>
                currentStores.some(
                  (store) =>
                    store.pickup_location_id === commonStore.pickup_location_id,
                ),
              )),
                0 === commonStores.length)
            )
              break;
          }
        }
        0 === commonStores.length
          ? ((hasCommonStores = !1), (availableStores = []))
          : (availableStores = commonStores.map((commonStore) => {
            const masterStore = stores.find(
              (s) => s.pickup_location_id === commonStore.pickup_location_id,
            );
            return {
              ...commonStore,
              id:
                (null == masterStore ? void 0 : masterStore.id) ||
                commonStore.id,
            };
          }));
      }
      ((this.availableStores = availableStores.map((store) => ({
        id: store.id,
        pickup_location_id: store.pickup_location_id,
        pickup_location_name: store.pickup_location_name,
      }))),
        hasCommonStores && 0 !== this.availableStores.length
          ? (null == (_f = this.storeSelection.noCommonStore) ||
            _f.classList.add("hidden"),
            null == (_g = this.storeSelection.header) ||
            _g.classList.remove("hidden"),
            selectedDestinationAddress
              ? (null == (_h = this.storeSelection.empty) ||
                _h.classList.add("hidden"),
                null == (_i = this.storeSelection.info) ||
                _i.classList.remove("hidden"))
              : (null == (_j = this.storeSelection.empty) ||
                _j.classList.remove("hidden"),
                null == (_k = this.storeSelection.info) ||
                _k.classList.add("hidden")))
          : (null == (_b = this.storeSelection.noCommonStore) ||
            _b.classList.remove("hidden"),
            null == (_c = this.storeSelection.header) ||
            _c.classList.add("hidden"),
            null == (_d = this.storeSelection.empty) ||
            _d.classList.add("hidden"),
            null == (_e = this.storeSelection.info) ||
            _e.classList.add("hidden")),
        this.updatePickupAvailability(
          hasCommonStores && this.availableStores.length > 0,
        ));
    }
    updatePickupAvailability(isAvailable) {
      const pickupRadio = Array.from(this.deliveryMethod.radios).find(
        (radio) => "pickup" === radio.value,
      );
      pickupRadio &&
        (isAvailable
          ? (pickupRadio.disabled = !1)
          : pickupRadio.checked &&
          ((pickupRadio.checked = !1),
            this.storeSelection.section.classList.add("hidden"),
            this.deliveryAddress.section.classList.add("hidden")));
    }
    updateDeliveryMethodAvailability(selectedProducts) {
      const classifications = selectedProducts
        .map((p) => p.receivestore_clsf)
        .filter(Boolean);
      if (0 === classifications.length) return;
      const deliveryRadio = Array.from(this.deliveryMethod.radios).find(
        (r) => "delivery" === r.value,
      ),
        pickupRadio = Array.from(this.deliveryMethod.radios).find(
          (r) => "pickup" === r.value,
        );
      if (!deliveryRadio || !pickupRadio) return;
      const allPickupOnly = classifications.every((c) => "店舗受取専用" === c),
        allPickupNotAvailable = classifications.every(
          (c) => "店舗受取不可" === c,
        ),
        hasPickupOnly = classifications.some((c) => "店舗受取専用" === c),
        hasPickupNotAvailable = classifications.some(
          (c) => "店舗受取不可" === c,
        );
      hasPickupOnly && hasPickupNotAvailable
        ? (alert(
          "選択された商品には配送方法が異なる商品が含まれています。配送方法を統一してください。",
        ),
          (deliveryRadio.disabled = !0),
          (pickupRadio.disabled = !0))
        : allPickupOnly || hasPickupOnly
          ? ((deliveryRadio.disabled = !0),
            (pickupRadio.disabled = !1),
            deliveryRadio.checked
              ? ((deliveryRadio.checked = !1),
                (pickupRadio.checked = !0),
                this.changeDeliveryMethod(new Event("change")))
              : pickupRadio.checked ||
              ((pickupRadio.checked = !0),
                this.changeDeliveryMethod(new Event("change"))))
          : allPickupNotAvailable || hasPickupNotAvailable
            ? ((deliveryRadio.disabled = !1),
              (pickupRadio.disabled = !0),
              pickupRadio.checked
                ? ((pickupRadio.checked = !1),
                  (deliveryRadio.checked = !0),
                  this.changeDeliveryMethod(new Event("change")))
                : deliveryRadio.checked ||
                ((deliveryRadio.checked = !0),
                  this.changeDeliveryMethod(new Event("change"))))
            : ((deliveryRadio.disabled = !1), (pickupRadio.disabled = !1));
    }
    disconnectedCallback() {
      var _a, _b, _c, _d, _e, _f, _g;
      (null == (_a = this.deliveryAddress.actions.selectAddressButton) ||
        _a.removeEventListener(
          "click",
          this.__listeners.deliveryAddress.openModal,
        ),
        null == (_b = this.deliveryAddress.actions.copyAddressButton) ||
        _b.removeEventListener(
          "click",
          this.__listeners.deliveryAddress.copyAddress,
        ),
        null == (_c = this.products.actions.selectProductsButton) ||
        _c.removeEventListener("click", this.__listeners.products.openModal),
        null == (_d = this.products.modal.cancelButton) ||
        _d.removeEventListener("click", this.__listeners.products.closeModal),
        null == (_e = this.products.modal.confirmButton) ||
        _e.removeEventListener(
          "click",
          this.__listeners.products.submitModal,
        ),
        this.deliveryMethod.radios.forEach((radio) => {
          radio.removeEventListener(
            "change",
            this.__changeDeliveryMethodListener,
          );
        }),
        this.removeEventListener(
          "settings-change",
          this.__datetimeSelectedListener,
        ),
        document.removeEventListener(
          "cart:updated",
          this.__cartUpdatedListener,
        ),
        null == (_f = this.storeSelection.actions.selectStoreButton) ||
        _f.removeEventListener("click", this.__listeners.store.openModal),
        null == (_g = this.storeSelection.actions.editStoreButton) ||
        _g.removeEventListener("click", this.__listeners.store.openModal));
    }
    changeDeliveryMethod(_event) {
      const selectedRadio = Array.from(this.deliveryMethod.radios).find(
        (radio) => radio.checked,
      ),
        deliveryMethod =
          (null == selectedRadio ? void 0 : selectedRadio.value) || "";
      ("delivery" === deliveryMethod
        ? (this.deliveryAddress.section.classList.remove("hidden"),
          this.storeSelection.section.classList.add("hidden"),
          this.deliveryAddress.title &&
          (this.deliveryAddress.title.textContent = "お届け先"))
        : "pickup" === deliveryMethod
          ? (this.storeSelection.section.classList.remove("hidden"),
            this.deliveryAddress.section.classList.add("hidden"))
          : (this.deliveryAddress.section.classList.add("hidden"),
            this.storeSelection.section.classList.add("hidden")),
        this.recalculateShippingFeeDebounced());
    }
    openStoreModal() {
      var _a;
      if (0 === this.availableStores.length)
        return void alert("選択可能な店舗がありません");
      const currentData =
        null == (_a = this.storeSelection.selectedStoreData.textContent)
          ? void 0
          : _a.trim();
      let currentStorePickupId;
      if (currentData)
        try {
          const parsed = JSON.parse(currentData);
          currentStorePickupId =
            (null == parsed ? void 0 : parsed.storeId) || void 0;
        } catch {
          currentStorePickupId = void 0;
        }
      const config = {
        title: "店舗選択",
        stores: this.availableStores,
        selectedStoreId: currentStorePickupId,
        onSelect: (store) => {
          this.handleStoreSelected(store);
        },
      };
      this.storeModal.open(config);
    }
    handleStoreSelected(store) {
      var _a, _b, _c, _d;
      const selectedRadio = Array.from(this.deliveryMethod.radios).find(
        (radio) => radio.checked,
      ),
        deliveryMethod =
          (null == selectedRadio ? void 0 : selectedRadio.value) || "",
        storeData = {
          storeMetaobjectId: store.id,
          storeId: store.pickup_location_id,
          storeName: store.pickup_location_name,
        };
      ((this.storeSelection.selectedStoreData.textContent =
        JSON.stringify(storeData)),
        (this.storeSelection.selectedStoreName.textContent = `店舗名：${store.pickup_location_name}`),
        (this.storeSelection.selectedStoreId.textContent = `店舗コード：${store.pickup_location_id}`),
        null == (_a = this.storeSelection.empty) || _a.classList.add("hidden"),
        this.storeSelection.info.classList.remove("hidden"),
        null == (_b = this.storeSelection.actions.selectStoreButton) ||
        _b.classList.add("hidden"),
        null == (_c = this.storeSelection.actions.editStoreButton) ||
        _c.classList.remove("hidden"),
        (this.deliveryAddress.selectedDestinationData.textContent = ""));
      const customEvent = new CustomEvent("settings-change", {
        bubbles: !0,
        detail: {
          type: "destination-store-selected",
          data: {
            groupId:
              null == (_d = this.closest("delivery-group"))
                ? void 0
                : _d.getAttribute("data-delivery-group-id"),
            destination: {
              destinationId: this.getAttribute("data-destination-id"),
              deliveryMethod: deliveryMethod,
              ...storeData,
            },
          },
        },
      });
      (this.dispatchEvent(customEvent), this.recalculateShippingFeeDebounced());
    }
    handleCopyAddressClick() {
      const sourceDataText =
        this.deliveryAddress.selectedDestinationData.textContent;
      if (!sourceDataText) return void alert("お届け先が選択されていません");
      const sourceData = JSON.parse(sourceDataText),
        groupElement = this.closest("delivery-group"),
        groupId =
          (null == groupElement
            ? void 0
            : groupElement.getAttribute("data-delivery-group-id")) || "",
        groupIndex = parseInt(
          (null == groupElement
            ? void 0
            : groupElement.getAttribute("data-delivery-group-index")) || "0",
        ),
        destinationIndex = parseInt(
          this.getAttribute("data-destination-index") || "0",
        ),
        copyDialog = document.querySelector("copy-destination-dialog");
      copyDialog
        ? copyDialog.open({
          groupId: groupId,
          groupIndex: groupIndex,
          destinationId: this.destinationId,
          destinationIndex: destinationIndex,
          destinationData: sourceData,
        })
        : alert(
          "複写ダイアログが見つかりません。ページを再読み込みしてください。",
        );
    }
    handleCopyProductsClick() {
      var _a, _b;
      if (0 === this.products.shippingUnits.children.length)
        return void alert("複写する商品が選択されていません");
      const groupElement = this.closest("delivery-group"),
        groupId =
          (null == groupElement
            ? void 0
            : groupElement.getAttribute("data-delivery-group-id")) || "",
        groupIndex = parseInt(
          (null == groupElement
            ? void 0
            : groupElement.getAttribute("data-delivery-group-index")) || "0",
        ),
        destinationIndex = parseInt(
          this.getAttribute("data-destination-index") || "0",
        ),
        multiShippingSettings = this.closest("multi-shipping-settings"),
        settings =
          null ==
            (_a =
              null == multiShippingSettings
                ? void 0
                : multiShippingSettings.getSettings)
            ? void 0
            : _a.call(multiShippingSettings);
      if (!settings || !settings.deliveryGroups)
        return void alert("設定データが取得できません");
      const group = settings.deliveryGroups.find((g) => g.groupId === groupId),
        destination =
          null == (_b = null == group ? void 0 : group.destinations)
            ? void 0
            : _b.find((d) => d.destinationId === this.destinationId);
      if (!destination || !destination.items || 0 === destination.items.length)
        return void alert("複写する商品が見つかりません");
      const allItems = destination.items,
        destinationDataText =
          this.deliveryAddress.selectedDestinationData.textContent;
      let name = "-",
        firstName = "",
        lastName = "";
      if (destinationDataText)
        try {
          const destinationData = JSON.parse(destinationDataText);
          ((firstName = destinationData.firstName || ""),
            (lastName = destinationData.lastName || ""),
            (name =
              destinationData.name ||
              `${lastName} ${firstName}`.trim() ||
              "-"));
        } catch (e) { }
      const copyDialog = document.querySelector("copy-products-dialog");
      copyDialog
        ? copyDialog.open({
          groupId: groupId,
          groupIndex: groupIndex,
          destinationId: this.destinationId,
          destinationIndex: destinationIndex,
          destinationData: {
            name: name,
            firstName: firstName,
            lastName: lastName,
            items: allItems,
          },
        })
        : alert(
          "複写ダイアログが見つかりません。ページを再読み込みしてください。",
        );
    }
    async openModal(target) {
      var _a, _b, _c, _d, _e, _f;
      if ("address" === target) {
        const addresses =
          (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _a.addresses) || [],
          currentData =
            this.deliveryAddress.selectedDestinationData.textContent,
          currentAddressId = undefined,
          config = {
            title: "配送設定",
            mode: "destination",
            addresses: addresses,
            selectedAddressId: currentData
              ? null ==
                (_d =
                  null ==
                    (_c =
                      null == (_b = JSON.parse(currentData))
                        ? void 0
                        : _b.shopifyAddressId)
                    ? void 0
                    : _c.match(/\/(\d+)$/))
                ? void 0
                : _d[1]
              : null,
            restoredAddress: this.restoredAddress || void 0,
            onSelect: (address) => {
              this.handleAddressSelected(address);
            },
          };
        this.addressModal.open(config);
      } else if ("products" === target) {
        const cart2 =
          null == (_e = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _e.cart;
        cart2 && hasCartLevelDiscounts(cart2);
        const latestCart = await cartCache.get(!0);
        if (latestCart) {
          const cartVariantIds = new Set(
            latestCart.items.map((item) => String(item.variant_id)),
          ),
            productRows = undefined;
          this.querySelectorAll(".product-item-row").forEach((row) => {
            const variantId = row.dataset.variantId;
            if (variantId && !cartVariantIds.has(String(variantId))) {
              row.style.display = "none";
              const checkbox = row.querySelector('input[type="checkbox"]');
              checkbox && (checkbox.checked = !1);
            } else row.style.display = "";
          });
        }
        ((this.products.modal.productCheckboxes = this.querySelectorAll(
          selectors.products.modal.productCheckboxes,
        )),
          (this.products.modal.productQuantityInputs = this.querySelectorAll(
            selectors.products.modal.productQuantityInputs,
          )),
          this.syncCheckboxKeys());
        const multiShippingSettings = this.closest("multi-shipping-settings"),
          currentOrderType = undefined,
          purposeValue =
            "home" ===
              (null == multiShippingSettings
                ? void 0
                : multiShippingSettings.currentOrderType)
              ? "自宅用"
              : null ==
                (_f =
                  null == multiShippingSettings
                    ? void 0
                    : multiShippingSettings.querySelector("input[name='purpose']:checked"))
                ? void 0
                : _f.value;
        (purposeValue && this.updateProductPurposeValidation(purposeValue),
          this.products.modal.productCheckboxes.forEach((checkbox, index) => {
            const quantityInput =
              this.products.modal.productQuantityInputs[index];
            if (quantityInput) {
              quantityInput.disabled = !checkbox.checked;
              const handleCheckboxChange = () => {
                ((quantityInput.disabled = !checkbox.checked),
                  checkbox.checked || (quantityInput.value = "1"));
              };
              (checkbox.removeEventListener("change", handleCheckboxChange),
                checkbox.addEventListener("change", handleCheckboxChange));
            }
          }),
          this.applyInventoryMaxToQuantityInputs(),
          this.clearProductInventoryError(),
          this.products.modal.dialog.showModal());
      }
    }
    closeModal(target) {
      "address" === target
        ? this.addressModal.close()
        : "products" === target && this.products.modal.dialog.close();
    }
    async handleDatetimeSelected(event) {
      var _a, _b;
      const customEvent = event;
      if (
        "shipping-unit-datetime-selected" !==
        (null == (_a = customEvent.detail) ? void 0 : _a.type)
      )
        return;
      window._IS_DATE_TIME_SELECTED = !0;
      const eventData = customEvent.detail.data,
        destinationId = this.getAttribute("data-destination-id");
      (null == (_b = null == eventData ? void 0 : eventData.shippingUnit)
        ? void 0
        : _b.destinationId) &&
        eventData.shippingUnit.destinationId.startsWith(destinationId) &&
        this.recalculateShippingFeeDebounced();
    }
    handleCartUpdated(event) {
      var _a;
      const customEvent = undefined,
        source = undefined;
      if (
        "multi-shipping-settings" ===
        (null == (_a = event.detail) ? void 0 : _a.source)
      )
        return;
      const isDialogOpen = undefined;
      this.products.modal.dialog &&
        !this.products.modal.dialog.matches(":not([open])")
        ? setTimeout(() => this.refreshProductModal(), 100)
        : ((this.products.modal.productCheckboxes =
          document.querySelectorAll(".__non_existent__")),
          (this.products.modal.productQuantityInputs =
            document.querySelectorAll(".__non_existent__")));
    }
    syncCheckboxKeys() {
      var _a, _b;
      const cartItems =
        (null ==
          (_b =
            null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.cart)
          ? void 0
          : _b.items) || [],
        usedCartKeys = new Set();
      this.products.modal.productCheckboxes.forEach((checkbox) => {
        const currentKey = checkbox.dataset.key;
        if (!currentKey) return;
        const exactMatch = undefined;
        if (cartItems.find((item) => item.key === currentKey))
          return void usedCartKeys.add(currentKey);
        const variantIdFromKey = currentKey.split(":")[0],
          candidates = cartItems.filter(
            (item) =>
              item.variant_id.toString() === variantIdFromKey &&
              !usedCartKeys.has(item.key),
          );
        if (candidates.length > 0) {
          const newItem = candidates[0];
          ((checkbox.dataset.key = newItem.key), usedCartKeys.add(newItem.key));
        }
      });
    }
    refreshProductModal() {
      ((this.products.modal.productCheckboxes = this.querySelectorAll(
        selectors.products.modal.productCheckboxes,
      )),
        (this.products.modal.productQuantityInputs = this.querySelectorAll(
          selectors.products.modal.productQuantityInputs,
        )),
        this.syncCheckboxKeys(),
        this.products.modal.productCheckboxes.forEach((checkbox, index) => {
          const quantityInput =
            this.products.modal.productQuantityInputs[index];
          if (quantityInput) {
            quantityInput.disabled = !checkbox.checked;
            const handleCheckboxChange = () => {
              ((quantityInput.disabled = !checkbox.checked),
                checkbox.checked || (quantityInput.value = "1"));
            };
            (checkbox.removeEventListener("change", handleCheckboxChange),
              checkbox.addEventListener("change", handleCheckboxChange));
          }
        }));
    }
    async recalculateShipping() {
      await this.recalculateShippingFeeDebounced();
    }
    recalculateShippingFeeDebounced() {
      (null !== this.shippingFeeDebounceTimer &&
        window.clearTimeout(this.shippingFeeDebounceTimer),
        (this.shippingFeeDebounceTimer = window.setTimeout(() => {
          (this.recalculateShippingFee(),
            (this.shippingFeeDebounceTimer = null));
        }, this.SHIPPING_FEE_DEBOUNCE_MS)));
    }
    async recalculateShippingFee() {
      var _a, _b, _c, _d;
      const hasProducts = this.products.shippingUnits.children.length > 0,
        selectedRadio = Array.from(this.deliveryMethod.radios).find(
          (radio) => radio.checked,
        ),
        deliveryMethod =
          (null == selectedRadio ? void 0 : selectedRadio.value) || "";
      if (!hasProducts || !deliveryMethod) return;
      if ("pickup" === deliveryMethod) {
        const customEvent2 = new CustomEvent("settings-change", {
          bubbles: !0,
          detail: {
            type: "destination-shipping-fee-updated",
            data: {
              groupId:
                null == (_a = this.closest("delivery-group"))
                  ? void 0
                  : _a.getAttribute("data-delivery-group-id"),
              destination: {
                destinationId: this.getAttribute("data-destination-id"),
                shippingFee: 0,
                totalWeight: 0,
              },
            },
          },
        });
        return void this.dispatchEvent(customEvent2);
      }

      let errorElement = this.deliveryAddress.section.querySelector(
        ".delivery-unavailable-error",
      ),
        errorAddressElement = this.deliveryAddress.section.querySelector(
          ".delivery-unavailable-error-address",
        );
      if (!errorElement) {
        const addressInfo = this.deliveryAddress.section.querySelector(
          ".delivery-address-info",
        );
        if (addressInfo) {
          const errorContainer = document.createElement("div");
          ((errorContainer.className = "delivery-unavailable-error hidden"),
            (errorContainer.style.cssText =
              "\n            background-color: #fef2f2;\n            border: 2px solid #dc2626;\n            border-radius: 4px;\n            padding: 16px;\n            margin-top: 16px;\n          "),
            (errorContainer.innerHTML =
              '\n            <div class="delivery-unavailable-error-message" style="color: #991b1b; font-weight: 600; font-size: 14px; line-height: 1.5; margin-bottom: 8px;">\n              ⚠️ 申し訳ございません。選択されたお届け先は配送対象外地域です。別のお届け先をご選択ください。\n            </div>\n            <div class="delivery-unavailable-error-address" style="color: #7f1d1d; font-size: 13px; line-height: 1.5;"></div>\n          '),
            addressInfo.insertAdjacentElement("afterend", errorContainer),
            (errorElement = errorContainer),
            (errorAddressElement = errorContainer.querySelector(
              ".delivery-unavailable-error-address",
            )));
        }
      }

      const destinationId = this.getAttribute("data-destination-id"),
        shippingUnits = Object.entries(
          window.BINDEC_SHIPPING_UNIT_SETTINGS || {},
        )
          .filter(([unitId, unit]) => unitId.startsWith(`${destinationId}_`))
          .map(([unitId, unit]) => unit);

      const productMetafields = (window.BINDEC_SHIPPING_SETTINGS && window.BINDEC_SHIPPING_SETTINGS.productMetafields) || {};
      const slipTypeMaster = (window.BINDEC_SHIPPING_SETTINGS && window.BINDEC_SHIPPING_SETTINGS.slipTypeMaster) || {};
      let hasQuantityError = false;
      let currentMaxQty = 1;

      if (shippingUnits.length > 0) {
        for (const unit of shippingUnits) {
          for (const item of (unit.items || [])) {
            const variantId = String(item.variant_id);
            const meta = productMetafields[variantId];
            if (!meta || !meta.slip_type_id) continue;

            let handles = Array.isArray(meta.slip_type_id) ? meta.slip_type_id : [meta.slip_type_id];

            for (const handle of handles) {
              const slipTypeData = slipTypeMaster[handle];
              if (slipTypeData && null != slipTypeData.max_quantity) {
                const maxQty = parseInt(slipTypeData.max_quantity, 10);
                if (!isNaN(maxQty) && item.quantity > maxQty) {
                  hasQuantityError = true;
                  currentMaxQty = maxQty;
                  break;
                }
              }
            }
            if (hasQuantityError) break;
          }
          if (hasQuantityError) break;
        }
      }

      if (hasQuantityError) {
        if (errorElement) {
          errorElement.classList.remove("hidden");
          const messageElement = errorElement.querySelector(
            ".delivery-unavailable-error-message",
          );
          if (messageElement) {
            messageElement.textContent = `⚠️ 1商品あたりの数量は最大${currentMaxQty}個までです。数量を減らすか、届け先を分けてご注文ください。`;
          }
        }
        if (errorAddressElement) {
          errorAddressElement.textContent = "";
        }

        const customEvent = new CustomEvent("settings-change", {
          bubbles: !0,
          detail: {
            type: "destination-shipping-fee-updated",
            data: {
              groupId: null == this.closest("delivery-group") ? void 0 : this.closest("delivery-group").getAttribute("data-delivery-group-id"),
              destination: {
                destinationId: destinationId,
                shippingFee: 0,
                originalShippingFee: 0,
                totalWeight: 0,
                shippingUnits: {},
                hasUndeliverableDestination: true,
                undeliverableErrorMessage: `1商品あたりの数量は最大${currentMaxQty}個までです。数量を減らすか、届け先を分けてご注文ください。`
              },
            },
          },
        });
        this.dispatchEvent(customEvent);
        return;
      }

      if (errorElement) errorElement.classList.add("hidden");

      const selectedDestinationAddress = JSON.parse(
        this.deliveryAddress.selectedDestinationData.textContent || "null",
      );
      if (!selectedDestinationAddress || !selectedDestinationAddress.address)
        return;

      if (0 === shippingUnits.length) return;
      const purposeSelect = document.querySelector("input[name='purpose']:checked"),
        currentPurpose =
          (null == purposeSelect ? void 0 : purposeSelect.value) ||
          (null == (_b = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _b.currentPurpose),
        destinations = shippingUnits.map((unit) => {
          var _a2, _b2, _c2, _d2, _e, _f, _g;
          const firstItem = null == (_a2 = unit.items) ? void 0 : _a2[0];
          let documentTypeId, deliveryControlId, shippingRuleId;
          if (firstItem) {
            const variantId =
              null == (_b2 = firstItem.variant_id) ? void 0 : _b2.toString(),
              metafields =
                null ==
                  (_d2 =
                    null == (_c2 = window.BINDEC_SHIPPING_SETTINGS)
                      ? void 0
                      : _c2.productMetafields)
                  ? void 0
                  : _d2[variantId],
              variantRuleInfo =
                null ==
                  (_f =
                    null == (_e = window.BINDEC_SHIPPING_SETTINGS)
                      ? void 0
                      : _e.variantShippingRuleIds)
                  ? void 0
                  : _f[variantId];
            if (
              ((null == variantRuleInfo ? void 0 : variantRuleInfo.variant) &&
                (shippingRuleId = Array.isArray(variantRuleInfo.variant)
                  ? variantRuleInfo.variant[0]
                  : variantRuleInfo.variant),
                metafields)
            ) {
              const primarySlipType = undefined;
              ((documentTypeId =
                selectPrimarySlipType(metafields.slip_type_id) ||
                unit.deliverySlipType ||
                void 0),
                (deliveryControlId = metafields.delivery_control || void 0));
            }
          }
          return {
            address: {
              prefecture: selectedDestinationAddress.address.prefecture,
              zipcode: selectedDestinationAddress.address.zipcode,
            },
            items: unit.items || [],
            shippingRuleId: shippingRuleId,
            documentTypeId: documentTypeId,
            deliveryControlId: deliveryControlId,
            purpose: currentPurpose,
            shippingStartDate: unit.shippingStartDate,
            destination_id: destinationId || void 0,
            desiredDeliveryDate: unit.deliveryDate || void 0,
          };
        }),
        multipleResult =
          await calculateShippingForMultipleDestinations(destinations);
      if ("error" === multipleResult.success) return;
      const shippingUnitResults = [];
      let hasUndeliverableDestination = !1,
        undeliverableErrorMessage = "";
      shippingUnits.forEach((unit, index) => {
        const calculation = multipleResult.calculations[index],
          totalWeight = (unit.items || []).reduce(
            (sum, item) => sum + item.grams * item.quantity,
            0,
          );
        (!1 === calculation.is_deliverable &&
          ((hasUndeliverableDestination = !0),
            (undeliverableErrorMessage =
              calculation.error ||
              "申し訳ございません。選択されたお届け先は配送対象外地域です。別のお届け先をご選択ください。")),
          shippingUnitResults.push({
            ...unit,
            items: (unit.items || []).map((i) => {
              var _a2, _b2, _c2;
              const vid =
                null == (_a2 = i.variant_id) ? void 0 : _a2.toString(),
                meta =
                  null ==
                    (_c2 =
                      null == (_b2 = window.BINDEC_SHIPPING_SETTINGS)
                        ? void 0
                        : _b2.productMetafields)
                    ? void 0
                    : _c2[vid];
              return {
                ...i,
                shipper_code:
                  (null == meta ? void 0 : meta.shipper_code) || void 0,
              };
            }),
            shippingCost: calculation.shipping_cost,
            totalWeight: totalWeight,
            shipperCode: calculation.shipperCode,
            company: calculation.company,
            delivery_method: calculation.delivery_method,
            shipping_rule_id: calculation.shipping_rule_id,
            earliestDeliveryDate: calculation.earliest_delivery_date,
            latestDeliveryDate: calculation.latest_delivery_date,
            scheduledShipDate: calculation.scheduled_ship_date,
            deliverySlipType: calculation.deliverySlipType,
            documentTypeId: calculation.documentTypeId,
            documentTypeHandle: calculation.documentTypeHandle,
            productLeadTimeDays: calculation.product_lead_time_days,
            deliveryLeadTimeDays: calculation.delivery_lead_time_days,
            totalLeadTimeDays: calculation.total_lead_time_days,
            isDeliveryDateAvailable: calculation.is_delivery_date_available,
            isDeliverable: calculation.is_deliverable,
            availableDeliveryPeriods: calculation.availableDeliveryPeriods,
            availableDeliveryTime: calculation.available_delivery_time,
            desiredDeliveryDateError: calculation.desired_delivery_date_error,
          }));
      });
      const totalShippingFee = multipleResult.total_shipping,
        originalTotalShippingFee =
          multipleResult.original_total_shipping ?? totalShippingFee;
      originalTotalShippingFee > totalShippingFee &&
        shippingUnitResults.length > 0 &&
        shippingUnitResults.forEach((unitResult, index) => {
          const calculation = undefined,
            originalCost =
              multipleResult.calculations[index].original_shipping_cost ??
              unitResult.shippingCost;
          ((unitResult.originalShippingCost = originalCost),
            (unitResult.shippingCost = originalCost));
        });
      const shippingInfo = {
        shippingFee: originalTotalShippingFee,
        originalShippingFee: originalTotalShippingFee,
        totalWeight: 0,
        shippingUnits: shippingUnitResults,
      };
      ((shippingInfo.totalWeight = shippingInfo.shippingUnits.reduce(
        (sum, unit) => sum + unit.totalWeight,
        0,
      )),
        shippingInfo.shippingUnits.forEach((unitWithCost, index) => {
          const matchingEntry = Object.entries(
            window.BINDEC_SHIPPING_UNIT_SETTINGS || {},
          )
            .filter(([unitId]) => unitId.startsWith(`${destinationId}_`))
            .find(([_, unit]) => unit.id === unitWithCost.id);
          if (matchingEntry) {
            const [unitId, unit] = matchingEntry;
            window.BINDEC_SHIPPING_UNIT_SETTINGS[unitId] = {
              ...unit,
              items: unitWithCost.items,
              shippingCost: unitWithCost.shippingCost,
              totalWeight: unitWithCost.totalWeight,
              shipperCode: unitWithCost.shipperCode,
              company: unitWithCost.company,
              delivery_method: unitWithCost.delivery_method,
              shipping_rule_id: unitWithCost.shipping_rule_id,
              earliestDeliveryDate: unitWithCost.earliestDeliveryDate,
              latestDeliveryDate: unitWithCost.latestDeliveryDate,
              scheduledShipDate: unitWithCost.scheduledShipDate,
              deliverySlipType: unitWithCost.deliverySlipType,
              documentTypeId: unitWithCost.documentTypeId,
              documentTypeHandle: unitWithCost.documentTypeHandle,
              productLeadTimeDays: unitWithCost.productLeadTimeDays,
              deliveryLeadTimeDays: unitWithCost.deliveryLeadTimeDays,
              totalLeadTimeDays: unitWithCost.totalLeadTimeDays,
              isDeliveryDateAvailable: unitWithCost.isDeliveryDateAvailable,
              isDeliverable: unitWithCost.isDeliverable,
              availableDeliveryPeriods: unitWithCost.availableDeliveryPeriods,
              availableDeliveryTime: unitWithCost.availableDeliveryTime,
              desiredDeliveryDateError: unitWithCost.desiredDeliveryDateError,
              deliveryDate: unit.deliveryDate || "",
              deliveryTime: unit.deliveryTime || "",
              deliveryPeriod: unit.deliveryPeriod || "",
              shippingStartDate: unit.shippingStartDate || void 0,
              giftSettings: unit.giftSettings || void 0,
            };
          }
        }),
        shippingInfo.shippingUnits.forEach((unitWithCost) => {
          const shippingUnitKey = `${destinationId}_${unitWithCost.id}`,
            datetimeSelector = this.deliveryAddress.section.querySelector(
              `datetime-selector[data-shipping-unit-id="${unitWithCost.id}"]`,
            );
          if (!datetimeSelector) return;
          const datetimeOptions =
            datetimeSelector.querySelector(".datetime-options");
          if (!datetimeOptions) return;
          const existingError = datetimeOptions.querySelector(
            ".desired-delivery-date-error",
          );
          if (unitWithCost.desiredDeliveryDateError)
            if (existingError)
              existingError.textContent = unitWithCost.desiredDeliveryDateError;
            else {
              const errorDiv = document.createElement("div");
              ((errorDiv.className = "desired-delivery-date-error"),
                (errorDiv.style.cssText =
                  "color: #dc2626; font-size: 13px; margin-top: 8px;"),
                (errorDiv.textContent = unitWithCost.desiredDeliveryDateError),
                datetimeOptions.appendChild(errorDiv));
            }
          else existingError && existingError.remove();
        }),
        shippingInfo.shippingUnits.forEach((unitWithCost) => {
          const datetimeSelector = this.deliveryAddress.section.querySelector(
            `datetime-selector[data-shipping-unit-id="${unitWithCost.id}"]`,
          );
          datetimeSelector &&
            datetimeSelector.dispatchEvent(
              new CustomEvent("delivery-time-updated", { bubbles: !1 }),
            );
        }),
        shippingInfo.shippingUnits.forEach((unitWithCost) => {
          const availableTime = unitWithCost.availableDeliveryTime;
          if (!availableTime || 0 === availableTime.length) return;
          const matchingEntry = Object.entries(
            window.BINDEC_SHIPPING_UNIT_SETTINGS || {},
          )
            .filter(([unitId]) => unitId.startsWith(`${destinationId}_`))
            .find(([_, unit]) => unit.id === unitWithCost.id);
          if (!matchingEntry) return;
          const [, savedUnit] = matchingEntry,
            deliveryTime = savedUnit.deliveryTime,
            deliveryPeriod = savedUnit.deliveryPeriod;
          if (deliveryTime && !deliveryPeriod) {
            const matchedSlot = availableTime.find(
              (slot) => slot.value === deliveryTime,
            );
            if (matchedSlot) {
              const datetimeSelector = document.querySelector(
                `datetime-selector[data-shipping-unit-id="${unitWithCost.id}"]`,
              ),
                timeSummary =
                  null == datetimeSelector
                    ? void 0
                    : datetimeSelector.querySelector(
                      ".js-datetime-summary-time",
                    );
              timeSummary && (timeSummary.textContent = matchedSlot.label);
            }
          }
        }));

      if (hasUndeliverableDestination) {
        if (errorElement) {
          errorElement.classList.remove("hidden");
          const messageElement = errorElement.querySelector(
            ".delivery-unavailable-error-message",
          );
          messageElement &&
            (messageElement.textContent = `⚠️ ${undeliverableErrorMessage}`);
        }
        if (errorAddressElement) {
          const address = selectedDestinationAddress.address;
          errorAddressElement.textContent = `お届け先: ${address.prefecture || ""} ${address.zipcode || ""}`;
        }
      } else errorElement && errorElement.classList.add("hidden");
      const shippingUnitsRecord = {};
      Object.entries(window.BINDEC_SHIPPING_UNIT_SETTINGS || {})
        .filter(([unitId]) => unitId.startsWith(`${destinationId}_`))
        .forEach(([unitId, unit]) => {
          shippingUnitsRecord[unitId] = unit;
        });
      const customEvent = new CustomEvent("settings-change", {
        bubbles: !0,
        detail: {
          type: "destination-shipping-fee-updated",
          data: {
            groupId:
              null == (_d = this.closest("delivery-group"))
                ? void 0
                : _d.getAttribute("data-delivery-group-id"),
            destination: {
              destinationId: this.getAttribute("data-destination-id"),
              shippingFee: shippingInfo.shippingFee,
              originalShippingFee: shippingInfo.originalShippingFee,
              totalWeight: shippingInfo.totalWeight,
              shippingUnits: shippingUnitsRecord,
              hasUndeliverableDestination: hasUndeliverableDestination,
              undeliverableErrorMessage: undeliverableErrorMessage,
            },
          },
        },
      });
      this.dispatchEvent(customEvent);
    }
    updateProductPurposeValidation(currentPurpose) {
      var _a, _b;
      const cartItemsWithPurposes =
        (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
          ? void 0
          : _a.cartItemsWithPurposes) || [],
        purposeMaster =
          (null == (_b = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _b.purposeMaster) || [],
        productRows = undefined;
      this.products.modal.dialog
        .querySelectorAll(".product-item-row")
        .forEach((row, index) => {
          const checkbox = row.querySelector(".js-product-checkbox"),
            warningDiv = row.querySelector(".product-purpose-warning"),
            variantId = null == checkbox ? void 0 : checkbox.dataset.variantId;
          if (!checkbox || !variantId) return;
          const cartItem = cartItemsWithPurposes.find((item) => {
            var _a2;
            return (
              (null == (_a2 = item.variant_id) ? void 0 : _a2.toString()) ===
              variantId
            );
          });
          if (!cartItem || !cartItem.purposes) return;
          const hasPurpose = undefined;
          if (
            !cartItem.purposes.some((purposeItem) => {
              const purposeId =
                "string" == typeof purposeItem
                  ? purposeItem
                  : purposeItem.id || purposeItem.name || purposeItem._title,
                productPurpose = purposeMaster.find(
                  (p) =>
                    p.id === purposeId ||
                    p.name === purposeId ||
                    p._title === purposeId,
                );
              if (!productPurpose) return !1;
              const selectedPurpose = purposeMaster.find(
                (p) =>
                  p.id === currentPurpose ||
                  p.name === currentPurpose ||
                  p._title === currentPurpose,
              );
              if (!selectedPurpose) return !1;
              const idMatches =
                productPurpose.id &&
                selectedPurpose.id &&
                productPurpose.id === selectedPurpose.id,
                productName = productPurpose.name || productPurpose._title,
                selectedName = selectedPurpose.name || selectedPurpose._title,
                nameMatches =
                  productName && selectedName && productName === selectedName,
                productIsActive =
                  void 0 !== productPurpose.is_published
                    ? productPurpose.is_published
                    : productPurpose._isActive,
                selectedIsActive =
                  void 0 !== selectedPurpose.is_published
                    ? selectedPurpose.is_published
                    : selectedPurpose._isActive,
                matches = undefined;
              return (
                productIsActive &&
                selectedIsActive &&
                (idMatches || nameMatches)
              );
            }) &&
            warningDiv
          ) {
            (warningDiv.classList.remove("hidden"),
              (checkbox.disabled = !0),
              (checkbox.checked = !1),
              (row.style.opacity = "0.5"));
            const quantityInput = row.querySelector(".js-product-quantity");
            quantityInput && (quantityInput.disabled = !0);
          } else if (warningDiv) {
            (warningDiv.classList.add("hidden"),
              (checkbox.disabled = !1),
              (row.style.opacity = "1"));
            const quantityInput = row.querySelector(".js-product-quantity");
            quantityInput && checkbox.checked && (quantityInput.disabled = !1);
          }
        });
    }
    handleAddressSelected(address) {
      var _a, _b, _c, _d, _e;
      const selectedRadio = Array.from(this.deliveryMethod.radios).find(
        (radio) => radio.checked,
      ),
        deliveryMethod =
          (null == selectedRadio ? void 0 : selectedRadio.value) || "",
        addressCustomData =
          (null ==
            (_d =
              null ==
                (_c =
                  null ==
                    (_b =
                      null == (_a = window.BINDEC_SHIPPING_SETTINGS)
                        ? void 0
                        : _a.addressCustomData)
                    ? void 0
                    : _b.addresses)
                ? void 0
                : _c.find((item) => item.id === address.id))
            ? void 0
            : _d.data) || {},
        data = {
          deliveryMethod: deliveryMethod,
          name: `${(address = { ...address, ...(addressCustomData || {}) }).last_name} ${address.first_name}`,
          firstName: address.first_name,
          lastName: address.last_name,
          firstNameKana: address.first_name_kana || "",
          lastNameKana: address.last_name_kana || "",
          honorific: address.honorific,
          shopifyAddressId: `gid://shopify/MailingAddress/${address.id}`,
          company: address.company,
          department: address.department || address.job_title || "",
          email: address.email,
          phone: getDisplayPhone(address),
          address: {
            zipcode: address.zip,
            prefecture: address.province,
            city: address.city,
            town: address.town ?? void 0,
            address_line1: address.address_line1 ?? void 0,
            address1: address.address1,
            address2: address.address2,
          },
        },
        customEvent = new CustomEvent("settings-change", {
          bubbles: !0,
          detail: {
            type: "destination-delivery-address-selected",
            data: {
              groupId:
                null == (_e = this.closest("delivery-group"))
                  ? void 0
                  : _e.getAttribute("data-delivery-group-id"),
              destination: {
                destinationId: this.getAttribute("data-destination-id"),
                deliveryMethod: deliveryMethod,
                ...data,
              },
            },
          },
        });
      if (
        ((this.deliveryAddress.selectedDestinationData.textContent =
          JSON.stringify(data)),
          (this.deliveryAddress.selectedDestinationDepartment.textContent =
            `${address.company || ""} ${address.department || address.job_title || ""}`.trim()),
          (this.deliveryAddress.selectedDestinationName.textContent =
            `${address.last_name} ${address.first_name}`.trim()),
          this.deliveryAddress.selectedDestinationNameKana)
      ) {
        const kana =
          `${address.last_name_kana || ""} ${address.first_name_kana || ""}`.trim();
        this.deliveryAddress.selectedDestinationNameKana.textContent =
          kana || "";
      }
      this.deliveryAddress.selectedDestinationHonorific.textContent = (
        address.honorific || "様"
      ).trim();
      const streetPart = getStreetPartForDisplay(address);
      ((this.deliveryAddress.selectedDestinationAddress.textContent =
        `住所：〒 ${address.zip} ${convertProvinceToJapanese(address.province)} ${address.city} ${streetPart} ${address.address2 || ""}`.trim()),
        (this.deliveryAddress.selectedDestinationPhone.textContent =
          `電話番号：${getDisplayPhone(address)}`.trim()),
        this.deliveryAddress.actions.selectAddressButton.classList.add(
          "hidden",
        ),
        this.deliveryAddress.actions.editAddressButton.classList.remove(
          "hidden",
        ),
        this.deliveryAddress.actions.copyAddressButton.classList.remove(
          "hidden",
        ),
        this.recalculateShippingFeeDebounced(),
        (this.storeSelection.selectedStoreData.textContent = ""),
        this.dispatchEvent(customEvent));
    }
    async submitModal(target) {
      var _a, _b;
      if ("products" === target)
        try {
          const selectedProducts = [];
          if (
            (this.products.modal.productCheckboxes.forEach(
              (checkbox, index) => {
                var _a2, _b2;
                if (checkbox.checked) {
                  const quantityInput =
                    this.products.modal.productQuantityInputs[index],
                    quantity =
                      (quantityInput && parseInt(quantityInput.value, 10)) || 1,
                    cartKey = checkbox.dataset.key,
                    item = window.BINDEC_SHIPPING_SETTINGS.cart.items.find(
                      (item2) =>
                        cartKey
                          ? item2.key === cartKey
                          : item2.variant_id.toString() ===
                          checkbox.dataset.variantId,
                    );
                  if (!item) return;
                  const variantId = item.variant_id.toString(),
                    productMetafields =
                      (null ==
                        (_b2 =
                          null == (_a2 = window.BINDEC_SHIPPING_SETTINGS)
                            ? void 0
                            : _a2.productMetafields)
                        ? void 0
                        : _b2[variantId]) || {},
                    taxRate =
                      productMetafields.tax_rate ||
                      checkbox.dataset.taxRate ||
                      null,
                    taxClsf =
                      productMetafields.tax_clsf ||
                      checkbox.dataset.taxClsf ||
                      null,
                    noDiscountAllowed =
                      productMetafields.no_discount_allowed_flg ??
                      (checkbox.dataset.noDiscountAllowed || null),
                    receivestoreClsf =
                      productMetafields.receivestore_clsf ||
                      checkbox.dataset.receivestoreClsf ||
                      null,
                    itemCode = productMetafields.item_cd || null,
                    symbol = productMetafields.symbol || null,
                    handlingCode = productMetafields.handling_clsf || null,
                    deptCode = productMetafields.dept || null,
                    deptCodeForStore = productMetafields.dept_for_store || null,
                    classCode = productMetafields.class || null,
                    priceSettingType =
                      productMetafields.price_setting_type || null,
                    consumablesFlg = productMetafields.consumables_flg ?? null,
                    supplierCode = productMetafields.supplier_cd || null,
                    makerCode = productMetafields.maker_cd || null,
                    catalogCode = productMetafields.catalog_cd || null,
                    deliveryPeriodFlg =
                      productMetafields.delivery_period_flg ?? !0,
                    deliveryTimeFlg = productMetafields.delivery_time_flg ?? !0,
                    deliveryDateFlg = productMetafields.delivery_date_flg ?? !0,
                    deliveryClsf = productMetafields.delivery_clsf || null,
                    noDiscountBool =
                      "true" === noDiscountAllowed ||
                      "1" === noDiscountAllowed ||
                      !0 === noDiscountAllowed ||
                      1 === noDiscountAllowed;
                  selectedProducts.push({
                    ...item,
                    quantity: quantity,
                    final_price: item.final_price,
                    tax_rate: taxRate,
                    tax_clsf: taxClsf,
                    no_discount_allowed_flg: noDiscountBool,
                    receivestore_clsf: receivestoreClsf,
                    item_cd: itemCode,
                    symbol: symbol,
                    handling_clsf: handlingCode,
                    dept: deptCode,
                    dept_for_store: deptCodeForStore,
                    class: classCode,
                    price_setting_type: priceSettingType,
                    consumables_flg: consumablesFlg,
                    supplier_cd: supplierCode,
                    maker_cd: makerCode,
                    catalog_cd: catalogCode,
                    delivery_period_flg: deliveryPeriodFlg,
                    delivery_time_flg: deliveryTimeFlg,
                    delivery_date_flg: deliveryDateFlg,
                    delively_clsf: deliveryClsf,
                  });
                }
              },
            ),
              0 === selectedProducts.length)
          )
            return void this.products.modal.dialog.close();
          const selectedStoreData = JSON.parse(
            this.storeSelection.selectedStoreData.textContent || "null",
          );
          if (selectedStoreData) {
            const storeCheckResult = this.checkSelectedStoreIsCommonStore(
              selectedStoreData,
              selectedProducts,
            );
            if (!storeCheckResult.isCommonStore) {
              const unavailableProductNames = undefined,
                message = `選択された店舗は、以下の商品では受け取りできません：\n${storeCheckResult.unavailableProducts.map((p) => `・${p.product_title}${p.variant_title ? ` (${p.variant_title})` : ""}`).join("\n")}\n別の店舗を選択するか、商品を変更してください。`;
              return void alert(message);
            }
          }
          if (
            window.BINDEC_SHIPPING_UNIT_SETTINGS &&
            (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
              ? void 0
              : _a.deliveryGroups)
          )
            for (const group of window.BINDEC_SHIPPING_SETTINGS
              .deliveryGroups) {
              const isCurrentGroup = undefined;
              if (
                (group.destinations || []).some(
                  (dest) => dest.destinationId === this.destinationId,
                )
              ) {
                for (const dest of group.destinations || []) {
                  const prefix = `${dest.destinationId}_`;
                  Object.keys(window.BINDEC_SHIPPING_UNIT_SETTINGS).forEach(
                    (unitKey) => {
                      unitKey.startsWith(prefix) &&
                        delete window.BINDEC_SHIPPING_UNIT_SETTINGS[unitKey];
                    },
                  );
                }
                break;
              }
            }
          const totalQuantitiesByVariant =
            this.calculateTotalQuantitiesAcrossDestinations(selectedProducts),
            shortages = findInventoryShortages(totalQuantitiesByVariant);
          if (shortages.length > 0)
            return void this.showProductInventoryError(
              formatShortageMessage(shortages),
            );
          this.clearProductInventoryError();
          const cartUpdates = [];
          if (
            (Object.entries(totalQuantitiesByVariant).forEach(
              ([variantId, totalQuantity]) => {
                const item = window.BINDEC_SHIPPING_SETTINGS.cart.items.find(
                  (item2) => item2.variant_id.toString() === variantId,
                );
                item &&
                  item.quantity !== totalQuantity &&
                  cartUpdates.push({
                    key: item.key,
                    variantId: variantId,
                    quantity: totalQuantity,
                  });
              },
            ),
              cartUpdates.length > 0)
          ) {
            const batchUpdateProductTitles = [];
            try {
              const cart2 = await cartCache.get(!0);
              if (!cart2) throw new Error("カート情報の取得に失敗しました");
              const token =
                null == (_b = cart2.attributes)
                  ? void 0
                  : _b._multi_shipping_session_token,
                batchUpdates = [];
              (cartUpdates.forEach((update) => {
                var _a2;
                const item =
                  cart2.items.find((item2) =>
                    update.key
                      ? item2.key === update.key
                      : item2.variant_id.toString() === update.variantId,
                  ) ||
                  cart2.items.find(
                    (item2) => item2.variant_id.toString() === update.variantId,
                  );
                if (!item) return;
                item.product_title &&
                  batchUpdateProductTitles.push(String(item.product_title));
                const existingProperties = undefined,
                  properties = { ...(item.properties || {}) },
                  isFirstItem =
                    (null == (_a2 = cart2.items[0]) ? void 0 : _a2.key) ===
                    item.key;
                (token &&
                  isFirstItem &&
                  (properties._multi_shipping_token = token),
                  batchUpdates.push({
                    id: item.key,
                    quantity: update.quantity,
                    properties: properties,
                  }));
              }),
                batchUpdates.length > 0 &&
                (await updateCartItemsBatch(batchUpdates, !0)));
            } catch (error) {
              return void this.showProductInventoryError([
                formatCartUpdateErrorForAlert(error, {
                  productTitles: batchUpdateProductTitles,
                }),
              ]);
            }
          }
          const settingsService = undefined,
            productGroups = new SettingsService().separateByShippingUnit(
              selectedProducts,
              window.BINDEC_SHIPPING_SETTINGS.shippingData || [],
              window.BINDEC_SHIPPING_SETTINGS.variantShippingRuleIds,
            ),
            items = [],
            destinationId = this.destinationId;
          window.BINDEC_SHIPPING_UNIT_SETTINGS &&
            Object.keys(window.BINDEC_SHIPPING_UNIT_SETTINGS).forEach(
              (unitKey) => {
                unitKey.startsWith(`${destinationId}_`) &&
                  delete window.BINDEC_SHIPPING_UNIT_SETTINGS[unitKey];
              },
            );
          const groupsHTML = Object.values(productGroups)
            .map((group) => {
              var _a2, _b2, _c, _d, _e, _f, _g, _h;
              const uniqueUnitKey = `${this.destinationId}_${group.id}`,
                existingUnit =
                  null == (_a2 = window.BINDEC_SHIPPING_UNIT_SETTINGS)
                    ? void 0
                    : _a2[uniqueUnitKey],
                groupWithDatetime = {
                  ...group,
                  deliveryDate:
                    (null == existingUnit
                      ? void 0
                      : existingUnit.deliveryDate) || "",
                  deliveryTime:
                    (null == existingUnit
                      ? void 0
                      : existingUnit.deliveryTime) || "",
                  deliveryPeriod:
                    (null == existingUnit
                      ? void 0
                      : existingUnit.deliveryPeriod) || "",
                  giftSettings:
                    (null == existingUnit
                      ? void 0
                      : existingUnit.giftSettings) || void 0,
                  shippingCost: 0,
                  totalWeight: 0,
                  earliestDeliveryDate: void 0,
                  productLeadTimeDays: void 0,
                  deliveryLeadTimeDays: void 0,
                  totalLeadTimeDays: void 0,
                  isDeliveryDateAvailable: void 0,
                };
              window.BINDEC_SHIPPING_UNIT_SETTINGS = {
                ...window.BINDEC_SHIPPING_UNIT_SETTINGS,
                [uniqueUnitKey]: groupWithDatetime,
              };
              const productsHTML = group.items
                .map((product) => {
                  var _a3, _b3;
                  const productMetafields =
                    (null ==
                      (_b3 =
                        null == (_a3 = window.BINDEC_SHIPPING_SETTINGS)
                          ? void 0
                          : _a3.productMetafields)
                      ? void 0
                      : _b3[product.variant_id]) || {},
                    itemWithTax = {
                      productId: `gid://shopify/Product/${product.product_id}`,
                      variantId: `gid://shopify/ProductVariant/${product.variant_id}`,
                      productTitle: product.product_title,
                      variantTitle: product.variant_title,
                      sku: product.sku,
                      quantity: product.quantity,
                      price: product.original_price || product.price,
                      original_price:
                        product.original_price || product.price,
                      original_line_price:
                        product.original_line_price ||
                        product.price * product.quantity,
                      final_price: product.final_price || product.price,
                      final_line_price:
                        product.final_line_price ||
                        product.final_price * product.quantity,
                      totalPrice:
                        product.original_line_price ||
                        product.price * product.quantity,
                      finalPrice: product.final_price || product.price,
                      finalLinePrice:
                        product.final_line_price ||
                        product.final_price * product.quantity,
                      productProperties: Object.entries(
                        product.properties || {},
                      ).map(([key, value]) => ({
                        name: key,
                        value: value,
                      })),
                      total_discount: product.total_discount || null,
                      line_level_discount_allocations:
                        product.line_level_discount_allocations || null,
                      weight: product.grams,
                      weightUnit: "g",
                      tax_rate:
                        productMetafields.tax_rate ??
                        product.tax_rate ??
                        null,
                      tax_clsf:
                        productMetafields.tax_clsf ??
                        product.tax_clsf ??
                        null,
                      no_discount_allowed_flg:
                        productMetafields.no_discount_allowed_flg ??
                        product.no_discount_allowed_flg ??
                        null,
                      item_cd:
                        productMetafields.item_cd ??
                        product.item_cd ??
                        null,
                      symbol:
                        productMetafields.symbol ?? product.symbol ?? null,
                      handling_clsf:
                        productMetafields.handling_clsf ??
                        product.handling_clsf ??
                        null,
                      dept: productMetafields.dept ?? product.dept ?? null,
                      dept_for_store:
                        productMetafields.dept_for_store ??
                        product.dept_for_store ??
                        null,
                      class:
                        productMetafields.class ?? product.class ?? null,
                      price_setting_type:
                        productMetafields.price_setting_type ??
                        product.price_setting_type ??
                        null,
                      consumables_flg:
                        productMetafields.consumables_flg ??
                        product.consumables_flg ??
                        null,
                      supplier_cd:
                        productMetafields.supplier_cd ??
                        product.supplier_cd ??
                        null,
                      maker_cd:
                        productMetafields.maker_cd ??
                        product.maker_cd ??
                        null,
                      catalog_cd:
                        productMetafields.catalog_cd ??
                        product.catalog_cd ??
                        null,
                      shipper_code:
                        productMetafields.shipper_code ??
                        product.shipper_code ??
                        null,
                      delivery_period_flg:
                        productMetafields.delivery_period_flg ?? !0,
                      delivery_time_flg:
                        productMetafields.delivery_time_flg ?? !0,
                      delivery_date_flg:
                        productMetafields.delivery_date_flg ?? !0,
                      delively_clsf:
                        productMetafields.delivery_clsf ??
                        product.delivery_clsf ??
                        null,
                    };
                  return (
                    items.push(itemWithTax),
                    `\n                <div class="product-item" data-variant-id="${product.variant_id}">\n                  <div class="product-info">\n                    <div class="product-image"></div>\n                    <div class="product-details">\n                      <div class="product-name js-product-name">${product.title}</div>\n                      <div class="product-price js-product-price">価格 ${new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(product.final_price / 100)}</div>\n                      <div class="product-quantity js-product-quantity">数量 ${product.quantity}個</div>\n                    </div>\n                  </div>\n                  <div class="product-total">\n                    <div class="product-total-price js-product-total-price">${new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format((product.final_price * product.quantity) / 100)}</div>\n                  </div>\n                </div>\n              `
                  );
                })
                .join(""),
                shippingUnitKey = `${this.destinationId}_${group.id}`,
                savedShippingUnit =
                  null == (_b2 = window.BINDEC_SHIPPING_UNIT_SETTINGS)
                    ? void 0
                    : _b2[shippingUnitKey],
                deliveryDate =
                  (null == savedShippingUnit
                    ? void 0
                    : savedShippingUnit.deliveryDate) || "",
                deliveryTime =
                  (null == savedShippingUnit
                    ? void 0
                    : savedShippingUnit.deliveryTime) || "",
                deliveryPeriod =
                  (null == savedShippingUnit
                    ? void 0
                    : savedShippingUnit.deliveryPeriod) || "";
              let dateLabel = "お届け日時",
                dateValue = "設定なし",
                timeRowStyle = "";
              deliveryPeriod
                ? ((dateLabel = "お届け時期"),
                  (dateValue = deliveryPeriod),
                  (timeRowStyle = ' style="display: none;"'))
                : deliveryDate && (dateValue = deliveryDate);
              let timeValue = "設定なし";
              if (deliveryTime && !deliveryPeriod) {
                const availableTime =
                  null == savedShippingUnit
                    ? void 0
                    : savedShippingUnit.availableDeliveryTime,
                  matchedSlot =
                    null == availableTime
                      ? void 0
                      : availableTime.find(
                        (slot) => slot.value === deliveryTime,
                      );
                timeValue =
                  (null == matchedSlot ? void 0 : matchedSlot.label) ||
                  (null == savedShippingUnit
                    ? void 0
                    : savedShippingUnit.deliveryTimeLabel) ||
                  deliveryTime;
              }
              const firstProduct = group.items[0],
                deliveryPeriodFlg =
                  (null == firstProduct
                    ? void 0
                    : firstProduct.delivery_period_flg) ?? !0,
                deliveryDateFlg =
                  (null == firstProduct
                    ? void 0
                    : firstProduct.delivery_date_flg) ?? !0,
                isPattern4 = undefined,
                datetimeSelectorStyle =
                  !deliveryPeriodFlg && !deliveryDateFlg
                    ? ' style="display: none;"'
                    : "";
              let deliveryMethodLabel = groupWithDatetime.delivery_method;
              if (!deliveryMethodLabel && group.items.length > 0) {
                const firstProduct2 = group.items[0],
                  productMetafields = undefined;
                deliveryMethodLabel =
                  (
                    (null ==
                      (_d =
                        null == (_c = window.BINDEC_SHIPPING_SETTINGS)
                          ? void 0
                          : _c.productMetafields)
                      ? void 0
                      : _d[firstProduct2.variant_id]) || {}
                  ).delivery_clsf || "常温";
              } else deliveryMethodLabel || (deliveryMethodLabel = "常温");
              let companyLabel = groupWithDatetime.company;
              if (!companyLabel && group.items.length > 0) {
                const firstProduct2 = group.items[0],
                  productMetafields = undefined,
                  primarySlipTypeId = selectPrimarySlipType(
                    (
                      (null ==
                        (_f =
                          null == (_e = window.BINDEC_SHIPPING_SETTINGS)
                            ? void 0
                            : _e.productMetafields)
                        ? void 0
                        : _f[firstProduct2.variant_id]) || {}
                    ).slip_type_id,
                  );
                if (primarySlipTypeId) {
                  const slipTypeMaster = undefined,
                    slipTypeInfo = ((null ==
                      (_g = window.BINDEC_SHIPPING_SETTINGS)
                      ? void 0
                      : _g.slipTypeMaster) || {})[primarySlipTypeId];
                  slipTypeInfo &&
                    slipTypeInfo.carrier_name &&
                    (companyLabel = slipTypeInfo.carrier_name);
                }
              }
              companyLabel = companyLabel || "配送会社未設定";
              // グループ内商品のname_inscription_flgを評価
              const enableNameInscription = group.items.every(product => {
                const meta =
                  (window.BINDEC_SHIPPING_SETTINGS?.productMetafields || {})[product.variant_id] || {};
                return meta.name_inscription_flg !== false && meta.name_inscription_flg !== 'false';
              });
              const hasGiftData = undefined,
                giftSelectorHTML =
                  window.GIFT_DATA &&
                    group.items.some((product) => {
                      const pid = String(product.product_id);
                      return (
                        (window.GIFT_DATA[pid] &&
                          window.GIFT_DATA[pid].length > 0) ||
                        (window.GIFT_DATA[parseInt(pid, 10)] &&
                          window.GIFT_DATA[parseInt(pid, 10)].length > 0)
                      );
                    })
                    ? `\n                <gift-selector\n                  data-shipping-unit-id="${group.id}"\n                  data-group-id="${this.deliveryGroupId}"\n                  data-destination-id="${this.destinationId}"\n                  data-enable-name-inscription="${enableNameInscription}"\n                class="${enableNameInscription ? '' : 'no-name-inscription'}"\n                >\n                  <div class="wl-section-heading">ギフトに関するお申込を選択してください。</div>\n                  <div class="gift-options">\n                    <div class="gift-option-row js-gift-row-noshi">\n                      <div class="gift-option-label">のし</div>\n                      <div class="gift-option-value">\n                        <p class="gift-option-value-text js-gift-summary-noshi">設定なし</p>\n                      </div>\n                    </div>\n                    <div class="gift-option-row js-gift-row-noshi-name">\n                      <div class="gift-option-label">名入れ</div>\n                      <div class="gift-option-value">\n                        <p class="gift-option-value-text js-gift-summary-noshi-name">設定なし</p>\n                      </div>\n                    </div>\n                    ${!1 !== (null == (_h = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _h.enableNoshiRequest) ? '\n                    <div class="gift-option-row js-gift-row-noshi-note">\n                      <div class="gift-option-label">ご要望</div>\n                      <div class="gift-option-value">\n                        <p class="gift-option-value-text js-gift-summary-noshi-note">設定なし</p>\n                      </div>\n                    </div>\n                    ' : ""}\n                    <div class="gift-option-row js-gift-row-wrapping">\n                      <div class="gift-option-label">包装形態・ラッピング</div>\n                      <div class="gift-option-value">\n                        <p class="gift-option-value-text js-gift-summary-wrapping">設定なし</p>\n                      </div>\n                    </div>\n                    <div class="gift-option-row js-gift-row-message-card">\n                      <div class="gift-option-label">メッセージカード</div>\n                      <div class="gift-option-value">\n                        <p class="gift-option-value-text js-gift-summary-message-card">設定なし</p>\n                      </div>\n                    </div>\n                    <div class="gift-option-row js-gift-row-message-content">\n                      <div class="gift-option-label">メッセージ</div>\n                      <div class="gift-option-value">\n                        <p class="gift-option-value-text-long js-gift-summary-message-content">設定なし</p>\n                      </div>\n                    </div>\n                    <div class="gift-options-actions">\n                      <button class="btn-select-gift btn">のし等を選択</button>\n                    </div>\n                  </div>\n                </gift-selector>`
                    : "";
              return `\n              <div class="product-group" data-shipping-unit-id="${group.id}">\n                <div class="product-group-header">\n                  <div class="shipping-company-banner">以下の商品は「${deliveryMethodLabel}:${companyLabel}」で配送します</div>\n                </div>\n                ${productsHTML}\n                ${giftSelectorHTML}\n                <datetime-selector\n                  data-shipping-unit-id="${group.id}"\n                  data-group-id="${this.deliveryGroupId}"\n                  data-destination-id="${this.destinationId}"\n                  ${group.company ? `data-company="${JSON.stringify(group.company).replace(/"/g, "&quot;")}"` : ""}\n                  ${datetimeSelectorStyle}\n                >\n                  <div class="datetime-options">\n                    <div class="datetime-option-row">\n                      <div class="datetime-option-label">${dateLabel}</div>\n                      <div class="datetime-option-value">\n                        <p class="datetime-option-value-text js-datetime-summary-date">${dateValue}</p>\n                      </div>\n                    </div>\n                    <div class="datetime-option-row"${timeRowStyle}>\n                      <div class="datetime-option-label">お届け時間帯</div>\n                      <div class="datetime-option-value">\n                        <p class="datetime-option-value-text js-datetime-summary-time">${timeValue}</p>\n                      </div>\n                    </div>\n                    <div class="datetime-options-actions">\n                      <button class="btn-select-datetime btn">お届け日時を選択</button>\n                    </div>\n                  </div>\n                </datetime-selector>\n              </div>\n            `;
            })
            .join(""),
            customEvent = new CustomEvent("settings-change", {
              bubbles: !0,
              detail: {
                type: "destination-products-selected",
                data: {
                  groupId: this.deliveryGroupId,
                  destination: {
                    destinationId: this.destinationId,
                    items: items,
                  },
                },
              },
            });
          (this.dispatchEvent(customEvent),
            (this.products.shippingUnits.innerHTML = groupsHTML),
            this.products.actions.copyProductsButton.classList.remove(
              "hidden",
            ));
          const groupCount = undefined;
          (Object.keys(productGroups).length > 1
            ? this.products.warning.classList.remove("hidden")
            : this.products.warning.classList.add("hidden"),
            this.deliveryMethod.section.classList.remove("hidden"),
            this.products.modal.dialog.close(),
            this.populateStoreOptions(selectedProducts, selectedStoreData),
            this.updateDeliveryMethodAvailability(selectedProducts),
            this.recalculateShippingFeeDebounced());

          document.dispatchEvent(new CustomEvent('custom:cart:badge-update'));
        } catch (error) {
          alert("商品の追加に失敗しました。ページを再読み込みしてください。");
        }
    }
    restoreAddress(addressData) {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p;
      if (!addressData) return;
      if (addressData.deliveryMethod)
        if (
          (this.deliveryMethod.radios.forEach((radio) => {
            radio.value === addressData.deliveryMethod && (radio.checked = !0);
          }),
            this.deliveryMethod.section.classList.remove("hidden"),
            "pickup" === addressData.deliveryMethod)
        ) {
          if (
            (this.storeSelection.section.classList.remove("hidden"),
              this.deliveryAddress.section.classList.add("hidden"),
              addressData.storeMetaobjectId &&
              ((this.storeSelection.selectedStoreData.textContent =
                JSON.stringify({
                  storeMetaobjectId: addressData.storeMetaobjectId,
                  storeId: addressData.storeId,
                  storeName: addressData.storeName,
                })),
                (this.storeSelection.selectedStoreName.textContent = `店舗名：${addressData.storeName || ""}`),
                (this.storeSelection.selectedStoreId.textContent = `店舗コード：${addressData.storeId || ""}`),
                null == (_a = this.storeSelection.empty) ||
                _a.classList.add("hidden"),
                this.storeSelection.info.classList.remove("hidden"),
                null == (_b = this.storeSelection.actions.selectStoreButton) ||
                _b.classList.add("hidden"),
                null == (_c = this.storeSelection.actions.editStoreButton) ||
                _c.classList.remove("hidden")),
              addressData.firstName || addressData.lastName)
          ) {
            if (
              ((this.deliveryAddress.selectedDestinationData.textContent =
                JSON.stringify({
                  deliveryMethod: addressData.deliveryMethod,
                  name: addressData.name,
                  firstName: addressData.firstName,
                  lastName: addressData.lastName,
                  firstNameKana: addressData.firstNameKana,
                  lastNameKana: addressData.lastNameKana,
                  honorific: addressData.honorific,
                  shopifyAddressId: addressData.shopifyAddressId,
                  company: addressData.company,
                  department: addressData.department,
                  email: addressData.email,
                  phone:
                    getDisplayPhone(addressData) || addressData.phone || "",
                  address: addressData.address,
                })),
                (this.deliveryAddress.selectedDestinationDepartment.textContent =
                  `${addressData.company || ""} ${addressData.department || addressData.job_title || ""}`.trim()),
                (this.deliveryAddress.selectedDestinationName.textContent =
                  `${addressData.lastName || ""} ${addressData.firstName || ""}`.trim()),
                this.deliveryAddress.selectedDestinationNameKana)
            ) {
              const kana =
                `${addressData.lastNameKana || ""} ${addressData.firstNameKana || ""}`.trim();
              this.deliveryAddress.selectedDestinationNameKana.textContent =
                kana || "";
            }
            this.deliveryAddress.selectedDestinationHonorific.textContent = (
              addressData.honorific || ""
            ).trim();
            const streetPartStore = addressData.address
              ? getStreetPartForDisplay(addressData.address)
              : "";
            ((this.deliveryAddress.selectedDestinationAddress.textContent =
              `住所：〒 ${(null == (_d = addressData.address) ? void 0 : _d.zipcode) || ""} ${convertProvinceToJapanese((null == (_e = addressData.address) ? void 0 : _e.prefecture) || "")} ${(null == (_f = addressData.address) ? void 0 : _f.city) || ""} ${streetPartStore} ${(null == (_g = addressData.address) ? void 0 : _g.address2) || ""}`.trim()),
              (this.deliveryAddress.selectedDestinationPhone.textContent =
                `電話番号：${getDisplayPhone(addressData)}`.trim()),
              this.deliveryAddress.actions.selectAddressButton.classList.add(
                "hidden",
              ),
              this.deliveryAddress.actions.editAddressButton.classList.remove(
                "hidden",
              ),
              this.deliveryAddress.actions.copyAddressButton.classList.remove(
                "hidden",
              ));
          }
        } else if ("delivery" === addressData.deliveryMethod) {
          if (
            (this.deliveryAddress.section.classList.remove("hidden"),
              this.storeSelection.section.classList.add("hidden"),
              this.deliveryAddress.title &&
              (this.deliveryAddress.title.textContent = "お届け先"),
              (this.deliveryAddress.selectedDestinationData.textContent =
                JSON.stringify({
                  deliveryMethod: addressData.deliveryMethod,
                  name: addressData.name,
                  firstName: addressData.firstName,
                  lastName: addressData.lastName,
                  firstNameKana: addressData.firstNameKana,
                  lastNameKana: addressData.lastNameKana,
                  honorific: addressData.honorific,
                  shopifyAddressId: addressData.shopifyAddressId,
                  company: addressData.company,
                  department: addressData.department,
                  email: addressData.email,
                  phone: getDisplayPhone(addressData) || addressData.phone || "",
                  address: addressData.address,
                })),
              (this.deliveryAddress.selectedDestinationDepartment.textContent =
                `${addressData.company || ""} ${addressData.department || addressData.job_title || ""}`.trim()),
              (this.deliveryAddress.selectedDestinationName.textContent =
                `${addressData.lastName || ""} ${addressData.firstName || ""}`.trim()),
              this.deliveryAddress.selectedDestinationNameKana)
          ) {
            const kana =
              `${addressData.lastNameKana || ""} ${addressData.firstNameKana || ""}`.trim();
            this.deliveryAddress.selectedDestinationNameKana.textContent =
              kana || "";
          }
          this.deliveryAddress.selectedDestinationHonorific.textContent = (
            addressData.honorific || ""
          ).trim();
          const streetPartDelivery = addressData.address
            ? getStreetPartForDisplay(addressData.address)
            : "";
          ((this.deliveryAddress.selectedDestinationAddress.textContent =
            `住所：〒 ${(null == (_h = addressData.address) ? void 0 : _h.zipcode) || ""} ${convertProvinceToJapanese((null == (_i = addressData.address) ? void 0 : _i.prefecture) || "")} ${(null == (_j = addressData.address) ? void 0 : _j.city) || ""} ${streetPartDelivery} ${(null == (_k = addressData.address) ? void 0 : _k.address2) || ""}`.trim()),
            (this.deliveryAddress.selectedDestinationPhone.textContent =
              `電話番号：${getDisplayPhone(addressData)}`.trim()),
            this.deliveryAddress.actions.selectAddressButton.classList.add(
              "hidden",
            ),
            this.deliveryAddress.actions.editAddressButton.classList.remove(
              "hidden",
            ),
            this.deliveryAddress.actions.copyAddressButton.classList.remove(
              "hidden",
            ));
        }
      const isAuthenticated = undefined;
      !CustomerService.isAuthenticated() &&
        addressData &&
        (addressData.firstName || addressData.lastName)
        ? (this.restoredAddress = {
          id: `temp_${Date.now()}`,
          first_name: addressData.firstName || "",
          last_name: addressData.lastName || "",
          first_name_kana: addressData.firstNameKana || "",
          last_name_kana: addressData.lastNameKana || "",
          honorific: addressData.honorific || "",
          company: addressData.company || "",
          department: addressData.department || "",
          email: addressData.email || "",
          phone: getDisplayPhone(addressData) || addressData.phone || "",
          zip:
            (null == (_l = addressData.address) ? void 0 : _l.zipcode) || "",
          province:
            (null == (_m = addressData.address) ? void 0 : _m.prefecture) ||
            "",
          city: (null == (_n = addressData.address) ? void 0 : _n.city) || "",
          address1:
            (null == (_o = addressData.address) ? void 0 : _o.address1) || "",
          address2:
            (null == (_p = addressData.address) ? void 0 : _p.address2) || "",
        })
        : (this.restoredAddress = null);
    }
    restoreProducts(destination) {
      if (!destination || !destination.items || 0 === destination.items.length)
        return;
      const items = destination.items,
        cartItems = items.map((item) => {
          var _a, _b, _c, _d, _e, _f;
          const variantId = parseInt(item.variantId.split("/").pop() || "0"),
            productMetafields =
              (null ==
                (_b =
                  null == (_a = window.BINDEC_SHIPPING_SETTINGS)
                    ? void 0
                    : _a.productMetafields)
                ? void 0
                : _b[variantId]) || {},
            latestCartItem =
              null ==
                (_e =
                  null ==
                    (_d =
                      null == (_c = window.BINDEC_SHIPPING_SETTINGS)
                        ? void 0
                        : _c.cart)
                    ? void 0
                    : _d.items)
                ? void 0
                : _e.find((cartItem) => cartItem.variant_id === variantId),
            finalPrice =
              (null == latestCartItem ? void 0 : latestCartItem.final_price) ||
              item.final_price ||
              item.price,
            finalLinePrice =
              (null == latestCartItem
                ? void 0
                : latestCartItem.final_line_price) ||
              item.final_line_price ||
              finalPrice * item.quantity,
            originalPrice =
              (null == latestCartItem
                ? void 0
                : latestCartItem.original_price) ||
              item.original_price ||
              item.price,
            totalDiscount =
              (null == latestCartItem
                ? void 0
                : latestCartItem.total_discount) ||
              item.total_discount ||
              0,
            grams =
              (null == latestCartItem ? void 0 : latestCartItem.grams) ||
              item.weight ||
              item.grams ||
              0;
          return {
            variant_id: variantId,
            product_id: parseInt(item.productId.split("/").pop() || "0"),
            title: item.productTitle,
            product_title: item.productTitle,
            variant_title: item.variantTitle,
            sku: item.sku,
            quantity: item.quantity,
            price: item.price,
            original_price: originalPrice,
            final_price: finalPrice,
            final_line_price: finalLinePrice,
            total_discount: totalDiscount,
            properties:
              (null == (_f = item.productProperties)
                ? void 0
                : _f.reduce(
                  (acc, prop) => ((acc[prop.name] = prop.value), acc),
                  {},
                )) || {},
            grams: grams,
            delivery_period_flg: productMetafields.delivery_period_flg ?? !0,
            delivery_time_flg: productMetafields.delivery_time_flg ?? !0,
            delivery_date_flg: productMetafields.delivery_date_flg ?? !0,
          };
        }),
        settingsService = undefined,
        shippingUnitsMap = new SettingsService().separateByShippingUnit(
          cartItems,
          window.BINDEC_SHIPPING_SETTINGS.shippingData || [],
          window.BINDEC_SHIPPING_SETTINGS.variantShippingRuleIds,
        ),
        destinationId = this.destinationId;
      window.BINDEC_SHIPPING_UNIT_SETTINGS &&
        Object.keys(window.BINDEC_SHIPPING_UNIT_SETTINGS).forEach((unitKey) => {
          unitKey.startsWith(`${destinationId}_`) &&
            delete window.BINDEC_SHIPPING_UNIT_SETTINGS[unitKey];
        });
      const groupsHTML = Object.entries(shippingUnitsMap)
        .map(([_groupId, group]) => {
          var _a, _b, _c, _d, _e, _f, _g, _h;
          if (!group) return "";
          const uniqueUnitKey = `${this.destinationId}_${group.id}`,
            keyPrefix = uniqueUnitKey.replace(/_\d+$/, "");
          let sessionShippingUnit =
            null == (_a = destination.shippingUnits)
              ? void 0
              : _a[uniqueUnitKey];
          if (
            !sessionShippingUnit ||
            (!sessionShippingUnit.deliveryDate &&
              !sessionShippingUnit.deliveryTime &&
              !sessionShippingUnit.deliveryPeriod)
          ) {
            const matchingKey = Object.keys(
              destination.shippingUnits || {},
            ).find((key) => key.startsWith(keyPrefix));
            matchingKey &&
              (sessionShippingUnit = destination.shippingUnits[matchingKey]);
          }
          const deliveryDateFromSession =
            (null == sessionShippingUnit
              ? void 0
              : sessionShippingUnit.deliveryDate) || "",
            deliveryTimeFromSession =
              (null == sessionShippingUnit
                ? void 0
                : sessionShippingUnit.deliveryTime) || "",
            deliveryPeriodFromSession =
              (null == sessionShippingUnit
                ? void 0
                : sessionShippingUnit.deliveryPeriod) || "",
            shippingStartDateFromSession =
              (null == sessionShippingUnit
                ? void 0
                : sessionShippingUnit.shippingStartDate) || void 0,
            giftSettingsFromSession =
              (null == sessionShippingUnit
                ? void 0
                : sessionShippingUnit.giftSettings) || void 0,
            groupWithDatetime = {
              ...group,
              deliveryDate: deliveryDateFromSession,
              deliveryTime: deliveryTimeFromSession,
              deliveryPeriod: deliveryPeriodFromSession,
              shippingStartDate: shippingStartDateFromSession,
              giftSettings: giftSettingsFromSession,
            };
          window.BINDEC_SHIPPING_UNIT_SETTINGS = {
            ...window.BINDEC_SHIPPING_UNIT_SETTINGS,
            [uniqueUnitKey]: groupWithDatetime,
          };
          const productsHTML = group.items
            .map(
              (product) =>
                `\n            <div class="product-item" data-variant-id="${product.variant_id}">\n              <div class="product-info">\n                <div class="product-image"></div>\n                <div class="product-details">\n                  <div class="product-name js-product-name">${product.title}</div>\n                  <div class="product-price js-product-price">価格 ${new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(product.final_price / 100)}</div>\n                  <div class="product-quantity js-product-quantity">数量 ${product.quantity}個</div>\n                </div>\n              </div>\n              <div class="product-total">\n                <div class="product-total-price js-product-total-price">${new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format((product.final_price * product.quantity) / 100)}</div>\n              </div>\n            </div>\n          `,
            )
            .join(""),
            shippingUnitKey = `${this.destinationId}_${group.id}`,
            savedShippingUnit =
              null == (_b = window.BINDEC_SHIPPING_UNIT_SETTINGS)
                ? void 0
                : _b[shippingUnitKey],
            deliveryDate =
              (null == savedShippingUnit
                ? void 0
                : savedShippingUnit.deliveryDate) || "",
            deliveryTime =
              (null == savedShippingUnit
                ? void 0
                : savedShippingUnit.deliveryTime) || "",
            deliveryPeriod =
              (null == savedShippingUnit
                ? void 0
                : savedShippingUnit.deliveryPeriod) || "";
          let dateLabel = "お届け日時",
            dateValue = "設定なし",
            timeRowStyle = "";
          deliveryPeriod
            ? ((dateLabel = "お届け時期"),
              (dateValue = deliveryPeriod),
              (timeRowStyle = ' style="display: none;"'))
            : deliveryDate && (dateValue = deliveryDate);
          let timeValue = "設定なし";
          if (deliveryTime && !deliveryPeriod) {
            const availableTime =
              null == savedShippingUnit
                ? void 0
                : savedShippingUnit.availableDeliveryTime,
              matchedSlot =
                null == availableTime
                  ? void 0
                  : availableTime.find((slot) => slot.value === deliveryTime);
            timeValue =
              (null == matchedSlot ? void 0 : matchedSlot.label) ||
              deliveryTime;
          }
          const firstProduct = group.items[0],
            deliveryPeriodFlg =
              (null == firstProduct
                ? void 0
                : firstProduct.delivery_period_flg) ?? !0,
            deliveryDateFlg =
              (null == firstProduct
                ? void 0
                : firstProduct.delivery_date_flg) ?? !0,
            isPattern4 = undefined,
            datetimeSelectorStyle =
              !deliveryPeriodFlg && !deliveryDateFlg
                ? ' style="display: none;"'
                : "";
          let deliveryMethodLabel = groupWithDatetime.delivery_method;
          if (!deliveryMethodLabel && group.items.length > 0) {
            const firstProduct2 = group.items[0],
              productMetafields = undefined;
            deliveryMethodLabel =
              (
                (null ==
                  (_d =
                    null == (_c = window.BINDEC_SHIPPING_SETTINGS)
                      ? void 0
                      : _c.productMetafields)
                  ? void 0
                  : _d[firstProduct2.variant_id]) || {}
              ).delivery_clsf || "常温";
          } else deliveryMethodLabel || (deliveryMethodLabel = "常温");
          let companyLabel = groupWithDatetime.company;
          if (!companyLabel && group.items.length > 0) {
            const firstProduct2 = group.items[0],
              productMetafields = undefined,
              primarySlipTypeId = selectPrimarySlipType(
                (
                  (null ==
                    (_f =
                      null == (_e = window.BINDEC_SHIPPING_SETTINGS)
                        ? void 0
                        : _e.productMetafields)
                    ? void 0
                    : _f[firstProduct2.variant_id]) || {}
                ).slip_type_id,
              );
            if (primarySlipTypeId) {
              const slipTypeMaster = undefined,
                slipTypeInfo = ((null == (_g = window.BINDEC_SHIPPING_SETTINGS)
                  ? void 0
                  : _g.slipTypeMaster) || {})[primarySlipTypeId];
              slipTypeInfo &&
                slipTypeInfo.carrier_name &&
                (companyLabel = slipTypeInfo.carrier_name);
            }
          }
          companyLabel = companyLabel || "配送会社未設定";
          // グループ内商品のname_inscription_flgを評価
          const enableNameInscription = group.items.every(product => {
            const meta =
              (window.BINDEC_SHIPPING_SETTINGS?.productMetafields || {})[product.variant_id] || {};
            return meta.name_inscription_flg !== false && meta.name_inscription_flg !== 'false';
          });
          const hasGiftData = undefined,
            giftSelectorHTML =
              window.GIFT_DATA &&
                group.items.some((product) => {
                  const pid = String(product.product_id);
                  return (
                    (window.GIFT_DATA[pid] && window.GIFT_DATA[pid].length > 0) ||
                    (window.GIFT_DATA[parseInt(pid, 10)] &&
                      window.GIFT_DATA[parseInt(pid, 10)].length > 0)
                  );
                })
                ? `\n            <gift-selector\n              data-shipping-unit-id="${group.id}"\n              data-group-id="${this.deliveryGroupId}"\n              data-destination-id="${this.destinationId}"\n                  data-enable-name-inscription="${enableNameInscription}"\n                class="${enableNameInscription ? '' : 'no-name-inscription'}"\n            >\n              <div class="wl-section-heading">ギフトに関するお申込を選択してください。</div>\n              <div class="gift-options">\n                <div class="gift-option-row js-gift-row-noshi">\n                  <div class="gift-option-label">のし</div>\n                  <div class="gift-option-value">\n                    <p class="gift-option-value-text js-gift-summary-noshi">設定なし</p>\n                  </div>\n                </div>\n                <div class="gift-option-row js-gift-row-noshi-name">\n                  <div class="gift-option-label">名入れ</div>\n                  <div class="gift-option-value">\n                    <p class="gift-option-value-text js-gift-summary-noshi-name">設定なし</p>\n                  </div>\n                </div>\n                ${!1 !== (null == (_h = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _h.enableNoshiRequest) ? '\n                <div class="gift-option-row js-gift-row-noshi-note">\n                  <div class="gift-option-label">ご要望</div>\n                  <div class="gift-option-value">\n                    <p class="gift-option-value-text js-gift-summary-noshi-note">設定なし</p>\n                  </div>\n                </div>\n                ' : ""}\n                <div class="gift-option-row js-gift-row-wrapping">\n                  <div class="gift-option-label">包装形態・ラッピング</div>\n                  <div class="gift-option-value">\n                    <p class="gift-option-value-text js-gift-summary-wrapping">設定なし</p>\n                  </div>\n                </div>\n                <div class="gift-option-row js-gift-row-message-card">\n                  <div class="gift-option-label">メッセージカード</div>\n                  <div class="gift-option-value">\n                    <p class="gift-option-value-text js-gift-summary-message-card">設定なし</p>\n                  </div>\n                </div>\n                <div class="gift-option-row js-gift-row-message-content">\n                  <div class="gift-option-label">メッセージ</div>\n                  <div class="gift-option-value">\n                    <p class="gift-option-value-text-long js-gift-summary-message-content">設定なし</p>\n                  </div>\n                </div>\n                <div class="gift-options-actions">\n                  <button class="btn-select-gift btn">のし等を選択</button>\n                </div>\n              </div>\n            </gift-selector>`
                : "";
          return `\n          <div class="product-group" data-shipping-unit-id="${group.id}">\n            <div class="product-group-header">\n              <div class="shipping-company-banner">以下の商品は「${deliveryMethodLabel}:${companyLabel}」で配送します</div>\n            </div>\n            ${productsHTML}\n            ${giftSelectorHTML}\n            <datetime-selector\n              data-shipping-unit-id="${group.id}"\n              data-group-id="${this.deliveryGroupId}"\n              data-destination-id="${this.destinationId}"\n              ${group.company ? `data-company="${JSON.stringify(group.company).replace(/"/g, "&quot;")}"` : ""}\n              ${datetimeSelectorStyle}\n            >\n              <div class="datetime-options">\n                <div class="datetime-option-row">\n                  <div class="datetime-option-label">${dateLabel}</div>\n                  <div class="datetime-option-value">\n                    <p class="datetime-option-value-text js-datetime-summary-date">${dateValue}</p>\n                  </div>\n                </div>\n                <div class="datetime-option-row"${timeRowStyle}>\n                  <div class="datetime-option-label">お届け時間帯</div>\n                  <div class="datetime-option-value">\n                    <p class="datetime-option-value-text js-datetime-summary-time">${timeValue}</p>\n                  </div>\n                </div>\n                <div class="datetime-options-actions">\n                  <button class="btn-select-datetime btn">お届け日時を選択</button>\n                </div>\n              </div>\n            </datetime-selector>\n          </div>\n        `;
        })
        .join("");
      ((this.products.shippingUnits.innerHTML = groupsHTML),
        this.products.actions.copyProductsButton.classList.remove("hidden"));
      const groupCount = undefined;
      (Object.keys(shippingUnitsMap).length > 1
        ? this.products.warning.classList.remove("hidden")
        : this.products.warning.classList.add("hidden"),
        this.deliveryMethod.section.classList.remove("hidden"),
        this.updateModalCheckboxes(items),
        this.populateStoreOptions(cartItems));
    }
    updateModalCheckboxes(items) {
      const selectedVariantIds = items.map((item) => {
        const variantId = item.variantId.split("/").pop();
        return (null == variantId ? void 0 : variantId.toString()) || "";
      }),
        checkboxes = this.querySelectorAll(
          selectors.products.modal.productCheckboxes,
        ),
        quantityInputs = this.querySelectorAll(
          selectors.products.modal.productQuantityInputs,
        );
      checkboxes.forEach((checkbox, index) => {
        const variantId = checkbox.dataset.variantId;
        if (variantId && selectedVariantIds.includes(variantId)) {
          checkbox.checked = !0;
          const quantityInput = quantityInputs[index];
          if (quantityInput) {
            quantityInput.disabled = !1;
            const item = items.find((item2) => {
              const itemVariantId = item2.variantId.split("/").pop();
              return (
                (null == itemVariantId ? void 0 : itemVariantId.toString()) ===
                variantId
              );
            });
            item &&
              item.quantity &&
              (quantityInput.value = item.quantity.toString());
          }
        }
      });
    }
    showProductInventoryError(messages) {
      const container = this.products.modal.dialog.querySelector(
        ".product-inventory-error",
      ),
        list = this.products.modal.dialog.querySelector(
          ".product-inventory-error-list",
        );
      container &&
        list &&
        ((list.innerHTML = messages.map((m) => `<li>${m}</li>`).join("")),
          container.classList.remove("hidden"),
          container.scrollIntoView({ behavior: "smooth", block: "nearest" }));
    }
    clearProductInventoryError() {
      const container = this.products.modal.dialog.querySelector(
        ".product-inventory-error",
      ),
        list = this.products.modal.dialog.querySelector(
          ".product-inventory-error-list",
        );
      (container && container.classList.add("hidden"),
        list && (list.innerHTML = ""));
    }
    applyInventoryMaxToQuantityInputs() {
      const destinationId = this.destinationId;
      this.products.modal.productQuantityInputs.forEach((input) => {
        const variantId = input.dataset.variantId;
        if (!variantId) return;
        const max = getMaxQuantityForDestination(variantId, destinationId);
        if (null === max) return void input.removeAttribute("max");
        input.max = String(max);
        const current = undefined;
        (parseInt(input.value, 10) || 1) > max &&
          max > 0 &&
          (input.value = String(max));
      });
    }
    calculateTotalQuantitiesAcrossDestinations(currentSelectedProducts) {
      const totalQuantities = {};
      currentSelectedProducts.forEach((product) => {
        const variantId = product.variant_id.toString();
        totalQuantities[variantId] =
          (totalQuantities[variantId] || 0) + product.quantity;
      });
      const unitSettings = window.BINDEC_SHIPPING_UNIT_SETTINGS;
      if (unitSettings) {
        const currentDestinationPrefix = `${this.destinationId}_`;
        Object.entries(unitSettings).forEach(([unitKey, unit]) => {
          if (unitKey.startsWith(currentDestinationPrefix)) return;
          const items = null == unit ? void 0 : unit.items;
          Array.isArray(items) &&
            items.forEach((item) => {
              let variantId = item.variantId || item.variant_id;
              if (!variantId) return;
              ((variantId = variantId.toString()),
                variantId.includes("/") &&
                (variantId = variantId.split("/").pop() || variantId));
              const quantity = parseInt(item.quantity, 10) || 0;
              quantity > 0 &&
                (totalQuantities[variantId] =
                  (totalQuantities[variantId] || 0) + quantity);
            });
        });
      }
      return totalQuantities;
    }
    async updateCartQuantity(key, variantId, quantity) {
      var _a, _b;
      const cart2 = await cartCache.get(!0);
      if (!cart2) throw new Error("カート情報の取得に失敗しました");
      const item = cart2.items.find((item2) =>
        key ? item2.key === key : item2.variant_id.toString() === variantId,
      );
      if (!item)
        throw new Error(
          `カートに商品が見つかりません (key: ${key}, variantId: ${variantId})`,
        );
      const actualKey = item.key,
        existingProperties = item.properties || {},
        token =
          null == (_a = cart2.attributes)
            ? void 0
            : _a._multi_shipping_session_token,
        isFirstItem =
          (null == (_b = cart2.items[0]) ? void 0 : _b.key) === item.key,
        properties = { ...existingProperties };
      (token && isFirstItem && (properties._multi_shipping_token = token),
        await updateCartItem(actualKey, quantity, properties, !0));
    }
    async refreshCartSections() {
      try {
        const response = await fetch(window.location.pathname, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        if (!response.ok)
          throw new Error(`Cart page fetch failed: ${response.statusText}`);
        const html = await response.text(),
          parser = undefined,
          doc = new DOMParser().parseFromString(html, "text/html"),
          cartItemsWrapper = document.querySelector(
            ".wl-cart-items-wrapper.main-cart-items",
          ),
          newCartItems = doc.querySelector(
            ".wl-cart-items-wrapper.main-cart-items",
          );
        cartItemsWrapper &&
          newCartItems &&
          (cartItemsWrapper.innerHTML = newCartItems.innerHTML);
        const cartFooterContents = document.querySelector(
          ".wl-main-cart-footer .js-contents",
        ),
          newCartFooterContents = doc.querySelector(
            ".wl-main-cart-footer .js-contents",
          );
        (cartFooterContents &&
          newCartFooterContents &&
          (cartFooterContents.innerHTML = newCartFooterContents.innerHTML),
          await this.updateGlobalCartData());
      } catch (error) {
        window.location.reload();
      }
    }
    async updateGlobalCartData() {
      try {
        const cartData = await cartCache.get(!0);
        if (!cartData) throw new Error("Cart data fetch failed");
        window.BINDEC_SHIPPING_SETTINGS &&
          window.BINDEC_SHIPPING_SETTINGS.cart &&
          (window.BINDEC_SHIPPING_SETTINGS.cart = cartData);
      } catch (error) { }
    }
  }
  customElements.define("destination-selector", DestinationSelector);
}
if (!customElements.get("delivery-group")) {
  const selectors = {
    addDestinationButton: ".js-add-destination-button",
    deleteGroupButton: ".js-btn-delete-group",
    deleteDestinationButtons: ".js-btn-delete-destination",
  };
  class DeliveryGroup extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "__addDestinationListener"),
        __publicField(this, "__deleteGroupListener"),
        __publicField(this, "addDestinationButton"),
        __publicField(this, "deleteGroupButton"),
        (this.addDestinationButton = this.querySelector(
          selectors.addDestinationButton,
        )),
        (this.deleteGroupButton = this.querySelector(
          selectors.deleteGroupButton,
        )));
    }
    connectedCallback() {
      ((this.__addDestinationListener = this.addGroupDestination.bind(this)),
        this.addDestinationButton.addEventListener(
          "click",
          this.__addDestinationListener,
        ),
        this.deleteGroupButton &&
        ((this.__deleteGroupListener = this.deleteGroup.bind(this)),
          this.deleteGroupButton.addEventListener(
            "click",
            this.__deleteGroupListener,
          )),
        this.addEventListener(
          "click",
          this.handleDestinationDelete.bind(this),
        ));
    }
    disconnectedCallback() {
      (this.addDestinationButton.removeEventListener(
        "click",
        this.__addDestinationListener,
      ),
        this.deleteGroupButton &&
        this.__deleteGroupListener &&
        this.deleteGroupButton.removeEventListener(
          "click",
          this.__deleteGroupListener,
        ));
    }
    addGroupDestination(event) {
      const customEvent = new CustomEvent("add-destination", {
        bubbles: !0,
        detail: { groupId: this.getAttribute("data-delivery-group-id") },
      });
      this.dispatchEvent(customEvent);
    }
    deleteGroup(event) {
      event.stopPropagation();
      const groupId = this.getAttribute("data-delivery-group-id");
      const groupTitleEl = this.querySelector(".js-group-title");
      const groupLabel = groupTitleEl ? groupTitleEl.textContent.trim() : `お申し込み ${groupId}`;
      if (!confirm(`${groupLabel} を削除してもよろしいですか？`))
        return;
      const customEvent = new CustomEvent("delete-group", {
        bubbles: !0,
        detail: { groupId: groupId },
      });
      this.dispatchEvent(customEvent);
    }
    handleDestinationDelete(event) {
      const target = event.target;
      if (!target.closest(".js-btn-delete-destination")) return;
      event.stopPropagation();
      const deliveryCard = target.closest(".delivery-card");
      if (!deliveryCard) return;
      const destinationSelector = deliveryCard.querySelector(
        "destination-selector",
      );
      if (!destinationSelector) return;
      const destinationId = destinationSelector.getAttribute(
        "data-destination-id",
      ),
        groupId = this.getAttribute("data-delivery-group-id");
      const destinationTitleEl = deliveryCard.querySelector(".js-destination-title");
      const destinationLabel = destinationTitleEl ? destinationTitleEl.textContent.trim() : `お届け先 ${destinationId}`;
      if (!confirm(`${destinationLabel} を削除してもよろしいですか？`))
        return;
      const customEvent = new CustomEvent("delete-destination", {
        bubbles: !0,
        detail: { groupId: groupId, destinationId: destinationId },
      });
      this.dispatchEvent(customEvent);
    }
  }
  customElements.define("delivery-group", DeliveryGroup);
}
if (!customElements.get("gift-selector")) {
  var selectos$1 = {
    modal: ".gift-selector-modal",
    details: {
      wrapping: ".wl-Details-wrapping-type",
      messageCard: ".wl-Details-message-card-type",
      noshi: ".wl-Details-noshi-type",
    },
    form: ".wl-gift-wrapping-form",
    noshiOptions: ".js-noshi-options",
    messageCardOptions: ".js-message-card-options",
    wrappingOptions: ".js-wrapping-options",
    input: {
      noshi: {
        type: "noshi-type",
        typeName: "noshi-type-name",
        name1: "name1",
        name2: "name2",
        name3: "name3",
        name4: "name4",
        note: "note",
      },
      messageCard: {
        type: "message-card-type",
        typeName: "message-card-type-name",
        content: "message",
      },
      wrapping: {
        type: "wrapping-type",
        typeName: "wrapping-type-name",
        bag: "shopping-bag",
        variantId: "wrapping-variant-id",
        variantPrice: "wrapping-variant-price",
      },
    },
    actions: {
      open: ".btn-select-gift",
      close: ".wl-gift-actions-cancel",
      submit: ".wl-gift-actions-submit",
    },
    summary: {
      noshi: ".js-gift-summary-noshi",
      noshiName: ".js-gift-summary-noshi-name",
      noshiNote: ".js-gift-summary-noshi-note",
      messageCard: ".js-gift-summary-message-card",
      messageContent: ".js-gift-summary-message-content",
      wrapping: ".js-gift-summary-wrapping",
    },
  };
  class GiftSelector extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "form"),
        __publicField(this, "shippingUnitId"),
        __publicField(this, "eventTitles"),
        __publicField(this, "noshiOptions"),
        __publicField(this, "messageCardOptions"),
        __publicField(this, "wrappingOptions"),
        __publicField(this, "deliveryGroupId"),
        __publicField(this, "destinationId"),
        __publicField(this, "modal"),
        __publicField(this, "openGiftSelectorButton"),
        __publicField(this, "cancelButton"),
        __publicField(this, "submitButton"),
        __publicField(this, "summary"),
        __publicField(this, "__openListener"),
        __publicField(this, "__closeListener"),
        __publicField(this, "__submitListener"),
        __publicField(this, "__cancelListener"),
        __publicField(this, "__modalCloseListener"),
        __publicField(this, "__isIntentionalClose", !1),
        (this.shippingUnitId =
          this.getAttribute("data-shipping-unit-id") || ""),
        (this.deliveryGroupId = this.getAttribute("data-group-id") || ""),
        (this.destinationId = this.getAttribute("data-destination-id") || ""));
    }
    connectedCallback() {
      setTimeout(() => {
        (this._initializeElements(),
          this._initializeGiftData(),
          this._updateVisibility(),
          this._attachEventListeners(),
          this._restoreGiftSettings());
      }, 0);
    }
    _updateVisibility() {
      const hasNoshiOptions =
        this.noshiOptions &&
        this.noshiOptions.filter((item) => this._isDisplay(item)).length > 0,
        hasMessageCardOptions =
          this.messageCardOptions &&
          this.messageCardOptions.filter((item) => this._isDisplay(item))
            .length > 0,
        hasWrappingOptions =
          this.wrappingOptions &&
          this.wrappingOptions.filter((item) => this._isDisplay(item)).length >
          0;
      if (!hasNoshiOptions && !hasMessageCardOptions && !hasWrappingOptions)
        return void (this.style.display = "none");
      this.style.display = "";
      const toggleRow = (selector, visible) => {
        const row = this.querySelector(selector);
        row && (row.style.display = visible ? "" : "none");
      };
      (toggleRow(".js-gift-row-noshi", hasNoshiOptions),
        toggleRow(".js-gift-row-noshi-name", hasNoshiOptions),
        toggleRow(".js-gift-row-noshi-note", hasNoshiOptions),
        toggleRow(".js-gift-row-wrapping", hasWrappingOptions),
        toggleRow(".js-gift-row-message-card", hasMessageCardOptions),
        toggleRow(".js-gift-row-message-content", hasMessageCardOptions));
    }
    _initializeElements() {
      (this._createModal(),
        (this.openGiftSelectorButton = this.querySelector(
          selectos$1.actions.open,
        )),
        (this.summary = {
          noshi: this.querySelector(selectos$1.summary.noshi),
          noshiName: this.querySelector(selectos$1.summary.noshiName),
          noshiNote: this.querySelector(selectos$1.summary.noshiNote),
          messageCard: this.querySelector(selectos$1.summary.messageCard),
          messageContent: this.querySelector(selectos$1.summary.messageContent),
          wrapping: this.querySelector(selectos$1.summary.wrapping),
        }));
    }
    _createModal() {
      var _a;
      const modalId = `gift-selector-modal-${this.shippingUnitId}`;
      if (this.querySelector(".gift-selector-modal"))
        return (
          (this.modal = this.querySelector(".gift-selector-modal")),
          (this.form = this.modal.querySelector(selectos$1.form)),
          (this.cancelButton = this.modal.querySelector(
            selectos$1.actions.close,
          )),
          void (this.submitButton = this.modal.querySelector(
            selectos$1.actions.submit,
          ))
        );
      const enableNameInscription = this.dataset.enableNameInscription !== 'false';
      const enableNoshiRequest = undefined,
        modalHTML = `\n        <dialog class="gift-selector-modal wl-modal" id="${modalId}">\n          <form class="wl-gift-wrapping-form wl-gift-wrapping">\n            <div class="wl-gift-wrapping__header">\n              <h2 class="wl-gift-wrapping__title">${enableNameInscription ? 'のし・名入れ' : 'のし'}/包装形態・ラッピング/メッセージカード設定</h2>\n            </div>\n            <div class="wl-gift-wrapping__options">\n              <div class="gift-error-message hidden" style="padding: 40px 20px; text-align: center;border-bottom: 0.1rem solid #bfbfbf;">\n                <p style="color: #dc2626; font-size: 16px; font-weight: 500; margin: 0;">選択できる商品がありません</p>\n              </div>\n              <details class="wl-collapse-group wl-Details-noshi-type">\n                <summary>\n                  <div class="wl-summary__title">\n                    <h2 class="h4 wl-accordion__title">${enableNameInscription ? 'のし・名入れ' : 'のし'}を設定する</h2>\n                  </div>\n                </summary>\n                <div class="wl-accordion__content">\n                  <h2 class="h4 wl-accordion__content-title">のしの種類を選択</h2>\n                  <div class="js-noshi-options wl-accordion__content-options"></div>\n                  <div style="${enableNameInscription ? '' : 'display: none;'}">\n                  <h2 class="h4 wl-accordion__content-title">\n                    名入れの種類を選択してください\n                  </h2>\n                  <div class="wl-accordion__content-options">\n                    <label class="wl-accordion__content-option">\n                      <div class="wl-option-container">\n                        <div class="wl-radio-label">\n                          <input\n                            type="radio"\n                            name="name-entry-type"\n                            value="none"\n                            class="wl-radio-input js-name-entry-type"\n                            checked\n                          >\n                          <span class="wl-radio-custom">\n                            <span class="wl-radio-indicator"></span>\n                          </span>\n                          <span class="wl-option-text">不要</span>\n                        </div>\n                      </div>\n                    </label>\n                    <label class="wl-accordion__content-option">\n                      <div class="wl-option-container">\n                        <div class="wl-radio-label">\n                          <input\n                            type="radio"\n                            name="name-entry-type"\n                            value="name-only"\n                            class="wl-radio-input js-name-entry-type"\n                          >\n                          <span class="wl-radio-custom">\n                            <span class="wl-radio-indicator"></span>\n                          </span>\n                          <span class="wl-option-text">名</span>\n                        </div>\n                      </div>\n                    </label>\n                    <label class="wl-accordion__content-option">\n                      <div class="wl-option-container">\n                        <div class="wl-radio-label">\n                          <input\n                            type="radio"\n                            name="name-entry-type"\n                            value="company-name"\n                            class="wl-radio-input js-name-entry-type"\n                          >\n                          <span class="wl-radio-custom">\n                            <span class="wl-radio-indicator"></span>\n                          </span>\n                          <span class="wl-option-text">会社と名</span>\n                        </div>\n                      </div>\n                    </label>\n                    <label class="wl-accordion__content-option">\n                      <div class="wl-option-container">\n                        <div class="wl-radio-label">\n                          <input\n                            type="radio"\n                            name="name-entry-type"\n                            value="company-dept-name"\n                            class="wl-radio-input js-name-entry-type"\n                          >\n                          <span class="wl-radio-custom">\n                            <span class="wl-radio-indicator"></span>\n                          </span>\n                          <span class="wl-option-text">会社と部署と名前</span>\n                        </div>\n                      </div>\n                    </label>\n                  </div>\n                  <div class="js-name-entry-fields" style="display: none;">\n                    <div class="wl-accordion__content-input js-name-field-company" style="display: none;">\n                      <label class="wl-text-field-label">会社名</label>\n                      <input\n                        type="text"\n                        name="name1"\n                        class="wl-text-field"\n                        placeholder="例：株式会社◯◯"\n                        aria-label="会社名"\n                      >\n                    </div>\n                    <div class="wl-accordion__content-input js-name-field-dept" style="display: none;">\n                      <label class="wl-text-field-label">部署・役職</label>\n                      <input\n                        type="text"\n                        name="name2"\n                        class="wl-text-field"\n                        placeholder="例：〇〇部"\n                        aria-label="部署・役職"\n                      >\n                    </div>\n                    <div class="wl-accordion__content-input js-name-field-name" style="display: none;">\n                      <label class="wl-text-field-label">名前</label>\n                      <input\n                        type="text"\n                        name="name3"\n                        class="wl-text-field"\n                        placeholder="例：◯◯　◯◯"\n                        aria-label="名前"\n                      >\n                      <span class="wl-accordion__content-helptext">※苗字のみの場合は、苗字のみ入力してください</span>\n                    </div>\n                    <div class="wl-accordion__content-input js-name-field-name2" style="display: none;">\n                      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">\n                        <label class="wl-text-field-label" style="margin: 0;">連名2人目</label>\n                        <button type="button" class="wl-btn-remove-joint-name" data-target="name4" style="color: #dc2626; font-size: 12px; padding: 2px 8px; background: transparent; border: 1px solid #dc2626; border-radius: 4px; cursor: pointer;">削除</button>\n                      </div>\n                      <input\n                        type="text"\n                        name="name4"\n                        class="wl-text-field"\n                        placeholder="例：◯◯　◯◯"\n                        aria-label="連名2人目"\n                      >\n                      <span class="wl-accordion__content-helptext">※苗字のみの場合は、苗字のみ入力してください</span>\n                    </div>\n                    <div class="js-add-joint-name-container" style="display: none; margin-top: 12px; margin-bottom: 24px; text-align: right;">\n                      <button type="button" class="button button--secondary wl-button wl-button--secondary wl-btn-add-joint-name">\n                        <span class="wl-button__content">+ 連名を追加</span>\n                      </button>\n                    </div>\n                  </div>\n                  </div>\n                  ${!1 !== (null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.enableNoshiRequest) ? '\n                  <h2 class="h4 wl-accordion__content-title">ご要望</h2>\n                  <div class="wl-accordion__content-input">\n                    <textarea\n                      rows="4"\n                      type="text"\n                      name="note"\n                      class="wl-text-field"\n                      placeholder="ご要望がございましたら記述をお願いします"\n                      aria-label=""\n                    ></textarea>\n                  </div>\n                  ' : ""}\n                </div>\n              </details>\n\n              <details class="wl-collapse-group wl-Details-wrapping-type">\n                <summary>\n                  <div class="wl-summary__title">\n                    <h2 class="h4 wl-accordion__title">包装形態・ラッピングを設定する</h2>\n                  </div>\n                </summary>\n                <div class="wl-accordion__content">\n                  <h2 class="h4 wl-accordion__content-title">包装形態・ラッピングの種類を選択</h2>\n                  <div class="js-wrapping-options wl-accordion__content-options"></div>\n                </div>\n              </details>\n\n              <details class="wl-collapse-group wl-Details-message-card-type">\n                <summary>\n                  <div class="wl-summary__title">\n                    <h2 class="h4 wl-accordion__title">メッセージカードを設定する</h2>\n                  </div>\n                </summary>\n                <div class="wl-accordion__content">\n                  <h2 class="h4 wl-accordion__content-title">メッセージカードの種類を選択</h2>\n                  <div class="js-message-card-options wl-accordion__content-options"></div>\n                  <h2 class="h4 wl-accordion__content-title">\n                    メッセージを入力\n                    <span class="wl-accordion__content-helptext"> ※100文字以内でご入力ください </span>\n                  </h2>\n                  <div class="wl-accordion__content-input">\n                    <textarea\n                      rows="4"\n                      type="text"\n                      name="message"\n                      class="wl-text-field"\n                      placeholder="例：テキストテキストテキストテキストテキストテキストテキストテキスト"\n                      aria-label=""\n                    ></textarea>\n                  </div>\n                </div>\n              </details>\n\n            </div>\n\n            <div class="wl-gift-actions">\n              <button\n                type="button"\n                class="button button--secondary wl-button wl-button--secondary wl-gift-actions__button wl-gift-actions-cancel"\n              >\n                <span class="wl-button__content">キャンセル</span>\n              </button>\n              <button\n                type="button"\n                class="button button--primary wl-button wl-button--primary wl-gift-actions__button wl-gift-actions-submit"\n              >\n                <span class="wl-button__content">決定する</span>\n              </button>\n            </div>\n          </form>\n        </dialog>\n      `;
      (this.insertAdjacentHTML("beforeend", modalHTML),
        (this.modal = this.querySelector(selectos$1.modal)),
        (this.form = this.modal.querySelector(selectos$1.form)),
        (this.cancelButton = this.modal.querySelector(
          selectos$1.actions.close,
        )),
        (this.submitButton = this.modal.querySelector(
          selectos$1.actions.submit,
        )));
    }
    _initializeGiftData() {
      const events = this._getGiftData();
      if (
        ((this.eventTitles =
          (null == events
            ? void 0
            : events.map((event) => event.name || event.eventTitle)) || []),
          !events || 0 === events.length)
      )
        return (
          (this.noshiOptions = []),
          (this.messageCardOptions = []),
          void (this.wrappingOptions = [])
        );
      ((this.noshiOptions = this._getCommonGiftItems(
        events.map((event) => event.data.noshi),
      )),
        (this.messageCardOptions = this._getCommonGiftItems(
          events.map((event) => event.data.messageCard),
        )),
        (this.wrappingOptions = this._getCommonGiftItems(
          events.map((event) => event.data.wrapping),
        )));
    }
    _getCommonGiftItems(itemsByEvent) {
      if (0 === itemsByEvent.length) return [];
      if (1 === itemsByEvent.length) return itemsByEvent[0];
      const getKey = (item) =>
        item.metaobjectHandle ||
        item.metaobjectId ||
        item.name ||
        item.title ||
        "",
        keySets = undefined,
        commonKeys = itemsByEvent
          .map((items) => new Set(items.map(getKey)))
          .reduce(
            (common, current) =>
              new Set([...common].filter((key) => current.has(key))),
          ),
        seen = new Set();
      return itemsByEvent[0].filter((item) => {
        const key = getKey(item);
        return !(!commonKeys.has(key) || seen.has(key)) && (seen.add(key), !0);
      });
    }
    _intersectGiftItemsAcrossProducts(itemsByProduct) {
      if (0 === itemsByProduct.length) return [];
      if (1 === itemsByProduct.length) return itemsByProduct[0];
      const getKey = (item) =>
        item.metaobjectHandle ||
        item.metaobjectId ||
        item.name ||
        item.title ||
        "",
        keySets = undefined,
        commonKeys = itemsByProduct
          .map((items) => new Set(items.map(getKey)))
          .reduce(
            (common, current) =>
              new Set([...common].filter((key) => current.has(key))),
          ),
        seen = new Set();
      return itemsByProduct[0].filter((item) => {
        const key = getKey(item);
        return !(!commonKeys.has(key) || seen.has(key)) && (seen.add(key), !0);
      });
    }
    _attachEventListeners() {
      var _a, _b, _c, _d;
      this.openGiftSelectorButton &&
        ((this.__openListener = this.open.bind(this)),
          this.openGiftSelectorButton.addEventListener(
            "click",
            this.__openListener,
          ),
          (this.__closeListener = this.close.bind(this)),
          null == (_a = this.cancelButton) ||
          _a.addEventListener("click", this.__closeListener),
          (this.__submitListener = this.submit.bind(this)),
          null == (_b = this.submitButton) ||
          _b.addEventListener("click", this.__submitListener),
          (this.__cancelListener = (event) => {
            event.preventDefault();
          }),
          null == (_c = this.modal) ||
          _c.addEventListener("cancel", this.__cancelListener),
          (this.__modalCloseListener = () => {
            this.__isIntentionalClose ||
              setTimeout(() => {
                this.modal.showModal();
              }, 0);
          }),
          null == (_d = this.modal) ||
          _d.addEventListener("close", this.__modalCloseListener),
          this._attachNameEntryTypeListener());
    }
    _attachNameEntryTypeListener() {
      const nameEntryTypeInputs = undefined;
      this.modal.querySelectorAll(".js-name-entry-type").forEach((input) => {
        input.addEventListener("change", (e) => {
          this._handleNameEntryTypeChange(e.target.value);
        });
      });
      const addJointNameBtn = this.modal.querySelector(
        ".wl-btn-add-joint-name",
      );
      addJointNameBtn &&
        addJointNameBtn.addEventListener("click", () => {
          this._handleAddJointName();
        });
      const removeJointNameBtns = undefined;
      this.modal
        .querySelectorAll(".wl-btn-remove-joint-name")
        .forEach((btn) => {
          btn.addEventListener("click", (e) => {
            const target = e.target.dataset.target;
            "name4" === target && this._handleRemoveJointName(target);
          });
        });
    }
    _handleNameEntryTypeChange(type) {
      const fieldsContainer = this.modal.querySelector(".js-name-entry-fields"),
        companyField = this.modal.querySelector(".js-name-field-company"),
        deptField = this.modal.querySelector(".js-name-field-dept"),
        nameField = this.modal.querySelector(".js-name-field-name"),
        name2Field = this.modal.querySelector(".js-name-field-name2"),
        addJointNameContainer = this.modal.querySelector(
          ".js-add-joint-name-container",
        ),
        name1Input = this.modal.querySelector('[name="name1"]'),
        name2Input = this.modal.querySelector('[name="name2"]'),
        name3Input = this.modal.querySelector('[name="name3"]'),
        name4Input = this.modal.querySelector('[name="name4"]');
      switch (type) {
        case "none":
          ((fieldsContainer.style.display = "none"),
            (companyField.style.display = "none"),
            (deptField.style.display = "none"),
            (nameField.style.display = "none"),
            (name2Field.style.display = "none"),
            (addJointNameContainer.style.display = "none"),
            name1Input && (name1Input.value = ""),
            name2Input && (name2Input.value = ""),
            name3Input && (name3Input.value = ""),
            name4Input && (name4Input.value = ""));
          break;
        case "name-only":
          ((fieldsContainer.style.display = "block"),
            (companyField.style.display = "none"),
            (deptField.style.display = "none"),
            (nameField.style.display = "block"),
            (name2Field.style.display = "none"),
            (addJointNameContainer.style.display = "block"),
            name1Input && (name1Input.value = ""),
            name2Input && (name2Input.value = ""));
          break;
        case "company-name":
          ((fieldsContainer.style.display = "block"),
            (companyField.style.display = "block"),
            (deptField.style.display = "none"),
            (nameField.style.display = "block"),
            (name2Field.style.display = "none"),
            (addJointNameContainer.style.display = "block"),
            name2Input && (name2Input.value = ""));
          break;
        case "company-dept-name":
          ((fieldsContainer.style.display = "block"),
            (companyField.style.display = "block"),
            (deptField.style.display = "block"),
            (nameField.style.display = "block"),
            (name2Field.style.display = "none"),
            (addJointNameContainer.style.display = "block"));
      }
    }
    _handleAddJointName() {
      const name2Field = this.modal.querySelector(".js-name-field-name2"),
        addJointNameContainer = this.modal.querySelector(
          ".js-add-joint-name-container",
        );
      "none" === name2Field.style.display &&
        ((name2Field.style.display = "block"),
          addJointNameContainer &&
          (addJointNameContainer.style.display = "none"));
    }
    _handleRemoveJointName(target) {
      const addJointNameContainer = this.modal.querySelector(
        ".js-add-joint-name-container",
      ),
        name4Input = this.modal.querySelector('[name="name4"]'),
        name2Field = this.modal.querySelector(".js-name-field-name2");
      "name4" === target &&
        (name4Input && (name4Input.value = ""),
          (name2Field.style.display = "none"),
          addJointNameContainer &&
          (addJointNameContainer.style.display = "block"));
    }
    _handleMessageCardTypeChange(value) {
      const messageContentInput = this.modal.querySelector(
        `[name="${selectos$1.input.messageCard.content}"]`,
      );
      messageContentInput &&
        ("" === value
          ? ((messageContentInput.disabled = !0),
            (messageContentInput.value = ""))
          : (messageContentInput.disabled = !1));
    }
    _handleNoshiTypeChange(value) {
      const nameEntryTypeInputs = this.modal.querySelectorAll(
        '[name="name-entry-type"]',
      ),
        noteTextarea = this.modal.querySelector(
          `[name="${selectos$1.input.noshi.note}"]`,
        );
      if ("" === value) {
        nameEntryTypeInputs.forEach((input) => {
          input.disabled = !0;
        });
        const noneRadio = this.modal.querySelector(
          '[name="name-entry-type"][value="none"]',
        );
        (noneRadio && (noneRadio.checked = !0),
          this._handleNameEntryTypeChange("none"),
          noteTextarea &&
          ((noteTextarea.disabled = !0), (noteTextarea.value = "")));
      } else
        (nameEntryTypeInputs.forEach((input) => {
          input.disabled = !1;
        }),
          noteTextarea && (noteTextarea.disabled = !1));
    }
    disconnectedCallback() {
      (this.openGiftSelectorButton &&
        this.__openListener &&
        this.openGiftSelectorButton.removeEventListener(
          "click",
          this.__openListener,
        ),
        this.cancelButton &&
        this.__closeListener &&
        this.cancelButton.removeEventListener("click", this.__closeListener),
        this.submitButton &&
        this.__submitListener &&
        this.submitButton.removeEventListener("click", this.__submitListener),
        this.modal &&
        this.__cancelListener &&
        this.modal.removeEventListener("cancel", this.__cancelListener),
        this.modal &&
        this.__modalCloseListener &&
        this.modal.removeEventListener("close", this.__modalCloseListener),
        this.modal && this.modal.remove());
    }
    _getGiftData() {
      const fullShippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`;
      if (
        !window.BINDEC_SHIPPING_UNIT_SETTINGS ||
        !window.BINDEC_SHIPPING_UNIT_SETTINGS[fullShippingUnitKey]
      )
        return null;
      const productIds = window.BINDEC_SHIPPING_UNIT_SETTINGS[
        fullShippingUnitKey
      ].items.map((item) => String(item.product_id));
      if (0 === productIds.length) return null;
      if (!window.GIFT_DATA) return null;
      const allEventsByProduct = {};
      for (const productId of productIds) {
        let productEvents = window.GIFT_DATA[productId];
        if (!productEvents) {
          const numericId = parseInt(productId, 10);
          productEvents = window.GIFT_DATA[numericId];
        }
        if (!(productEvents && productEvents.length > 0)) return null;
        allEventsByProduct[productId] = productEvents;
      }
      if (0 === Object.keys(allEventsByProduct).length) return null;
      const perProductCommonNoshi = [],
        perProductCommonMessageCard = [],
        perProductCommonWrapping = [];
      for (const productId of Object.keys(allEventsByProduct)) {
        const events = allEventsByProduct[productId].filter((e) =>
          this.isWithinDateRange(e.startTime, e.endTime),
        );
        (perProductCommonNoshi.push(
          this._getCommonGiftItems(events.map((e) => e.data.noshi || [])),
        ),
          perProductCommonMessageCard.push(
            this._getCommonGiftItems(
              events.map((e) => e.data.messageCard || []),
            ),
          ),
          perProductCommonWrapping.push(
            this._getCommonGiftItems(events.map((e) => e.data.wrapping || [])),
          ));
      }
      const finalNoshi = undefined,
        finalMessageCard = undefined,
        finalWrapping = undefined;
      return [
        {
          name: "common",
          eventTitle: "common",
          data: {
            noshi: this._intersectGiftItemsAcrossProducts(
              perProductCommonNoshi,
            ),
            messageCard: this._intersectGiftItemsAcrossProducts(
              perProductCommonMessageCard,
            ),
            wrapping: this._intersectGiftItemsAcrossProducts(
              perProductCommonWrapping,
            ),
          },
        },
      ];
    }
    normalizeGiftDateInput(dateStr, currentYear) {
      if (!dateStr) return null;
      const trimmed = String(dateStr).trim();
      return trimmed && "null" !== trimmed && "undefined" !== trimmed
        ? /^\d{2}-\d{2}$/.test(trimmed)
          ? `${currentYear}-${trimmed}`
          : trimmed
        : null;
    }
    isWithinDateRange(startStr, endStr) {
      const now = new Date(),
        currentYear = now.getFullYear(),
        normalizedStart = this.normalizeGiftDateInput(startStr, currentYear),
        normalizedEnd = this.normalizeGiftDateInput(endStr, currentYear);
      if (normalizedStart) {
        const start = new Date(normalizedStart);
        if (!Number.isNaN(start.getTime()) && now < start) return !1;
      }
      if (normalizedEnd) {
        const end = new Date(normalizedEnd);
        if (
          !Number.isNaN(end.getTime()) &&
          (/T\d{2}:\d{2}/.test(normalizedEnd) || end.setHours(23, 59, 59, 999),
            now > end)
        )
          return !1;
      }
      return !0;
    }
    _renderNoshiOptions() {
      var _a;
      const container = this.modal.querySelector(selectos$1.noshiOptions);
      if (!container || !this.noshiOptions || 0 === this.noshiOptions.length)
        return void (container && (container.innerHTML = ""));
      const hasDefaultItem = this.noshiOptions.some(
        (item) =>
          (void 0 !== item.is_default ? item.is_default : item.default) &&
          !1 !== item.available &&
          this._isDisplay(item),
      ),
        defaultNoshiImageUrl =
          null == (_a = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _a.defaultNoshiImageUrl,
        noshiNoneImageHtml = undefined,
        noneOption = `\n        <label class="wl-accordion__content-option wl-accordion__content-option--noshi">\n          ${defaultNoshiImageUrl && "" !== String(defaultNoshiImageUrl).trim() ? `<img src="${String(defaultNoshiImageUrl).replace(/"/g, "&quot;")}" alt="のしの設定なし" class="wl-option-image" loading="lazy">` : '<div class="wl-option-image-placeholder"></div>'}\n          <div class="wl-option-container">\n            <div class="wl-radio-label">\n              <input\n                type="radio"\n                name="noshi-type"\n                value=""\n                class="wl-radio-input"\n                ${hasDefaultItem ? "" : "checked"}\n              >\n              <span class="wl-radio-custom">\n                <span class="wl-radio-indicator"></span>\n              </span>\n              <span class="wl-option-text">設定なし</span>\n            </div>\n          </div>\n        </label>\n      `,
        html = this.noshiOptions
          .filter((item) => this._isDisplay(item))
          .map((item) => {
            const isAvailable = !1 !== item.available,
              itemTitle = item.name || item.title,
              isDefault =
                void 0 !== item.is_default ? item.is_default : item.default,
              priceText =
                item.price && item.price > 0
                  ? ` (¥${Math.floor(item.price)}円)`
                  : "";
            return `\n            <label class="wl-accordion__content-option wl-accordion__content-option--noshi ${isAvailable ? "" : "sold-out"}">\n              ${item.image ? `<img src="${item.image}" alt="" class="wl-option-image" loading="lazy">` : ""}\n              <div class="wl-option-container">\n              <div class="wl-radio-label">\n                  ${isAvailable ? `<input type="hidden" name="${item.variantId}-${selectos$1.input.noshi.typeName}" value="${itemTitle}">` : ""}\n                  <input\n                    type="radio"\n                    name="noshi-type"\n                    value="${item.variantId}"\n                    class="wl-radio-input"\n                    ${isAvailable ? "" : "disabled"}\n                    ${isDefault && isAvailable ? "checked" : ""}\n                  >\n                  <span class="wl-radio-custom">\n                    <span class="wl-radio-indicator"></span>\n                  </span>\n                  <span class="wl-option-text">\n                    ${itemTitle}${priceText}\n                    ${isAvailable ? "" : '<span class="sold-out-badge">在庫なし</span>'}\n                  </span>\n                </div>\n              </div>\n            </label>\n          `;
          })
          .join("");
      container.innerHTML = noneOption + html;
      const noshiTypeInputs = undefined;
      container.querySelectorAll('[name="noshi-type"]').forEach((input) => {
        input.addEventListener("change", (e) => {
          this._handleNoshiTypeChange(e.target.value);
        });
      });
      const checkedNoshiInput = container.querySelector(
        '[name="noshi-type"]:checked',
      );
      this._handleNoshiTypeChange(
        checkedNoshiInput ? checkedNoshiInput.value : "",
      );
    }
    _renderMessageCardOptions() {
      var _a;
      const container = this.modal.querySelector(selectos$1.messageCardOptions);
      if (
        !container ||
        !this.messageCardOptions ||
        0 === this.messageCardOptions.length
      )
        return void (container && (container.innerHTML = ""));
      const hasDefaultItem = this.messageCardOptions.some(
        (item) =>
          (void 0 !== item.is_default ? item.is_default : item.default) &&
          !1 !== item.available &&
          this._isDisplay(item),
      ),
        defaultMessageCardImageUrl =
          null == (_a = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _a.defaultMessageCardImageUrl,
        messageCardNoneImageHtml = undefined,
        noneOption = `\n        <label class="wl-accordion__content-option wl-accordion__content-option--message-card">\n          ${defaultMessageCardImageUrl && "" !== String(defaultMessageCardImageUrl).trim() ? `<img src="${String(defaultMessageCardImageUrl).replace(/"/g, "&quot;")}" alt="メッセージカードの設定なし" class="wl-option-image" loading="lazy">` : '<div class="wl-option-image-placeholder"></div>'}\n          <div class="wl-option-container">\n            <div class="wl-radio-label">\n              <input\n                type="radio"\n                name="message-card-type"\n                value=""\n                class="wl-radio-input"\n                ${hasDefaultItem ? "" : "checked"}\n              >\n              <span class="wl-radio-custom">\n                <span class="wl-radio-indicator"></span>\n              </span>\n              <span class="wl-option-text">設定なし</span>\n            </div>\n          </div>\n        </label>\n      `,
        html = this.messageCardOptions
          .filter((item) => this._isDisplay(item))
          .map((item) => {
            const isAvailable = !1 !== item.available,
              itemTitle = item.name || item.title,
              isDefault =
                void 0 !== item.is_default ? item.is_default : item.default,
              priceText =
                item.price && item.price > 0
                  ? ` (¥${Math.floor(item.price)}円)`
                  : "";
            return `\n            <label class="wl-accordion__content-option wl-accordion__content-option--message-card ${isAvailable ? "" : "sold-out"}">\n              ${item.image ? `<img src="${item.image}" alt="" class="wl-option-image" loading="lazy">` : ""}\n              <div class="wl-option-container">\n                <div class="wl-radio-label">\n                  ${isAvailable ? `<input type="hidden" name="${item.variantId}-message-card-type-name" value="${itemTitle}">` : ""}\n                  <input\n                    type="radio"\n                    name="message-card-type"\n                    value="${item.variantId}"\n                    class="wl-radio-input"\n                    ${isAvailable ? "" : "disabled"}\n                    ${isDefault && isAvailable ? "checked" : ""}\n                  >\n                  <span class="wl-radio-custom">\n                    <span class="wl-radio-indicator"></span>\n                  </span>\n                  <span class="wl-option-text">\n                    ${itemTitle}${priceText}\n                    ${isAvailable ? "" : '<span class="sold-out-badge">在庫なし</span>'}\n                  </span>\n                </div>\n              </div>\n            </label>\n          `;
          })
          .join("");
      container.innerHTML = noneOption + html;
      const messageCardTypeInputs = undefined;
      container
        .querySelectorAll('[name="message-card-type"]')
        .forEach((input) => {
          input.addEventListener("change", (e) => {
            this._handleMessageCardTypeChange(e.target.value);
          });
        });
      const checkedInput = container.querySelector(
        '[name="message-card-type"]:checked',
      );
      this._handleMessageCardTypeChange(checkedInput ? checkedInput.value : "");
    }
    _renderWrappingOptions() {
      var _a;
      const container = this.modal.querySelector(selectos$1.wrappingOptions);
      if (
        !container ||
        !this.wrappingOptions ||
        0 === this.wrappingOptions.length
      )
        return void (container && (container.innerHTML = ""));
      const hasDefaultItem = this.wrappingOptions.some(
        (item) =>
          (void 0 !== item.is_default ? item.is_default : item.default) &&
          !1 !== item.available &&
          this._isDisplay(item),
      ),
        defaultWrappingImageUrl =
          null == (_a = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _a.defaultWrappingImageUrl,
        wrappingNoneImageHtml = undefined,
        noneOption = `\n        <label class="wl-accordion__content-option wl-accordion__content-option--message-card">\n          ${defaultWrappingImageUrl && "" !== String(defaultWrappingImageUrl).trim() ? `<img src="${String(defaultWrappingImageUrl).replace(/"/g, "&quot;")}" alt="ラッピングの設定なし" class="wl-option-image" loading="lazy">` : '<div class="wl-option-image-placeholder"></div>'}\n          <div class="wl-option-container">\n            <div class="wl-radio-label">\n              <input\n                type="radio"\n                name="wrapping-type"\n                value=""\n                class="wl-radio-input"\n                ${hasDefaultItem ? "" : "checked"}\n              >\n              <span class="wl-radio-custom">\n                <span class="wl-radio-indicator"></span>\n              </span>\n              <span class="wl-option-text">設定なし</span>\n            </div>\n          </div>\n        </label>\n      `,
        html = this.wrappingOptions
          .filter((item) => this._isDisplay(item))
          .map((item) => {
            const isAvailable = !1 !== item.available,
              itemTitle = item.name || item.title,
              isDefault =
                void 0 !== item.is_default ? item.is_default : item.default,
              priceText =
                item.price && item.price > 0
                  ? ` (¥${Math.floor(item.price)}円)`
                  : "";
            return `\n            <label class="wl-accordion__content-option wl-accordion__content-option--message-card ${isAvailable ? "" : "sold-out"}">\n              ${item.image ? `<img src="${item.image}" alt="" class="wl-option-image" loading="lazy">` : ""}\n              <div class="wl-option-container">\n                <div class="wl-radio-label">\n                  ${isAvailable ? `<input type="hidden" name="${item.variantId}-${selectos$1.input.wrapping.typeName}" value="${itemTitle}">` : ""}\n                  <input\n                    type="radio"\n                    name="wrapping-type"\n                    value="${item.variantId}"\n                    class="wl-radio-input"\n                    ${isAvailable ? "" : "disabled"}\n                    ${isDefault && isAvailable ? "checked" : ""}\n                  >\n                  <span class="wl-radio-custom">\n                    <span class="wl-radio-indicator"></span>\n                  </span>\n                  <span class="wl-option-text">\n                    ${itemTitle}${priceText}\n                    ${isAvailable ? "" : '<span class="sold-out-badge">在庫なし</span>'}\n                  </span>\n                </div>\n              </div>\n            </label>\n          `;
          })
          .join("");
      container.innerHTML = noneOption + html;
    }
    _renderGiftSummary(data) {
      const formatPrice = (price) =>
        !price || price <= 0 ? "" : `（¥${price.toLocaleString("ja-JP")}円）`;
      if (data.noshiProductId && "" !== data.noshiProductId) {
        const typeName = data.noshiType || "",
          priceText = formatPrice(data.noshiPrice);
        this.summary.noshi.textContent = typeName + priceText;
      } else this.summary.noshi.textContent = "設定なし";
      if (data.noshiProductId && "" !== data.noshiProductId) {
        const nameEntries = [];
        (data.noshiNameEntry2 && nameEntries.push(data.noshiNameEntry2),
          data.noshiNameEntry3 && nameEntries.push(data.noshiNameEntry3),
          data.noshiNameEntry && nameEntries.push(data.noshiNameEntry),
          data.noshiNameEntry4 && nameEntries.push(data.noshiNameEntry4),
          nameEntries.length > 0
            ? (this.summary.noshiName.textContent = nameEntries.join(" / "))
            : (this.summary.noshiName.textContent = "設定なし"));
      } else this.summary.noshiName.textContent = "設定なし";
      if (
        (data.noshiProductId && "" !== data.noshiProductId
          ? (this.summary.noshiNote.textContent =
            data.noshiCustomText || "設定なし")
          : (this.summary.noshiNote.textContent = "設定なし"),
          data.wrappingProductId && "" !== data.wrappingProductId)
      ) {
        const typeName = data.giftWrappingType || "",
          priceText = formatPrice(data.wrappingPrice);
        this.summary.wrapping.textContent = typeName + priceText;
      } else this.summary.wrapping.textContent = "設定なし";
      if (data.messageCardProductId && "" !== data.messageCardProductId) {
        const typeName = data.messageCardType || "",
          priceText = formatPrice(data.messageCardPrice);
        this.summary.messageCard.textContent = typeName + priceText;
      } else this.summary.messageCard.textContent = "設定なし";
      if (data.messageCardProductId && "" !== data.messageCardProductId) {
        const messageText = data.messageCardMessage || "";
        this.summary.messageContent.textContent =
          messageText && "" !== messageText ? messageText : "設定なし";
      } else this.summary.messageContent.textContent = "設定なし";
    }
    _isDisplay(item) {
      const now = new Date(),
        currentYear = now.getFullYear(),
        hasDatetimeFields = undefined;
      if (
        (item.startTime && "" !== item.startTime) ||
        (item.endTime && "" !== item.endTime)
      ) {
        const start =
          item.startTime && "" !== item.startTime
            ? new Date(item.startTime)
            : null,
          end =
            item.endTime && "" !== item.endTime ? new Date(item.endTime) : null;
        if (start && now < start) return !1;
        if (end) {
          const endDate = new Date(end);
          if ((endDate.setHours(23, 59, 59, 999), now > endDate)) return !1;
        }
      } else {
        if (
          !(
            (item._start_date && "" !== item._start_date) ||
            (item._end_date && "" !== item._end_date)
          )
        )
          return !0;
        if (item._start_date && "" !== item._start_date) {
          const normalizedStartDate = this._normalizeDateString(
            item._start_date,
            currentYear,
          ),
            startDate = undefined;
          if (now < new Date(normalizedStartDate)) return !1;
        }
        if (item._end_date && "" !== item._end_date) {
          const normalizedEndDate = this._normalizeDateString(
            item._end_date,
            currentYear,
          ),
            endDate = new Date(normalizedEndDate);
          if ((endDate.setHours(23, 59, 59, 999), now > endDate)) return !1;
        }
      }
      return !0;
    }
    _normalizeDateString(dateStr, currentYear) {
      return /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
        ? dateStr
        : /^\d{2}-\d{2}$/.test(dateStr)
          ? `${currentYear}-${dateStr}`
          : dateStr;
    }
    open() {
      const errorMessage = this.modal.querySelector(".gift-error-message"),
        collapseGroups = this.modal.querySelectorAll(".wl-collapse-group"),
        submitButton = this.modal.querySelector(".wl-gift-actions-submit"),
        hasNoshiOptions =
          this.noshiOptions &&
          this.noshiOptions.filter((item) => this._isDisplay(item)).length > 0,
        hasMessageCardOptions =
          this.messageCardOptions &&
          this.messageCardOptions.filter((item) => this._isDisplay(item))
            .length > 0,
        hasWrappingOptions =
          this.wrappingOptions &&
          this.wrappingOptions.filter((item) => this._isDisplay(item)).length >
          0;
      if (!hasNoshiOptions && !hasMessageCardOptions && !hasWrappingOptions)
        return (
          errorMessage && errorMessage.classList.remove("hidden"),
          collapseGroups.forEach((group) => {
            group.classList.add("hidden");
          }),
          submitButton && submitButton.classList.add("hidden"),
          void this.modal.showModal()
        );
      (errorMessage && errorMessage.classList.add("hidden"),
        submitButton && submitButton.classList.remove("hidden"),
        this._renderNoshiOptions(),
        this._renderMessageCardOptions(),
        this._renderWrappingOptions());
      const noshiDetails = this.modal.querySelector(selectos$1.details.noshi),
        wrappingDetails = this.modal.querySelector(selectos$1.details.wrapping),
        messageCardDetails = this.modal.querySelector(
          selectos$1.details.messageCard,
        );
      (noshiDetails &&
        (hasNoshiOptions
          ? noshiDetails.classList.remove("hidden")
          : noshiDetails.classList.add("hidden")),
        wrappingDetails &&
        (hasWrappingOptions
          ? wrappingDetails.classList.remove("hidden")
          : wrappingDetails.classList.add("hidden")),
        messageCardDetails &&
        (hasMessageCardOptions
          ? messageCardDetails.classList.remove("hidden")
          : messageCardDetails.classList.add("hidden")),
        this.form.setAttribute("data-shipping-unit-id", this.shippingUnitId));
      const fullShippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`;
      (window.BINDEC_SHIPPING_UNIT_SETTINGS &&
        window.BINDEC_SHIPPING_UNIT_SETTINGS[fullShippingUnitKey] &&
        this._renderCurrentGiftSettings(
          window.BINDEC_SHIPPING_UNIT_SETTINGS[fullShippingUnitKey]
            .giftSettings,
        ),
        this.modal.showModal());
    }
    close() {
      ((this.__isIntentionalClose = !0),
        this.modal.close(),
        this.form.reset(),
        this.modal.querySelectorAll(".wl-collapse-group").forEach((group) => {
          group.open = !1;
        }),
        setTimeout(() => {
          this.__isIntentionalClose = !1;
        }, 100));
    }
    async submit(event) {
      event.preventDefault();
      const formData = new FormData(this.form),
        data = Object.fromEntries(formData.entries()),
        giftSettingsData = this._getGiftSettingsData(data);
      this._renderGiftSummary(giftSettingsData);
      const fullShippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`;
      (window.BINDEC_SHIPPING_UNIT_SETTINGS &&
        window.BINDEC_SHIPPING_UNIT_SETTINGS[fullShippingUnitKey] &&
        (window.BINDEC_SHIPPING_UNIT_SETTINGS[
          fullShippingUnitKey
        ].giftSettings = giftSettingsData),
        await this._addGiftItemsToCart(giftSettingsData));
      const customEvent = new CustomEvent("settings-change", {
        bubbles: !0,
        detail: {
          type: "shipping-unit-gift-selected",
          data: {
            groupId: this.deliveryGroupId,
            destinationId: this.destinationId,
            shippingUnitId: this.shippingUnitId,
            giftSettings: giftSettingsData,
          },
        },
      });
      (this.dispatchEvent(customEvent), this.close());
    }
    _renderCurrentGiftSettings(initialData) {
      if (!initialData) return;
      const {
        wrappingProductId: wrappingProductId,
        giftWrappingType: giftWrappingType,
        messageCardProductId: messageCardProductId,
        messageCardMessage: messageCardMessage,
        noshiProductId: noshiProductId,
        noshiType: noshiType,
        noshiNameEntry: noshiNameEntry,
        noshiNameEntry1: noshiNameEntry1,
        noshiNameEntry2: noshiNameEntry2,
        noshiNameEntry3: noshiNameEntry3,
        noshiNameEntry4: noshiNameEntry4,
        noshiCustomText: noshiCustomText,
        noshiPosition: noshiPosition,
      } = initialData;
      if (wrappingProductId) {
        const wrappingInput = this.form.querySelector(
          `[name="${selectos$1.input.wrapping.type}"][value="${wrappingProductId}"]`,
        );
        if ((wrappingInput && (wrappingInput.checked = !0), giftWrappingType)) {
          const wrappingTypeNameInput = this.form.querySelector(
            `[name="${wrappingProductId}-${selectos$1.input.wrapping.typeName}"][value="${giftWrappingType}"]`,
          );
          wrappingTypeNameInput && (wrappingTypeNameInput.checked = !0);
        }
      }
      if (messageCardProductId) {
        const messageCardInput = this.form.querySelector(
          `[name="${selectos$1.input.messageCard.type}"][value="${messageCardProductId}"]`,
        );
        messageCardInput && (messageCardInput.checked = !0);
      }
      if (
        (this._handleMessageCardTypeChange(messageCardProductId || ""),
          messageCardMessage && messageCardProductId)
      ) {
        const messageCardMessageInput = this.form.querySelector(
          `[name="${selectos$1.input.messageCard.content}"]`,
        );
        messageCardMessageInput &&
          (messageCardMessageInput.value = messageCardMessage);
      }
      if (noshiProductId) {
        const noshiInput = this.form.querySelector(
          `[name="${selectos$1.input.noshi.type}"][value="${noshiProductId}"]`,
        );
        if ((noshiInput && (noshiInput.checked = !0), noshiType)) {
          const noshiTypeInput = this.form.querySelector(
            `[name="${noshiProductId}-${selectos$1.input.noshi.typeName}"][value="${noshiType}"]`,
          );
          noshiTypeInput && (noshiTypeInput.checked = !0);
        }
      }
      this._handleNoshiTypeChange(noshiProductId || "");
      let nameEntryType = "none";
      noshiNameEntry || noshiNameEntry2 || noshiNameEntry3 || noshiNameEntry4
        ? noshiNameEntry2 && noshiNameEntry3 && noshiNameEntry
          ? (nameEntryType = "company-dept-name")
          : noshiNameEntry2 && noshiNameEntry
            ? (nameEntryType = "company-name")
            : noshiNameEntry && (nameEntryType = "name-only")
        : noshiNameEntry1 && (nameEntryType = "name-only");
      const nameEntryTypeInput = this.form.querySelector(
        `[name="name-entry-type"][value="${nameEntryType}"]`,
      );
      nameEntryTypeInput &&
        ((nameEntryTypeInput.checked = !0),
          this._handleNameEntryTypeChange(nameEntryType));
      const name2Field = this.form.querySelector(".js-name-field-name2"),
        addJointNameContainer = this.form.querySelector(
          ".js-add-joint-name-container",
        );
      if (
        (("name-only" !== nameEntryType &&
          "company-name" !== nameEntryType &&
          "company-dept-name" !== nameEntryType) ||
          (noshiNameEntry4 &&
            ((name2Field.style.display = "block"),
              addJointNameContainer &&
              (addJointNameContainer.style.display = "none"))),
          noshiNameEntry)
      ) {
        const name3Input = this.form.querySelector(
          `[name="${selectos$1.input.noshi.name3}"]`,
        );
        name3Input && (name3Input.value = noshiNameEntry);
      }
      if (noshiNameEntry2) {
        const name1Input = this.form.querySelector(
          `[name="${selectos$1.input.noshi.name1}"]`,
        );
        name1Input && (name1Input.value = noshiNameEntry2);
      }
      if (noshiNameEntry3) {
        const name2Input = this.form.querySelector(
          `[name="${selectos$1.input.noshi.name2}"]`,
        );
        name2Input && (name2Input.value = noshiNameEntry3);
      }
      if (noshiNameEntry4) {
        const name4Input = this.form.querySelector(
          `[name="${selectos$1.input.noshi.name4}"]`,
        );
        name4Input && (name4Input.value = noshiNameEntry4);
      }
      if (noshiNameEntry1 && !noshiNameEntry) {
        const name3Input = this.form.querySelector(
          `[name="${selectos$1.input.noshi.name3}"]`,
        );
        name3Input && (name3Input.value = noshiNameEntry1);
      }
      if (noshiCustomText) {
        const noshiCustomTextInput = this.form.querySelector(
          `[name="${selectos$1.input.noshi.note}"]`,
        );
        noshiCustomTextInput && (noshiCustomTextInput.value = noshiCustomText);
      }
      const wrappingOptions = this.modal.querySelector(
        selectos$1.details.wrapping,
      );
      wrappingOptions && (wrappingOptions.open = !0);
      const messageCardOptions = this.modal.querySelector(
        selectos$1.details.messageCard,
      );
      messageCardOptions && (messageCardOptions.open = !0);
      const noshiOptions = this.modal.querySelector(selectos$1.details.noshi);
      noshiOptions && (noshiOptions.open = !0);
    }
    _restoreGiftSettings() {
      const fullShippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`;
      if (
        !window.BINDEC_SHIPPING_UNIT_SETTINGS ||
        !window.BINDEC_SHIPPING_UNIT_SETTINGS[fullShippingUnitKey]
      )
        return;
      const giftSettings =
        window.BINDEC_SHIPPING_UNIT_SETTINGS[fullShippingUnitKey].giftSettings;
      giftSettings && this._renderGiftSummary(giftSettings);
    }
    _getGiftSettingsData(data) {
      const wrappingProductId = data[selectos$1.input.wrapping.type],
        messageCardProductId = data[selectos$1.input.messageCard.type],
        noshiProductId = data[selectos$1.input.noshi.type],
        wrappingOption = wrappingProductId
          ? this.wrappingOptions.find(
            (opt) => String(opt.variantId) === wrappingProductId,
          )
          : null,
        wrappingPrice =
          (null == wrappingOption ? void 0 : wrappingOption.price) || null,
        messageCardOption = messageCardProductId
          ? this.messageCardOptions.find(
            (opt) => String(opt.variantId) === messageCardProductId,
          )
          : null,
        messageCardPrice =
          (null == messageCardOption ? void 0 : messageCardOption.price) ||
          null,
        noshiOption = noshiProductId
          ? this.noshiOptions.find(
            (opt) => String(opt.variantId) === noshiProductId,
          )
          : null,
        noshiPrice = (null == noshiOption ? void 0 : noshiOption.price) || null;
      return {
        isGift: !0,
        wrappingProductId: wrappingProductId,
        wrappingMetaobjectId:
          (null == wrappingOption ? void 0 : wrappingOption.metaobjectId) ||
          null,
        wrappingMetaobjectHandle:
          (null == wrappingOption ? void 0 : wrappingOption.metaobjectHandle) ||
          null,
        giftWrappingType:
          data[`${wrappingProductId}-${selectos$1.input.wrapping.typeName}`],
        wrappingPrice: wrappingPrice,
        messageCardProductId: messageCardProductId,
        messageCardMetaobjectId:
          (null == messageCardOption
            ? void 0
            : messageCardOption.metaobjectId) || null,
        messageCardMetaobjectHandle:
          (null == messageCardOption
            ? void 0
            : messageCardOption.metaobjectHandle) || null,
        messageCardType:
          data[
          `${messageCardProductId}-${selectos$1.input.messageCard.typeName}`
          ],
        messageCardMessage:
          (messageCardProductId &&
            data[selectos$1.input.messageCard.content]) ||
          "",
        messageCardSenderName: "",
        messageCardPrice: messageCardPrice,
        noshiProductId: noshiProductId,
        noshiMetaobjectId:
          (null == noshiOption ? void 0 : noshiOption.metaobjectId) || null,
        noshiMetaobjectHandle:
          (null == noshiOption ? void 0 : noshiOption.metaobjectHandle) || null,
        noshiType: data[`${noshiProductId}-${selectos$1.input.noshi.typeName}`],
        noshiPrice: noshiPrice,
        noshiCustomText: data[selectos$1.input.noshi.note],
        noshiNameEntry: data[selectos$1.input.noshi.name3],
        noshiNameEntry2: data[selectos$1.input.noshi.name1],
        noshiNameEntry3: data[selectos$1.input.noshi.name2],
        noshiNameEntry4: data[selectos$1.input.noshi.name4],
      };
    }
    _getAllGiftVariantIds() {
      const giftVariantIds = new Set();
      return window.GIFT_DATA
        ? (Object.values(window.GIFT_DATA).forEach((events) => {
          events.forEach((event) => {
            var _a, _b, _c;
            (null == (_a = event.data.noshi) ||
              _a.forEach((item) => {
                item.variantId && giftVariantIds.add(String(item.variantId));
              }),
              null == (_b = event.data.messageCard) ||
              _b.forEach((item) => {
                item.variantId &&
                  giftVariantIds.add(String(item.variantId));
              }),
              null == (_c = event.data.wrapping) ||
              _c.forEach((item) => {
                item.variantId &&
                  giftVariantIds.add(String(item.variantId));
              }));
          });
        }),
          giftVariantIds)
        : giftVariantIds;
    }
    _calculateRequiredGiftQuantities() {
      const quantityMap = new Map();
      return window.BINDEC_SHIPPING_UNIT_SETTINGS
        ? (Object.values(window.BINDEC_SHIPPING_UNIT_SETTINGS).forEach(
          (unit) => {
            const giftSettings = unit.giftSettings;
            if (giftSettings) {
              if (
                giftSettings.noshiProductId &&
                "" !== giftSettings.noshiProductId
              ) {
                const variantId = String(giftSettings.noshiProductId);
                quantityMap.set(
                  variantId,
                  (quantityMap.get(variantId) || 0) + 1,
                );
              }
              if (
                giftSettings.messageCardProductId &&
                "" !== giftSettings.messageCardProductId
              ) {
                const variantId = String(giftSettings.messageCardProductId);
                quantityMap.set(
                  variantId,
                  (quantityMap.get(variantId) || 0) + 1,
                );
              }
              if (
                giftSettings.wrappingProductId &&
                "" !== giftSettings.wrappingProductId
              ) {
                const variantId = String(giftSettings.wrappingProductId);
                quantityMap.set(
                  variantId,
                  (quantityMap.get(variantId) || 0) + 1,
                );
              }
            }
          },
        ),
          quantityMap)
        : quantityMap;
    }
    async _updateGiftItemsInCart() {
      try {
        const cart2 = await cartCache.get(!0);
        if (!cart2) throw new Error("カート情報の取得に失敗しました");
        const giftVariantIds = this._getAllGiftVariantIds(),
          requiredQuantities = this._calculateRequiredGiftQuantities(),
          currentQuantities = new Map();
        cart2.items.forEach((item) => {
          var _a;
          const variantId = String(item.variant_id);
          giftVariantIds.has(variantId) &&
            currentQuantities.set(variantId, {
              quantity:
                ((null == (_a = currentQuantities.get(variantId))
                  ? void 0
                  : _a.quantity) || 0) + item.quantity,
              key: item.key,
            });
        });
        const updates = {};
        if (
          (requiredQuantities.forEach((requiredQty, variantId) => {
            const current = currentQuantities.get(variantId),
              currentQty = undefined;
            ((null == current ? void 0 : current.quantity) || 0) !==
              requiredQty &&
              ((null == current ? void 0 : current.key)
                ? (updates[current.key] = requiredQty)
                : (updates[variantId] = requiredQty));
          }),
            currentQuantities.forEach((current, variantId) => {
              requiredQuantities.has(variantId) || (updates[current.key] = 0);
            }),
            Object.keys(updates).length > 0)
        ) {
          const sectionsToRender = [],
            mainCartItems = document.getElementById("main-cart-items");
          (null == mainCartItems ? void 0 : mainCartItems.dataset.id) &&
            sectionsToRender.push({
              id: "main-cart-items",
              section: mainCartItems.dataset.id,
              selector: ".js-contents",
            });
          const mainCartFooter = document.getElementById("main-cart-footer");
          (null == mainCartFooter ? void 0 : mainCartFooter.dataset.id) &&
            sectionsToRender.push({
              id: "main-cart-footer",
              section: mainCartFooter.dataset.id,
              selector: ".js-contents",
            });
          const sectionIds = sectionsToRender.map((s) => s.section),
            requestBody = { updates: updates };
          sectionIds.length > 0 &&
            ((requestBody.sections = sectionIds.join(",")),
              (requestBody.sections_url = window.location.pathname));
          const updateResponse = await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          });
          if (!updateResponse.ok) {
            const errorText = await updateResponse.text();
            throw new Error(
              `カートの更新に失敗しました: ${updateResponse.statusText}`,
            );
          }
          const responseData = await updateResponse.json();
          if ((cartCache.invalidate(), responseData.sections)) {
            const parser = new DOMParser();
            for (const section of sectionsToRender) {
              const sectionHtml = responseData.sections[section.section];
              if (!sectionHtml) continue;
              const element = document.getElementById(section.id);
              if (!element) continue;
              const doc = parser.parseFromString(sectionHtml, "text/html");
              if (section.selector) {
                const newContent = doc.querySelector(section.selector),
                  oldContent = element.querySelector(section.selector);
                newContent &&
                  oldContent &&
                  (oldContent.innerHTML = newContent.innerHTML);
              }
            }
          }
          document.dispatchEvent(
            new CustomEvent("cart:updated", {
              detail: { cart: responseData, source: "gift-selector" },
            }),
          );
        }
      } catch (error) {
        throw error;
      }
    }
    async _addGiftItemsToCart(giftSettings) {
      await this._updateGiftItemsInCart();
    }
  }
  customElements.define("gift-selector", GiftSelector);
}
class CheckoutValidator {
  constructor() {
    __publicField(this, "errors", []);
  }
  validate(data) {
    var _a;
    ((this.errors = []),
      (null == (_a = data.notes) ? void 0 : _a.purpose) ||
      this.errors.push({
        field: "notes.purpose",
        message: "ご購入商品を選択してください。",
      }));
    const hasDeliveryGroups =
      data.deliveryGroups && data.deliveryGroups.length > 0,
      hasHomeDestinations =
        data.homeDestinations && data.homeDestinations.length > 0;
    return hasDeliveryGroups || hasHomeDestinations
      ? (hasDeliveryGroups &&
        data.deliveryGroups.forEach((group, groupIdx) => {
          this.validateDeliveryGroup(group, groupIdx);
        }),
        this.validateStorePickupConsistency(data),
        { valid: 0 === this.errors.length, errors: this.errors })
      : (this.errors.push({
        field: "deliveryGroups",
        message: "お届け先が設定されていません。お届け先を追加してください。",
      }),
        { valid: !1, errors: this.errors });
  }
  validateDeliveryGroup(group, groupIdx) {
    const groupNumber = groupIdx + 1,
      groupId = group.groupId;
    (group.sender && 0 !== group.sender.length
      ? this.validateSender(group.sender[0], groupNumber, groupId)
      : this.errors.push({
        field: "sender",
        message: `お申込 ${groupNumber}: ご依頼主が選択されていません。`,
        groupId: groupId,
      }),
      group.destinations && 0 !== group.destinations.length
        ? group.destinations.forEach((destination, destIdx) => {
          this.validateDestination(
            destination,
            groupNumber,
            destIdx,
            groupId,
          );
        })
        : this.errors.push({
          field: "destinations",
          message: `お申込 ${groupNumber}: お届け先が設定されていません。`,
          groupId: groupId,
        }));
  }
  validateSender(sender, groupNumber, groupId) {
    ((sender.firstName && sender.lastName) ||
      this.errors.push({
        field: "sender.name",
        message: `お申込 ${groupNumber}: ご依頼主の氏名が設定されていません。`,
        groupId: groupId,
      }),
      sender.phone ||
      this.errors.push({
        field: "sender.phone",
        message: `お申込 ${groupNumber}: ご依頼主の電話番号が設定されていません。`,
        groupId: groupId,
      }),
      (sender.address && sender.address.zipcode && sender.address.prefecture) ||
      this.errors.push({
        field: "sender.address",
        message: `お申込 ${groupNumber}: ご依頼主の住所が設定されていません。`,
        groupId: groupId,
      }));
  }
  validateDestination(destination, groupNumber, destIdx, groupId) {
    const destNumber = destIdx + 1,
      destinationId = destination.destinationId;
    (destination.deliveryMethod ||
      this.errors.push({
        field: "destination.deliveryMethod",
        message: `お申込 ${groupNumber} - お届け先 ${destNumber}: 引き渡し方法が選択されていません。`,
        groupId: groupId,
        destinationId: destinationId,
      }),
      "delivery" === destination.deliveryMethod &&
      ((destination.firstName && destination.lastName) ||
        this.errors.push({
          field: "destination.name",
          message: `お申込 ${groupNumber} - お届け先 ${destNumber}: お届け先の氏名が設定されていません。`,
          groupId: groupId,
          destinationId: destinationId,
        }),
        destination.phone ||
        this.errors.push({
          field: "destination.phone",
          message: `お申込 ${groupNumber} - お届け先 ${destNumber}: お届け先の電話番号が設定されていません。`,
          groupId: groupId,
          destinationId: destinationId,
        }),
        (destination.address &&
          destination.address.zipcode &&
          destination.address.prefecture) ||
        this.errors.push({
          field: "destination.address",
          message: `お申込 ${groupNumber} - お届け先 ${destNumber}: お届け先の住所が設定されていません。`,
          groupId: groupId,
          destinationId: destinationId,
        })),
      (destination.items && 0 !== destination.items.length) ||
      this.errors.push({
        field: "destination.items",
        message: `お申込 ${groupNumber} - お届け先 ${destNumber}: 商品が選択されていません。`,
        groupId: groupId,
        destinationId: destinationId,
      }),
      (void 0 !== destination.shippingFee &&
        null !== destination.shippingFee) ||
      this.errors.push({
        field: "destination.shippingFee",
        message: `お申込 ${groupNumber} - お届け先 ${destNumber}: 送料を計算できませんでした`,
        groupId: groupId,
        destinationId: destinationId,
      }));
  }
  quickValidate(data) {
    const hasDeliveryGroups =
      data.deliveryGroups && data.deliveryGroups.length > 0,
      hasHomeDestinations =
        data.homeDestinations && data.homeDestinations.length > 0;
    if (!hasDeliveryGroups && !hasHomeDestinations) return !1;
    if (hasDeliveryGroups)
      for (const group of data.deliveryGroups) {
        if (!group.sender || 0 === group.sender.length) return !1;
        if (!group.destinations || 0 === group.destinations.length) return !1;
        for (const destination of group.destinations)
          if (!destination.items || 0 === destination.items.length) return !1;
      }
    return !0;
  }
  getErrorMessages() {
    return this.errors.map((error) => error.message);
  }
  getErrorsByField(field) {
    return this.errors.filter((error) => error.field === field);
  }
  getErrorsByGroup(groupId) {
    return this.errors.filter((error) => error.groupId === groupId);
  }
  getErrorsByDestination(destinationId) {
    return this.errors.filter((error) => error.destinationId === destinationId);
  }
  validateStorePickupConsistency(data) {
    const allDestinations = [];
    if (
      (data.deliveryGroups &&
        data.deliveryGroups.length > 0 &&
        data.deliveryGroups.forEach((group) => {
          group.destinations &&
            group.destinations.length > 0 &&
            allDestinations.push(...group.destinations);
        }),
        0 === allDestinations.length)
    )
      return;
    const allArePickup = undefined;
    if (!allDestinations.every((dest) => "pickup" === dest.deliveryMethod))
      return;
    const storeIdentifiers = allDestinations
      .map((dest) => {
        var _a;
        return (null == (_a = dest.purchaseStore) ? void 0 : _a.storeCode)
          ? dest.purchaseStore.storeCode
          : dest.storeId
            ? dest.storeId
            : dest.pickupStoreCode
              ? dest.pickupStoreCode
              : null;
      })
      .filter((id) => null != id);
    if (0 === storeIdentifiers.length) return;
    const uniqueStores = undefined;
    new Set(storeIdentifiers).size > 1 &&
      this.errors.push({
        field: "deliveryMethod.store",
        message: "店舗受け取りの場合、同じ店舗で指定してください",
      });
  }
}
class SessionManager {
  constructor() {
    (__publicField(this, "sessionToken", null),
      __publicField(this, "autoSaveEnabled", !0),
      __publicField(this, "autoSaveDebounceMs", 2e3),
      __publicField(this, "autoSaveTimer", null),
      __publicField(this, "initPromise", null),
      (this.initPromise = this.loadTokenFromCart()));
  }
  async ensureInitialized() {
    this.initPromise && (await this.initPromise);
  }
  async save(data) {
    try {
      const apiUrl = `${getAppProxyUrl()}/api/multi-shipping/session`,
        response = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: data }),
        }),
        responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        return null;
      }
      return result.success && result.token
        ? ((this.sessionToken = result.token),
          await this.updateCartAttributes(result.token),
          result.token)
        : null;
    } catch (error) {
      return null;
    }
  }
  async update(data) {
    try {
      const cart2 = undefined;
      if (0 === (await cartCache.get()).item_count) return !0;
    } catch (error) { }
    if (!this.sessionToken) {
      const token = undefined;
      return null !== (await this.save(data));
    }
    try {
      const apiUrl = `${getAppProxyUrl()}/api/multi-shipping/session/${this.sessionToken}`,
        response = await fetch(apiUrl, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: data }),
        });
      if (404 === response.status) {
        const token = undefined;
        return null !== (await this.save(data));
      }
      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        return !1;
      }
      return (
        !!result.success &&
        (this.sessionToken &&
          (await this.updateCartAttributes(this.sessionToken)),
          !0)
      );
    } catch (error) {
      return !1;
    }
  }
  async load(token) {
    const targetToken = token || this.sessionToken;
    if (!targetToken) return null;
    try {
      const apiUrl = `${getAppProxyUrl()}/api/multi-shipping/session/load`,
        response = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: targetToken }),
        }),
        responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        return null;
      }
      return result.success && result.data
        ? ((this.sessionToken = targetToken), result.data)
        : null;
    } catch (error) {
      return null;
    }
  }
  async delete() {
    if (!this.sessionToken) return !0;
    try {
      const apiUrl = `${getAppProxyUrl()}/api/multi-shipping/session/${this.sessionToken}`,
        response = await fetch(apiUrl, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }),
        result = undefined;
      return (
        !!(await response.json()).success &&
        ((this.sessionToken = null), await this.clearCartAttributes(), !0)
      );
    } catch (error) {
      return !1;
    }
  }
  enableAutoSave(debounceMs = 2e3) {
    ((this.autoSaveEnabled = !0), (this.autoSaveDebounceMs = debounceMs));
  }
  disableAutoSave() {
    ((this.autoSaveEnabled = !1),
      this.autoSaveTimer &&
      (clearTimeout(this.autoSaveTimer), (this.autoSaveTimer = null)));
  }
  scheduleAutoSave(data) {
    this.autoSaveEnabled &&
      (this.autoSaveTimer && clearTimeout(this.autoSaveTimer),
        (this.autoSaveTimer = window.setTimeout(() => {
          this.update(data);
        }, this.autoSaveDebounceMs)));
  }
  saveImmediately(data) {
    (this.autoSaveTimer &&
      (clearTimeout(this.autoSaveTimer), (this.autoSaveTimer = null)),
      this.update(data));
  }
  async updateCartAttributes(token) {
    try {
      const attrResponse = undefined;
      return (
        !!(
          await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              attributes: { _multi_shipping_session_token: token },
            }),
          })
        ).ok && (cartCache.invalidate(), !0)
      );
    } catch (error) {
      return !1;
    }
  }
  async clearCartAttributes() {
    var _a;
    try {
      const attrResponse = undefined;
      if (
        !(
          await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              attributes: { _multi_shipping_session_token: "" },
            }),
          })
        ).ok
      )
        return !1;
      cartCache.invalidate();
      const cart2 = await cartCache.get();
      if (!cart2.items || 0 === cart2.items.length) return !0;
      for (const item of cart2.items) {
        const lineIndex = cart2.items.indexOf(item) + 1;
        if (null == (_a = item.properties) ? void 0 : _a._multi_shipping_token)
          try {
            const {
              _multi_shipping_token: _multi_shipping_token,
              ...otherProperties
            } = item.properties,
              response = undefined;
            (
              await fetch("/cart/change.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  line: lineIndex,
                  quantity: item.quantity,
                  properties: otherProperties,
                }),
              })
            ).ok;
          } catch (err) { }
      }
      return !0;
    } catch (error) {
      return !1;
    }
  }
  async loadTokenFromCart() {
    var _a, _b;
    try {
      const cart2 = await cartCache.get(),
        attrToken =
          null == (_a = cart2.attributes)
            ? void 0
            : _a._multi_shipping_session_token,
        validAttrToken = attrToken && "" !== attrToken ? attrToken : null;
      let itemToken = null;
      if (cart2.items && cart2.items.length > 0)
        for (const item of cart2.items) {
          const token =
            null == (_b = item.properties) ? void 0 : _b._multi_shipping_token;
          if (token && "" !== token) {
            itemToken = token;
            break;
          }
        }
      if (validAttrToken && itemToken && validAttrToken !== itemToken) {
        this.sessionToken = itemToken;
        try {
          await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              attributes: { _multi_shipping_session_token: itemToken },
            }),
          });
        } catch (err) { }
        return;
      }
      if (validAttrToken) return void (this.sessionToken = validAttrToken);
      if (itemToken) {
        this.sessionToken = itemToken;
        try {
          await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              attributes: { _multi_shipping_session_token: itemToken },
            }),
          });
        } catch (err) { }
        return;
      }
    } catch (error) { }
  }
  getToken() {
    return this.sessionToken;
  }
  setToken(token) {
    this.sessionToken = token;
  }
}
const sessionManager = new SessionManager();
function transformToOrderMetadata(data) {
  var _a,
    _b,
    _c,
    _d,
    _e,
    _f,
    _g,
    _h,
    _i,
    _j,
    _k,
    _l,
    _m,
    _n,
    _o,
    _p,
    _q,
    _r,
    _s,
    _t,
    _u,
    _v,
    _w,
    _x,
    _y,
    _z,
    _A,
    _B,
    _C,
    _D;
  const now = new Date().toISOString();
  return {
    version: "1.0.0",
    createdAt: now,
    updatedAt: now,
    customer: (() => {
      var _a2,
        _b2,
        _c2,
        _d2,
        _e2,
        _f2,
        _g2,
        _h2,
        _i2,
        _j2,
        _k2,
        _l2,
        _m2,
        _n2,
        _o2,
        _p2,
        _q2,
        _r2,
        _s2;
      const name =
        (null == (_a2 = data.customer) ? void 0 : _a2.name) ||
        `${(null == (_b2 = data.customer) ? void 0 : _b2.lastName) || ""} ${(null == (_c2 = data.customer) ? void 0 : _c2.firstName) || ""}`.trim(),
        company =
          (null == (_d2 = data.customer) ? void 0 : _d2.company) ?? void 0,
        department =
          (null == (_e2 = data.customer) ? void 0 : _e2.department) ?? void 0,
        phone = (null == (_f2 = data.customer) ? void 0 : _f2.phone) ?? void 0,
        addr = null == (_g2 = data.customer) ? void 0 : _g2.address,
        namePart = [company, department, name].filter(Boolean).join(""),
        displayNameLine = namePart ? `${namePart}様` : void 0,
        displayAddressLine =
          addr &&
            (addr.zipcode ||
              addr.prefecture ||
              addr.city ||
              addr.address1 ||
              addr.address2)
            ? [
              addr.zipcode ? `〒${addr.zipcode}` : "",
              addr.prefecture,
              addr.city,
              addr.address1,
              addr.address2,
            ]
              .filter(Boolean)
              .join(" ")
            : void 0,
        displayPhoneLine = phone || void 0;
      return {
        customerId:
          (null == (_h2 = data.customer) ? void 0 : _h2.customerId) || "",
        shopifyCustomerId:
          null == (_i2 = data.customer) ? void 0 : _i2.shopifyCustomerId,
        name: name,
        firstName:
          (null == (_j2 = data.customer) ? void 0 : _j2.firstName) ?? void 0,
        lastName:
          (null == (_k2 = data.customer) ? void 0 : _k2.lastName) ?? void 0,
        kanaFirstName:
          (null == (_l2 = data.customer) ? void 0 : _l2.firstNameKana) ??
          void 0,
        kanaLastName:
          (null == (_m2 = data.customer) ? void 0 : _m2.lastNameKana) ?? void 0,
        honorific:
          (null == (_n2 = data.customer) ? void 0 : _n2.honorific) ?? void 0,
        company: company,
        department: department,
        phone: phone,
        email: null == (_o2 = data.customer) ? void 0 : _o2.email,
        address: (null == (_p2 = data.customer) ? void 0 : _p2.address)
          ? {
            zipcode: data.customer.address.zipcode ?? void 0,
            prefecture: data.customer.address.prefecture ?? void 0,
            city: data.customer.address.city ?? void 0,
            address1: data.customer.address.address1 ?? void 0,
            address2: data.customer.address.address2 ?? void 0,
          }
          : void 0,
        memberRank: null == (_q2 = data.customer) ? void 0 : _q2.memberRank,
        ritsCardNumber:
          null == (_r2 = data.customer) ? void 0 : _r2.ritsCardNumber,
        purpose: (null == (_s2 = data.notes) ? void 0 : _s2.purpose)
          ? { value: data.notes.purpose, label: data.notes.purpose }
          : void 0,
        displayNameLine: displayNameLine,
        displayAddressLine: displayAddressLine,
        displayPhoneLine: displayPhoneLine,
      };
    })(),
    payment: {
      paymentMethod: {
        type:
          (null ==
            (_b = null == (_a = data.payment) ? void 0 : _a.paymentMethod)
            ? void 0
            : _b.type) || "credit",
        label:
          (null ==
            (_d = null == (_c = data.payment) ? void 0 : _c.paymentMethod)
            ? void 0
            : _d.label) || "",
      },
      invoiceEmail: null == (_e = data.payment) ? void 0 : _e.invoiceEmail,
      paymentStatus: {
        status:
          (null ==
            (_g = null == (_f = data.payment) ? void 0 : _f.paymentStatus)
            ? void 0
            : _g.status) || "pending",
        updatedAt:
          (null ==
            (_i = null == (_h = data.payment) ? void 0 : _h.paymentStatus)
            ? void 0
            : _i.updatedAt) || now,
        note: void 0,
      },
      paymentDetails: void 0,
    },
    discounts: {
      giftCertificates: (
        null == (_j = data.discounts) ? void 0 : _j.giftCertificateNumber
      )
        ? [
          {
            certificateNumber: data.discounts.giftCertificateNumber,
            availableAmount: data.discounts.giftCertificatePrice || 0,
            usedAmount: data.discounts.giftCertificatePrice || 0,
            appliedAt: now,
          },
        ]
        : [],
      coupons: (
        (null == (_k = data.discounts) ? void 0 : _k.coupons) || []
      ).map((coupon) => ({
        code: coupon.code,
        title: coupon.description || coupon.code,
        discountType: "FIXED_AMOUNT",
        discountValue: coupon.amount,
        discountAmount: coupon.amount,
        appliedAt: now,
      })),
      pointUsage: (null == (_l = data.discounts) ? void 0 : _l.pointUsage)
        ? {
          availablePoints: 0,
          usedPoints: parseInt(data.discounts.pointUsage) || 0,
          pointValue: parseInt(data.discounts.pointUsage) || 0,
          appliedAt: now,
        }
        : void 0,
      priceAdjustments:
        (null == (_m = data.discounts) ? void 0 : _m.priceAdjustment) &&
          data.discounts.priceAdjustmentDescription
          ? [
            {
              type:
                data.discounts.priceAdjustment > 0 ? "surcharge" : "discount",
              amount: data.discounts.priceAdjustment,
              reason: data.discounts.priceAdjustmentDescription,
              appliedBy: "system",
              appliedAt: now,
            },
          ]
          : [],
      totalDiscountAmount:
        (null == (_n = data.discounts) ? void 0 : _n.totalDiscountAmount) || 0,
    },
    deliveryGroups: (data.deliveryGroups || []).map((group) => ({
      groupId: group.groupId,
      groupName: void 0,
      sender: {
        name:
          group.sender && group.sender.length > 0
            ? `${group.sender[0].lastName || ""} ${group.sender[0].firstName || ""}`.trim()
            : "",
        company:
          group.sender && group.sender.length > 0
            ? group.sender[0].company
            : void 0,
        address:
          group.sender && group.sender.length > 0 && group.sender[0].address
            ? {
              postalCode: group.sender[0].address.zipcode,
              prefecture: group.sender[0].address.prefecture,
              city: group.sender[0].address.city,
              addressLine1: group.sender[0].address.address1,
              addressLine2: group.sender[0].address.address2 || void 0,
              country: "Japan",
            }
            : {
              postalCode: "",
              prefecture: "",
              city: "",
              addressLine1: "",
              country: "Japan",
            },
        phone:
          group.sender && group.sender.length > 0
            ? group.sender[0].phone
            : void 0,
        email:
          group.sender && group.sender.length > 0
            ? group.sender[0].email
            : void 0,
      },
      deliveryDestinations: (() => {
        var _a2, _b2, _c2;
        const allDestinations = [];
        for (const dest of group.destinations) {
          const unitMatch = dest.destinationId.match(/^(.+)_unit(\d+)$/),
            isShippingUnitSeparated = null !== unitMatch,
            originalDestId = unitMatch ? unitMatch[1] : dest.destinationId,
            unitIndex = unitMatch ? parseInt(unitMatch[2]) - 1 : 0;
          let shippingUnit = null;
          if (isShippingUnitSeparated) {
            const shippingUnitKey = Object.keys(
              window.BINDEC_SHIPPING_UNIT_SETTINGS || {},
            ).filter((key) => key.startsWith(`${originalDestId}_`))[unitIndex];
            shippingUnitKey &&
              (shippingUnit =
                window.BINDEC_SHIPPING_UNIT_SETTINGS[shippingUnitKey]);
          }
          shippingUnit
            ? allDestinations.push({
              destinationId: dest.destinationId,
              recipientName:
                `${dest.lastName || ""} ${dest.firstName || ""}`.trim(),
              company: dest.company,
              address: dest.address
                ? {
                  postalCode: dest.address.zipcode,
                  prefecture: dest.address.prefecture,
                  city: dest.address.city,
                  addressLine1: dest.address.address1,
                  addressLine2: dest.address.address2 || void 0,
                  country: "Japan",
                }
                : {
                  postalCode: "",
                  prefecture: "",
                  city: "",
                  addressLine1: "",
                  country: "Japan",
                },
              phone: dest.phone,
              products: (shippingUnit.items || []).map((item) => ({
                productId: item.product_id,
                variantId: item.variant_id,
                shopifyProductId: item.product_id,
                shopifyVariantId: item.variant_id,
                name: item.product_title || "",
                sku: item.sku,
                quantity: item.quantity,
                price: item.original_price,
                totalPrice: item.original_line_price,
                finalPrice: item.final_price,
                finalLinePrice: item.final_line_price,
                weight: item.grams,
                dimensions: void 0,
                imageUrl: void 0,
                customization: void 0,
                deliverySchedule: {
                  preferredDate: dest.deliveryDate || void 0,
                  preferredTimeSlot: dest.deliveryTime
                    ? {
                      start: dest.deliveryTime,
                      end: dest.deliveryTime,
                      label: dest.deliveryTime,
                    }
                    : void 0,
                  deliveryType: "standard",
                  actualDeliveryDate: void 0,
                  estimatedArrival: void 0,
                },
                deliveryMethod: {
                  method:
                    "delivery" === dest.deliveryMethod
                      ? "home_delivery"
                      : "pickup" === dest.deliveryMethod
                        ? "pickup"
                        : "home_delivery",
                  methodLabel:
                    "delivery" === dest.deliveryMethod
                      ? "宅配"
                      : "pickup" === dest.deliveryMethod
                        ? "店舗受取"
                        : "宅配",
                  pickupLocation:
                    "pickup" === dest.deliveryMethod && dest.purchaseStore
                      ? {
                        name: dest.purchaseStore.storeName,
                        address: {
                          postalCode: "",
                          prefecture: "",
                          city: "",
                          addressLine1: "",
                          country: "Japan",
                        },
                        businessHours: void 0,
                        note: void 0,
                      }
                      : void 0,
                  carrier: shippingUnit.shippingData
                    ? {
                      carrierName:
                        shippingUnit.shippingData.shipping_company,
                      serviceType:
                        shippingUnit.shippingData.shipping_method,
                    }
                    : void 0,
                },
                giftSettings: item.giftSettings
                  ? {
                    noshi: item.giftSettings.noshiProductId
                      ? {
                        type: item.giftSettings.noshiType || "",
                        purpose: "",
                        senderName: item.giftSettings.noshiNameEntry,
                        customText: item.giftSettings.noshiCustomText,
                        metaobjectId: item.giftSettings.noshiMetaobjectId,
                        metaobjectHandle:
                          item.giftSettings.noshiMetaobjectHandle,
                      }
                      : void 0,
                    wrapping: item.giftSettings.wrappingProductId
                      ? {
                        type: item.giftSettings.giftWrappingType || "",
                        color: void 0,
                        ribbon: void 0,
                        metaobjectId:
                          item.giftSettings.wrappingMetaobjectId,
                        metaobjectHandle:
                          item.giftSettings.wrappingMetaobjectHandle,
                      }
                      : void 0,
                    messageCard: item.giftSettings.messageCardProductId
                      ? {
                        type: "",
                        message:
                          item.giftSettings.messageCardMessage || "",
                        senderName:
                          item.giftSettings.messageCardSenderName,
                        metaobjectId:
                          item.giftSettings.messageCardMetaobjectId,
                        metaobjectHandle:
                          item.giftSettings.messageCardMetaobjectHandle,
                      }
                      : void 0,
                    giftFee: 0,
                  }
                  : void 0,
                productDeliverySettings: void 0,
              })),
              shippingCost: {
                baseShippingFee: shippingUnit.shippingCost || 0,
                weightSurcharge: 0,
                sizeSurcharge: 0,
                distanceSurcharge: 0,
                expressCharge: 0,
                totalShippingCost: shippingUnit.shippingCost || 0,
                calculationMethod:
                  "regular" ===
                    (null == (_a2 = shippingUnit.shippingData)
                      ? void 0
                      : _a2.shipping_classification)
                    ? "table"
                    : "flat_rate" ===
                      (null == (_b2 = shippingUnit.shippingData)
                        ? void 0
                        : _b2.shipping_classification)
                      ? "fixed"
                      : "free",
                calculationDetails: {
                  weightKg: shippingUnit.totalWeight
                    ? shippingUnit.totalWeight / 1e3
                    : void 0,
                },
              },
              destinationDeliverySettings: {
                priority: "normal",
                deliveryInstructions: dest.deliveryInstructions,
                absenteeHandling: "redelivery",
                deliveryConstraints: void 0,
                receiptOptions: void 0,
              },
              deliveryStatus: {
                status: dest.status || "pending",
                statusLabel: dest.status || "準備中",
                updatedAt: now,
                updatedBy: void 0,
                statusHistory: [
                  {
                    status: dest.status || "pending",
                    timestamp: now,
                    location: void 0,
                    note: "注文受付",
                    updatedBy: void 0,
                  },
                ],
                trackingInfo: dest.trackingNumber
                  ? {
                    trackingNumber: dest.trackingNumber.toString(),
                    carrierName:
                      (null == (_c2 = shippingUnit.shippingData)
                        ? void 0
                        : _c2.shipping_company) ||
                      dest.carrier ||
                      "",
                    trackingUrl: void 0,
                    lastUpdate: now,
                    currentLocation: void 0,
                  }
                  : void 0,
                note: dest.memo,
              },
              returnInfo: void 0,
            })
            : allDestinations.push({
              destinationId: dest.destinationId,
              recipientName:
                `${dest.lastName || ""} ${dest.firstName || ""}`.trim(),
              company: dest.company,
              address: dest.address
                ? {
                  postalCode: dest.address.zipcode,
                  prefecture: dest.address.prefecture,
                  city: dest.address.city,
                  addressLine1: dest.address.address1,
                  addressLine2: dest.address.address2 || void 0,
                  country: "Japan",
                }
                : {
                  postalCode: "",
                  prefecture: "",
                  city: "",
                  addressLine1: "",
                  country: "Japan",
                },
              phone: dest.phone,
              products: (dest.items || []).map((item) => ({
                productId: item.productId,
                variantId: item.variantId,
                shopifyProductId: item.productId,
                shopifyVariantId: item.variantId,
                name: item.productTitle || "",
                sku: item.sku,
                quantity: item.quantity,
                price: item.original_price,
                totalPrice: item.original_line_price,
                finalPrice: item.final_price,
                finalLinePrice: item.final_line_price,
                weight: item.weight,
                dimensions: item.dimensions,
                imageUrl: void 0,
                customization: void 0,
                deliverySchedule: {
                  preferredDate: dest.deliveryDate || void 0,
                  preferredTimeSlot: dest.deliveryTime
                    ? {
                      start: dest.deliveryTime,
                      end: dest.deliveryTime,
                      label: dest.deliveryTime,
                    }
                    : void 0,
                  deliveryType: "standard",
                  actualDeliveryDate: void 0,
                  estimatedArrival: void 0,
                },
                deliveryMethod: {
                  method:
                    "delivery" === dest.deliveryMethod
                      ? "home_delivery"
                      : "pickup" === dest.deliveryMethod
                        ? "pickup"
                        : "home_delivery",
                  methodLabel:
                    "delivery" === dest.deliveryMethod
                      ? "宅配"
                      : "pickup" === dest.deliveryMethod
                        ? "店舗受取"
                        : "宅配",
                  pickupLocation:
                    "pickup" === dest.deliveryMethod && dest.purchaseStore
                      ? {
                        name: dest.purchaseStore.storeName,
                        address: {
                          postalCode: "",
                          prefecture: "",
                          city: "",
                          addressLine1: "",
                          country: "Japan",
                        },
                        businessHours: void 0,
                        note: void 0,
                      }
                      : void 0,
                  carrier: void 0,
                },
                giftSettings: item.giftSettings
                  ? {
                    noshi: item.giftSettings.noshiProductId
                      ? {
                        type: item.giftSettings.noshiType || "",
                        purpose: "",
                        senderName: item.giftSettings.noshiNameEntry,
                        customText: item.giftSettings.noshiCustomText,
                        metaobjectId: item.giftSettings.noshiMetaobjectId,
                        metaobjectHandle:
                          item.giftSettings.noshiMetaobjectHandle,
                      }
                      : void 0,
                    wrapping: item.giftSettings.wrappingProductId
                      ? {
                        type: item.giftSettings.giftWrappingType || "",
                        color: void 0,
                        ribbon: void 0,
                        metaobjectId:
                          item.giftSettings.wrappingMetaobjectId,
                        metaobjectHandle:
                          item.giftSettings.wrappingMetaobjectHandle,
                      }
                      : void 0,
                    messageCard: item.giftSettings.messageCardProductId
                      ? {
                        type: "",
                        message:
                          item.giftSettings.messageCardMessage || "",
                        senderName:
                          item.giftSettings.messageCardSenderName,
                        metaobjectId:
                          item.giftSettings.messageCardMetaobjectId,
                        metaobjectHandle:
                          item.giftSettings.messageCardMetaobjectHandle,
                      }
                      : void 0,
                    giftFee: 0,
                  }
                  : void 0,
                productDeliverySettings: void 0,
              })),
              shippingCost: {
                baseShippingFee: dest.shippingFee || 0,
                weightSurcharge: 0,
                sizeSurcharge: 0,
                distanceSurcharge: 0,
                expressCharge: 0,
                totalShippingCost: dest.shippingFee || 0,
                calculationMethod: "fixed",
                calculationDetails: {
                  weightKg: dest.totalWeight
                    ? dest.totalWeight / 1e3
                    : void 0,
                },
              },
              destinationDeliverySettings: {
                priority: "normal",
                deliveryInstructions: dest.deliveryInstructions,
                absenteeHandling: "redelivery",
                deliveryConstraints: void 0,
                receiptOptions: void 0,
              },
              deliveryStatus: {
                status: dest.status || "pending",
                statusLabel: dest.status || "準備中",
                updatedAt: now,
                updatedBy: void 0,
                statusHistory: [
                  {
                    status: dest.status || "pending",
                    timestamp: now,
                    location: void 0,
                    note: "注文受付",
                    updatedBy: void 0,
                  },
                ],
                trackingInfo: dest.trackingNumber
                  ? {
                    trackingNumber: dest.trackingNumber.toString(),
                    carrierName: dest.carrier || "",
                    trackingUrl: void 0,
                    lastUpdate: now,
                    currentLocation: void 0,
                  }
                  : void 0,
                note: dest.memo,
              },
              returnInfo: void 0,
            });
        }
        return allDestinations;
      })(),
      status: {
        overallStatus: "pending",
        progress: {
          totalDestinations: group.destinations.length,
          preparedDestinations: 0,
          shippedDestinations: 0,
          deliveredDestinations: 0,
        },
        statusUpdatedAt: now,
        statusUpdatedBy: void 0,
      },
    })),
    additionalSettings: {
      contractNumber: void 0,
      route: "1",
      introducerCode: void 0,
      orderHandlerCode: void 0,
      salesPersonCode: void 0,
      tags: null == (_o = data.orderSettings) ? void 0 : _o.tags,
      category: void 0,
      memo: null == (_p = data.orderSettings) ? void 0 : _p.orderMemo,
      customerNote: null == (_q = data.notes) ? void 0 : _q.customerMemo,
      flags: {
        isNotNeeded:
          (null ==
            (_s = null == (_r = data.additionalSettings) ? void 0 : _r.flags)
            ? void 0
            : _s.isNotNeeded) || !1,
        isPickup:
          (null ==
            (_u = null == (_t = data.additionalSettings) ? void 0 : _t.flags)
            ? void 0
            : _u.isNotNeeded) || !1,
        isHomeDelivery: !1,
        isGiftOrder:
          (null ==
            (_w = null == (_v = data.additionalSettings) ? void 0 : _v.flags)
            ? void 0
            : _w.isGiftOrder) || !1,
        isUrgent:
          (null ==
            (_y = null == (_x = data.additionalSettings) ? void 0 : _x.flags)
            ? void 0
            : _y.isUrgent) || !1,
        isBusinessOrder:
          (null ==
            (_A = null == (_z = data.additionalSettings) ? void 0 : _z.flags)
            ? void 0
            : _A.isBusinessOrder) || !1,
      },
    },
    status: {
      orderStatus:
        (null == (_B = data.status) ? void 0 : _B.orderStatus) || "pending",
      detailedStatus: {
        orderConfirmed: !1,
        paymentProcessed: !1,
        inventoryAllocated: !1,
        packingStarted: !1,
        allItemsShipped: !1,
        allItemsDelivered: !1,
      },
      statusHistory: [
        {
          status:
            (null == (_C = data.status) ? void 0 : _C.orderStatus) || "pending",
          timestamp: now,
          updatedBy: void 0,
          note: "注文作成",
          systemNote: void 0,
        },
      ],
      cancellationInfo: void 0,
    },
    integration: {
      preApplicationInfo: (
        null == (_D = data.metadata) ? void 0 : _D.preApplicationNumber
      )
        ? {
          preApplicationNumber: data.metadata.preApplicationNumber,
          listId: data.metadata.preApplicationItemId || "",
          applicationDate: now,
          approvalStatus: "pending",
          approvedBy: void 0,
          approvedAt: void 0,
        }
        : void 0,
      erpIntegration: {
        erpOrderId: void 0,
        syncStatus: "pending",
        lastSyncAt: void 0,
        syncError: void 0,
        nextSyncAt: void 0,
      },
      externalSystems: void 0,
      apiCallHistory: void 0,
    },
  };
}
function isAccessoryOrGiftItem(item, giftVariantIds) {
  var _a, _b, _c, _d, _e, _f;
  const variantId = item.variant_id
    ? `gid://shopify/ProductVariant/${item.variant_id}`
    : item.variantId;
  if (giftVariantIds && variantId && giftVariantIds.has(variantId)) return !0;
  const productType =
    item.product_type ||
    (null == (_a = item.product) ? void 0 : _a.product_type),
    excludedProductTypes = undefined;
  if (
    productType &&
    ["のし", "ラッピング", "メッセージカード", "ギフト付属品"].some((type) =>
      productType.includes(type),
    )
  )
    return !0;
  const productTitle =
    item.product_title ||
    item.title ||
    (null == (_b = item.product) ? void 0 : _b.title),
    properties =
      item.properties || (null == (_c = item.product) ? void 0 : _c.properties);
  if (
    "true" === (null == properties ? void 0 : properties._is_accessory_item) ||
    !0 === (null == properties ? void 0 : properties._is_accessory_item)
  )
    return !0;
  const metafields =
    (null == (_e = null == (_d = item.product) ? void 0 : _d.metafields)
      ? void 0
      : _e.itfor) || (null == (_f = item.metafields) ? void 0 : _f.itfor);
  return (
    "true" === (null == metafields ? void 0 : metafields.is_accessory_item) ||
    !0 === (null == metafields ? void 0 : metafields.is_accessory_item)
  );
}
function getGiftProductVariantIds() {
  var _a, _b;
  const giftVariantIds = new Set(),
    gifts =
      (null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.gifts) ||
      {};
  Object.values(gifts).forEach((events) => {
    events.forEach((event) => {
      var _a2, _b2, _c;
      const data = event.data;
      (null == (_a2 = data.noshi) ||
        _a2.forEach((item) => {
          item.variantId &&
            giftVariantIds.add(
              `gid://shopify/ProductVariant/${item.variantId}`,
            );
        }),
        null == (_b2 = data.messageCard) ||
        _b2.forEach((item) => {
          item.variantId &&
            giftVariantIds.add(
              `gid://shopify/ProductVariant/${item.variantId}`,
            );
        }),
        null == (_c = data.wrapping) ||
        _c.forEach((item) => {
          item.variantId &&
            giftVariantIds.add(
              `gid://shopify/ProductVariant/${item.variantId}`,
            );
        }));
    });
  });
  const productMetafields =
    (null == (_b = window.BINDEC_SHIPPING_SETTINGS)
      ? void 0
      : _b.productMetafields) || {};
  return (
    Object.entries(productMetafields).forEach(([variantId, metafields]) => {
      const giftCategoryValue =
        null == metafields ? void 0 : metafields.gift_category;
      (!0 !== giftCategoryValue && "true" !== giftCategoryValue) ||
        giftVariantIds.add(`gid://shopify/ProductVariant/${variantId}`);
    }),
    giftVariantIds
  );
}
function filterOutAccessoryItems(items) {
  const giftVariantIds = getGiftProductVariantIds();
  return items.filter((item) => !isAccessoryOrGiftItem(item, giftVariantIds));
}
const KEYS_STATE = "_cart_recovery_state",
  KEYS_SNAPSHOT = "_cart_recovery_snapshot",
  KEYS_PURCHASED_VARIANTS = "_cart_recovery_purchased_variants",
  KEYS_CHECKOUT_TOKEN = "_cart_recovery_checkout_token",
  LEGACY_KEYS_CART_SNAPSHOT = "_cart_snapshot_before_checkout",
  LEGACY_KEYS_DELETED_FLAG = "_checkout_products_deleted",
  LEGACY_KEYS_PURCHASED_VARIANTS = "_checkout_purchased_variants",
  LEGACY_KEYS_RESTORED_FLAG = "_cart_restored_after_back";
class CartRecoveryService {
  saveSnapshotBeforeCheckout(cart2, purpose) {
    try {
      this.cleanup();
      const snapshot = {
        items: cart2.items.map((item) => ({
          key: item.key,
          variant_id: item.variant_id,
          product_id: item.product_id,
          quantity: item.quantity,
          product_title: item.product_title,
          variant_title: item.variant_title,
          properties: item.properties || {},
        })),
        timestamp: Date.now(),
        purpose: purpose,
      };
      (sessionStorage.setItem(KEYS_SNAPSHOT, JSON.stringify(snapshot)),
        this._setState("snapshot_saved"));
    } catch (error) { }
  }
  markItemsDeleted(purchasedVariantIds, checkoutToken) {
    try {
      (sessionStorage.setItem(
        KEYS_PURCHASED_VARIANTS,
        JSON.stringify(purchasedVariantIds),
      ),
        sessionStorage.setItem(KEYS_CHECKOUT_TOKEN, checkoutToken),
        this._setState("items_deleted"));
    } catch (error) { }
  }
  getState() {
    const state = undefined;
    return sessionStorage.getItem(KEYS_STATE) || "idle";
  }
  isRecoveryInProgress() {
    const state = undefined;
    return "idle" !== this.getState();
  }
  getSnapshot() {
    try {
      const data = sessionStorage.getItem(KEYS_SNAPSHOT);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }
  getPurchasedVariantIds() {
    try {
      const data = sessionStorage.getItem(KEYS_PURCHASED_VARIANTS);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }
  getCheckoutToken() {
    return sessionStorage.getItem(KEYS_CHECKOUT_TOKEN);
  }
  _setState(state) {
    "idle" === state
      ? sessionStorage.removeItem(KEYS_STATE)
      : sessionStorage.setItem(KEYS_STATE, state);
  }
  cleanup() {
    (sessionStorage.removeItem(KEYS_STATE),
      sessionStorage.removeItem(KEYS_SNAPSHOT),
      sessionStorage.removeItem(KEYS_PURCHASED_VARIANTS),
      sessionStorage.removeItem(KEYS_CHECKOUT_TOKEN),
      this.cleanupLegacyKeys());
  }
  migrateLegacyKeys() {
    const deletedFlag = undefined;
    if ("true" !== sessionStorage.getItem(LEGACY_KEYS_DELETED_FLAG)) return !1;
    const oldSnapshot = sessionStorage.getItem(LEGACY_KEYS_CART_SNAPSHOT);
    if (oldSnapshot)
      try {
        const parsed = JSON.parse(oldSnapshot),
          newSnapshot = {
            items: parsed.items || [],
            timestamp: parsed.timestamp || Date.now(),
            purpose: "",
          };
        sessionStorage.setItem(KEYS_SNAPSHOT, JSON.stringify(newSnapshot));
      } catch { }
    const oldPurchased = sessionStorage.getItem(LEGACY_KEYS_PURCHASED_VARIANTS);
    oldPurchased &&
      sessionStorage.setItem(KEYS_PURCHASED_VARIANTS, oldPurchased);
    const wasRestored = undefined;
    return "true" === sessionStorage.getItem(LEGACY_KEYS_RESTORED_FLAG)
      ? (this.cleanup(), !1)
      : (this._setState("items_deleted"), this.cleanupLegacyKeys(), !0);
  }
  cleanupLegacyKeys() {
    (sessionStorage.removeItem(LEGACY_KEYS_CART_SNAPSHOT),
      sessionStorage.removeItem(LEGACY_KEYS_DELETED_FLAG),
      sessionStorage.removeItem(LEGACY_KEYS_PURCHASED_VARIANTS),
      sessionStorage.removeItem(LEGACY_KEYS_RESTORED_FLAG));
  }
}
const cartRecoveryService = new CartRecoveryService();
if (!customElements.get("checkout-button")) {
  class CheckoutButton extends HTMLElement {
    constructor() {
      (super(...arguments),
        __publicField(this, "button"),
        __publicField(this, "isProcessing", !1),
        __publicField(this, "themeCheckoutButton", null),
        __publicField(this, "maskOverlay", null),
        __publicField(this, "buttonClickHandler", null),
        __publicField(this, "formSubmitHandler", null),
        __publicField(this, "sectionLoadHandler", null),
        __publicField(this, "cartForm", null),
        __publicField(this, "ritsService"));
    }
    connectedCallback() {
      "loading" === document.readyState
        ? document.addEventListener("DOMContentLoaded", () => {
          this.initializeWithRetry();
        })
        : this.initializeWithRetry();
    }
    initializeWithRetry(attempt = 1, maxAttempts = 5) {
      const themeButtonSelectors = [
        'button[name="checkout"]',
        'input[name="checkout"]',
        'a[href*="/checkouts"]',
        ".cart__checkout-button",
        "button.cart__checkout-button",
        "#checkout",
        '[data-testid="checkout-button"]',
      ];
      let buttonFound = !1;
      for (const selector of themeButtonSelectors) {
        const button = undefined;
        if (document.querySelector(selector)) {
          buttonFound = !0;
          break;
        }
      }
      !buttonFound && attempt < maxAttempts
        ? setTimeout(() => {
          this.initializeWithRetry(attempt + 1, maxAttempts);
        }, 500 * attempt)
        : this.initialize();
    }
    initialize() {
      (this.setupThemeButtonInterception(),
        this.setupFormSubmitInterception(),
        this.renderErrorContainer(),
        this.setupSectionLoadListener());
    }
    disconnectedCallback() {
      this.cleanup();
    }
    setupSectionLoadListener() {
      ((this.sectionLoadHandler = (e) => {
        var _a;
        const customEvent = undefined,
          sectionId = null == (_a = e.detail) ? void 0 : _a.sectionId;
        setTimeout(() => {
          (this.cleanupButtonListener(),
            this.cleanupFormListener(),
            this.setupThemeButtonInterception(),
            this.setupFormSubmitInterception(),
            this.renderErrorContainer());
        }, 100);
      }),
        document.addEventListener(
          "shopify:section:load",
          this.sectionLoadHandler,
        ));
    }
    cleanupButtonListener() {
      this.themeCheckoutButton &&
        this.buttonClickHandler &&
        this.themeCheckoutButton.removeEventListener(
          "click",
          this.buttonClickHandler,
          !0,
        );
    }
    cleanupFormListener() {
      this.cartForm &&
        this.formSubmitHandler &&
        this.cartForm.removeEventListener("submit", this.formSubmitHandler, !0);
    }
    cleanup() {
      (this.cleanupButtonListener(),
        this.cleanupFormListener(),
        this.sectionLoadHandler &&
        document.removeEventListener(
          "shopify:section:load",
          this.sectionLoadHandler,
        ));
    }
    setupThemeButtonInterception() {
      const themeButtonSelectors = [
        'button[name="checkout"]',
        'input[name="checkout"]',
        'a[href*="/checkouts"]',
        ".cart__checkout-button",
        "button.cart__checkout-button",
        "#checkout",
        '[data-testid="checkout-button"]',
      ];
      let foundSelector = null;
      for (const selector of themeButtonSelectors)
        if (
          ((this.themeCheckoutButton = document.querySelector(selector)),
            this.themeCheckoutButton)
        ) {
          foundSelector = selector;
          break;
        }
      this.themeCheckoutButton
        ? this.interceptButtonClick()
        : this.renderFallbackButton();
    }
    interceptButtonClick() {
      this.themeCheckoutButton &&
        ((this.buttonClickHandler = async (e) => {
          (e.preventDefault(),
            e.stopPropagation(),
            e.stopImmediatePropagation(),
            await this.handleCheckout());
        }),
          this.themeCheckoutButton.addEventListener(
            "click",
            this.buttonClickHandler,
            !0,
          ));
    }
    setupFormSubmitInterception() {
      const formSelectors = [
        'form[action="/cart"]',
        "form#cart",
        "form.cart",
        '[data-type="add-to-cart-form"]',
      ];
      for (const selector of formSelectors)
        if (((this.cartForm = document.querySelector(selector)), this.cartForm))
          break;
      this.cartForm &&
        ((this.formSubmitHandler = async (e) => {
          const submitEvent = undefined,
            submitter = e.submitter,
            isCheckoutSubmit = undefined;
          submitter &&
            ("checkout" === submitter.getAttribute("name") ||
              "checkout" === submitter.getAttribute("id") ||
              submitter.classList.contains("cart__checkout-button")) &&
            (e.preventDefault(),
              e.stopPropagation(),
              e.stopImmediatePropagation(),
              await this.handleCheckout());
        }),
          this.cartForm.addEventListener("submit", this.formSubmitHandler, !0));
    }
    renderErrorContainer() {
      var _a;
      const errorContainer = document.createElement("div");
      ((errorContainer.className = "checkout-error-container hidden"),
        (errorContainer.style.cssText =
          "\n        margin-top: 16px;\n        padding: 16px;\n        background-color: #f8d7da;\n        border: 1px solid #f5c6cb;\n        border-radius: 4px;\n        color: #721c24;\n      "),
        (errorContainer.innerHTML =
          '\n        <div class="checkout-error-title" style="font-weight: bold; margin-bottom: 8px;">入力内容を確認してください:</div>\n        <ul class="checkout-error-list" style="margin: 0; padding-left: 20px;"></ul>\n      '),
        (null == (_a = this.themeCheckoutButton) ? void 0 : _a.parentElement) &&
        this.themeCheckoutButton.parentElement.insertBefore(
          errorContainer,
          this.themeCheckoutButton.nextSibling,
        ));
    }
    renderFallbackButton() {
      ((this.innerHTML =
        '\n        <div class="checkout-button-container">\n          <button class="checkout-button" type="button">\n            <span class="checkout-button-text">決定する</span>\n            <span class="checkout-button-loader hidden">処理中...</span>\n          </button>\n          <div class="checkout-error-container hidden">\n            <div class="checkout-error-title">入力内容を確認してください:</div>\n            <ul class="checkout-error-list"></ul>\n          </div>\n        </div>\n      '),
        (this.button = this.querySelector(".checkout-button")),
        this.button.addEventListener("click", () => this.handleCheckout()));
    }
    async handleCheckout() {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
      if (this.isProcessing) return;
      ((this.isProcessing = !0), this.clearErrors());
      const multiShippingSettings = document.querySelector(
        "multi-shipping-settings",
      );
      if (!multiShippingSettings)
        return (
          this.showError(["複数配送設定が見つかりません。"]),
          void (this.isProcessing = !1)
        );
      cartRecoveryService.cleanup();
      const currentPurpose = multiShippingSettings.purpose;
      if ((await this.getLatestPurposeAndGiftData(), currentPurpose)) {
        if (!PurposeService.isPurposeValid(currentPurpose)) {
          const purposeName = currentPurpose || "選択中の用途";
          return (
            this.showError([
              `「${purposeName}」の注文受付期間が終了しました。ページを更新して再度お試しください。`,
            ]),
            void (this.isProcessing = !1)
          );
        }
        const invalidProductIds =
          multiShippingSettings._getProductsWithoutPurpose(currentPurpose);
        if (invalidProductIds.length > 0) {
          const cart2 = await cartCache.get();
          (cartRecoveryService.saveSnapshotBeforeCheckout(
            cart2,
            currentPurpose,
          ),
            multiShippingSettings._saveCartToSession(cart2, currentPurpose));
          const itemsToRemove = cart2.items.filter((item) =>
            invalidProductIds.includes(item.product_id.toString()),
          );
          if (itemsToRemove.length > 0) {
            for (const item of itemsToRemove) {
              const deleteResponse = undefined;
              if (
                !(
                  await fetch("/cart/change.js", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ id: item.key, quantity: 0 }),
                  })
                ).ok
              )
                throw new Error(
                  `商品 ${item.product_title} の削除に失敗しました`,
                );
            }
            cartCache.invalidate();
            const cartAfterDeleteData = undefined,
              purchasedVariantIds = (await cartCache.get()).items.map((item) =>
                item.variant_id.toString(),
              );
            cartRecoveryService.markItemsDeleted(purchasedVariantIds, "");
          }
        }
      }
      const settings = multiShippingSettings.getSettings();
      if (currentPurpose) {
        const invalidDeliveryPeriods = this.findInvalidDeliveryPeriods(
          settings,
          currentPurpose,
        );
        if (invalidDeliveryPeriods.length > 0)
          return (
            this.showError([
              "お届け希望時期の選択受付期間が終了しました。ページを更新して再度お試しください。",
              `対象お届け時期: ${invalidDeliveryPeriods.join("、")}`,
            ]),
            void (this.isProcessing = !1)
          );
      }
      const invalidDeliveryDates = this.findInvalidDeliveryDates();
      if (invalidDeliveryDates.length > 0)
        return (
          this.showError([
            "お届け希望日の選択受付期間が終了しました。ページを更新して再度お試しください。",
            `対象お届け希望日: ${invalidDeliveryDates.join("、")}`,
          ]),
          void (this.isProcessing = !1)
        );
      const expiredGiftItems = this.findExpiredGiftSettings(settings);
      if (expiredGiftItems.length > 0) {
        const detailMessage = `対象ギフト: ${expiredGiftItems.join("、")}`;
        return (
          this.showError([
            "選択されたギフトオプションの受付期間が終了しました。ギフト設定を見直してからもう一度お試しください。",
            detailMessage,
          ]),
          void (this.isProcessing = !1)
        );
      }
      const validator = undefined,
        result = new CheckoutValidator().validate(settings);
      if (!result.valid)
        return (
          this.showErrors(result.errors.map((e) => e.message)),
          void (this.isProcessing = !1)
        );
      let undeliverableMessage = "";
      const hasUndeliverable = undefined;
      if (
        (null == (_a = settings.deliveryGroups)
          ? void 0
          : _a.some((group) => {
            var _a2;
            return null == (_a2 = group.destinations)
              ? void 0
              : _a2.some(
                (dest) =>
                  !0 === dest.hasUndeliverableDestination &&
                  ((undeliverableMessage =
                    dest.undeliverableErrorMessage || ""),
                    !0),
              );
          })) ||
        !1
      )
        return (
          this.showErrors([
            undeliverableMessage ||
            "配送不可地域が選択されています。別のお届け先をご選択ください。",
          ]),
          void (this.isProcessing = !1)
        );
      const settingsJson = JSON.stringify(settings),
        dataSizeInBytes = new Blob([settingsJson]).size,
        dataSizeInMB = (dataSizeInBytes / 1048576).toFixed(2);
      if (dataSizeInBytes > 1887436.8) {
        const destinationCount = undefined,
          homeDestinationCount = undefined,
          totalDestinations =
            ((null == (_b = settings.deliveryGroups)
              ? void 0
              : _b.reduce((sum, g) => {
                var _a2;
                return (
                  sum +
                  ((null == (_a2 = g.destinations) ? void 0 : _a2.length) ||
                    0)
                );
              }, 0)) || 0) +
            ((null == (_c = settings.homeDestinations) ? void 0 : _c.length) ||
              0);
        return (
          this.showErrors([
            `データサイズが大きすぎます (${dataSizeInMB}MB)`,
            "注文メタフィールドの制限(2MB)により、このまま注文を作成できません。",
            `現在の配送先数: ${totalDestinations}件`,
            "配送先を減らしてから再度お試しください。",
            `目安: 配送先を ${Math.ceil(totalDestinations / 2)} 件以下に減らしてください。`,
          ]),
          void (this.isProcessing = !1)
        );
      }
      if (dataSizeInBytes > 1572864) {
        const destinationCount = undefined,
          homeDestinationCount = undefined,
          totalDestinations =
            ((null == (_d = settings.deliveryGroups)
              ? void 0
              : _d.reduce((sum, g) => {
                var _a2;
                return (
                  sum +
                  ((null == (_a2 = g.destinations) ? void 0 : _a2.length) ||
                    0)
                );
              }, 0)) || 0) +
            ((null == (_e = settings.homeDestinations) ? void 0 : _e.length) ||
              0);
      }
      const finalGateError =
        await this.runFinalConsistencyAndInventoryCheck(settings);
      if (finalGateError)
        return (this.showErrors(finalGateError), void (this.isProcessing = !1));
      this.setLoading(!0);
      try {
        const ritsCartType =
          null ==
            (_g =
              null == (_f = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _f.RITS)
            ? void 0
            : _g.cartType,
          ritsID = undefined;
        (null ==
          (_i = null == (_h = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _h.RITS)
          ? void 0
          : _i.cartNo) &&
          ritsCartType &&
          1 === Number(ritsCartType) &&
          (await this.callRitsApi());
        const separatedSettings = this.applyShippingUnitSeparation(settings),
          settingsWithShippingUnits = {
            ...separatedSettings,
            shippingUnits: window.BINDEC_SHIPPING_UNIT_SETTINGS || {},
          },
          success = undefined;
        if (!(await sessionManager.update(settingsWithShippingUnits)))
          throw new Error(
            "データの保存に失敗しました。もう一度お試しください。",
          );
        const token = sessionManager.getToken();
        if (!token)
          throw new Error(
            "セッショントークンが取得できませんでした。ページをリロードして再度お試しください。",
          );
        if (cartRecoveryService.isRecoveryInProgress()) {
          const purchasedIds = cartRecoveryService.getPurchasedVariantIds();
          purchasedIds.length > 0 &&
            cartRecoveryService.markItemsDeleted(purchasedIds, token);
        }
        const orderMetadata = transformToOrderMetadata(separatedSettings);
        (await this.applyTokenToCartItems(token),
          await this.updateCartAttributes(token),
          this.outputCheckoutDebugLog(separatedSettings, orderMetadata, token));
        const homeDestinations = separatedSettings.homeDestinations,
          isHomeDelivery = homeDestinations && homeDestinations.length > 0,
          bindecSettings = window.BINDEC_SHIPPING_SETTINGS,
          profileAddressId =
            null ==
              (_j =
                null == bindecSettings ? void 0 : bindecSettings.profileAddress)
              ? void 0
              : _j.id;
        if (isHomeDelivery) {
          const homeAddressId = this.extractAddressId(
            null == (_k = homeDestinations[0]) ? void 0 : _k.shopifyAddressId,
          );
          homeAddressId && (await this.setAddressAsDefault(homeAddressId));
        } else
          profileAddressId &&
            (await this.setAddressAsDefault(String(profileAddressId)));
        const checkoutUrl = this.buildCheckoutUrlWithCustomerAddress(
          settings,
          isHomeDelivery ? homeDestinations : null,
        );
        window.location.href = checkoutUrl;
      } catch (error) {
        (this.showError([
          "チェックアウトの準備中にエラーが発生しました。",
          "もう一度お試しください。",
        ]),
          this.setLoading(!1));
      }
    }
    async getLatestPurposeAndGiftData() {
      var _a;
      try {
        const response = await fetch(
          `/cart?section=${(null == (_a = document.getElementById("main-cart-items")) ? void 0 : _a.dataset.id) || ""}`,
        ),
          responseText = await response.text(),
          html = new DOMParser().parseFromString(responseText, "text/html");
        (this.executeParsedInlineScript(
          html.querySelector("#bindec-purpose-json"),
        ),
          this.executeParsedInlineScript(
            html.querySelector("#bindec-gift-json"),
          ));
      } catch (e) { }
    }
    executeParsedInlineScript(parsedScript) {
      var _a;
      if (!parsedScript || "SCRIPT" !== parsedScript.tagName) return !1;
      const text = undefined;
      if (!(null == (_a = parsedScript.textContent) ? void 0 : _a.trim()))
        return !1;
      const newScript = document.createElement("script");
      (Array.from(parsedScript.attributes).forEach((attr) => {
        "id" !== attr.name.toLowerCase() &&
          newScript.setAttribute(attr.name, attr.value);
      }),
        (newScript.textContent = parsedScript.textContent));
      try {
        const parent = undefined;
        return (
          (document.head || document.documentElement).appendChild(newScript),
          newScript.remove(),
          !0
        );
      } catch (err) {
        return (newScript.remove(), !1);
      }
    }
    normalizeGiftDateInput(dateStr, currentYear) {
      if (!dateStr) return null;
      const trimmed = String(dateStr).trim();
      return trimmed && "null" !== trimmed && "undefined" !== trimmed
        ? /^\d{2}-\d{2}$/.test(trimmed)
          ? `${currentYear}-${trimmed}`
          : trimmed
        : null;
    }
    isWithinDateRange(startStr, endStr) {
      const now = new Date(),
        currentYear = now.getFullYear(),
        normalizedStart = this.normalizeGiftDateInput(startStr, currentYear),
        normalizedEnd = this.normalizeGiftDateInput(endStr, currentYear);
      if (normalizedStart) {
        const start = new Date(normalizedStart);
        if (!Number.isNaN(start.getTime()) && now < start) return !1;
      }
      if (normalizedEnd) {
        const end = new Date(normalizedEnd);
        if (
          !Number.isNaN(end.getTime()) &&
          (/T\d{2}:\d{2}/.test(normalizedEnd) || end.setHours(23, 59, 59, 999),
            now > end)
        )
          return !1;
      }
      return !0;
    }
    resolveGiftDataProductKeys(giftData, productIds) {
      const allKeys = Object.keys(giftData);
      if (0 === productIds.length) return allKeys;
      const extractNumericId = (value) => {
        const match = String(value).match(/(\d+)(?!.*\d)/);
        return match ? match[1] : null;
      },
        resolved = new Set();
      for (const rawProductId of productIds) {
        const productId = String(rawProductId).trim();
        if (!productId) continue;
        const productNumericId = extractNumericId(productId);
        for (const key of allKeys) {
          if (key === productId) {
            resolved.add(key);
            continue;
          }
          const keyNumericId = extractNumericId(key);
          productNumericId &&
            keyNumericId &&
            productNumericId === keyNumericId &&
            resolved.add(key);
        }
      }
      return resolved.size > 0 ? Array.from(resolved) : allKeys;
    }
    findGiftCandidatesByHandle(handle, giftData, productIds) {
      const productKeys = this.resolveGiftDataProductKeys(giftData, productIds),
        candidates = [];
      for (const productKey of productKeys) {
        const events = giftData[productKey] || [];
        for (const event of events) {
          const allItems = undefined,
            found = [
              ...(event.data.noshi || []),
              ...(event.data.messageCard || []),
              ...(event.data.wrapping || []),
            ].find((item) => item.metaobjectHandle === handle);
          found &&
            candidates.push({
              item: found,
              event: event,
              productId: productKey,
            });
        }
      }
      return candidates;
    }
    isGiftItemWithinDateRange(item) {
      return this.isWithinDateRange(
        item.startTime || item._start_date,
        item.endTime || item._end_date,
      );
    }
    isGiftEventWithinDateRange(event) {
      return this.isWithinDateRange(event.startTime, event.endTime);
    }
    findExpiredGiftSettings(settings) {
      var _a;
      const giftData = window._GIFT_DATA ?? window.GIFT_DATA;
      if (!giftData) return [];
      const expired = new Set(),
        checkGiftSettings = (giftSettings, productIds) => {
          if (!giftSettings || !giftSettings.isGift) return;
          const handles = [
            { handle: giftSettings.noshiMetaobjectHandle, label: "のし" },
            {
              handle: giftSettings.wrappingMetaobjectHandle,
              label: "ラッピング",
            },
            {
              handle: giftSettings.messageCardMetaobjectHandle,
              label: "メッセージカード",
            },
          ];
          for (const { handle: handle, label: label } of handles) {
            if (!handle) continue;
            const candidates = this.findGiftCandidatesByHandle(
              handle,
              giftData,
              productIds,
            );
            if (0 === candidates.length) continue;
            const hasValidCandidate = undefined;
            if (
              !candidates.some(({ item: item, event: event }) => {
                const itemValid = this.isGiftItemWithinDateRange(item),
                  eventValid = this.isGiftEventWithinDateRange(event);
                return itemValid && eventValid;
              })
            ) {
              const details = undefined;
              candidates
                .map(({ item: item }) => {
                  const itemName = item.name || item.title || handle;
                  return `${label}「${itemName}」`;
                })
                .forEach((detail) => {
                  expired.add(detail);
                });
            }
          }
        };
      return (
        null == (_a = settings.deliveryGroups) ||
        _a.forEach((group) => {
          var _a2;
          null == (_a2 = group.destinations) ||
            _a2.forEach((dest) => {
              var _a3;
              (null == (_a3 = dest.items) ||
                _a3.forEach((item) => {
                  checkGiftSettings(item.giftSettings, [
                    String(item.productId),
                  ]);
                }),
                dest.shippingUnits &&
                Object.values(dest.shippingUnits).forEach((unit) => {
                  const productIds = (unit.items || [])
                    .map((unitItem) =>
                      null == unitItem ? void 0 : unitItem.product_id,
                    )
                    .filter((id) => null != id)
                    .map((id) => String(id));
                  checkGiftSettings(unit.giftSettings, productIds);
                }));
            });
        }),
        Array.from(expired)
      );
    }
    findInvalidDeliveryPeriods(settings, currentPurpose) {
      var _a, _b, _c;
      const w = window,
        purposes =
          w._PURPOSE_MASTER ??
          (null == (_a = w.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _a.purposeMaster);
      if (!purposes || 0 === purposes.length) return [];
      const targetPurpose = purposes.find(
        (p) => (p.name || p._title) === currentPurpose,
      );
      if (!targetPurpose) return [];
      const rawPeriods =
        targetPurpose.desired_periods || targetPurpose._deliveryperiod || [],
        isGidArray = rawPeriods.length > 0 && "string" == typeof rawPeriods[0],
        periodObjects = isGidArray ? [] : rawPeriods,
        periodTitles = new Set(
          periodObjects
            .map(
              (p) =>
                (null == p ? void 0 : p.period_name) ||
                (null == p ? void 0 : p._title) ||
                (null == p ? void 0 : p._category),
            )
            .filter((t) => "string" == typeof t && t.length > 0),
        ),
        invalid = new Set(),
        isPeriodWithinDisplayRange = (period) => {
          const start =
            (null == period
              ? void 0
              : period._period_datetime_selection_start) ||
            (null == period ? void 0 : period.display_start_date) ||
            (null == period ? void 0 : period._start_date),
            end =
              (null == period
                ? void 0
                : period._period_datetime_selection_end) ||
              (null == period ? void 0 : period.display_end_date) ||
              (null == period ? void 0 : period._end_date);
          return this.isWithinDateRange(start, end);
        },
        checkDeliveryPeriod = (deliveryPeriod) => {
          if (!deliveryPeriod) return;
          if (isGidArray) return;
          if (!periodTitles.has(deliveryPeriod))
            return void invalid.add(deliveryPeriod);
          const matchedPeriod = periodObjects.find(
            (p) =>
              ((null == p ? void 0 : p.period_name) ||
                (null == p ? void 0 : p._title) ||
                (null == p ? void 0 : p._category)) === deliveryPeriod,
          );
          matchedPeriod &&
            !isPeriodWithinDisplayRange(matchedPeriod) &&
            invalid.add(deliveryPeriod);
        },
        checkDestination = (dest) => {
          dest &&
            (checkDeliveryPeriod(dest.deliveryPeriod),
              dest.shippingUnits &&
              Object.values(dest.shippingUnits).forEach((unit) => {
                checkDeliveryPeriod(
                  null == unit ? void 0 : unit.deliveryPeriod,
                );
              }));
        };
      return (
        null == (_b = settings.deliveryGroups) ||
        _b.forEach((group) => {
          var _a2;
          null == (_a2 = group.destinations) ||
            _a2.forEach((dest) => checkDestination(dest));
        }),
        null == (_c = settings.homeDestinations) ||
        _c.forEach((dest) => checkDestination(dest)),
        Array.from(invalid)
      );
    }
    findInvalidDeliveryDates() {
      const invalid = new Set(),
        selectors = undefined;
      return (
        document.querySelectorAll("datetime-selector").forEach((el) => {
          var _a;
          const selector = el;
          if ("function" != typeof selector.validateSelectedDate) return;
          const destinationId = undefined,
            shippingUnitId = undefined,
            key = `${selector.getAttribute("data-destination-id") || ""}_${selector.getAttribute("data-shipping-unit-id") || ""}`,
            unit =
              null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
                ? void 0
                : _a[key],
            selectedDate = (null == unit ? void 0 : unit.deliveryDate) || "";
          selectedDate &&
            "none" !== selector.style.display &&
            (selector.validateSelectedDate(selectedDate) ||
              invalid.add(selectedDate));
        }),
        Array.from(invalid)
      );
    }
    outputCheckoutDebugLog(settings, orderMetadata, token) {
      var _a, _b, _c;
      try {
        null == (_a = settings.deliveryGroups) ||
          _a.forEach((group, groupIdx) => {
            var _a2, _b2, _c2;
            const groupNumber = groupIdx + 1;
            if (group.sender && group.sender.length > 0) {
              const sender = group.sender[0];
              sender.address && sender.address.building;
            }
            null == (_a2 = group.destinations) ||
              _a2.forEach((destination, destIdx) => {
                var _a3;
                const destNumber = destIdx + 1;
                ("delivery" === destination.deliveryMethod &&
                  destination.address &&
                  destination.address.building,
                  null == (_a3 = destination.items) ||
                  _a3.forEach((item, itemIdx) => {
                    item.variantTitle;
                  }));
                const shippingUnits = this.getDestinationShippingUnits(
                  destination.destinationId,
                );
                let totalDestWeight = 0,
                  totalDestShippingFee = 0;
                shippingUnits.forEach((unit, unitIdx) => {
                  var _a4;
                  (unit.excluded,
                    (totalDestWeight += unit.totalWeight || 0),
                    (totalDestShippingFee += unit.shippingCost || 0),
                    null == (_a4 = unit.items) || _a4.forEach((item) => { }));
                });
              });
            const groupTotalShippingFee =
              (null == (_b2 = group.destinations)
                ? void 0
                : _b2.reduce((sum, dest) => {
                  const units = undefined;
                  return (
                    sum +
                    this.getDestinationShippingUnits(
                      dest.destinationId,
                    ).reduce(
                      (unitSum, unit) => unitSum + (unit.shippingCost || 0),
                      0,
                    )
                  );
                }, 0)) || 0,
              groupTotalWeight =
                (null == (_c2 = group.destinations)
                  ? void 0
                  : _c2.reduce((sum, dest) => {
                    const units = undefined;
                    return (
                      sum +
                      this.getDestinationShippingUnits(
                        dest.destinationId,
                      ).reduce(
                        (unitSum, unit) => unitSum + (unit.totalWeight || 0),
                        0,
                      )
                    );
                  }, 0)) || 0;
          });
        let totalOrderShippingFee = 0,
          totalOrderWeight = 0,
          totalOrderProductPrice = 0;
        (null == (_b = settings.deliveryGroups) ||
          _b.forEach((group) => {
            var _a2;
            null == (_a2 = group.destinations) ||
              _a2.forEach((dest) => {
                var _a3;
                const units = this.getDestinationShippingUnits(
                  dest.destinationId,
                );
                ((totalOrderShippingFee += units.reduce(
                  (sum, unit) => sum + (unit.shippingCost || 0),
                  0,
                )),
                  (totalOrderWeight += units.reduce(
                    (sum, unit) => sum + (unit.totalWeight || 0),
                    0,
                  )),
                  (totalOrderProductPrice +=
                    (null == (_a3 = dest.items)
                      ? void 0
                      : _a3.reduce(
                        (sum, item) => sum + item.price * item.quantity,
                        0,
                      )) || 0));
              });
          }),
          null == (_c = settings.homeDestinations) ||
          _c.forEach((dest) => {
            var _a2;
            const units = this.getDestinationShippingUnits(
              dest.destinationId,
            );
            ((totalOrderShippingFee += units.reduce(
              (sum, unit) => sum + (unit.shippingCost || 0),
              0,
            )),
              (totalOrderWeight += units.reduce(
                (sum, unit) => sum + (unit.totalWeight || 0),
                0,
              )),
              (totalOrderProductPrice +=
                (null == (_a2 = dest.items)
                  ? void 0
                  : _a2.reduce(
                    (sum, item) => sum + item.price * item.quantity,
                    0,
                  )) || 0));
          }));
      } catch (logError) { }
    }
    getDestinationShippingUnits(destinationId) {
      const shippingSettings = window.BINDEC_SHIPPING_UNIT_SETTINGS || {};
      if (/_unit\d+$/.test(destinationId)) return [];
      const matchedUnits = undefined;
      return Object.entries(shippingSettings)
        .filter(([unitId]) => unitId.startsWith(`${destinationId}_`))
        .map(([_, unit]) => unit);
    }
    applyShippingUnitSeparation(settings) {
      var _a, _b;
      const separatedSettings = JSON.parse(JSON.stringify(settings));
      if (
        (separatedSettings.deliveryGroups &&
          separatedSettings.deliveryGroups.length > 0 &&
          (separatedSettings.deliveryGroups =
            separatedSettings.deliveryGroups.map((group) => {
              const separatedDestinations = [];
              return (
                (group.destinations || []).forEach((destination) => {
                  const shippingUnits = this.getDestinationShippingUnits(
                    destination.destinationId,
                  );
                  0 !== shippingUnits.length
                    ? shippingUnits.forEach((unit, unitIndex) => {
                      var _a2, _b2;
                      const shippingUnit = unit,
                        unitVariantIds = new Set(
                          (shippingUnit.items || []).map((item) =>
                            item.variant_id
                              ? `gid://shopify/ProductVariant/${item.variant_id}`
                              : item.variantId,
                          ),
                        ),
                        unitItems = (shippingUnit.items || [])
                          .map((unitItem) => {
                            const variantId = unitItem.variant_id
                              ? `gid://shopify/ProductVariant/${unitItem.variant_id}`
                              : unitItem.variantId,
                              originalItem = (destination.items || []).find(
                                (item) => item.variantId === variantId,
                              );
                            return originalItem
                              ? {
                                ...originalItem,
                                price: unitItem.original_price,
                                totalPrice: unitItem.original_line_price,
                                finalPrice: unitItem.final_price,
                                finalLinePrice: unitItem.final_line_price,
                                totalDiscount: unitItem.total_discount,
                              }
                              : null;
                          })
                          .filter((item) => null !== item);
                      if (0 === unitItems.length) return;
                      const newDestinationId = `${destination.destinationId}_unit${unitIndex + 1}`,
                        newDestination = {
                          ...destination,
                          destinationId: newDestinationId,
                          items: unitItems,
                          carrier:
                            shippingUnit.company || destination.carrier,
                          shippingMethod:
                            (null == (_a2 = shippingUnit.shippingData)
                              ? void 0
                              : _a2.shipping_method) ||
                            destination.shippingMethod,
                          shippingFee: shippingUnit.shippingCost || 0,
                          deliveryDate:
                            shippingUnit.deliveryDate ||
                            destination.deliveryDate,
                          deliveryTime:
                            shippingUnit.deliveryTime ||
                            destination.deliveryTime,
                          deliveryPeriod:
                            shippingUnit.deliveryPeriod ||
                            destination.deliveryPeriod,
                          shippingStartDate:
                            shippingUnit.shippingStartDate ||
                            destination.shippingStartDate,
                          shipperCode:
                            shippingUnit.shipperCode ||
                            destination.shipperCode,
                          scheduledShipDate:
                            shippingUnit.scheduledShipDate ||
                            destination.scheduledShipDate,
                          deliverySlipType:
                            shippingUnit.deliverySlipType ||
                            destination.deliverySlipType,
                          deliveryMethodCode:
                            shippingUnit.shipping_rule_id ||
                            (null == (_b2 = shippingUnit.shippingData)
                              ? void 0
                              : _b2.id) ||
                            destination.deliveryMethodCode,
                          deliveryInstructionCategory:
                            shippingUnit.documentTypeHandle ||
                            destination.deliveryInstructionCategory,
                          productLeadTimeDays:
                            shippingUnit.productLeadTimeDays ||
                            destination.productLeadTimeDays,
                          deliveryLeadTimeDays:
                            shippingUnit.deliveryLeadTimeDays ||
                            destination.deliveryLeadTimeDays,
                          totalLeadTimeDays:
                            shippingUnit.totalLeadTimeDays ||
                            destination.totalLeadTimeDays,
                          earliestDeliveryDate:
                            shippingUnit.earliestDeliveryDate ||
                            destination.earliestDeliveryDate,
                          isDeliveryDateAvailable:
                            shippingUnit.isDeliveryDateAvailable ??
                            destination.isDeliveryDateAvailable,
                          workInstructionCategory:
                            destination.workInstructionCategory,
                          shippingUnits: void 0,
                        };
                      separatedDestinations.push(newDestination);
                    })
                    : separatedDestinations.push(destination);
                }),
                { ...group, destinations: separatedDestinations }
              );
            })),
          separatedSettings.homeDestinations &&
          separatedSettings.homeDestinations.length > 0)
      ) {
        const separatedHomeDestinations = [];
        (separatedSettings.homeDestinations.forEach((destination) => {
          const shippingUnits = this.getDestinationShippingUnits(
            destination.destinationId,
          );
          0 !== shippingUnits.length
            ? shippingUnits.forEach((unit, unitIndex) => {
              var _a2, _b2;
              const shippingUnit = unit,
                unitItems = (shippingUnit.items || [])
                  .map((unitItem) => {
                    const variantId = unitItem.variant_id
                      ? `gid://shopify/ProductVariant/${unitItem.variant_id}`
                      : unitItem.variantId,
                      originalItem = (destination.items || []).find(
                        (item) => item.variantId === variantId,
                      );
                    return originalItem
                      ? {
                        ...originalItem,
                        price: unitItem.original_price,
                        totalPrice: unitItem.original_line_price,
                        finalPrice: unitItem.final_price,
                        finalLinePrice: unitItem.final_line_price,
                        totalDiscount: unitItem.total_discount,
                      }
                      : null;
                  })
                  .filter((item) => null !== item);
              if (0 === unitItems.length) return;
              const newDestinationId = `${destination.destinationId}_unit${unitIndex + 1}`,
                newDestination = {
                  ...destination,
                  destinationId: newDestinationId,
                  items: unitItems,
                  carrier: shippingUnit.company || destination.carrier,
                  shippingMethod:
                    (null == (_a2 = shippingUnit.shippingData)
                      ? void 0
                      : _a2.shipping_method) || destination.shippingMethod,
                  shippingFee: shippingUnit.shippingCost || 0,
                  deliveryDate:
                    shippingUnit.deliveryDate || destination.deliveryDate,
                  deliveryTime:
                    shippingUnit.deliveryTime || destination.deliveryTime,
                  deliveryPeriod:
                    shippingUnit.deliveryPeriod || destination.deliveryPeriod,
                  shippingStartDate:
                    shippingUnit.shippingStartDate ||
                    destination.shippingStartDate,
                  shipperCode:
                    shippingUnit.shipperCode || destination.shipperCode,
                  scheduledShipDate:
                    shippingUnit.scheduledShipDate ||
                    destination.scheduledShipDate,
                  deliverySlipType:
                    shippingUnit.deliverySlipType ||
                    destination.deliverySlipType,
                  deliveryMethodCode:
                    shippingUnit.shipping_rule_id ||
                    (null == (_b2 = shippingUnit.shippingData)
                      ? void 0
                      : _b2.id) ||
                    destination.deliveryMethodCode,
                  deliveryInstructionCategory:
                    shippingUnit.documentTypeHandle ||
                    destination.deliveryInstructionCategory,
                  productLeadTimeDays:
                    shippingUnit.productLeadTimeDays ||
                    destination.productLeadTimeDays,
                  deliveryLeadTimeDays:
                    shippingUnit.deliveryLeadTimeDays ||
                    destination.deliveryLeadTimeDays,
                  totalLeadTimeDays:
                    shippingUnit.totalLeadTimeDays ||
                    destination.totalLeadTimeDays,
                  earliestDeliveryDate:
                    shippingUnit.earliestDeliveryDate ||
                    destination.earliestDeliveryDate,
                  isDeliveryDateAvailable:
                    shippingUnit.isDeliveryDateAvailable ??
                    destination.isDeliveryDateAvailable,
                  workInstructionCategory:
                    destination.workInstructionCategory,
                  shippingUnits: void 0,
                };
              separatedHomeDestinations.push(newDestination);
            })
            : separatedHomeDestinations.push(destination);
        }),
          (separatedSettings.homeDestinations = separatedHomeDestinations));
      }
      if (
        separatedSettings.homeDestinations &&
        separatedSettings.homeDestinations.length > 0
      ) {
        separatedSettings.deliveryGroups =
          (null == (_a = separatedSettings.deliveryGroups)
            ? void 0
            : _a.filter((group) => "自宅用" !== group.purpose)) || [];
        const homeDestinations = separatedSettings.homeDestinations;
        for (const homeDestination of homeDestinations) {
          const baseDestId = undefined,
            homeGroupId = `home_group_${homeDestination.destinationId.replace(/_unit\d+$/, "")}`;
          let homeGroup =
            null == (_b = separatedSettings.deliveryGroups)
              ? void 0
              : _b.find((g) => g.groupId === homeGroupId);
          if (!homeGroup) {
            const sender = this.buildCustomerSender(separatedSettings);
            ((homeGroup = {
              groupId: homeGroupId,
              purpose: "自宅用",
              sender: [sender],
              destinations: [],
            }),
              separatedSettings.deliveryGroups ||
              (separatedSettings.deliveryGroups = []),
              separatedSettings.deliveryGroups.push(homeGroup));
          }
          homeGroup.destinations.push(homeDestination);
        }
      }
      return separatedSettings;
    }
    buildCheckoutUrlWithCustomerAddress(settings, homeDestinations) {
      const baseUrl = "/checkout";
      if (!settings.customer) return baseUrl;
      const customer = settings.customer,
        bindecSettings = window.BINDEC_SHIPPING_SETTINGS,
        profileAddress =
          null == bindecSettings ? void 0 : bindecSettings.profileAddress,
        params = new URLSearchParams();
      (customer.email && params.append("checkout[email]", customer.email),
        params.append("checkout[shipping_address][country_code]", "JP"),
        homeDestinations && homeDestinations.length > 0 && profileAddress
          ? this.appendAddressParams(params, "billing_address", profileAddress)
          : params.append("checkout[billing_address][country_code]", "JP"));
      const queryString = params.toString();
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }
    extractAddressId(idValue) {
      if (!idValue) return null;
      const s = String(idValue);
      if (s.startsWith("gid://")) {
        const match = s.match(/\/(\d+)$/);
        return match ? match[1] : null;
      }
      return s;
    }
    async setAddressAsDefault(addressId) {
      const bindecSettings = window.BINDEC_SHIPPING_SETTINGS,
        defaultAddressId = undefined;
      if (
        addressId ===
        String(
          (null == bindecSettings ? void 0 : bindecSettings.defaultAddressId) ||
          "",
        )
      )
        return !0;
      try {
        const formData = new FormData();
        (formData.append("_method", "put"),
          formData.append("address[default]", "1"));
        const response = await fetch(`/account/addresses/${addressId}`, {
          method: "POST",
          body: formData,
          redirect: "manual",
        });
        return !0;
      } catch (err) {
        return !1;
      }
    }
    appendAddressParams(params, type, address) {
      if (!address) return;
      const prefix = `checkout[${type}]`;
      (address.first_name &&
        params.append(`${prefix}[first_name]`, address.first_name),
        address.last_name &&
        params.append(`${prefix}[last_name]`, address.last_name),
        address.company && params.append(`${prefix}[company]`, address.company),
        address.address1 &&
        params.append(`${prefix}[address1]`, address.address1),
        address.address2 &&
        params.append(`${prefix}[address2]`, address.address2),
        address.city && params.append(`${prefix}[city]`, address.city),
        address.province &&
        params.append(`${prefix}[province]`, address.province),
        params.append(`${prefix}[country_code]`, address.country_code || "JP"),
        address.zip && params.append(`${prefix}[zip]`, address.zip),
        address.phone && params.append(`${prefix}[phone]`, address.phone));
    }
    buildCustomerSender(settings) {
      const customer = settings.customer;
      if (customer)
        return {
          name:
            customer.name ||
            `${customer.lastName || ""} ${customer.firstName || ""}`.trim(),
          firstName: customer.firstName || "",
          lastName: customer.lastName || "",
          firstNameKana: customer.kanaFirstName || null,
          lastNameKana: customer.kanaLastName || null,
          honorific: customer.honorific || null,
          shopifyAddressId: customer.defaultAddressId
            ? `gid://shopify/MailingAddress/${customer.defaultAddressId}`
            : null,
          company: customer.company || null,
          department: customer.department || null,
          email: customer.email || null,
          phone: customer.phone || null,
          address: customer.address || null,
        };
      const bindecSettings = window.BINDEC_SHIPPING_SETTINGS,
        profileAddress =
          null == bindecSettings ? void 0 : bindecSettings.profileAddress;
      return profileAddress
        ? {
          name: `${profileAddress.last_name || ""} ${profileAddress.first_name || ""}`.trim(),
          firstName: profileAddress.first_name || "",
          lastName: profileAddress.last_name || "",
          honorific: null,
          shopifyAddressId: profileAddress.id
            ? `gid://shopify/MailingAddress/${profileAddress.id}`
            : null,
          company: profileAddress.company || null,
          department: null,
          email: null,
          phone: profileAddress.phone || null,
          address: {
            zipcode: profileAddress.zip || "",
            prefecture: profileAddress.province || "",
            city: profileAddress.city || "",
            address1: profileAddress.address1 || "",
            address2: profileAddress.address2 || null,
          },
        }
        : null;
    }
    toGidVariantId(v) {
      if (null == v) return "";
      const s = String(v);
      return s.startsWith("gid://") ? s : `gid://shopify/ProductVariant/${s}`;
    }
    async runFinalConsistencyAndInventoryCheck(settings) {
      var _a, _b, _c, _d, _e;
      try {
        await this.refreshProductMetafieldsFromSection();
        const cart2 = await cartCache.get(!0);
        if (!(null == cart2 ? void 0 : cart2.items))
          return ["カート情報の取得に失敗しました。ページを更新してください。"];
        const requiredByGid = this.calculateProductQuantities(settings),
          requiredByVariant = new Map();
        requiredByGid.forEach((qty, gid) => {
          const plain = normalizeVariantId(gid);
          requiredByVariant.set(
            plain,
            (requiredByVariant.get(plain) || 0) + qty,
          );
        });
        const giftVariantIds = getGiftProductVariantIds(),
          productMetafieldsMap =
            (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
              ? void 0
              : _a.productMetafields) || {};
        requiredByVariant.forEach((requiredQty, vid) => {
          const meta = productMetafieldsMap[vid];
        });
        const cartByVariant = new Map();
        for (const item of cart2.items) {
          if (isAccessoryOrGiftItem(item, giftVariantIds)) continue;
          const vid = String(item.variant_id),
            meta = productMetafieldsMap[vid];
          if (!meta) continue;
          const giftCategoryValue = undefined;
          if (
            !0 === meta.gift_category ||
            "true" === meta.gift_category ||
            !0 === (null == (_b = meta.gift_category) ? void 0 : _b.value)
          )
            continue;
          const existing = cartByVariant.get(vid);
          cartByVariant.set(vid, {
            quantity:
              ((null == existing ? void 0 : existing.quantity) || 0) +
              (item.quantity || 0),
            productTitle:
              (null == existing ? void 0 : existing.productTitle) ||
              item.product_title ||
              item.title ||
              "",
          });
        }
        const errors = [],
          allVariantIds = new Set([
            ...Array.from(cartByVariant.keys()),
            ...Array.from(requiredByVariant.keys()),
          ]);
        for (const vid of allVariantIds) {
          const cartQty =
            (null == (_c = cartByVariant.get(vid)) ? void 0 : _c.quantity) ||
            0,
            requiredQty = undefined;
          if (cartQty === (requiredByVariant.get(vid) || 0)) continue;
          const title =
            (null == (_d = cartByVariant.get(vid))
              ? void 0
              : _d.productTitle) ||
            (null == (_e = productMetafieldsMap[vid])
              ? void 0
              : _e.product_title) ||
            "",
            titleLabel = title ? `「${title}」` : "";
          errors.push(
            `商品${titleLabel}の配送先指定数とカート数量が一致しません。お届け先を見直してください。`,
          );
        }
        if (errors.length > 0) return errors;
        const shortages = findInventoryShortages(requiredByVariant);
        return shortages.length > 0 ? formatShortageMessage(shortages) : null;
      } catch (e) {
        return [
          "チェックアウト前の検証に失敗しました。ページを更新して再度お試しください。",
        ];
      }
    }
    calculateProductQuantities(settings) {
      var _a;
      const quantityMap = new Map();
      return (
        null == (_a = settings.deliveryGroups) ||
        _a.forEach((group) => {
          var _a2;
          null == (_a2 = group.destinations) ||
            _a2.forEach((destination) => {
              var _a3;
              null == (_a3 = destination.items) ||
                _a3.forEach((item) => {
                  const variantId = this.toGidVariantId(item.variantId);
                  if (!variantId) return;
                  const currentQty = quantityMap.get(variantId) || 0;
                  quantityMap.set(variantId, currentQty + item.quantity);
                });
            });
        }),
        quantityMap
      );
    }
    async refreshProductMetafieldsFromSection() {
      try {
        const el =
          document.querySelector(
            '[data-block-handle="multi-shipping-settings"]',
          ) || document.querySelector("multi-shipping-settings"),
          section =
            (null == el ? void 0 : el.closest(".shopify-section")) ||
            (null == el ? void 0 : el.closest('[id^="shopify-section-"]'));
        if (!(null == section ? void 0 : section.id)) return;
        const sectionId = section.id.replace("shopify-section-", ""),
          res = await fetch(`/?sections=${sectionId}&_=${Date.now()}`, {
            cache: "no-store",
          });
        if (!res.ok) return;
        const data = undefined,
          html = (await res.json())[sectionId];
        if (!html || "string" != typeof html) return;
        const productMetafields = this.extractProductMetafieldsFromHtml(html);
        productMetafields &&
          window.BINDEC_SHIPPING_SETTINGS &&
          ((window.BINDEC_SHIPPING_SETTINGS.productMetafields =
            productMetafields),
            Object.entries(productMetafields).forEach(([vid, meta]) => { }));
      } catch (err) { }
    }
    extractProductMetafieldsFromHtml(html) {
      const marker = "productMetafields:",
        idx = html.indexOf(marker);
      if (-1 === idx) return null;
      const start = html.indexOf("{", idx + 18);
      if (-1 === start) return null;
      let depth = 0,
        end = -1;
      for (let i = start; i < html.length; i++) {
        const c = html[i];
        if ("{" === c) depth++;
        else if ("}" === c && (depth--, 0 === depth)) {
          end = i;
          break;
        }
      }
      if (-1 === end) return null;
      try {
        return JSON.parse(html.substring(start, end + 1));
      } catch {
        return null;
      }
    }
    async applyTokenToCartItems(token) {
      var _a, _b, _c, _d;
      try {
        const multiShippingSettings = document.querySelector(
          "multi-shipping-settings",
        );
        if (!multiShippingSettings) throw new Error("配送設定が見つかりません");
        await this.refreshProductMetafieldsFromSection();
        const settings = multiShippingSettings.getSettings(),
          requiredQuantities = this.calculateProductQuantities(settings),
          cart2 = await cartCache.get(),
          giftVariantIds = getGiftProductVariantIds(),
          productMetafieldsMap =
            (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
              ? void 0
              : _a.productMetafields) || {},
          itemsNotInSettings = [];
        for (const item of cart2.items) {
          const variantId = `gid://shopify/ProductVariant/${item.variant_id}`;
          if (isAccessoryOrGiftItem(item, giftVariantIds)) continue;
          const metafields = productMetafieldsMap[String(item.variant_id)],
            giftCategoryValue = undefined;
          if (
            !0 === (null == metafields ? void 0 : metafields.gift_category) ||
            "true" ===
            (null == metafields ? void 0 : metafields.gift_category) ||
            !0 ===
            (null ==
              (_b = null == metafields ? void 0 : metafields.gift_category)
              ? void 0
              : _b.value)
          )
            continue;
          const title = undefined;
          if ("御見舞" === (item.product_title || item.title || "").trim())
            continue;
          const requiredQty = requiredQuantities.get(variantId);
          (void 0 !== requiredQty && 0 !== requiredQty) ||
            itemsNotInSettings.push(item);
        }
        let itemsDeleted = !1;
        if (itemsNotInSettings.length > 0) {
          const productNames = itemsNotInSettings
            .map((item) => `「${item.product_title}」`)
            .join("、"),
            confirmed = undefined;
          if (
            !confirm(
              `商品${productNames}が配送設定に含まれていません。含まれていない商品を削除してもよろしいでしょうか？`,
            )
          )
            throw new Error("カートの更新がキャンセルされました。");
          if (!cartRecoveryService.getSnapshot()) {
            const currentPurpose = multiShippingSettings.purpose || "";
            cartRecoveryService.saveSnapshotBeforeCheckout(
              cart2,
              currentPurpose,
            );
          }
          for (const item of itemsNotInSettings) {
            const deleteResponse = undefined;
            if (
              !(
                await fetch("/cart/change.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ id: item.key, quantity: 0 }),
                })
              ).ok
            )
              throw new Error(
                `商品 ${item.product_title} の削除に失敗しました`,
              );
          }
          (cartCache.invalidate(), (itemsDeleted = !0));
          const cartAfterDelete = undefined,
            purchasedVariantIds = (await cartCache.get()).items.map((item) =>
              item.variant_id.toString(),
            );
          cartRecoveryService.markItemsDeleted(purchasedVariantIds, "");
        }
        if (itemsDeleted) {
          const updatedCart = await cartCache.get();
          let hasChanges = !1,
            tokenApplied = !1;
          for (const item of updatedCart.items) {
            const variantId = `gid://shopify/ProductVariant/${item.variant_id}`,
              requiredQty = requiredQuantities.get(variantId);
            if (void 0 === requiredQty || 0 === requiredQty) continue;
            const shouldApplyToken = !tokenApplied,
              expectedToken = shouldApplyToken ? token : void 0;
            if (
              item.quantity === requiredQty &&
              (null == (_c = item.properties)
                ? void 0
                : _c._multi_shipping_token) === expectedToken
            ) {
              shouldApplyToken && (tokenApplied = !0);
              continue;
            }
            const newProperties = { ...item.properties };
            shouldApplyToken
              ? ((newProperties._multi_shipping_token = token),
                (tokenApplied = !0))
              : delete newProperties._multi_shipping_token;
            const updateResponse = undefined;
            if (
              !(
                await fetch("/cart/change.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    id: item.key,
                    quantity: requiredQty,
                    properties: newProperties,
                  }),
                })
              ).ok
            )
              throw new Error(
                `商品 ${item.product_title} の更新に失敗しました`,
              );
            hasChanges = !0;
          }
          hasChanges && cartCache.invalidate();
        } else {
          const firstRealItem = cart2.items.find(
            (item) => !isAccessoryOrGiftItem(item, giftVariantIds),
          );
          if (
            firstRealItem &&
            (null == (_d = firstRealItem.properties)
              ? void 0
              : _d._multi_shipping_token) !== token
          ) {
            const updateResponse = undefined;
            if (
              !(
                await fetch("/cart/change.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    id: firstRealItem.key,
                    quantity: firstRealItem.quantity,
                    properties: {
                      ...firstRealItem.properties,
                      _multi_shipping_token: token,
                    },
                  }),
                })
              ).ok
            )
              throw new Error(
                `商品 ${firstRealItem.product_title} へのトークン付与に失敗しました`,
              );
            cartCache.invalidate();
          }
        }
      } catch (error) {
        throw error;
      }
    }
    async updateCartAttributes(token) {
      try {
        const multiShippingSettings = document.querySelector(
          "multi-shipping-settings",
        );
        if (!multiShippingSettings) throw new Error("配送設定が見つかりません");

        const preappEl = document.querySelector('input[name="attributes[preapplication_clsf]"]:checked');
        const preappValue = preappEl ? preappEl.value : "";

        const settings = multiShippingSettings.getSettings(),
          settingsService = new SettingsService(),
          isGiftFlow = "gift" === settings.orderType,
          isDaibikiUnavailable = settingsService.isDaibikiUnavailable(
            settings.deliveryGroups,
            settings.orderType,
          ),
          isInStorePayment = settingsService.isInStorePayment(
            settings.deliveryGroups,
          ),
          isAllItemsAreGift = undefined,
          attributesToSet = {
            _bindec_shipping_enabled: "true",
            _multi_shipping_enabled: isGiftFlow ? "true" : "false",
            _multi_shipping_session_token: token,
            daibiki: !isDaibikiUnavailable,
            "instore-payment": isInStorePayment,
            voucher: !settingsService.isAllItemsAreGift(
              settings.deliveryGroups,
            ),
          };

        if (preappValue) {
          attributesToSet['preapplication_clsf'] = preappValue;
        }

        await settingsService.withInterceptorPaused(async () => {
          const response = await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attributes: attributesToSet }),
          });
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
              `Cart attributes update failed: ${response.statusText}`,
            );
          }
          const updatedCart = await response.json();
          cartCache.invalidate();
          const verifiedCart = await cartCache.get();
        });
      } catch (error) {
        throw error;
      }
    }
    async verifyCartAttributes() {
      var _a, _b;
      try {
        const cart2 = await cartCache.get(),
          hasToken =
            null == (_a = cart2.attributes)
              ? void 0
              : _a._multi_shipping_session_token,
          isEnabled =
            "true" ===
            (null == (_b = cart2.attributes)
              ? void 0
              : _b._multi_shipping_enabled);
        (hasToken && isEnabled) ||
          (await sessionManager.save(
            document.querySelector("multi-shipping-settings").getSettings(),
          ));
      } catch (error) { }
    }
    setLoading(loading) {
      if (((this.isProcessing = loading), this.themeCheckoutButton))
        loading
          ? (this.themeCheckoutButton instanceof HTMLButtonElement &&
            (this.themeCheckoutButton.disabled = !0),
            (this.themeCheckoutButton.style.opacity = "0.5"),
            (this.themeCheckoutButton.style.cursor = "wait"),
            (this.themeCheckoutButton.textContent = "処理中..."))
          : (this.themeCheckoutButton instanceof HTMLButtonElement &&
            (this.themeCheckoutButton.disabled = !1),
            (this.themeCheckoutButton.style.opacity = ""),
            (this.themeCheckoutButton.style.cursor = ""),
            (this.themeCheckoutButton.textContent = "ご購入手続きへ"));
      else if (this.button) {
        const textSpan = this.querySelector(".checkout-button-text"),
          loaderSpan = this.querySelector(".checkout-button-loader");
        loading
          ? ((this.button.disabled = !0),
            this.button.classList.add("loading"),
            textSpan && textSpan.classList.add("hidden"),
            loaderSpan && loaderSpan.classList.remove("hidden"))
          : ((this.button.disabled = !1),
            this.button.classList.remove("loading"),
            textSpan && textSpan.classList.remove("hidden"),
            loaderSpan && loaderSpan.classList.add("hidden"));
      }
    }
    async callRitsApi() {
      var _a, _b, _c, _d, _e, _f;
      this.ritsService ||
        (this.ritsService = window.createRitsService({
          gwDomain: window.gwDomain,
          storeDomain: window.storeDomain,
          channel: window.channel,
        }));
      const request = {
        ritsCardNo:
          null ==
            (_b =
              null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.RITS)
            ? void 0
            : _b.cartNo,
        birthday:
          null ==
            (_d =
              null == (_c = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _c.RITS)
            ? void 0
            : _d.birthday.replace(/-/g, ""),
        customerId:
          null ==
            (_f =
              null == (_e = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _e.RITS)
            ? void 0
            : _f.shopifyCustomerId,
      };
      return await this.ritsService.customerInquiry(request);
    }
    showErrors(messages) {
      var _a;
      let container, list;
      (this.themeCheckoutButton
        ? ((container =
          null == (_a = this.themeCheckoutButton.parentElement)
            ? void 0
            : _a.querySelector(".checkout-error-container")),
          (list =
            null == container
              ? void 0
              : container.querySelector(".checkout-error-list")))
        : ((container = this.querySelector(".checkout-error-container")),
          (list = this.querySelector(".checkout-error-list"))),
        container &&
        list &&
        ((list.innerHTML = messages.map((msg) => `<li>${msg}</li>`).join("")),
          container.classList.remove("hidden"),
          (container.style.display = "block"),
          container.scrollIntoView({ behavior: "smooth", block: "nearest" })));
    }
    showError(messages) {
      this.showErrors(messages);
    }
    clearErrors() {
      var _a;
      let container, list;
      (this.themeCheckoutButton
        ? ((container =
          null == (_a = this.themeCheckoutButton.parentElement)
            ? void 0
            : _a.querySelector(".checkout-error-container")),
          (list =
            null == container
              ? void 0
              : container.querySelector(".checkout-error-list")))
        : ((container = this.querySelector(".checkout-error-container")),
          (list = this.querySelector(".checkout-error-list"))),
        container &&
        list &&
        ((list.innerHTML = ""),
          container.classList.add("hidden"),
          (container.style.display = "none")));
    }
    showSuccess(messages) {
      var _a;
      const existingSuccess = this.querySelector(".checkout-success-container");
      existingSuccess && existingSuccess.remove();
      const container = document.createElement("div");
      ((container.className = "checkout-success-container"),
        (container.style.cssText =
          "\n        margin-top: 16px;\n        padding: 16px;\n        background-color: #d4edda;\n        border: 1px solid #c3e6cb;\n        border-radius: 4px;\n        color: #155724;\n      "));
      const messageList = messages
        .map((msg) => `<p style="margin: 8px 0;">${msg}</p>`)
        .join("");
      container.innerHTML = messageList;
      const buttonContainer = this.querySelector(".checkout-button-container");
      (buttonContainer &&
        (null == (_a = buttonContainer.parentElement) ||
          _a.insertBefore(container, buttonContainer.nextSibling)),
        container.scrollIntoView({ behavior: "smooth", block: "nearest" }));
    }
  }
  customElements.define("checkout-button", CheckoutButton);
}
if (!customElements.get("purpose-selector")) {
  class PurposeSelector extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "selectElement"),
        __publicField(this, "selectedPurpose", null),
        __publicField(this, "purposes", []),
        __publicField(this, "validationMessageElement"),
        __publicField(this, "handleReload", null),
        __publicField(this, "handlePurposeChange", (event) => {
          const selectedId = event.target.value;
          if (!selectedId)
            return (
              (this.selectedPurpose = null),
              this.clearValidationMessage(),
              void this.dispatchSelectionEvent(null)
            );
          const purpose = this.purposes.find(
            (p) =>
              p.id === selectedId ||
              p.name === selectedId ||
              p._title === selectedId,
          );
          purpose &&
            ((this.selectedPurpose = purpose),
              this.validatePurposeWithCart(purpose));
        }));
    }
    connectedCallback() {
      (this.render(),
        this.attachEventListeners(),
        this.loadPurposes(),
        (this.handleReload = () => this.loadPurposes()),
        this.addEventListener("purpose:reload", this.handleReload));
    }
    disconnectedCallback() {
      var _a;
      (null == (_a = this.selectElement) ||
        _a.removeEventListener("change", this.handlePurposeChange),
        this.handleReload &&
        this.removeEventListener("purpose:reload", this.handleReload));
    }
    loadPurposes() {
      var _a, _b;
      const allPurposes =
        (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
          ? void 0
          : _a.purposeMaster) || [],
        allCartItems = undefined,
        cartItemsWithPurposes = filterOutAccessoryItems(
          (null == (_b = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _b.cartItemsWithPurposes) || [],
        );
      cartItemsWithPurposes.forEach((item, index) => {
        var _a2, _b2;
        const purposeNames =
          (null == (_a2 = item.purposes)
            ? void 0
            : _a2.map((p) => p.name || p._title || "(名前なし)")) || [];
      });
      const commonPurposes = PurposeService.getPurposes(
        cartItemsWithPurposes,
        allPurposes,
      );
      ((this.purposes = PurposeService.getActivePurposes(commonPurposes)),
        this.populateSelect(),
        0 === this.purposes.length
          ? this.showErrorMessage(
            "※ カートの商品の組み合わせでは用途を選択することができません。",
          )
          : this.clearValidationMessage());
    }
    populateSelect() {
      if (this.buttonContainer) {
        // 一度リセット
        this.buttonContainer.innerHTML = "";
        
        this.purposes.forEach((purpose) => {
          const value = purpose.id || purpose.name || purpose._title || "";
          const text = purpose.label || purpose.name || purpose._title || "";
          
          const labelHtml = `
            <label class="purpose-button-label">
              <input type="radio" name="purpose" value="${value}" class="purpose-radio-input">
              <span class="purpose-button-text btn">${text}</span>
            </label>
          `;
          this.buttonContainer.insertAdjacentHTML('beforeend', labelHtml);
        });
      }
    }
    validatePurposeWithCart(purpose) {
      var _a;
      const allCartItems =
        null == (_a = window.BINDEC_SHIPPING_SETTINGS)
          ? void 0
          : _a.cartItemsWithPurposes;
      if (!allCartItems || 0 === allCartItems.length) return;
      const cartItemsWithPurposes = filterOutAccessoryItems(allCartItems),
        validationResult = PurposeService.validateCartPurposes(
          cartItemsWithPurposes,
          purpose,
        );
      validationResult.isValid
        ? (this.clearValidationMessage(),
          this.dispatchSelectionEvent(purpose, !0))
        : (this.showErrorMessage(
          `（${validationResult.invalidProducts.join("、")}）と同時に購入できません。この商品を削除してからお進みください。`,
        ),
          this.dispatchSelectionEvent(
            purpose,
            !1,
            validationResult.invalidProducts,
          ));
    }
    showSuccessMessage(message) {
      this.validationMessageElement &&
        ((this.validationMessageElement.textContent = message),
          (this.validationMessageElement.className =
            "purpose-validation-message success"),
          (this.validationMessageElement.style.display = "block"));
    }
    showErrorMessage(message) {
      this.validationMessageElement &&
        ((this.validationMessageElement.textContent = message),
          (this.validationMessageElement.className =
            "purpose-validation-message error"),
          (this.validationMessageElement.style.display = "block"));
    }
    clearValidationMessage() {
      this.validationMessageElement &&
        ((this.validationMessageElement.textContent = ""),
          (this.validationMessageElement.style.display = "none"));
    }
    dispatchSelectionEvent(purpose, isValid = !1, invalidProducts = []) {
      const detail = {
        purpose: purpose,
        isValid: isValid,
        invalidProducts: invalidProducts,
      };
      this.dispatchEvent(
        new CustomEvent("purpose-selected", {
          detail: detail,
          bubbles: !0,
          composed: !0,
        }),
      );
    }
    attachEventListeners() {
      this.validationMessageElement = this.querySelector(".js-purpose-validation-message");
      this.buttonContainer = this.querySelector("#purpose-button-container");
      
      // ラジオボタンの変更イベントを監視
      if (this.buttonContainer) {
        this.buttonContainer.addEventListener("change", (e) => {
          if (e.target && e.target.classList.contains("purpose-radio-input")) {
            this.handlePurposeChange(e);
            // システム全体に値の変更を通知
            this.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      }
    }
    getSelectedPurpose() {
      return this.selectedPurpose;
    }
    render() {
      const purposeHeader = this.querySelector(".purpose-header");
      if (
        purposeHeader &&
        !this.querySelector(".js-purpose-validation-message")
      ) {
        const messageDiv = document.createElement("div");
        ((messageDiv.className =
          "js-purpose-validation-message purpose-validation-message"),
          (messageDiv.style.display = "none"),
          purposeHeader.appendChild(messageDiv));
      }
    }
  }
  customElements.define("purpose-selector", PurposeSelector);
}
const FULL_WIDTH_PATTERN =
  /^[\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\uFF01-\uFF60\uFFA0-\uFFEF\u4E00-\u9FAF\u3400-\u4DBF\uF900-\uFAFF]+$/,
  REQUIRED_FIELDS = [
    { key: "firstName", label: "姓" },
    { key: "lastName", label: "名" },
    // { key: "lastNameKana", label: "氏名カナ（セイ）" },
    // { key: "firstNameKana", label: "氏名カナ（メイ）" },
    { key: "honorific", label: "敬称" },
    { key: "zip", label: "郵便番号" },
    { key: "province", label: "都道府県" },
    { key: "city", label: "市区町村" },
    { key: "town", label: "町域" },
    { key: "address_line1", label: "番地" },
    { key: "phone_1", label: "電話番号1" },
    { key: "phone_2", label: "電話番号2" },
    { key: "phone_3", label: "電話番号3" },
  ],
  FULL_WIDTH_FIELDS = [
    { key: "firstName", label: "姓" },
    { key: "lastName", label: "名" },
    { key: "lastNameKana", label: "氏名カナ（セイ）" },
    { key: "firstNameKana", label: "氏名カナ（メイ）" },
    { key: "honorific", label: "敬称" },
    { key: "department", label: "部署・役職" },
    { key: "city", label: "市区町村" },
    { key: "town", label: "町域" },
    { key: "address_line1", label: "番地" },
    { key: "address2", label: "建物名、部屋番号等" },
  ],
  MAX_LENGTH_FIELDS = [
    { key: "firstName", label: "お名", max: 16 },
    { key: "lastName", label: "名", max: 16 },
    { key: "lastNameKana", label: "氏名カナ（セイ）", max: 10 },
    { key: "firstNameKana", label: "氏名カナ（メイ）", max: 10 },
    { key: "honorific", label: "敬称", max: 25 },
    { key: "company", label: "会社名", max: 25 },
    { key: "department", label: "部署・役職", max: 25 },
    { key: "city", label: "市区町村", max: 12 },
    { key: "town", label: "町域", max: 16 },
    { key: "address_line1", label: "番地", max: 16 },
    { key: "address2", label: "建物名、部屋番号等", max: 16 },
  ];
function validateAddressForm(data) {
  const errors = [];
  if (
    (REQUIRED_FIELDS.forEach((field) => {
      data[field.key].trim() || errors.push(`${field.label}を入力してください`);
    }),
      data.zip)
  ) {
    const zipValue = data.zip.replace(/-/g, "");
    /^\d{7}$/.test(zipValue) ||
      errors.push("郵便番号は7桁の数字で入力してください（例：4500002）");
  }
  (data.phone_1 &&
    !/^\d+$/.test(data.phone_1) &&
    errors.push("電話番号1は数字のみで入力してください"),
    data.phone_2 &&
    !/^\d+$/.test(data.phone_2) &&
    errors.push("電話番号2は数字のみで入力してください"),
    data.phone_3 &&
    !/^\d+$/.test(data.phone_3) &&
    errors.push("電話番号3は数字のみで入力してください"));
  const fullPhone = `${data.phone_1}${data.phone_2}${data.phone_3}`;
  return (
    fullPhone &&
    !/^\d{10,11}$/.test(fullPhone) &&
    errors.push(
      "電話番号は合計で10〜11桁の数字で入力してください（例：090-1234-5678）",
    ),
    FULL_WIDTH_FIELDS.forEach((field) => {
      data[field.key] &&
        !FULL_WIDTH_PATTERN.test(data[field.key]) &&
        errors.push(`${field.label}は全角文字を入力してください`);
    }),
    MAX_LENGTH_FIELDS.forEach((field) => {
      data[field.key] &&
        data[field.key].length > field.max &&
        errors.push(`${field.label}は${field.max}文字以内で入力してください`);
    }),
    { isValid: 0 === errors.length, errors: errors }
  );
}
const POSTAL_CODE_API_URL = "/apps/app-proxy/address/postal-code",
  PROVINCE_CODE_TO_NAME = {
    1: "北海道",
    2: "青森県",
    3: "岩手県",
    4: "宮城県",
    5: "秋田県",
    6: "山形県",
    7: "福島県",
    8: "茨城県",
    9: "栃木県",
    10: "群馬県",
    11: "埼玉県",
    12: "千葉県",
    13: "東京都",
    14: "神奈川県",
    15: "新潟県",
    16: "富山県",
    17: "石川県",
    18: "福井県",
    19: "山梨県",
    20: "長野県",
    21: "岐阜県",
    22: "静岡県",
    23: "愛知県",
    24: "三重県",
    25: "滋賀県",
    26: "京都府",
    27: "大阪府",
    28: "兵庫県",
    29: "奈良県",
    30: "和歌山県",
    31: "鳥取県",
    32: "島根県",
    33: "岡山県",
    34: "広島県",
    35: "山口県",
    36: "徳島県",
    37: "香川県",
    38: "愛媛県",
    39: "高知県",
    40: "福岡県",
    41: "佐賀県",
    42: "長崎県",
    43: "熊本県",
    44: "大分県",
    45: "宮崎県",
    46: "鹿児島県",
    47: "沖縄県",
  };
async function lookupPostalCode(postalCode) {
  const cleanCode = postalCode.replace(/-/g, "");
  if (!/^\d{7}$/.test(cleanCode)) return null;
  try {
    const response = await fetch(`${POSTAL_CODE_API_URL}/${cleanCode}`);
    if (!response.ok) return null;
    const json = await response.json();
    if (!json.success || !json.data) return null;
    const data = json.data,
      provinceCode = String(parseInt(data.provinceCode, 10));
    return {
      pref: PROVINCE_CODE_TO_NAME[provinceCode] || "",
      city: data.city || "",
      town: data.town || "",
    };
  } catch {
    return null;
  }
}
function getSnapshotAddressCustomData() {
  var _a;
  const snap =
    null == (_a = window.BINDEC_SHIPPING_SETTINGS)
      ? void 0
      : _a.addressCustomData;
  return snap && "object" == typeof snap
    ? {
      ...snap,
      addresses: Array.isArray(snap.addresses) ? snap.addresses : [],
    }
    : { addresses: [] };
}
async function fetchLatestAddressCustomData() {
  var _a, _b;
  const snapshot = getSnapshotAddressCustomData();
  try {
    const url = `/account/addresses?section=wl-new-added-address&_=${Date.now()}`,
      res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return snapshot;
    const html = undefined,
      jsonText =
        null ==
          (_b =
            null ==
              (_a = new DOMParser()
                .parseFromString(await res.text(), "text/html")
                .querySelector("#wlAddressCustomDataJson"))
              ? void 0
              : _a.textContent)
          ? void 0
          : _b.trim();
    if (!jsonText || "null" === jsonText) return snapshot;
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      return snapshot;
    }
    return parsed &&
      "object" == typeof parsed &&
      Array.isArray(parsed.addresses)
      ? parsed.addresses.length < snapshot.addresses.length
        ? snapshot
        : parsed
      : snapshot;
  } catch (e) {
    return snapshot;
  }
}
if (!customElements.get("address-modal")) {
  class AddressModal extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "modal"),
        __publicField(this, "searchInput"),
        __publicField(this, "addressList"),
        __publicField(this, "addressListTab"),
        __publicField(this, "newAddressTab"),
        __publicField(this, "addressSearchContainer"),
        __publicField(this, "newAddressForm"),
        __publicField(this, "tabItems"),
        __publicField(this, "cancelButton"),
        __publicField(this, "confirmButton"),
        __publicField(this, "config"),
        __publicField(this, "allAddresses", []),
        __publicField(this, "filteredAddresses", []),
        __publicField(this, "selectedAddress", null),
        __publicField(this, "selectedRadio", null),
        __publicField(this, "currentTab", "list"),
        __publicField(this, "formElement"),
        __publicField(this, "__isIntentionalClose", !1),
        __publicField(this, "savedFormData", null));
    }
    connectedCallback() {
      this.render();
    }
    disconnectedCallback() {
      this.removeEventListeners();
    }
    open(config) {
      var _a, _b;
      this.config = config;
      const customDataAddresses =
        null ==
          (_b =
            null == (_a = window.BINDEC_SHIPPING_SETTINGS)
              ? void 0
              : _a.addressCustomData)
          ? void 0
          : _b.addresses;
      ((this.allAddresses = config.addresses.map((addr) => {
        if (!Array.isArray(customDataAddresses)) return addr;
        const customEntry = customDataAddresses.find(
          (item) => null != item.id && String(item.id) === String(addr.id),
        );
        return (null == customEntry ? void 0 : customEntry.data)
          ? {
            ...addr,
            honorific: customEntry.data.honorific || addr.honorific,
            first_name_kana:
              customEntry.data.first_name_kana || addr.first_name_kana,
            last_name_kana:
              customEntry.data.last_name_kana || addr.last_name_kana,
            phone_1: customEntry.data.phone_1 || addr.phone_1,
            phone_2: customEntry.data.phone_2 || addr.phone_2,
            phone_3: customEntry.data.phone_3 || addr.phone_3,
          }
          : addr;
      })),
        (this.filteredAddresses = [...this.allAddresses]));
      const isAuthenticated = CustomerService.isAuthenticated(),
        titleElement = this.modal.querySelector(".modal-title");
      titleElement && (titleElement.textContent = config.title);
      const listTabItem = this.tabItems[0];
      if (
        (listTabItem &&
          (isAuthenticated
            ? ((listTabItem.style.display = "block"),
              (this.currentTab = "list"),
              this.showTab("list"),
              (this.savedFormData = null))
            : ((listTabItem.style.display = "none"),
              (this.currentTab = "new"),
              this.showTab("new"))),
          (this.searchInput.value = ""),
          isAuthenticated && this.renderAddressList(),
          this.attachEventListeners(),
          isAuthenticated && config.selectedAddressId)
      ) {
        const radio = this.modal.querySelector(
          `input[type="radio"][value="${config.selectedAddressId}"]`,
        );
        if (radio) {
          ((radio.checked = !0), (this.selectedRadio = radio));
          const address = this.allAddresses.find((a) => {
            var _a2;
            return (
              a.id.toString() ===
              (null == (_a2 = config.selectedAddressId)
                ? void 0
                : _a2.toString())
            );
          });
          address && (this.selectedAddress = address);
        }
      }
      (isAuthenticated ||
        (config.restoredAddress
          ? (this.fillForm(config.restoredAddress),
            (this.savedFormData = { ...config.restoredAddress }))
          : this.savedFormData && this.fillForm(this.savedFormData)),
        this.modal.showModal(),
        requestAnimationFrame(() => {
          document.activeElement === this.searchInput &&
            this.searchInput.blur();
        }));
    }
    close() {
      this.__isIntentionalClose = !0;
      const isAuthenticated = undefined;
      if (!CustomerService.isAuthenticated() && "new" === this.currentTab) {
        const formData = this.getFormData();
        if (formData) {
          const hasData = undefined;
          (formData.first_name ||
            formData.last_name ||
            formData.phone_1 ||
            formData.phone_2 ||
            formData.phone_3 ||
            formData.zip ||
            formData.town ||
            formData.address_line1) &&
            (this.savedFormData = formData);
        }
      }

      this.resetToNewAddressMode();

      (this.modal.close(),
        (this.searchInput.value = ""),
        (this.filteredAddresses = [...this.allAddresses]),
        this.removeEventListeners(),
        setTimeout(() => {
          this.__isIntentionalClose = !1;
        }, 100));
    }
    render() {
      ((this.innerHTML =
        '\n        <dialog class="address-modal">\n          <div class="address-modal-content">\n            <div class="address-modal-header">\n              <h2 class="modal-title">配送設定</h2>\n            </div>\n\n            <div class="address-modal-body">\n              <div class="address-modal-tabs">\n                <div class="tab-item active" data-tab="list">アドレス帳から選択</div>\n                <div class="tab-item" data-tab="new">新規登録</div>\n              </div>\n\n              <div class="address-list-tab">\n                <div class="address-search-container">\n                  <input\n                    type="text"\n                    class="address-search-input"\n                    placeholder="検索"\n                  />\n                </div>\n\n                <div class="address-list"></div>\n              </div>\n\n              <div class="new-address-form" style="display: none;">\n                <form class="address-form">\n                  <div class="form-row">\n                    <div class="form-group">\n                      <label for="first-name" class="form-label required">姓</label>\n                      <input type="text" id="first-name" name="firstName" class="form-input" placeholder="田中" maxlength="16" required>\n                    </div>\n                    <div class="form-group">\n                      <label for="last-name" class="form-label required">名</label>\n                      <input type="text" id="last-name" name="lastName" class="form-input" placeholder="太郎" maxlength="16" required>\n                    </div>\n                  </div>\n\n                  <div class="form-row">\n                    <div class="form-group">\n                      <label for="last-name-kana" class="form-label">氏名カナ（セイ）</label>\n                      <input type="text" id="last-name-kana" name="lastNameKana" class="form-input" placeholder="タナカ" maxlength="10" required>\n                    </div>\n                    <div class="form-group">\n                      <label for="first-name-kana" class="form-label">氏名カナ（メイ）</label>\n                      <input type="text" id="first-name-kana" name="firstNameKana" class="form-input" placeholder="タロウ" maxlength="10" required>\n                    </div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="honorific" class="form-label required">敬称</label>\n                    <input type="text" id="honorific" name="honorific" class="form-input" placeholder="様" maxlength="25" required>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="zip" class="form-label required">郵便番号</label>\n                    <div class="form-input-with-button">\n                      <input type="text" id="zip" name="zip" class="form-input" placeholder="4500002" required>\n                      <button type="button" class="postal-code-lookup-btn" id="postal-code-lookup-btn">住所検索</button>\n                    </div>\n                    <div class="postal-code-lookup-message" id="postal-code-lookup-message" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="province" class="form-label required">都道府県</label>\n                    <select id="province" name="province" class="form-select" required>\n                      <option value="">選択してください</option>\n                      <option value="東京都">東京都</option>\n                      <option value="北海道">北海道</option>\n                      <option value="青森県">青森県</option>\n                      <option value="岩手県">岩手県</option>\n                      <option value="宮城県">宮城県</option>\n                      <option value="秋田県">秋田県</option>\n                      <option value="山形県">山形県</option>\n                      <option value="福島県">福島県</option>\n                      <option value="茨城県">茨城県</option>\n                      <option value="栃木県">栃木県</option>\n                      <option value="群馬県">群馬県</option>\n                      <option value="埼玉県">埼玉県</option>\n                      <option value="千葉県">千葉県</option>\n                      <option value="神奈川県">神奈川県</option>\n                      <option value="新潟県">新潟県</option>\n                      <option value="富山県">富山県</option>\n                      <option value="石川県">石川県</option>\n                      <option value="福井県">福井県</option>\n                      <option value="山梨県">山梨県</option>\n                      <option value="長野県">長野県</option>\n                      <option value="岐阜県">岐阜県</option>\n                      <option value="静岡県">静岡県</option>\n                      <option value="愛知県">愛知県</option>\n                      <option value="三重県">三重県</option>\n                      <option value="滋賀県">滋賀県</option>\n                      <option value="京都府">京都府</option>\n                      <option value="大阪府">大阪府</option>\n                      <option value="兵庫県">兵庫県</option>\n                      <option value="奈良県">奈良県</option>\n                      <option value="和歌山県">和歌山県</option>\n                      <option value="鳥取県">鳥取県</option>\n                      <option value="島根県">島根県</option>\n                      <option value="岡山県">岡山県</option>\n                      <option value="広島県">広島県</option>\n                      <option value="山口県">山口県</option>\n                      <option value="徳島県">徳島県</option>\n                      <option value="香川県">香川県</option>\n                      <option value="愛媛県">愛媛県</option>\n                      <option value="高知県">高知県</option>\n                      <option value="福岡県">福岡県</option>\n                      <option value="佐賀県">佐賀県</option>\n                      <option value="長崎県">長崎県</option>\n                      <option value="熊本県">熊本県</option>\n                      <option value="大分県">大分県</option>\n                      <option value="宮崎県">宮崎県</option>\n                      <option value="鹿児島県">鹿児島県</option>\n                      <option value="沖縄県">沖縄県</option>\n                    </select>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="city" class="form-label required">市区町村</label>\n                    <input type="text" id="city" name="city" class="form-input" placeholder="中村区" maxlength="10" required>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="town" class="form-label required">町域</label>\n                    <input type="text" id="town" name="town" class="form-input" placeholder="名駅" maxlength="10" required>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="address_line1" class="form-label required">番地</label>\n                    <input type="text" id="address_line1" name="address_line1" class="form-input" placeholder="４丁目８番２６号（必ず全角入力してください）" maxlength="10" required>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="address2" class="form-label">建物名、部屋番号等</label>\n                    <input type="text" id="address2" name="address2" class="form-input" placeholder="例：◯ビル６ー６階（必ず全角入力してください）" maxlength="30" data-cf-validation="full-width" data-cf-trim="true">\n                  </div>\n\n                  <div class="form-group">\n                    <label class="form-label required">電話番号</label>\n                    <div class="form-row" style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px;">\n                      <input type="tel" id="phone_1" name="phone_1" class="form-input" placeholder="00" maxlength="5" required>\n                      <input type="tel" id="phone_2" name="phone_2" class="form-input" placeholder="0000" maxlength="4" required>\n                      <input type="tel" id="phone_3" name="phone_3" class="form-input" placeholder="0000" maxlength="4" required>\n                    </div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="company" class="form-label">会社名</label>\n                    <input type="text" id="company" name="company" class="form-input" placeholder="株式会社名鉄生活創研" maxlength="25">\n                  </div>\n\n                  <div class="form-group">\n                    <label for="department" class="form-label">部署・役職</label>\n                    <input type="text" id="department" name="department" class="form-input" placeholder="名鉄百貨店事業部" maxlength="25">\n                  </div>\n\n                  <div class="form-group">\n                    <label for="email" class="form-label">メールアドレス</label>\n                    <input type="email" id="email" name="email" class="form-input" placeholder="example@example.com">\n                  </div>\n                </form>\n              </div>\n            </div>\n\n            <div class="address-modal-footer">\n              <button type="button" class="modal-cancel-btn">キャンセル</button>\n              <button type="button" class="modal-confirm-btn">決定する</button>\n            </div>\n          </div>\n        </dialog>\n      '),
        (this.modal = this.querySelector("dialog")),
        (this.tabItems = this.querySelectorAll(".tab-item")),
        (this.addressListTab = this.querySelector(".address-list-tab")),
        (this.newAddressForm = this.querySelector(".new-address-form")),
        (this.searchInput = this.querySelector(".address-search-input")),
        (this.addressSearchContainer = this.querySelector(
          ".address-search-container",
        )),
        (this.addressList = this.querySelector(".address-list")),
        (this.cancelButton = this.querySelector(".modal-cancel-btn")),
        (this.confirmButton = this.querySelector(".modal-confirm-btn")),
        (this.formElement = this.querySelector(".address-form")));
    }
    renderAddressList() {
      this.addressList &&
        ((this.addressList.innerHTML = this.filteredAddresses
          .map((address, index) => {
            var _a, _b;
            const isSelected =
              (null == (_a = this.selectedAddress) ? void 0 : _a.id) ===
              address.id,
              isDefault =
                address.id ===
                (null == (_b = window.BINDEC_SHIPPING_SETTINGS)
                  ? void 0
                  : _b.defaultAddressId);
            return `
              <div class="address-item" style="position: relative;">
                
                <label class="address-item-radio" style="padding-right: 70px;">
                  <input type="radio" name="address" value="${address.id}" ${isSelected ? "checked" : ""} data-address-index="${index}" />
                  <div class="address-item-content">
                    <div class="address-item-header">
                      <span class="address-item-name">${address.last_name} ${address.first_name}</span>
                      <span class="address-item-honorific">${address.honorific || "様"}</span>
                      ${isDefault && "sender" === this.config.mode ? '<span class="address-item-badge">デフォルト</span>' : ""}
                    </div>
                    ${address.last_name_kana || address.first_name_kana ? `<div class="address-item-kana">${address.last_name_kana || ""} ${address.first_name_kana || ""}</div>` : ""}
                    <div class="address-item-details">
                      <div class="address-item-address">
                        ${convertProvinceToJapanese(address.province)}${address.city}${address.address1}${address.address2 || ""}
                      </div>
                      <div class="address-item-phone">${getDisplayPhone(address)}</div>
                    </div>
                  </div>
                </label>

                <div class="address-item-actions" style="position: absolute; right: 15px; top: 50%; transform: translateY(-50%);">
                  <button type="button" class="btn-edit-existing-address" data-address-index="${index}" style="padding: 6px 14px; min-height: auto; min-width: auto; font-size: 13px; font-weight: 500; color: #121212; background-color: #ffffff; border: 1px solid #121212; border-radius: 4px; cursor: pointer; z-index: 2; line-height: normal; display: inline-flex; align-items: center; justify-content: center; text-decoration: none;">編集</button>
                </div>
              </div>
            `;
          })
          .join("")),
          this.attachRadioListeners(),
          this.attachEditButtonListeners());
    }
    filterAddresses(searchTerm) {
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        this.filteredAddresses = this.allAddresses.filter((address) => {
          const fullName =
            `${address.last_name || ""}${address.first_name || ""}`.toLowerCase(),
            fullNameKana =
              `${address.last_name_kana || ""}${address.first_name_kana || ""}`.toLowerCase(),
            fullAddress =
              `${convertProvinceToJapanese(address.province) || ""}${address.city || ""}${address.address1 || ""}${address.address2 || ""}`.toLowerCase(),
            phone = (address.phone || "").toLowerCase();
          return (
            fullName.includes(term) ||
            fullNameKana.includes(term) ||
            fullAddress.includes(term) ||
            phone.includes(term)
          );
        });
      } else this.filteredAddresses = [...this.allAddresses];
      this.renderAddressList();
    }
    showTab(tab) {
      const isAuthenticated = CustomerService.isAuthenticated();
      ("list" !== tab || isAuthenticated) &&
        ((this.currentTab = tab),
          this.tabItems.forEach((item) => {
            item.getAttribute("data-tab") === tab
              ? item.classList.add("active")
              : item.classList.remove("active");
          }),
          "list" === tab
            ? ((this.addressListTab.style.display = "block"),
              (this.newAddressForm.style.display = "none"))
            : ((this.addressListTab.style.display = "none"),
              (this.newAddressForm.style.display = "block")));

      if (tab === 'new' && this.formElement) {
        const tabLabel = this.modal.querySelector('.tab-item[data-tab="new"]');
        if (tabLabel && !this.formElement.elements.namedItem("address[id]")?.value) {
          tabLabel.textContent = "新規登録";
        }
      }

      if (tab === 'list') {
        this.resetToNewAddressMode();
      }
    }
    attachEventListeners() {
      var _a, _b, _c, _d, _e;
      (this.removeEventListeners(),
        this.tabItems.forEach((item) => {
          const handleTabClick = () => {
            const tab = item.getAttribute("data-tab");
            this.showTab(tab);
          };
          (item.addEventListener("click", handleTabClick),
            (item._handleTabClick = handleTabClick));
        }));
      const handleSearchInput = (e) => {
        const target = e.target;
        this.filterAddresses(target.value);
      };
      (null == (_a = this.searchInput) ||
        _a.addEventListener("input", handleSearchInput),
        (this.searchInput._handleSearchInput = handleSearchInput));
      const handleCancel = () => {
        this.close();
      };
      (null == (_b = this.cancelButton) ||
        _b.addEventListener("click", handleCancel),
        (this.cancelButton._handleCancel = handleCancel));
      const handleConfirm = () => {
        "list" === this.currentTab
          ? this.selectedAddress && this.config.onSelect
            ? (this.config.onSelect(this.selectedAddress), this.close())
            : alert("住所を選択してください")
          : "new" === this.currentTab && this.handleNewAddress();
      };
      (null == (_c = this.confirmButton) ||
        _c.addEventListener("click", handleConfirm),
        (this.confirmButton._handleConfirm = handleConfirm));
      const postalCodeLookupBtn = this.modal.querySelector(
        "#postal-code-lookup-btn",
      ),
        handlePostalCodeLookup = async () => {
          const zipInput = this.formElement.elements.namedItem("zip");
          zipInput && (await this.handlePostalCodeLookup(zipInput.value));
        };
      (null == postalCodeLookupBtn ||
        postalCodeLookupBtn.addEventListener("click", handlePostalCodeLookup),
        (postalCodeLookupBtn._handlePostalCodeLookup = handlePostalCodeLookup));
      const handleModalEscapeKey = (event) => {
        event.preventDefault();
      };
      (null == (_d = this.modal) ||
        _d.addEventListener("cancel", handleModalEscapeKey),
        (this.modal._handleModalEscapeKey = handleModalEscapeKey));
      const handleModalClose = () => {
        this.__isIntentionalClose ||
          setTimeout(() => {
            this.modal.showModal();
          }, 0);
      };
      (null == (_e = this.modal) ||
        _e.addEventListener("close", handleModalClose),
        (this.modal._handleModalClose = handleModalClose));
    }
    attachRadioListeners() {
      const radios = undefined;
      this.addressList
        .querySelectorAll('input[type="radio"]')
        .forEach((radio) => {
          radio.addEventListener("change", (e) => {
            const target = e.target;
            if (target.checked) {
              this.selectedRadio = target;
              const addressIndex = parseInt(
                target.getAttribute("data-address-index") || "0",
              );
              this.selectedAddress = this.filteredAddresses[addressIndex];
            }
          });
        });
    }
    attachEditButtonListeners() {
      if (this._editListenerAttached) return;

      this.addressList.addEventListener('click', (event) => {
        const target = event.target;

        if (target.classList.contains('btn-edit-existing-address')) {
          event.preventDefault();

          const index = target.dataset.addressIndex;
          const addressData = this.filteredAddresses[index];
          if (!addressData) return;

          if (this.formElement) {
            let idInput = this.formElement.elements.namedItem("address[id]");
            if (!idInput) {
              idInput = document.createElement('input');
              idInput.type = 'hidden';
              idInput.name = 'address[id]';
              this.formElement.appendChild(idInput);
            }
            idInput.value = addressData.id;

            this.fillForm(addressData);
          }

          this.showTab('new');

          const tabLabel = this.modal.querySelector('.tab-item[data-tab="new"]');
          if (tabLabel) tabLabel.textContent = "アドレスの編集";
        }
      });

      this._editListenerAttached = true;
    }
    resetToNewAddressMode() {
      const tabLabel = this.modal.querySelector('.tab-item[data-tab="new"]');
      if (tabLabel) {
        tabLabel.textContent = "新規登録";
      }

      if (this.formElement) {
        const idInput = this.formElement.querySelector('input[name="address[id]"]');
        if (idInput) {
          idInput.remove();
        }

        if (CustomerService.isAuthenticated()) {
          this.clearForm();
        }
      }
    }
    async handlePostalCodeLookup(postalCode) {
      const messageEl = this.modal.querySelector("#postal-code-lookup-message"),
        lookupBtn = this.modal.querySelector("#postal-code-lookup-btn");
      if (!postalCode || 7 !== postalCode.replace(/-/g, "").length)
        return void (
          messageEl &&
          ((messageEl.textContent = "郵便番号は7桁の数字で入力してください"),
            (messageEl.style.display = "block"),
            (messageEl.className = "postal-code-lookup-message error"))
        );
      (lookupBtn &&
        ((lookupBtn.disabled = !0), (lookupBtn.textContent = "検索中...")),
        messageEl && (messageEl.style.display = "none"));
      const result = await lookupPostalCode(postalCode);
      if (
        (lookupBtn &&
          ((lookupBtn.disabled = !1), (lookupBtn.textContent = "住所検索")),
          result)
      ) {
        const provinceSelect = this.formElement.elements.namedItem("province"),
          cityInput = this.formElement.elements.namedItem("city"),
          townInput = this.formElement.elements.namedItem("town"),
          address1Input = this.formElement.elements.namedItem("address_line1"),
          address2Input = this.formElement.elements.namedItem("address2");

        if (provinceSelect) provinceSelect.value = result.pref;

        let processedCity = result.city || "";
        let processedTown = result.town || "";
        let processedAddress1 = address1Input ? address1Input.value : "";
        let processedAddress2 = address2Input ? address2Input.value : "";

        if (processedCity.length > 10) {
          const cityOverflow = processedCity.substring(10);
          processedCity = processedCity.substring(0, 10);
          processedTown = cityOverflow + processedTown;
        }
        if (processedTown.length > 10) {
          const townOverflow = processedTown.substring(10);
          processedTown = processedTown.substring(0, 10);
          processedAddress1 = townOverflow + processedAddress1;
        }
        if (processedAddress1.length > 10) {
          const address1Overflow = processedAddress1.substring(10);
          processedAddress1 = processedAddress1.substring(0, 10);
          processedAddress2 = address1Overflow + processedAddress2;
        }
        if (processedAddress2.length > 30) {
          processedAddress2 = processedAddress2.substring(0, 30);
        }

        if (cityInput) cityInput.value = processedCity;
        if (townInput) townInput.value = processedTown;
        if (address1Input) address1Input.value = processedAddress1;
        if (address2Input) address2Input.value = processedAddress2;
        if (messageEl) messageEl.style.display = "none";
      } else
        messageEl &&
          ((messageEl.textContent = "該当する住所が見つかりませんでした"),
            (messageEl.style.display = "block"),
            (messageEl.className = "postal-code-lookup-message error"));
    }
    getFormData() {
      if (!this.formElement) return null;
      const formData = new FormData(this.formElement),
        phone_1 = formData.get("phone_1") || "",
        phone_2 = formData.get("phone_2") || "",
        phone_3 = formData.get("phone_3") || "",
        phone = `${phone_1}-${phone_2}-${phone_3}`,
        town = formData.get("town") || "",
        address_line1 = formData.get("address_line1") || "",
        address1 = [town, address_line1].filter(Boolean).join(" ").trim(),
        address = undefined;
      return {
        id: `temp_${Date.now()}`,
        first_name: formData.get("lastName"),
        last_name: formData.get("firstName"),
        first_name_kana: formData.get("firstNameKana") || "",
        last_name_kana: formData.get("lastNameKana") || "",
        honorific: formData.get("honorific"),
        zip: formData.get("zip"),
        province: formData.get("province"),
        city: formData.get("city"),
        address1: address1,
        town: town,
        address_line1: address_line1,
        address2: formData.get("address2") || "",
        phone: phone,
        phone_1: phone_1,
        phone_2: phone_2,
        phone_3: phone_3,
        company: formData.get("company") || "",
        department: formData.get("department") || "",
        email: formData.get("email") || "",
      };
    }
    validateForm() {
      if (!this.formElement)
        return { isValid: !1, errors: ["フォームが見つかりません"] };
      const getValue = (name) => {
        const input = this.formElement.elements.namedItem(name);
        return input ? input.value : "";
      },
        formData = undefined;
      return validateAddressForm({
        firstName: getValue("firstName"),
        lastName: getValue("lastName"),
        firstNameKana: getValue("firstNameKana"),
        lastNameKana: getValue("lastNameKana"),
        honorific: getValue("honorific"),
        company: getValue("company"),
        department: getValue("department"),
        zip: getValue("zip"),
        province: getValue("province"),
        city: getValue("city"),
        town: getValue("town"),
        address_line1: getValue("address_line1"),
        address2: getValue("address2"),
        phone_1: getValue("phone_1"),
        phone_2: getValue("phone_2"),
        phone_3: getValue("phone_3"),
      });
    }
    showValidationErrors(errors) {
      const existingError = this.newAddressForm.querySelector(
        ".form-error-message",
      );
      if ((existingError && existingError.remove(), 0 === errors.length))
        return;
      const errorDiv = document.createElement("div");
      ((errorDiv.className = "form-error-message"),
        (errorDiv.innerHTML = `\n        <div class="error-title">入力内容にエラーがあります：</div>\n        <ul class="error-list">\n          ${errors.map((error) => `<li>${error}</li>`).join("")}\n        </ul>\n      `),
        this.formElement.insertBefore(errorDiv, this.formElement.firstChild),
        errorDiv.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
    clearForm() {
      if (this.formElement) {
        this.formElement.reset();
        const existingError = this.newAddressForm.querySelector(
          ".form-error-message",
        );
        existingError && existingError.remove();
      }
      this.savedFormData = null;
    }
    fillForm(address) {
      if (!this.formElement) return;

      const getEl = (name) => this.formElement.elements.namedItem(name);

      const lastNameInput = getEl("lastName");
      const firstNameInput = getEl("firstName");
      const lastNameKanaInput = getEl("lastNameKana");
      const firstNameKanaInput = getEl("firstNameKana");
      const honorificInput = getEl("honorific");

      const zipInput = getEl("zip");
      const provinceSelect = getEl("province");
      const cityInput = getEl("city");
      const townInput = getEl("town");
      const addressLine1Input = getEl("address_line1");
      const address2Input = getEl("address2");

      const companyInput = getEl("company");
      const departmentInput = getEl("department");
      const emailInput = getEl("email");

      const phone1Input = getEl("phone_1");
      const phone2Input = getEl("phone_2");
      const phone3Input = getEl("phone_3");

      if (lastNameInput) firstNameInput.value = address.last_name || "";
      if (firstNameInput) firstNameInput.value = address.last_name || "";
      if (lastNameInput) lastNameInput.value = address.first_name || "";
      if (firstNameKanaInput) firstNameKanaInput.value = address.first_name_kana || "";
      if (honorificInput) honorificInput.value = address.honorific || "様";

      if (zipInput) zipInput.value = (address.zip || "").replace("-", "");
      if (provinceSelect) provinceSelect.value = address.province || "";
      if (cityInput) cityInput.value = address.city || "";
      if (townInput) townInput.value = address.town || "";
      if (addressLine1Input) addressLine1Input.value = address.address_line1 || "";
      if (address2Input) address2Input.value = address.address2 || "";

      if (companyInput) companyInput.value = address.company || "";
      if (departmentInput) departmentInput.value = address.job_title || "";
      if (emailInput) emailInput.value = address.email || "";

      if (phone1Input && phone2Input && phone3Input) {
        phone1Input.value = address.phone_1 || "";
        phone2Input.value = address.phone_2 || "";
        phone3Input.value = address.phone_3 || "";
      }
    }
    async handleNewAddress() {
      // 連打防止 すでに処理中の場合は、リターン
      if (this._isProcessing) return;
      this._isProcessing = true;

      // ボタンの見た目を非活性化
      if (this.confirmButton) {
        this.confirmButton.disabled = true;
        this.confirmButton.textContent = "処理中...";
      }

      try {
        const validation = this.validateForm();
        if (!validation.isValid)
          return void this.showValidationErrors(validation.errors);

        const newAddress = this.getFormData();
        if (!newAddress) return void alert("住所データの取得に失敗しました");

        const isAuthenticated = CustomerService.isAuthenticated();
        if (!isAuthenticated) {
          this.savedFormData = { ...newAddress };
          if (this.config.onSelect) {
            this.config.onSelect(newAddress);
            this.close();
          }
          return;
        }

        const idInput = this.formElement.querySelector('input[name="address[id]"]');
        const isEdit = idInput && idInput.value;
        const targetAddressId = isEdit ? idInput.value : null;

        const formData = new FormData();
        formData.append("form_type", "customer_address");
        formData.append("utf8", "✓");

        formData.append("address[first_name]", newAddress.first_name);
        formData.append("address[last_name]", newAddress.last_name);
        formData.append("address[company]", newAddress.company || "");
        formData.append("address[address1]", newAddress.address1 || "");
        formData.append("address[address2]", newAddress.address2 || "");
        formData.append("address[city]", newAddress.city);
        formData.append("address[country]", "JP");
        formData.append("address[province]", newAddress.province);
        formData.append("address[zip]", newAddress.zip);
        formData.append("address[phone]", newAddress.phone);

        let fetchUrl = "/account/addresses";
        if (isEdit) {
          fetchUrl = `/account/addresses/${targetAddressId}`;
          formData.append("_method", "put");
        }

        const existingIds = await this._getAllAddressIds();

        if (this.submitButton) {
          this.submitButton.textContent = "保存中...";
          this.submitButton.disabled = true;
        }

        const createAddressResponse = await fetch(fetchUrl, {
          method: "POST",
          body: formData,
          redirect: "manual",
        });

        if (
          "opaqueredirect" !== createAddressResponse.type &&
          !createAddressResponse.ok
        ) {
          alert(`住所の保存に失敗しました (Status: ${createAddressResponse.status})。通信環境や入力内容をご確認ください。`);
          return;
        }

        const finalAddressId = isEdit ? targetAddressId : await this._detectNewAddressId(existingIds);

        if (!finalAddressId) {
          alert("住所は登録されましたが、追加情報の保存に失敗しました。アドレス帳から該当の住所を編集して再度保存してください。");
          return;
        }

        const customData = {
          job_title: newAddress.department,
          email: newAddress.email,
          honorific: newAddress.honorific,
          sender: "sender" === this.config.mode,
          first_name_kana: newAddress.first_name_kana || "",
          last_name_kana: newAddress.last_name_kana || "",
          phone_1: newAddress.phone_1 || "",
          phone_2: newAddress.phone_2 || "",
          phone_3: newAddress.phone_3 || "",
          town: newAddress.town || "",
          address_line1: newAddress.address_line1 || "",
          is_multi_shipping: !1,
        };

        if (!isEdit && existingIds.includes(+finalAddressId)) {
          alert("住所は登録されましたが、追加情報の保存に失敗しました。アドレス帳から該当の住所を編集して再度保存してください。");
          return;
        }

        let customerCustomData = await fetchLatestAddressCustomData();
        const existingEntry = customerCustomData.addresses.find(
          (address) => address.id === +finalAddressId,
        ),
          existingData = (null == existingEntry ? void 0 : existingEntry.data) || {};

        customerCustomData.addresses = customerCustomData.addresses.filter(
          (address) => address.id !== +finalAddressId,
        );
        customerCustomData.addresses.push({
          id: +finalAddressId,
          data: { ...existingData, ...customData },
        });

        const updateCustomDataResponse = await fetch(
          "/apps/ba-customerfields/update-customer",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              defaultAddress: {},
              metafields: [
                {
                  namespace: "itfor",
                  key: "address_custom_data",
                  value: JSON.stringify(customerCustomData),
                },
              ],
            }),
          }
        );

        if (updateCustomDataResponse.ok) {
          if (window.BINDEC_SHIPPING_SETTINGS) {
            window.BINDEC_SHIPPING_SETTINGS.addressCustomData = customerCustomData;

            if (Array.isArray(window.BINDEC_SHIPPING_SETTINGS.addresses)) {
              const addrIndex = window.BINDEC_SHIPPING_SETTINGS.addresses.findIndex(
                (a) => String(a.id) === String(finalAddressId)
              );
              if (addrIndex !== -1) {
                window.BINDEC_SHIPPING_SETTINGS.addresses[addrIndex] = {
                  ...window.BINDEC_SHIPPING_SETTINGS.addresses[addrIndex],
                  id: +finalAddressId,
                  first_name: newAddress.first_name,
                  last_name: newAddress.last_name,
                  company: newAddress.company || "",
                  address1: newAddress.address1,
                  address2: newAddress.address2 || "",
                  city: newAddress.city,
                  province: newAddress.province,
                  zip: newAddress.zip,
                  phone: newAddress.phone
                };
              } else {
                window.BINDEC_SHIPPING_SETTINGS.addresses.push({
                  id: +finalAddressId,
                  first_name: newAddress.first_name,
                  last_name: newAddress.last_name,
                  company: newAddress.company || "",
                  address1: newAddress.address1,
                  address2: newAddress.address2 || "",
                  city: newAddress.city,
                  province: newAddress.province,
                  zip: newAddress.zip,
                  phone: newAddress.phone,
                  country: "JP"
                });
              }
            }
          }

          await this._syncAddresses();

          if (this.config.onSelect) {
            const updatedAddressForSelect = {
              id: +finalAddressId,
              first_name: newAddress.first_name,
              last_name: newAddress.last_name,
              first_name_kana: newAddress.first_name_kana,
              last_name_kana: newAddress.last_name_kana,
              honorific: newAddress.honorific,
              company: newAddress.company,
              department: newAddress.department,
              email: newAddress.email,
              zip: newAddress.zip,
              province: newAddress.province,
              city: newAddress.city,
              address1: newAddress.address1,
              address2: newAddress.address2,
              phone: newAddress.phone,
              phone_1: newAddress.phone_1,
              phone_2: newAddress.phone_2,
              phone_3: newAddress.phone_3
            };

            this.config.onSelect(updatedAddressForSelect);
            this.clearForm();
            this.close();
          }
        } else {
          alert("追加情報の保存に失敗しました。");
        }
      } catch (error) {
        console.error(error);
        alert("予期せぬエラーが発生しました。");
      } finally {
        // 処理完了後（成功・エラー・途中のreturnに関わらず）活性化
        this._isProcessing = false;

        if (this.confirmButton) {
          this.confirmButton.disabled = false;
          this.confirmButton.textContent = "決定する";
        }
        if (this.submitButton) {
          this.submitButton.textContent = "決定する";
          this.submitButton.disabled = false;
        }
      }
    }

    async _syncAddresses() {
      var _a;
      return await fetch(
        `/cart?section=${(null == (_a = document.getElementById("main-cart-items")) ? void 0 : _a.dataset.id) || ""}`,
      )
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html",
          ),
            selector = "#multiShippingData",
            targetElement = document.querySelector(selector);
          if (!targetElement) return null;
          {
            const sourceElement = html.querySelector(selector);
            targetElement.innerHTML =
              (null == sourceElement ? void 0 : sourceElement.innerHTML) || "";
            const scripts = undefined;
            targetElement.querySelectorAll("script").forEach((oldScript) => {
              var _a2;
              const newScript = document.createElement("script");
              (Array.from(oldScript.attributes).forEach((attr) => {
                newScript.setAttribute(attr.name, attr.value);
              }),
                (newScript.textContent = oldScript.textContent),
                null == (_a2 = oldScript.parentNode) ||
                _a2.replaceChild(newScript, oldScript));
            });
          }
        })
        .catch((e) => null);
    }
    async _getAllAddressIds() {
      try {
        const cacheBuster = `_=${Date.now()}`,
          firstPageResponse = await fetch(
            `/account/addresses?section=wl-new-added-address&page=1&${cacheBuster}`,
            { cache: "no-store" },
          ),
          firstPageText = await firstPageResponse.text(),
          firstPageHtml = new DOMParser().parseFromString(
            firstPageText,
            "text/html",
          ),
          totalPagesEl = firstPageHtml.querySelector("#totalPages"),
          totalPages = totalPagesEl
            ? parseInt(totalPagesEl.textContent || "1", 10)
            : 1,
          firstPageIds = this._parseAddressIds(firstPageHtml);
        if (totalPages <= 1) return firstPageIds;
        const remainingPages = [];
        for (let page = 2; page <= totalPages; page++)
          remainingPages.push(
            fetch(
              `/account/addresses?section=wl-new-added-address&page=${page}&${cacheBuster}`,
              { cache: "no-store" },
            )
              .then((res) => res.text())
              .then((text) => {
                const html = new DOMParser().parseFromString(text, "text/html");
                return this._parseAddressIds(html);
              }),
          );
        const remainingResults = await Promise.all(remainingPages);
        return firstPageIds.concat(...remainingResults);
      } catch (e) {
        return [];
      }
    }
    async _detectNewAddressId(existingIds, maxRetries = 5, baseDelay = 300) {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        attempt > 0 &&
          (await new Promise((r) =>
            setTimeout(r, baseDelay * Math.pow(2, attempt - 1)),
          ));
        const updatedIds = undefined,
          newIds = (await this._getAllAddressIds()).filter(
            (id) => !existingIds.includes(id),
          );
        if (newIds.length > 0) return newIds[0];
      }
      return null;
    }
    _parseAddressIds(html) {
      var _a;
      const el = html.querySelector("#allAddressIds");
      return el && (null == (_a = el.textContent) ? void 0 : _a.trim())
        ? el.textContent
          .split(",")
          .map((id) => +id)
          .filter(Boolean)
        : [];
    }
    removeEventListeners() {
      var _a, _b;
      (null == (_a = this.tabItems) ||
        _a.forEach((item) => {
          const handler = item._handleTabClick;
          handler && item.removeEventListener("click", handler);
        }),
        this.searchInput &&
        this.searchInput._handleSearchInput &&
        this.searchInput.removeEventListener(
          "input",
          this.searchInput._handleSearchInput,
        ),
        this.cancelButton &&
        this.cancelButton._handleCancel &&
        this.cancelButton.removeEventListener(
          "click",
          this.cancelButton._handleCancel,
        ),
        this.confirmButton &&
        this.confirmButton._handleConfirm &&
        this.confirmButton.removeEventListener(
          "click",
          this.confirmButton._handleConfirm,
        ));
      const postalCodeLookupBtn =
        null == (_b = this.modal)
          ? void 0
          : _b.querySelector("#postal-code-lookup-btn");
      (postalCodeLookupBtn &&
        postalCodeLookupBtn._handlePostalCodeLookup &&
        postalCodeLookupBtn.removeEventListener(
          "click",
          postalCodeLookupBtn._handlePostalCodeLookup,
        ),
        this.modal &&
        this.modal._handleModalEscapeKey &&
        this.modal.removeEventListener(
          "cancel",
          this.modal._handleModalEscapeKey,
        ),
        this.modal &&
        this.modal._handleModalClose &&
        this.modal.removeEventListener(
          "close",
          this.modal._handleModalClose,
        ));
    }
  }
  customElements.define("address-modal", AddressModal);
}
if (!customElements.get("store-modal")) {
  class StoreModal extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "modal"),
        __publicField(this, "searchInput"),
        __publicField(this, "storeList"),
        __publicField(this, "cancelButton"),
        __publicField(this, "confirmButton"),
        __publicField(this, "config"),
        __publicField(this, "allStores", []),
        __publicField(this, "filteredStores", []),
        __publicField(this, "selectedStore", null),
        __publicField(this, "selectedRadio", null));
    }
    connectedCallback() {
      this.render();
    }
    disconnectedCallback() {
      this.removeEventListeners();
    }
    open(config) {
      ((this.config = config),
        (this.allStores = config.stores),
        (this.filteredStores = [...this.allStores]),
        (this.selectedStore = null),
        (this.selectedRadio = null));
      const titleElement = this.modal.querySelector(".modal-title");
      if (
        (titleElement && (titleElement.textContent = config.title),
          (this.searchInput.value = ""),
          config.selectedStoreId)
      ) {
        const store = this.allStores.find(
          (s) => s.pickup_location_id === config.selectedStoreId,
        );
        store && (this.selectedStore = store);
      }
      if (
        (this.selectedStore ||
          1 !== this.allStores.length ||
          (this.selectedStore = this.allStores[0]),
          this.renderStoreList(),
          this.selectedStore)
      ) {
        const radio = this.modal.querySelector(
          `input[type="radio"][value="${this.selectedStore.pickup_location_id}"]`,
        );
        radio && ((radio.checked = !0), (this.selectedRadio = radio));
      }
      (this.attachEventListeners(),
        this.modal.showModal(),
        requestAnimationFrame(() => {
          document.activeElement === this.searchInput &&
            this.searchInput.blur();
        }));
    }
    close() {
      (this.modal.close(),
        (this.searchInput.value = ""),
        (this.filteredStores = [...this.allStores]),
        this.removeEventListeners());
    }
    render() {
      ((this.innerHTML =
        '\n        <dialog class="address-modal">\n          <div class="address-modal-content">\n            <div class="address-modal-header">\n              <h2 class="modal-title">店舗選択</h2>\n            </div>\n\n            <div class="address-modal-body">\n              <div class="address-search-container">\n                <input\n                  type="text"\n                  class="address-search-input"\n                  placeholder="店舗名・店舗コードで検索"\n                />\n              </div>\n\n              <div class="address-list"></div>\n            </div>\n\n            <div class="address-modal-footer">\n              <button type="button" class="modal-cancel-btn">キャンセル</button>\n              <button type="button" class="modal-confirm-btn">決定する</button>\n            </div>\n          </div>\n        </dialog>\n      '),
        (this.modal = this.querySelector("dialog")),
        (this.searchInput = this.querySelector(".address-search-input")),
        (this.storeList = this.querySelector(".address-list")),
        (this.cancelButton = this.querySelector(".modal-cancel-btn")),
        (this.confirmButton = this.querySelector(".modal-confirm-btn")));
    }
    renderStoreList() {
      this.storeList &&
        (0 !== this.filteredStores.length
          ? ((this.storeList.innerHTML = this.filteredStores
            .map((store, index) => {
              var _a;
              const isSelected =
                (null == (_a = this.selectedStore)
                  ? void 0
                  : _a.pickup_location_id) === store.pickup_location_id,
                storeName = store.pickup_location_name,
                storeCode = store.pickup_location_id;
              return `\n          <div class="address-item">\n            <label class="address-item-radio">\n              <input\n                type="radio"\n                name="store"\n                value="${store.pickup_location_id}"\n                ${isSelected ? "checked" : ""}\n                data-store-index="${index}"\n              />\n              <div class="address-item-content">\n                <div class="address-item-header">\n                  <span class="address-item-name">${storeName}</span>\n                </div>\n                <div class="address-item-details">\n                  <div class="address-item-address">店舗コード: ${storeCode}</div>\n                </div>\n              </div>\n            </label>\n          </div>\n        `;
            })
            .join("")),
            this.attachRadioListeners())
          : (this.storeList.innerHTML =
            '\n          <div class="address-list-empty">\n            <p>該当する店舗が見つかりません</p>\n          </div>\n        '));
    }
    filterStores(searchTerm) {
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        this.filteredStores = this.allStores.filter((store) => {
          const storeName = (store.pickup_location_name || "").toLowerCase(),
            storeId = (store.pickup_location_id || "").toLowerCase();
          return storeName.includes(term) || storeId.includes(term);
        });
      } else this.filteredStores = [...this.allStores];
      this.renderStoreList();
    }
    attachEventListeners() {
      var _a, _b, _c, _d;
      this.removeEventListeners();
      const handleSearchInput = (e) => {
        const target = e.target;
        this.filterStores(target.value);
      };
      (null == (_a = this.searchInput) ||
        _a.addEventListener("input", handleSearchInput),
        (this.searchInput._handleSearchInput = handleSearchInput));
      const handleCancel = () => {
        this.close();
      };
      (null == (_b = this.cancelButton) ||
        _b.addEventListener("click", handleCancel),
        (this.cancelButton._handleCancel = handleCancel));
      const handleConfirm = () => {
        this.selectedStore && this.config.onSelect
          ? (this.config.onSelect(this.selectedStore), this.close())
          : alert("店舗を選択してください");
      };
      (null == (_c = this.confirmButton) ||
        _c.addEventListener("click", handleConfirm),
        (this.confirmButton._handleConfirm = handleConfirm));
      const handleModalClick = (e) => {
        e.target === this.modal && this.close();
      };
      (null == (_d = this.modal) ||
        _d.addEventListener("click", handleModalClick),
        (this.modal._handleModalClick = handleModalClick));
    }
    attachRadioListeners() {
      const radios = undefined;
      this.storeList
        .querySelectorAll('input[type="radio"]')
        .forEach((radio) => {
          radio.addEventListener("change", (e) => {
            const target = e.target;
            if (target.checked) {
              this.selectedRadio = target;
              const storeIndex = parseInt(
                target.getAttribute("data-store-index") || "0",
              );
              this.selectedStore = this.filteredStores[storeIndex];
            }
          });
        });
    }
    removeEventListeners() {
      (this.searchInput &&
        this.searchInput._handleSearchInput &&
        this.searchInput.removeEventListener(
          "input",
          this.searchInput._handleSearchInput,
        ),
        this.cancelButton &&
        this.cancelButton._handleCancel &&
        this.cancelButton.removeEventListener(
          "click",
          this.cancelButton._handleCancel,
        ),
        this.confirmButton &&
        this.confirmButton._handleConfirm &&
        this.confirmButton.removeEventListener(
          "click",
          this.confirmButton._handleConfirm,
        ),
        this.modal &&
        this.modal._handleModalClick &&
        this.modal.removeEventListener(
          "click",
          this.modal._handleModalClick,
        ));
    }
  }
  customElements.define("store-modal", StoreModal);
}
function parseLegacyDate(dateString, year) {
  const parts = dateString.split("-");
  if (2 !== parts.length) return null;
  const month = parseInt(parts[0], 10) - 1,
    day = parseInt(parts[1], 10);
  return isNaN(month) || isNaN(day) ? null : new Date(year, month, day);
}
function formatDateValue(date) {
  const year = undefined,
    month = undefined,
    day = undefined;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function isHoliday(date, settings) {
  const dayOfWeek = date.getDay();
  if (settings.regularHolidays && settings.regularHolidays.includes(dayOfWeek))
    return !0;
  const month = date.getMonth() + 1,
    day = date.getDate();
  if (
    settings.annualHolidays &&
    settings.annualHolidays.some((h) => h.month === month && h.day === day)
  )
    return !0;
  const dateStr = formatDateValue(date);
  return !(
    !settings.specificHolidays ||
    !settings.specificHolidays.some((h) => {
      const inRange = undefined;
      return dateStr >= h.startDate && (!h.endDate || dateStr <= h.endDate);
    })
  );
}
function addBusinessDays(date, businessDays, settings) {
  let currentDate = new Date(date),
    daysAdded = 0;
  for (; daysAdded < businessDays;)
    (currentDate.setDate(currentDate.getDate() + 1),
      isHoliday(currentDate, settings) || daysAdded++);
  return currentDate;
}
function validateDeliveryDate(input) {
  const {
    selectedDate: selectedDate,
    isDeliveryDateAllowed: isDeliveryDateAllowed,
    datetimeStart: datetimeStart,
    datetimeEnd: datetimeEnd,
    legacyStartDate: legacyStartDate,
    legacyEndDate: legacyEndDate,
    earliestDeliveryOffsetDays: earliestDeliveryOffsetDays,
    latestDeliveryOffsetDays: latestDeliveryOffsetDays,
    earliestDeliveryDate: earliestDeliveryDate,
    latestDeliveryDate: latestDeliveryDate,
    cutoffTime: cutoffTime,
    productLeadTime: productLeadTime,
    deliveryLeadTime: deliveryLeadTime,
    scheduleSettings: scheduleSettings,
  } = input;

  if (!selectedDate) return !0;
  if (!isDeliveryDateAllowed) return !0;

  const now = input.now ?? new Date(),
    currentYear = now.getFullYear();

  let today = new Date(now);
  today.setHours(0, 0, 0, 0);

  // 【1】配送締め時刻の判定（日本時間ベース ＆ サーバーフライング検知）
  let isPastCutoffJST = !1;
  let isServerAlreadyCutoff = !1;
  if (cutoffTime) {
    const [cutoffHours, cutoffMinutes] = cutoffTime.split(":").map(Number);
    
    // フロント（日本時間）の締め判定
    const cutoffDateJST = new Date(now);
    cutoffDateJST.setHours(cutoffHours, cutoffMinutes, 0, 0);
    now >= cutoffDateJST && (isPastCutoffJST = !0);

    // サーバー（UTC時計）のフライング発生判定
    const currentUTCHours = now.getUTCHours();
    const currentUTCMinutes = now.getUTCMinutes();
    if (currentUTCHours > cutoffHours || (currentUTCHours === cutoffHours && currentUTCMinutes >= cutoffMinutes)) {
      isServerAlreadyCutoff = !0;
    }
  }

  let startDate, endDate;

  // 【2】まず、選択可能な「最大日（endDate）」を計算（既存ロジックを維持）
  if (!(!datetimeStart && !datetimeEnd)) {
    if (datetimeEnd) {
      endDate = new Date(datetimeEnd);
    } else {
      endDate = new Date(now);
      endDate.setMonth(endDate.getMonth() + 3);
    }
  } else if (legacyStartDate && legacyEndDate) {
    const parsedEndDate = parseLegacyDate(legacyEndDate, currentYear);
    if (parsedEndDate) {
      endDate = parsedEndDate;
    } else {
      endDate = new Date(now);
      endDate.setMonth(endDate.getMonth() + 3);
    }
  } else {
    endDate = new Date(now);
    endDate.setMonth(endDate.getMonth() + 3);
  }

  // 【3】最終的な最小可能日（startDate）の決定（既存の定休日計算関数 addBusinessDays を適用）
  if (earliestDeliveryDate) {
    startDate = new Date(earliestDeliveryDate);
    startDate.setHours(0, 0, 0, 0);
    
    // 日本時間では締め後、かつサーバーがまだフライング繰り上げをしていない「隙間時間」に
    // カート画面を開きっぱなしでボタンを押した場合、フロント側で直接+1営業日を計算する
    if (isPastCutoffJST && !isServerAlreadyCutoff) {
      startDate = scheduleSettings
        ? addBusinessDays(startDate, 1, scheduleSettings)
        : new Date(startDate.setDate(startDate.getDate() + 1));
    }
    
    // 結果の日付がストアの出荷定休日なら、さらに次の営業日へ確実にジャンプさせる
    if (scheduleSettings && isHoliday(startDate, scheduleSettings)) {
      startDate = addBusinessDays(startDate, 1, scheduleSettings);
    }
  } else {
    // APIデータがない場合のフォールバック計算
    let calcStartDate = new Date(now);
    calcStartDate.setHours(0, 0, 0, 0);
    
    if (isPastCutoffJST) {
      calcStartDate = scheduleSettings
        ? addBusinessDays(calcStartDate, 1, scheduleSettings)
        : new Date(calcStartDate.setDate(calcStartDate.getDate() + 1));
    } else if (scheduleSettings && isHoliday(calcStartDate, scheduleSettings)) {
      calcStartDate = addBusinessDays(calcStartDate, 1, scheduleSettings);
    }

    if (!(!datetimeStart && !datetimeEnd) && datetimeStart) {
      const dStart = new Date(datetimeStart);
      dStart.setHours(0, 0, 0, 0);
      if (dStart > calcStartDate) calcStartDate = dStart;
    } else if (legacyStartDate && legacyEndDate) {
      const lStart = parseLegacyDate(legacyStartDate, currentYear);
      if (lStart) {
        lStart.setHours(0, 0, 0, 0);
        if (lStart > calcStartDate) calcStartDate = lStart;
      }
    }

    calcStartDate = productLeadTime > 0 && scheduleSettings
      ? addBusinessDays(calcStartDate, productLeadTime, scheduleSettings)
      : new Date(calcStartDate.setDate(calcStartDate.getDate() + productLeadTime));

    calcStartDate = new Date(calcStartDate.setDate(calcStartDate.getDate() + deliveryLeadTime));

    if (scheduleSettings && isHoliday(calcStartDate, scheduleSettings)) {
      calcStartDate = addBusinessDays(calcStartDate, 1, scheduleSettings);
    }
    startDate = calcStartDate;
  }

  // 過去日選択防止の最低基準日チェック
  let minAllowedDate = new Date(today);
  if (isPastCutoffJST) {
    minAllowedDate = scheduleSettings
      ? addBusinessDays(minAllowedDate, 1, scheduleSettings)
      : new Date(minAllowedDate.setDate(minAllowedDate.getDate() + 1));
  }
  if (startDate < minAllowedDate) {
    startDate = new Date(minAllowedDate);
  }

  // 【4】オフセット日数やAPI最大日の最終調整
  if (void 0 !== latestDeliveryOffsetDays && latestDeliveryOffsetDays > 0) {
    const companyMaxDate = new Date(today);
    companyMaxDate.setDate(companyMaxDate.getDate() + latestDeliveryOffsetDays);
    if (endDate > companyMaxDate) {
      endDate = companyMaxDate;
    }
  }
  if (void 0 !== earliestDeliveryOffsetDays && earliestDeliveryOffsetDays > 0) {
    const companyMinDate = new Date(today);
    companyMinDate.setDate(companyMinDate.getDate() + earliestDeliveryOffsetDays);
    if (companyMinDate > startDate) {
      startDate = companyMinDate;
    }
  }
  if (latestDeliveryDate) {
    const apiMaxDate = new Date(latestDeliveryDate);
    apiMaxDate.setHours(0, 0, 0, 0);
    if (apiMaxDate < endDate) {
      endDate = apiMaxDate;
    }
  }

  // 【5】最終バリデーション判定
  const selected = new Date(selectedDate);
  return (
    selected.setHours(0, 0, 0, 0),
    startDate.setHours(0, 0, 0, 0),
    endDate.setHours(0, 0, 0, 0),
    !(selected < startDate || selected > endDate) &&
    (!scheduleSettings || !isHoliday(selected, scheduleSettings))
  );
}
if (!customElements.get("datetime-selector")) {
  var selectos = {
    modal: "dialog#datetime-selector-modal",
    form: "form#wl-datetime-form",
    select: {
      date: "select[name='delivery-date']",
      time: "select[name='delivery-time']",
      period: "select[name='delivery-period']",
    },
    input: {
      date: "delivery-date",
      time: "delivery-time",
      period: "delivery-period",
    },
    actions: {
      open: ".btn-select-datetime",
      close: "#wl-datetime-actions-cancel",
      submit: "#wl-datetime-actions-submit",
    },
    summary: {
      date: ".js-datetime-summary-date",
      time: ".js-datetime-summary-time",
      period: ".js-datetime-summary-period",
    },
  };
  class DatetimeSelector extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "form"),
        __publicField(this, "shippingUnitId"),
        __publicField(this, "company"),
        __publicField(this, "purposeData"),
        __publicField(this, "eventTitles"),
        __publicField(this, "deliveryGroupId"),
        __publicField(this, "destinationId"),
        __publicField(this, "deliveryPeriods", []),
        __publicField(this, "modal"),
        __publicField(this, "openDatetimeSelectorButton"),
        __publicField(this, "cancelButton"),
        __publicField(this, "submitButton"),
        __publicField(this, "summary"),
        __publicField(this, "__openListener"),
        __publicField(this, "__closeListener"),
        __publicField(this, "__submitListener"),
        __publicField(this, "__purposeChangeListener"),
        (this.shippingUnitId =
          this.getAttribute("data-shipping-unit-id") || ""),
        (this.deliveryGroupId = this.getAttribute("data-group-id") || ""),
        (this.destinationId = this.getAttribute("data-destination-id") || ""),
        (this.modal = document.querySelector(selectos.modal)),
        (this.form = this.modal.querySelector(selectos.form)),
        (this.openDatetimeSelectorButton = this.querySelector(
          selectos.actions.open,
        )),
        (this.cancelButton = this.modal.querySelector(selectos.actions.close)),
        (this.submitButton = this.modal.querySelector(selectos.actions.submit)),
        (this.summary = {
          date: this.querySelector(selectos.summary.date),
          time: this.querySelector(selectos.summary.time),
          period: this.querySelector(selectos.summary.period),
        }),
        this._createNotificationModal());
    }
    _createNotificationModal() {
      let notificationModal;
      if (!document.querySelector("#datetime-notification-modal")) {
        const modalHTML =
          '\n          <style>\n            .datetime-notification-modal {\n              border: none;\n              border-radius: 8px;\n              padding: 0;\n              max-width: 400px;\n              width: 90%;\n              box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);\n            }\n\n            .datetime-notification-modal::backdrop {\n              background-color: rgba(0, 0, 0, 0.5);\n            }\n\n            .datetime-notification-content {\n              display: flex;\n              flex-direction: column;\n            }\n\n            .datetime-notification-header {\n              padding: 24px 24px 16px;\n              border-bottom: 1px solid #e5e5e5;\n            }\n\n            .datetime-notification-title {\n              margin: 0;\n              font-weight: 700;\n              font-size: 18px;\n              line-height: 1.5em;\n              color: #3b3834;\n              text-align: center;\n            }\n\n            .datetime-notification-body {\n              padding: 24px;\n              text-align: center;\n            }\n\n            .datetime-notification-message {\n              margin: 0;\n              font-weight: 500;\n              font-size: 14px;\n              line-height: 1.5em;\n              color: #3b3834;\n            }\n\n            .datetime-notification-footer {\n              padding: 16px 24px 24px;\n              display: flex;\n              justify-content: center;\n            }\n\n            .datetime-notification-close-btn {\n              min-width: 120px;\n              padding: 12px 24px;\n              border-radius: 100px;\n              font-weight: 700;\n              font-size: 14px;\n              cursor: pointer;\n              transition: all 0.3s;\n              background-color: #3b3834;\n              border: none;\n              color: #fff;\n            }\n\n            .datetime-notification-close-btn:hover {\n              background-color: #2a2722;\n            }\n          </style>\n          <dialog id="datetime-notification-modal" class="datetime-notification-modal">\n            <div class="datetime-notification-content">\n              <div class="datetime-notification-header">\n                <h2 class="datetime-notification-title">お知らせ</h2>\n              </div>\n              <div class="datetime-notification-body">\n                <p class="datetime-notification-message">お届け先を設定してください</p>\n              </div>\n              <div class="datetime-notification-footer">\n                <button type="button" class="datetime-notification-close-btn">閉じる</button>\n              </div>\n            </div>\n          </dialog>\n        ';
        document.body.insertAdjacentHTML("beforeend", modalHTML);
        const modal = document.querySelector("#datetime-notification-modal"),
          closeBtn =
            null == modal
              ? void 0
              : modal.querySelector(".datetime-notification-close-btn");
        closeBtn &&
          closeBtn.addEventListener("click", () => {
            modal.close();
          });
      }
    }
    _showNotificationModal(message) {
      const modal = document.querySelector("#datetime-notification-modal"),
        messageElement =
          null == modal
            ? void 0
            : modal.querySelector(".datetime-notification-message");
      modal &&
        messageElement &&
        ((messageElement.textContent = message), modal.showModal());
    }
    connectedCallback() {
      const purposeData = this._getPurposeData();
      purposeData && (this.purposeData = purposeData);
      const company = this._getCompany();
      (company && (this.company = company), this._togglePeriodTab());
      const displayPattern = undefined;
      4 !== this._determineDisplayPattern()
        ? (this._loadSavedDatetime(),
          this.addEventListener("delivery-time-updated", () => {
            this.modal.open &&
              (this._generateTimeOptions(), this._generatePeriodOptions());
          }),
          (this.__purposeChangeListener = (event) => {
            var _a;
            const purposeData2 = this._getPurposeData();
            (purposeData2 && (this.purposeData = purposeData2),
              this._togglePeriodTab());
            const displayPattern2 = this._determineDisplayPattern();
            if (4 === displayPattern2) {
              this.style.display = "none";
              const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`;
              (null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
                ? void 0
                : _a[shippingUnitKey]) &&
                ((window.BINDEC_SHIPPING_UNIT_SETTINGS[
                  shippingUnitKey
                ].deliveryDate = ""),
                  (window.BINDEC_SHIPPING_UNIT_SETTINGS[
                    shippingUnitKey
                  ].deliveryTime = ""),
                  (window.BINDEC_SHIPPING_UNIT_SETTINGS[
                    shippingUnitKey
                  ].deliveryPeriod = ""),
                  (window.BINDEC_SHIPPING_UNIT_SETTINGS[
                    shippingUnitKey
                  ].shippingStartDate = void 0));
            } else
              ((this.style.display = ""),
                this._applyDisplayPattern(displayPattern2));
            (this._generateDateOptios(),
              this._generateTimeOptions(),
              this._generatePeriodOptions(),
              this._loadSavedDatetime());
          }),
          document.addEventListener(
            "purpose-selected",
            this.__purposeChangeListener,
          ),
          (this.__openListener = this.open.bind(this)),
          this.openDatetimeSelectorButton.addEventListener(
            "click",
            this.__openListener,
          ))
        : (this.style.display = "none");
    }
    disconnectedCallback() {
      (this.openDatetimeSelectorButton.removeEventListener(
        "click",
        this.__openListener,
      ),
        this.__purposeChangeListener &&
        document.removeEventListener(
          "purpose-selected",
          this.__purposeChangeListener,
        ),
        this.__closeListener &&
        this.cancelButton.removeEventListener("click", this.__closeListener),
        this.__submitListener &&
        this.submitButton.removeEventListener(
          "click",
          this.__submitListener,
        ));
    }
    _getPurposeData() {
      var _a, _b;
      const purposeSelect = document.querySelector("input[name='purpose']:checked");
      let currentPurpose =
        (null == purposeSelect ? void 0 : purposeSelect.value) ||
        (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
          ? void 0
          : _a.currentPurpose);
      if (!currentPurpose) {
        const multiShippingSettings = document.querySelector(
          "multi-shipping-settings",
        );
        if (!multiShippingSettings) return;
        const settings = undefined,
          isHomeFlow = undefined;
        "home" === multiShippingSettings.getSettings().orderType &&
          (currentPurpose = "自宅用");
      }
      if (!currentPurpose) return;
      if (!window.BINDEC_SHIPPING_SETTINGS) return;
      if (!window.BINDEC_SHIPPING_SETTINGS.cartItemsWithPurposes) return;
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_b = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _b[shippingUnitKey];
      if (
        !shippingUnit ||
        !shippingUnit.items ||
        0 === shippingUnit.items.length
      )
        return;
      const productId = String(shippingUnit.items[0].product_id),
        cartItem = window.BINDEC_SHIPPING_SETTINGS.cartItemsWithPurposes.find(
          (item) => String(item.product_id) === productId,
        );
      if (!cartItem || !cartItem.purposes || 0 === cartItem.purposes.length)
        return;
      const allActivePurposes = cartItem.purposes.filter((purpose) =>
        void 0 !== purpose.is_published
          ? purpose.is_published
          : purpose._isActive,
      ),
        selectedPurposeData = allActivePurposes.find(
          (purpose) => (purpose.name || purpose._title) === currentPurpose,
        );
      if (!selectedPurposeData) return;
      const unionDateRange = this._calculateUnionDateRange(allActivePurposes);
      return {
        ...selectedPurposeData,
        _start_date:
          unionDateRange.startDate || selectedPurposeData._start_date,
        _end_date: unionDateRange.endDate || selectedPurposeData._end_date,
      };
    }
    _calculateUnionDateRange(purposes) {
      let minStartDate = null,
        maxEndDate = null;
      for (const purpose of purposes) {
        const startDate =
          purpose.desired_datetime_start_at ||
          purpose._datetime_selection_start ||
          purpose._start_date;
        startDate &&
          (!minStartDate || startDate < minStartDate) &&
          (minStartDate = startDate);
        const endDate =
          purpose.desired_datetime_end_at ||
          purpose._datetime_selection_end ||
          purpose._end_date;
        endDate &&
          (!maxEndDate || endDate > maxEndDate) &&
          (maxEndDate = endDate);
      }
      return { startDate: minStartDate, endDate: maxEndDate };
    }
    _calculateUnionDeliveryPeriods(purposes) {
      var _a, _b;
      const allPeriods = [],
        seenTitles = new Set();
      for (const purpose of purposes) {
        const deliveryPeriods =
          purpose.desired_periods || purpose._deliveryperiod;
        if (!deliveryPeriods || 0 === deliveryPeriods.length) continue;
        const firstPeriod = undefined;
        if ("string" == typeof deliveryPeriods[0]) {
          const purposeMasterData =
            null ==
              (_b =
                null == (_a = window.BINDEC_SHIPPING_SETTINGS)
                  ? void 0
                  : _a.purposeMaster)
              ? void 0
              : _b.find(
                (p) =>
                  (p.name || p._title) === (purpose.name || purpose._title),
              ),
            masterPeriods =
              (null == purposeMasterData
                ? void 0
                : purposeMasterData.desired_periods) ||
              (null == purposeMasterData
                ? void 0
                : purposeMasterData._deliveryperiod);
          if (masterPeriods)
            for (const period of masterPeriods) {
              const title =
                period.period_name || period._title || period._category;
              title &&
                !seenTitles.has(title) &&
                (allPeriods.push(period), seenTitles.add(title));
            }
        } else
          for (const period of deliveryPeriods) {
            const title =
              period.period_name || period._title || period._category;
            title &&
              !seenTitles.has(title) &&
              (allPeriods.push(period), seenTitles.add(title));
          }
      }
      return allPeriods.sort(
        (a, b) =>
          (a.display_order ?? a._order ?? 0) -
          (b.display_order ?? b._order ?? 0),
      );
    }
    _getCompany() {
      const company = this.getAttribute("data-company");
      return company ? JSON.parse(company) : null;
    }
    _togglePeriodTab() {
      var _a, _b;
      const deliveryPeriods =
        (null == (_a = this.purposeData) ? void 0 : _a.desired_periods) ||
        (null == (_b = this.purposeData) ? void 0 : _b._deliveryperiod),
        hasDeliveryPeriod = deliveryPeriods && deliveryPeriods.length > 0,
        isPeriodAllowed = this._isDeliveryPeriodAllowed(),
        periodTabButton = this.modal.querySelector(
          '[data-tab="select-period"]',
        );
      periodTabButton &&
        (periodTabButton.disabled = !hasDeliveryPeriod || !isPeriodAllowed);
    }
    _generateDateOptios() {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
      const dateSelect = this.modal.querySelector(selectos.select.date);
      if (!dateSelect) return;
      for (; dateSelect.options.length > 1;) dateSelect.remove(1);
      if (!this._isDeliveryDateAllowed()) {
        const option = document.createElement("option");
        return (
          (option.value = ""),
          (option.textContent = "この商品は配送日指定ができません"),
          (option.disabled = !0),
          void dateSelect.appendChild(option)
        );
      }
      const currentYear = new Date().getFullYear();
      let startDate, endDate;
      const datetimeStart =
        (null == (_a = this.purposeData)
          ? void 0
          : _a.desired_datetime_start_at) ||
        (null == (_b = this.purposeData)
          ? void 0
          : _b._datetime_selection_start),
        datetimeEnd =
          (null == (_c = this.purposeData)
            ? void 0
            : _c.desired_datetime_end_at) ||
          (null == (_d = this.purposeData)
            ? void 0
            : _d._datetime_selection_end),
        hasDatetimeFields = undefined;
      if (datetimeStart || datetimeEnd)
        (datetimeStart
          ? (startDate = new Date(datetimeStart))
          : ((startDate = new Date()), startDate.setHours(0, 0, 0, 0)),
          datetimeEnd
            ? (endDate = new Date(datetimeEnd))
            : ((endDate = new Date()),
              endDate.setMonth(endDate.getMonth() + 3)));
      else if (
        (null == (_e = this.purposeData) ? void 0 : _e._start_date) &&
        (null == (_f = this.purposeData) ? void 0 : _f._end_date)
      ) {
        const parsedStartDate = this._parseDate(
          this.purposeData._start_date,
          currentYear,
        ),
          parsedEndDate = this._parseDate(
            this.purposeData._end_date,
            currentYear,
          );
        parsedStartDate && parsedEndDate
          ? ((startDate = parsedStartDate), (endDate = parsedEndDate))
          : ((startDate = new Date()),
            startDate.setHours(0, 0, 0, 0),
            (endDate = new Date()),
            endDate.setMonth(endDate.getMonth() + 3));
      } else
        ((startDate = new Date()),
          startDate.setHours(0, 0, 0, 0),
          (endDate = new Date()),
          endDate.setMonth(endDate.getMonth() + 3));
      const today = new Date();
      (today.setHours(0, 0, 0, 0),
        startDate < today && (startDate = new Date(today)));
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_g = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _g[shippingUnitKey],
        scheduleSettings = this._getDeliveryScheduleSettings(),
        latestDeliveryOffsetDays =
          null ==
            (_h = null == shippingUnit ? void 0 : shippingUnit.shippingData)
            ? void 0
            : _h.latest_delivery_offset_days;
      if (void 0 !== latestDeliveryOffsetDays && latestDeliveryOffsetDays > 0) {
        const today2 = new Date();
        today2.setHours(0, 0, 0, 0);
        const companyMaxDate = new Date(today2);
        (companyMaxDate.setDate(
          companyMaxDate.getDate() + latestDeliveryOffsetDays,
        ),
          endDate > companyMaxDate && (endDate = companyMaxDate));
      }
      // 【1】日本時間（JST）と世界標準時（UTC）の双方の視点から、正確に締め切り状態を判定
      const cutoffTime = null == (_i = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _i.cutoffTime;
      let isPastCutoffJST = !1;         // 日本時間で15:00を過ぎているか
      let isServerAlreadyCutoff = !1;   // サーバー（UTC）側でもすでに15:00を過ぎているか
      const now = new Date();

      if (cutoffTime) {
        const [cutoffHours, cutoffMinutes] = cutoffTime.split(":").map(Number);
        
        // 日本時間での締め切り判定
        const cutoffDateJST = new Date();
        cutoffDateJST.setHours(cutoffHours, cutoffMinutes, 0, 0);
        now >= cutoffDateJST && (isPastCutoffJST = !0);

        // サーバー（UTC時計）側での締め切り判定（UTC時間での文字通り比較バグをフロントで完全シミュレート）
        const currentUTCHours = now.getUTCHours();
        const currentUTCMinutes = now.getUTCMinutes();
        if (currentUTCHours > cutoffHours || (currentUTCHours === cutoffHours && currentUTCMinutes >= cutoffMinutes)) {
          isServerAlreadyCutoff = !0;
        }
      }

      // 【2】最終的な選択可能最小日（startDate）の算出（定休日考慮型メソッドを適用）
      const earliestDeliveryDate = null == shippingUnit ? void 0 : shippingUnit.earliestDeliveryDate;
      if (earliestDeliveryDate) {
        startDate = new Date(earliestDeliveryDate);
        startDate.setHours(0, 0, 0, 0);
        
        // ★コアロジック：日本時間では締め切り後、かつサーバーが時差のせいでまだ日付を繰り上げていない「隙間時間」の場合のみ、
        // 単純な+1日ではなく、元々あったクラス内の定休日計算メソッドを使って「1営業日」後ろに進める
        if (isPastCutoffJST && !isServerAlreadyCutoff) {
          startDate = scheduleSettings 
            ? this._addBusinessDays(startDate, 1, scheduleSettings) 
            : new Date(startDate.setDate(startDate.getDate() + 1));
        }
        
        // 算出された着地予定日がストアの定休日・祝日なら、さらに次の営業日まで安全にジャンプさせる
        if (scheduleSettings && this._isHoliday(startDate, scheduleSettings)) {
          startDate = this._addBusinessDays(startDate, 1, scheduleSettings);
        }
      } else {
        // APIから日付が返ってこないフォールバックルート（既存のリードタイムロジックをJST締め判定に統合して維持）
        let calcStartDate = new Date(now);
        calcStartDate.setHours(0, 0, 0, 0);
        if (isPastCutoffJST) {
          calcStartDate = scheduleSettings 
            ? this._addBusinessDays(calcStartDate, 1, scheduleSettings) 
            : new Date(calcStartDate.setDate(calcStartDate.getDate() + 1));
        } else if (scheduleSettings && this._isHoliday(calcStartDate, scheduleSettings)) {
          calcStartDate = this._addBusinessDays(calcStartDate, 1, scheduleSettings);
        }

        const productLeadTime = this._getProductLeadTime();
        calcStartDate = productLeadTime > 0 && scheduleSettings
          ? this._addBusinessDays(calcStartDate, productLeadTime, scheduleSettings)
          : new Date(calcStartDate.setDate(calcStartDate.getDate() + productLeadTime));

        const deliveryLeadTime = this._getDeliveryLeadTime();
        calcStartDate = new Date(calcStartDate.setDate(calcStartDate.getDate() + deliveryLeadTime));
        
        if (scheduleSettings && this._isHoliday(calcStartDate, scheduleSettings)) {
          calcStartDate = this._addBusinessDays(calcStartDate, 1, scheduleSettings);
        }
        startDate = calcStartDate;
      }
      const earliestDeliveryOffsetDays =
        null == (_j = null == shippingUnit ? void 0 : shippingUnit.shippingData)
          ? void 0
          : _j.earliest_delivery_offset_days;
      if (
        void 0 !== earliestDeliveryOffsetDays &&
        earliestDeliveryOffsetDays > 0
      ) {
        const today2 = new Date();
        today2.setHours(0, 0, 0, 0);
        const companyMinDate = new Date(today2);
        (companyMinDate.setDate(
          companyMinDate.getDate() + earliestDeliveryOffsetDays,
        ),
          companyMinDate > startDate && (startDate = companyMinDate));
      }
      const latestDeliveryDate =
        null == shippingUnit ? void 0 : shippingUnit.latestDeliveryDate;
      if (latestDeliveryDate) {
        const apiMaxDate = new Date(latestDeliveryDate);
        (apiMaxDate.setHours(0, 0, 0, 0),
          apiMaxDate < endDate && (endDate = apiMaxDate));
      }
      if (startDate > endDate) {
        const option = document.createElement("option");
        return (
          (option.value = ""),
          (option.textContent = "この期間では配送日を選択できません"),
          (option.disabled = !0),
          void dateSelect.appendChild(option)
        );
      }
      const currentDate = new Date(startDate);
      let optionsGenerated = 0;
      for (; currentDate <= endDate;) {
        const isHoliday2 = undefined;
        if (
          !(
            !!scheduleSettings && this._isHoliday(currentDate, scheduleSettings)
          )
        ) {
          const option = document.createElement("option");
          ((option.value = this._formatDateValue(currentDate)),
            (option.textContent = this._formatDateJapanese(currentDate)),
            dateSelect.appendChild(option),
            optionsGenerated++);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      if (0 === optionsGenerated) {
        const option = document.createElement("option");
        ((option.value = ""),
          (option.textContent = "この期間は休業日のため配送日を選択できません"),
          (option.disabled = !0),
          dateSelect.appendChild(option));
      }
    }
    _parseDate(dateString, year) {
      const parts = dateString.split("-");
      if (2 !== parts.length) return null;
      const month = parseInt(parts[0], 10) - 1,
        day = parseInt(parts[1], 10);
      return isNaN(month) || isNaN(day) ? null : new Date(year, month, day);
    }
    _formatDateValue(date) {
      const year = undefined,
        month = undefined,
        day = undefined;
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    }
    _formatDateJapanese(date) {
      const year = undefined,
        month = undefined,
        day = undefined;
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    }
    validateSelectedDate(selectedDate) {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _a[shippingUnitKey];
      return validateDeliveryDate({
        selectedDate: selectedDate,
        isDeliveryDateAllowed: this._isDeliveryDateAllowed(),
        datetimeStart:
          (null == (_b = this.purposeData)
            ? void 0
            : _b.desired_datetime_start_at) ||
          (null == (_c = this.purposeData)
            ? void 0
            : _c._datetime_selection_start),
        datetimeEnd:
          (null == (_d = this.purposeData)
            ? void 0
            : _d.desired_datetime_end_at) ||
          (null == (_e = this.purposeData)
            ? void 0
            : _e._datetime_selection_end),
        legacyStartDate:
          null == (_f = this.purposeData) ? void 0 : _f._start_date,
        legacyEndDate: null == (_g = this.purposeData) ? void 0 : _g._end_date,
        earliestDeliveryOffsetDays:
          null ==
            (_h = null == shippingUnit ? void 0 : shippingUnit.shippingData)
            ? void 0
            : _h.earliest_delivery_offset_days,
        latestDeliveryOffsetDays:
          null ==
            (_i = null == shippingUnit ? void 0 : shippingUnit.shippingData)
            ? void 0
            : _i.latest_delivery_offset_days,
        earliestDeliveryDate:
          null == shippingUnit ? void 0 : shippingUnit.earliestDeliveryDate,
        latestDeliveryDate:
          null == shippingUnit ? void 0 : shippingUnit.latestDeliveryDate,
        cutoffTime:
          null == (_j = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _j.cutoffTime,
        productLeadTime: this._getProductLeadTime(),
        deliveryLeadTime: this._getDeliveryLeadTime(),
        scheduleSettings: this._getDeliveryScheduleSettings(),
      });
    }
    _generateTimeOptions() {
      var _a, _b;
      const timeSelect = this.modal.querySelector(selectos.select.time);
      if (!timeSelect) return;
      for (; timeSelect.options.length > 1;) timeSelect.remove(1);
      if (!this._isDeliveryTimeAllowed()) {
        const option = document.createElement("option");
        return (
          (option.value = ""),
          (option.textContent = "この商品は配送時間指定ができません"),
          (option.disabled = !0),
          void timeSelect.appendChild(option)
        );
      }
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _a[shippingUnitKey],
        availableDeliveryTime =
          null == shippingUnit ? void 0 : shippingUnit.availableDeliveryTime;
      if (availableDeliveryTime && availableDeliveryTime.length > 0)
        return void availableDeliveryTime.forEach((timeSlot) => {
          const option = document.createElement("option");
          ((option.value = timeSlot.value),
            (option.textContent = timeSlot.label),
            timeSelect.appendChild(option));
        });
      const scheduleSettings = this._getDeliveryScheduleSettings();
      if (
        (null == scheduleSettings
          ? void 0
          : scheduleSettings.deliveryTimeSlots) &&
        scheduleSettings.deliveryTimeSlots.length > 0
      ) {
        const sortedSlots = undefined;
        [...scheduleSettings.deliveryTimeSlots]
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .forEach((slot) => {
            const option = document.createElement("option");
            ((option.value = slot.value),
              (option.textContent = slot.label),
              timeSelect.appendChild(option));
          });
      } else
        (null == (_b = this.company) ? void 0 : _b.delivery_times) &&
          this.company.delivery_times.length > 0 &&
          this.company.delivery_times.forEach((deliveryTime) => {
            const option = document.createElement("option");
            ((option.value = deliveryTime),
              (option.textContent = deliveryTime),
              timeSelect.appendChild(option));
          });
    }
    _generatePeriodOptions() {
      var _a, _b, _c, _d;
      const periodSelect = this.modal.querySelector(selectos.select.period);
      if (!periodSelect) return;
      for (; periodSelect.options.length > 1;) periodSelect.remove(1);
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _a[shippingUnitKey],
        availableDeliveryPeriods =
          null == shippingUnit ? void 0 : shippingUnit.availableDeliveryPeriods;
      if (availableDeliveryPeriods && availableDeliveryPeriods.length > 0)
        return (
          (this.deliveryPeriods = availableDeliveryPeriods),
          void availableDeliveryPeriods.forEach((period) => {
            const option = document.createElement("option"),
              displayText =
                period.period_name || period._title || period._category;
            ((option.value = displayText),
              (option.textContent = displayText),
              periodSelect.appendChild(option));
          })
        );
      const deliveryPeriods =
        (null == (_b = this.purposeData) ? void 0 : _b.desired_periods) ||
        (null == (_c = this.purposeData) ? void 0 : _c._deliveryperiod);
      if (!deliveryPeriods || 0 === deliveryPeriods.length) return;
      const firstPeriod = undefined;
      let periods = [];
      if ("string" == typeof deliveryPeriods[0]) {
        if (
          !(null == (_d = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _d.purposeMaster)
        )
          return;
        const currentPurpose = this.purposeData.name || this.purposeData._title,
          purposeMasterData =
            window.BINDEC_SHIPPING_SETTINGS.purposeMaster.find(
              (p) => (p.name || p._title) === currentPurpose,
            ),
          masterPeriods =
            (null == purposeMasterData
              ? void 0
              : purposeMasterData.desired_periods) ||
            (null == purposeMasterData
              ? void 0
              : purposeMasterData._deliveryperiod);
        if (!masterPeriods || 0 === masterPeriods.length) return;
        periods = masterPeriods;
      } else periods = deliveryPeriods;
      const now = new Date(),
        currentYear = now.getFullYear();
      periods = periods.filter((period) => {
        const hasDatetimeFields = undefined;
        if (
          period._period_datetime_selection_start ||
          period._period_datetime_selection_end
        ) {
          if (period._period_datetime_selection_start) {
            const startDate = new Date(period._period_datetime_selection_start);
            if (now < startDate) return !1;
          }
          if (period._period_datetime_selection_end) {
            const endDate = new Date(period._period_datetime_selection_end);
            if (now > endDate) return !1;
          }
        } else {
          const periodStartDate =
            period.display_start_date || period._start_date;
          if (periodStartDate) {
            const normalizedStartDate = this._normalizeDateString(
              periodStartDate,
              currentYear,
            ),
              startDate = new Date(normalizedStartDate);
            if (now < startDate) return !1;
          }
          const periodEndDate = period.display_end_date || period._end_date;
          if (periodEndDate) {
            const normalizedEndDate = this._normalizeDateString(
              periodEndDate,
              currentYear,
            ),
              endDate = new Date(normalizedEndDate);
            if ((endDate.setHours(23, 59, 59, 999), now > endDate)) return !1;
          }
        }
        return !0;
      });
      const sortedPeriods = [...periods].sort(
        (a, b) =>
          (a.display_order ?? a._order ?? 0) -
          (b.display_order ?? b._order ?? 0),
      );
      if (0 === sortedPeriods.length) {
        const periodFields = undefined;
        return (
          this.modal
            .querySelectorAll('[data-field="period"]')
            .forEach((field) => {
              field.style.display = "none";
            }),
          void (this.deliveryPeriods = [])
        );
      }
      ((this.deliveryPeriods = sortedPeriods),
        sortedPeriods.forEach((period) => {
          const option = document.createElement("option"),
            displayText =
              period.period_name || period._title || period._category;
          ((option.value = displayText),
            (option.textContent = displayText),
            periodSelect.appendChild(option));
        }));
    }
    open() {
      var _a;
      const destinationSelector = this.closest("destination-selector");
      if (destinationSelector) {
        const selectedDestinationData = destinationSelector.querySelector(
          ".js-delivery-address-info",
        );
        if (
          !selectedDestinationData ||
          !selectedDestinationData.textContent ||
          "" === selectedDestinationData.textContent.trim()
        )
          return void this._showNotificationModal("お届け先を設定してください");
      }
      const purposeData = this._getPurposeData();
      purposeData && (this.purposeData = purposeData);
      const company = this._getCompany();
      company && (this.company = company);
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _a[shippingUnitKey];
      (this._togglePeriodTab(),
        this._generateDateOptios(),
        this._generateTimeOptions(),
        this._generatePeriodOptions());
      const displayPattern = this._determineDisplayPattern();
      if (4 === displayPattern)
        return void alert("この商品は配送日時の指定ができません。");
      (this._applyDisplayPattern(displayPattern),
        this.form.setAttribute("data-shipping-unit-id", this.shippingUnitId),
        this.form.setAttribute("data-destination-id", this.destinationId));
      let settingsToRestore = {
        deliveryDate:
          (null == shippingUnit ? void 0 : shippingUnit.deliveryDate) || "",
        deliveryTime:
          (null == shippingUnit ? void 0 : shippingUnit.deliveryTime) || "",
        deliveryPeriod:
          (null == shippingUnit ? void 0 : shippingUnit.deliveryPeriod) || "",
      };
      (settingsToRestore.deliveryPeriod
        ? ((settingsToRestore.deliveryDate = ""),
          (settingsToRestore.deliveryTime = ""))
        : settingsToRestore.deliveryDate &&
        (settingsToRestore.deliveryPeriod = ""),
        this._renderCurrentDatetimeSettings(settingsToRestore),
        1 === displayPattern && this._setupMutualExclusionListeners(),
        (this.__closeListener = this.close.bind(this)),
        (this.__submitListener = this.submit.bind(this)),
        this.cancelButton.addEventListener("click", this.__closeListener),
        this.submitButton.addEventListener("click", this.__submitListener),
        this.modal.showModal());
    }
    _setupMutualExclusionListeners() {
      const periodSelect = this.modal.querySelector(selectos.select.period),
        dateSelect = this.modal.querySelector(selectos.select.date),
        timeSelect = this.modal.querySelector(selectos.select.time);
      if (!periodSelect || !dateSelect || !timeSelect) return;
      const periodChangeHandler = () => {
        const periodValue = periodSelect.value;
        periodValue &&
          "" !== periodValue &&
          ((dateSelect.value = ""), (timeSelect.value = ""));
      },
        dateChangeHandler = () => {
          const dateValue = dateSelect.value;
          dateValue && "" !== dateValue && (periodSelect.value = "");
        },
        timeChangeHandler = () => {
          const timeValue = timeSelect.value;
          timeValue && "" !== timeValue && (periodSelect.value = "");
        };
      (periodSelect.addEventListener("change", periodChangeHandler),
        dateSelect.addEventListener("change", dateChangeHandler),
        timeSelect.addEventListener("change", timeChangeHandler),
        (periodSelect.disabled = !1),
        (dateSelect.disabled = !1),
        (timeSelect.disabled = !1));
    }
    close() {
      (this.__closeListener &&
        this.cancelButton.removeEventListener("click", this.__closeListener),
        this.__submitListener &&
        this.submitButton.removeEventListener("click", this.__submitListener),
        this.modal.close(),
        this.form.reset(),
        this.modal.querySelectorAll(".wl-collapse-group").forEach((group) => {
          group.open = !1;
        }));
    }
    submit(event) {
      var _a, _b, _c;
      event.preventDefault();
      const destinationId = this.form.getAttribute("data-destination-id") || "";
      if (!destinationId || this.destinationId !== destinationId) return;
      null == (_a = this.querySelector(".desired-delivery-date-error")) ||
        _a.remove();
      const formData = new FormData(this.form),
        data = Object.fromEntries(formData.entries()),
        datetimeSettingsData = this._getDatetimeSettingsData(data),
        activeTab = this.modal.querySelector(
          '.wl-datetime-tab[data-active="true"]',
        ),
        activeTabName =
          null == activeTab ? void 0 : activeTab.getAttribute("data-tab");
      ("select-period" === activeTabName
        ? ((datetimeSettingsData.deliveryDate = ""),
          (datetimeSettingsData.deliveryTime = ""))
        : "specify-datetime" === activeTabName &&
        (datetimeSettingsData.deliveryPeriod = ""),
        this._renderDatetimeSummary(datetimeSettingsData));
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`;
      if (
        window.BINDEC_SHIPPING_UNIT_SETTINGS &&
        window.BINDEC_SHIPPING_UNIT_SETTINGS[shippingUnitKey]
      ) {
        if (
          datetimeSettingsData.deliveryPeriod &&
          "" !== datetimeSettingsData.deliveryPeriod
        )
          ((window.BINDEC_SHIPPING_UNIT_SETTINGS[shippingUnitKey].deliveryDate =
            ""),
            (window.BINDEC_SHIPPING_UNIT_SETTINGS[
              shippingUnitKey
            ].deliveryTime = ""),
            (window.BINDEC_SHIPPING_UNIT_SETTINGS[
              shippingUnitKey
            ].deliveryPeriod = datetimeSettingsData.deliveryPeriod));
        else {
          ((window.BINDEC_SHIPPING_UNIT_SETTINGS[shippingUnitKey].deliveryDate =
            datetimeSettingsData.deliveryDate),
            (window.BINDEC_SHIPPING_UNIT_SETTINGS[
              shippingUnitKey
            ].deliveryTime = datetimeSettingsData.deliveryTime),
            (window.BINDEC_SHIPPING_UNIT_SETTINGS[
              shippingUnitKey
            ].deliveryPeriod = ""));
          const timeSelect =
            null == (_b = this.form)
              ? void 0
              : _b.querySelector(selectos.select.time),
            selectedTimeOption =
              null ==
                (_c = null == timeSelect ? void 0 : timeSelect.selectedOptions)
                ? void 0
                : _c[0];
          window.BINDEC_SHIPPING_UNIT_SETTINGS[
            shippingUnitKey
          ].deliveryTimeLabel =
            (null == selectedTimeOption
              ? void 0
              : selectedTimeOption.textContent) || "";
        }
        if (
          datetimeSettingsData.deliveryPeriod &&
          "" !== datetimeSettingsData.deliveryPeriod
        ) {
          const selectedPeriod = this.deliveryPeriods.find((period) => {
            const periodTitle = undefined;
            return (
              (period.period_name || period._title || period._category) ===
              datetimeSettingsData.deliveryPeriod
            );
          });
          selectedPeriod && selectedPeriod.shipping_start_date
            ? (window.BINDEC_SHIPPING_UNIT_SETTINGS[
              shippingUnitKey
            ].shippingStartDate = selectedPeriod.shipping_start_date)
            : (window.BINDEC_SHIPPING_UNIT_SETTINGS[
              shippingUnitKey
            ].shippingStartDate = void 0);
        } else
          window.BINDEC_SHIPPING_UNIT_SETTINGS[
            shippingUnitKey
          ].shippingStartDate = void 0;
      }
      const customEvent = new CustomEvent("settings-change", {
        bubbles: !0,
        detail: {
          type: "shipping-unit-datetime-selected",
          data: {
            groupId: this.deliveryGroupId,
            shippingUnit: {
              shippingUnitId: this.shippingUnitId,
              destinationId: this.destinationId,
              deliveryDate: datetimeSettingsData.deliveryDate,
              deliveryTime: datetimeSettingsData.deliveryTime,
              deliveryPeriod: datetimeSettingsData.deliveryPeriod,
            },
          },
        },
      });
      (this.dispatchEvent(customEvent), this.close());
    }
    _renderDatetimeSummary(datetimeSettingsData) {
      var _a, _b, _c, _d, _e, _f, _g, _h;
      if (!datetimeSettingsData) return;
      null == (_a = this.querySelector(".desired-delivery-date-error")) ||
        _a.remove();
      const {
        deliveryDate: deliveryDate,
        deliveryTime: deliveryTime,
        deliveryPeriod: deliveryPeriod,
      } = datetimeSettingsData;
      if (deliveryPeriod && "" !== deliveryPeriod) {
        const dateLabelElement = this.querySelector(
          ".datetime-option-row .datetime-option-label",
        );
        (dateLabelElement &&
          "お届け日時" === dateLabelElement.textContent &&
          (dateLabelElement.textContent = "お届け時期"),
          (null == (_b = this.summary) ? void 0 : _b.date) &&
          (this.summary.date.textContent = deliveryPeriod));
        const timeRow = this.querySelector(".datetime-option-row:nth-child(2)");
        timeRow && (timeRow.style.display = "none");
      } else {
        const dateLabelElement = this.querySelector(
          ".datetime-option-row .datetime-option-label",
        );
        dateLabelElement &&
          "お届け時期" === dateLabelElement.textContent &&
          (dateLabelElement.textContent = "お届け日時");
        const timeRow = this.querySelector(".datetime-option-row:nth-child(2)");
        if (
          (timeRow && (timeRow.style.display = ""),
            null == (_c = this.summary) ? void 0 : _c.date)
        ) {
          let dateLabel = deliveryDate;
          if (deliveryDate && "" !== deliveryDate) {
            const dateSelect =
              null == (_d = this.form)
                ? void 0
                : _d.querySelector(selectos.select.date);
            if (dateSelect) {
              const selectedOption = dateSelect.querySelector(
                `option[value="${deliveryDate}"]`,
              );
              selectedOption &&
                (dateLabel = selectedOption.textContent || deliveryDate);
            }
            if (dateLabel === deliveryDate) {
              const parsed = new Date(deliveryDate);
              isNaN(parsed.getTime()) ||
                (dateLabel = this._formatDateJapanese(parsed));
            }
          }
          this.summary.date.textContent =
            dateLabel && "" !== dateLabel ? dateLabel : "設定なし";
        }
        if (null == (_e = this.summary) ? void 0 : _e.time) {
          let timeLabel = deliveryTime;
          if (deliveryTime && "" !== deliveryTime) {
            const timeSelect =
              null == (_f = this.form)
                ? void 0
                : _f.querySelector(selectos.select.time);
            if (timeSelect) {
              const selectedOption = timeSelect.querySelector(
                `option[value="${deliveryTime}"]`,
              );
              selectedOption &&
                (timeLabel = selectedOption.textContent || deliveryTime);
            }
            if (timeLabel === deliveryTime) {
              const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
                availableDeliveryTime =
                  null ==
                    (_h =
                      null == (_g = window.BINDEC_SHIPPING_UNIT_SETTINGS)
                        ? void 0
                        : _g[shippingUnitKey])
                    ? void 0
                    : _h.availableDeliveryTime;
              if (availableDeliveryTime) {
                const matchedSlot = availableDeliveryTime.find(
                  (slot) => slot.value === deliveryTime,
                );
                matchedSlot && (timeLabel = matchedSlot.label);
              }
              if (timeLabel === deliveryTime) {
                const scheduleSettings = this._getDeliveryScheduleSettings();
                if (
                  null == scheduleSettings
                    ? void 0
                    : scheduleSettings.deliveryTimeSlots
                ) {
                  const matchedSlot = scheduleSettings.deliveryTimeSlots.find(
                    (slot) => slot.value === deliveryTime,
                  );
                  matchedSlot && (timeLabel = matchedSlot.label);
                }
              }
            }
          }
          this.summary.time.textContent =
            timeLabel && "" !== timeLabel ? timeLabel : "設定なし";
        }
      }
    }
    _getDatetimeSettingsData(data) {
      return {
        deliveryDate: data[selectos.input.date],
        deliveryTime: data[selectos.input.time],
        deliveryPeriod: data[selectos.input.period],
      };
    }
    _renderCurrentDatetimeSettings(initialData) {
      if (!initialData) return;
      const {
        deliveryDate: deliveryDate,
        deliveryTime: deliveryTime,
        deliveryPeriod: deliveryPeriod,
      } = initialData,
        dateSelect = this.form.querySelector(selectos.select.date);
      dateSelect && (dateSelect.value = deliveryDate || "");
      const timeSelect = this.form.querySelector(selectos.select.time);
      timeSelect && (timeSelect.value = deliveryTime || "");
      const periodSelect = this.form.querySelector(selectos.select.period);
      periodSelect && (periodSelect.value = deliveryPeriod || "");
    }
    _loadSavedDatetime() {
      var _a;
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _a[shippingUnitKey];
      if (!shippingUnit) return;
      const deliveryDate = shippingUnit.deliveryDate || "",
        deliveryTime = shippingUnit.deliveryTime || "",
        deliveryPeriod = shippingUnit.deliveryPeriod || "";
      (deliveryDate || deliveryTime || deliveryPeriod) &&
        this._renderDatetimeSummary({
          deliveryDate: deliveryDate,
          deliveryTime: deliveryTime,
          deliveryPeriod: deliveryPeriod,
        });
    }
    _isDeliveryPeriodAllowed() {
      var _a;
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _a[shippingUnitKey];
      if (
        !(null == shippingUnit ? void 0 : shippingUnit.items) ||
        0 === shippingUnit.items.length
      )
        return !0;
      const product = undefined;
      return shippingUnit.items[0].delivery_period_flg ?? !0;
    }
    _isDeliveryTimeAllowed() {
      var _a;
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _a[shippingUnitKey];
      if (
        !(null == shippingUnit ? void 0 : shippingUnit.items) ||
        0 === shippingUnit.items.length
      )
        return !0;
      const product = undefined;
      return shippingUnit.items[0].delivery_time_flg ?? !0;
    }
    _isDeliveryDateAllowed() {
      var _a;
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _a[shippingUnitKey];
      if (
        !(null == shippingUnit ? void 0 : shippingUnit.items) ||
        0 === shippingUnit.items.length
      )
        return !0;
      const product = undefined;
      return shippingUnit.items[0].delivery_date_flg ?? !0;
    }
    _getDeliveryScheduleSettings() {
      var _a, _b;
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _a[shippingUnitKey];
      return (
        null == (_b = null == shippingUnit ? void 0 : shippingUnit.shippingData)
          ? void 0
          : _b.deliveryScheduleSettings
      )
        ? shippingUnit.shippingData.deliveryScheduleSettings
        : null;
    }
    _getDestination() {
      var _a, _b, _c;
      if (
        !(null == (_a = window.BINDEC_SHIPPING_DESTINATION_GROUPS)
          ? void 0
          : _a[this.deliveryGroupId])
      )
        return null;
      const group = undefined,
        destination =
          null ==
            (_c =
              null ==
                (_b =
                  window.BINDEC_SHIPPING_DESTINATION_GROUPS[this.deliveryGroupId]
                    .data)
                ? void 0
                : _b[this.destinationId])
            ? void 0
            : _c[0];
      return (null == destination ? void 0 : destination.address)
        ? {
          address: {
            zipcode: destination.address.zipcode || "",
            prefecture: destination.address.prefecture || "",
          },
        }
        : null;
    }
    _checkIslandLead(islandLeadTimes, zipcode) {
      if (!zipcode || !islandLeadTimes || 0 === islandLeadTimes.length)
        return null;
      const cleanZipcode = zipcode.replace(/-/g, "");
      for (const islandLead of islandLeadTimes) {
        const postalCodes = islandLead.postalCodes
          .split(",")
          .map((code) => code.trim());
        for (const postalCode of postalCodes)
          if (cleanZipcode.startsWith(postalCode)) return islandLead;
      }
      return null;
    }
    _checkPrefectureLead(prefectureLeadTimes, prefecture) {
      if (
        !prefecture ||
        !prefectureLeadTimes ||
        0 === prefectureLeadTimes.length
      )
        return null;
      const prefectureCode = PREFECTURE_TO_PROVINCE_CODE_MAPPING[prefecture];
      return (
        (prefectureCode &&
          prefectureLeadTimes.find(
            (p) => p.prefectureCode === prefectureCode,
          )) ||
        null
      );
    }
    _getProductLeadTime() {
      var _a, _b;
      const shippingUnitKey = `${this.destinationId}_${this.shippingUnitId}`,
        shippingUnit =
          null == (_a = window.BINDEC_SHIPPING_UNIT_SETTINGS)
            ? void 0
            : _a[shippingUnitKey];
      if (
        !(null == shippingUnit ? void 0 : shippingUnit.items) ||
        0 === shippingUnit.items.length
      )
        return 0;
      const variantIds = shippingUnit.items.map((item) =>
        String(item.variant_id),
      ),
        largestLeadTime = undefined;
      return (
        null == (_b = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _b.leadTimes
      )
        ? Math.max(
          ...variantIds.map(
            (variantId) =>
              window.BINDEC_SHIPPING_SETTINGS.leadTimes[variantId] || 0,
          ),
        )
        : 0;
    }
    _getDeliveryLeadTime() {
      const scheduleSettings = this._getDeliveryScheduleSettings();
      if (!scheduleSettings) return 0;
      let leadDays = scheduleSettings.defaultLeadDays;
      const destination = this._getDestination();
      if (!destination) return leadDays;
      const islandLead = this._checkIslandLead(
        scheduleSettings.islandLeadTimes,
        destination.address.zipcode,
      );
      if (islandLead) return islandLead.leadDays;
      const prefectureLead = this._checkPrefectureLead(
        scheduleSettings.prefectureLeadTimes,
        destination.address.prefecture,
      );
      return prefectureLead ? prefectureLead.leadDays : leadDays;
    }
    _calculateLeadDays() {
      const productLeadTime = undefined,
        deliveryLeadTime = undefined;
      return this._getProductLeadTime() + this._getDeliveryLeadTime();
    }
    _isHoliday(date, settings) {
      const dayOfWeek = date.getDay();
      if (
        settings.regularHolidays &&
        settings.regularHolidays.includes(dayOfWeek)
      )
        return !0;
      const month = date.getMonth() + 1,
        day = date.getDate();
      if (
        settings.annualHolidays &&
        settings.annualHolidays.some((h) => h.month === month && h.day === day)
      )
        return !0;
      const dateStr = this._formatDateValue(date);
      return !(
        !settings.specificHolidays ||
        !settings.specificHolidays.some((h) => {
          const inRange = undefined;
          return dateStr >= h.startDate && (!h.endDate || dateStr <= h.endDate);
        })
      );
    }
    _addBusinessDays(date, businessDays, settings) {
      let currentDate = new Date(date),
        daysAdded = 0;
      for (; daysAdded < businessDays;)
        (currentDate.setDate(currentDate.getDate() + 1),
          this._isHoliday(currentDate, settings) || daysAdded++);
      return currentDate;
    }
    _normalizeDateString(dateStr, currentYear) {
      return /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
        ? dateStr
        : /^\d{2}-\d{2}$/.test(dateStr)
          ? `${currentYear}-${dateStr}`
          : dateStr;
    }
    _isDatetimeSelectionAvailable() {
      const isDateAllowed = this._isDeliveryDateAllowed(),
        isTimeAllowed = this._isDeliveryTimeAllowed();
      if (!isDateAllowed || !isTimeAllowed) return !1;
      const purposeData = this.purposeData;
      if (!purposeData) return !0;
      const datetimeStart =
        purposeData.desired_datetime_start_at ||
        purposeData._datetime_selection_start,
        datetimeEnd =
          purposeData.desired_datetime_end_at ||
          purposeData._datetime_selection_end,
        hasDatetimeFields = undefined;
      if (!(datetimeStart || datetimeEnd)) return !0;
      const now = new Date();
      if (datetimeStart) {
        const startDate = undefined;
        if (now < new Date(datetimeStart)) return !1;
      }
      if (datetimeEnd) {
        const endDate = undefined;
        if (now > new Date(datetimeEnd)) return !1;
      }
      return !0;
    }
    _isPeriodSelectionAvailable() {
      var _a, _b, _c;
      const isPeriodAllowed = undefined;
      if (!this._isDeliveryPeriodAllowed()) return !1;
      const deliveryPeriodsData =
        (null == (_a = this.purposeData) ? void 0 : _a.desired_periods) ||
        (null == (_b = this.purposeData) ? void 0 : _b._deliveryperiod),
        hasDeliveryPeriod = undefined;
      if (!(deliveryPeriodsData && deliveryPeriodsData.length > 0)) return !1;
      const deliveryPeriods = deliveryPeriodsData;
      if (!deliveryPeriods || 0 === deliveryPeriods.length) return !1;
      const firstPeriod = undefined;
      let periods = [];
      if ("string" == typeof deliveryPeriods[0]) {
        if (
          !(null == (_c = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _c.purposeMaster)
        )
          return !1;
        const currentPurpose = this.purposeData.name || this.purposeData._title,
          purposeMasterData =
            window.BINDEC_SHIPPING_SETTINGS.purposeMaster.find(
              (p) => (p.name || p._title) === currentPurpose,
            ),
          masterPeriods =
            (null == purposeMasterData
              ? void 0
              : purposeMasterData.desired_periods) ||
            (null == purposeMasterData
              ? void 0
              : purposeMasterData._deliveryperiod);
        if (!masterPeriods || 0 === masterPeriods.length) return !1;
        periods = masterPeriods;
      } else periods = deliveryPeriods;
      const now = new Date(),
        currentYear = now.getFullYear();
      let availablePeriods;
      return (
        periods.filter((period) => {
          const hasDatetimeFields = undefined;
          if (
            period._period_datetime_selection_start ||
            period._period_datetime_selection_end
          ) {
            if (period._period_datetime_selection_start) {
              const startDate = new Date(
                period._period_datetime_selection_start,
              );
              if (now < startDate) return !1;
            }
            if (period._period_datetime_selection_end) {
              const endDate = new Date(period._period_datetime_selection_end);
              if (now > endDate) return !1;
            }
          } else {
            const periodStartDate =
              period.display_start_date || period._start_date;
            if (periodStartDate) {
              const normalizedStartDate = this._normalizeDateString(
                periodStartDate,
                currentYear,
              ),
                startDate = new Date(normalizedStartDate);
              if (now < startDate) return !1;
            }
            const periodEndDate = period.display_end_date || period._end_date;
            if (periodEndDate) {
              const normalizedEndDate = this._normalizeDateString(
                periodEndDate,
                currentYear,
              ),
                endDate = new Date(normalizedEndDate);
              if ((endDate.setHours(23, 59, 59, 999), now > endDate)) return !1;
            }
          }
          return !0;
        }).length > 0
      );
    }
    _determineDisplayPattern() {
      const isDatetimeAvailable = this._isDatetimeSelectionAvailable(),
        isPeriodAvailable = this._isPeriodSelectionAvailable();
      return isDatetimeAvailable && isPeriodAvailable
        ? 1
        : !isDatetimeAvailable && isPeriodAvailable
          ? 2
          : isDatetimeAvailable && !isPeriodAvailable
            ? 3
            : 4;
    }
    _applyDisplayPattern(pattern) {
      const datetimeFields = this.modal.querySelectorAll(
        '[data-field="datetime"]',
      ),
        periodFields = this.modal.querySelectorAll('[data-field="period"]'),
        dividerFields = this.modal.querySelectorAll('[data-field="divider"]');
      switch (pattern) {
        case 1:
          (datetimeFields.forEach((field) => {
            field.style.display = "";
          }),
            periodFields.forEach((field) => {
              field.style.display = "";
            }),
            dividerFields.forEach((field) => {
              field.style.display = "";
            }));
          break;
        case 2:
          (datetimeFields.forEach((field) => {
            field.style.display = "none";
          }),
            periodFields.forEach((field) => {
              field.style.display = "";
            }),
            dividerFields.forEach((field) => {
              field.style.display = "none";
            }));
          break;
        case 3:
          (datetimeFields.forEach((field) => {
            field.style.display = "";
          }),
            periodFields.forEach((field) => {
              field.style.display = "none";
            }),
            dividerFields.forEach((field) => {
              field.style.display = "none";
            }));
      }
    }
  }
  customElements.define("datetime-selector", DatetimeSelector);
}
if (!customElements.get("copy-destination-dialog")) {
  const selectors = {
    modal: "#copy-destination-modal",
    form: "#wl-copy-destination-form",
    cancelButton: "#wl-copy-destination-actions-cancel",
    submitButton: "#wl-copy-destination-actions-submit",
    errorContainer: ".js-copy-error",
    errorMessage: ".js-copy-error-message",
    sourceGroup: ".js-copy-source-group",
    sourceDestination: ".js-copy-source-destination",
    sourceName: ".js-copy-source-name",
    sourceAddress: ".js-copy-source-address",
    input: ".js-copy-destination-input",
  };
  class CopyDestinationDialog extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "modal"),
        __publicField(this, "form"),
        __publicField(this, "cancelButton"),
        __publicField(this, "submitButton"),
        __publicField(this, "errorContainer"),
        __publicField(this, "errorMessage"),
        __publicField(this, "sourceGroup"),
        __publicField(this, "sourceDestination"),
        __publicField(this, "sourceName"),
        __publicField(this, "sourceAddress"),
        __publicField(this, "input"),
        __publicField(this, "sourceData", null),
        __publicField(this, "__cancelListener"),
        __publicField(this, "__submitListener"),
        __publicField(this, "__inputListener"));
    }
    connectedCallback() {
      ((this.modal = document.querySelector(selectors.modal)),
        (this.form = document.querySelector(selectors.form)),
        (this.cancelButton = document.querySelector(selectors.cancelButton)),
        (this.submitButton = document.querySelector(selectors.submitButton)),
        (this.errorContainer = document.querySelector(
          selectors.errorContainer,
        )),
        (this.errorMessage = document.querySelector(selectors.errorMessage)),
        (this.sourceGroup = document.querySelector(selectors.sourceGroup)),
        (this.sourceDestination = document.querySelector(
          selectors.sourceDestination,
        )),
        (this.sourceName = document.querySelector(selectors.sourceName)),
        (this.sourceAddress = document.querySelector(selectors.sourceAddress)),
        (this.input = document.querySelector(selectors.input)),
        (this.__cancelListener = this.close.bind(this)),
        (this.__submitListener = this.handleSubmit.bind(this)),
        (this.__inputListener = this.handleInputChange.bind(this)),
        this.cancelButton.addEventListener("click", this.__cancelListener),
        this.submitButton.addEventListener("click", this.__submitListener),
        this.input.addEventListener("input", this.__inputListener));
    }
    disconnectedCallback() {
      (this.cancelButton.removeEventListener("click", this.__cancelListener),
        this.submitButton.removeEventListener("click", this.__submitListener),
        this.input.removeEventListener("input", this.__inputListener));
    }
    open(source) {
      ((this.sourceData = source),
        this.renderSourceInfo(),
        (this.input.value = ""),
        this.errorContainer.classList.add("hidden"),
        (this.submitButton.disabled = !0),
        this.modal.showModal());
    }
    close() {
      (this.modal.close(), (this.sourceData = null), (this.input.value = ""));
    }
    renderSourceInfo() {
      if (!this.sourceData) return;
      const {
        groupIndex: groupIndex,
        destinationIndex: destinationIndex,
        destinationData: destinationData,
      } = this.sourceData;
      ((this.sourceGroup.textContent = `お申し込み ${groupIndex}`),
        (this.sourceDestination.textContent = `お届け先 ${destinationIndex}`));
      const name =
        destinationData.name ||
        `${destinationData.lastName || ""} ${destinationData.firstName || ""}`.trim() ||
        "-";
      if (((this.sourceName.textContent = name), destinationData.address)) {
        const addr = destinationData.address,
          address =
            `〒${addr.zipcode || ""} ${addr.prefecture || ""} ${addr.city || ""} ${addr.address1 || ""} ${addr.address2 || ""}`.trim();
        this.sourceAddress.textContent = address || "-";
      } else this.sourceAddress.textContent = "-";
    }
    handleInputChange() {
      const value = undefined;
      if (!this.input.value.trim())
        return (
          (this.submitButton.disabled = !0),
          void this.errorContainer.classList.add("hidden")
        );
      this.submitButton.disabled = !1;
    }
    normalizeInput(input) {
      return input
        .replace(/[０-９]/g, (s) =>
          String.fromCharCode(s.charCodeAt(0) - 65248),
        )
        .replace(/[ー−]/g, "-")
        .replace(/[、，]/g, ",")
        .replace(/\s/g, "");
    }
    parseInput(input) {
      const normalized = this.normalizeInput(input),
        formatRegex = /^\d+-\d+(,\d+-\d+)*$/;
      if (!normalized.match(formatRegex))
        return (
          this.showError(
            "複写先のフォーマットが正しくありません（例: 2-1, 3-2）",
          ),
          null
        );
      const targets = [],
        parts = normalized.split(","),
        multiShippingSettings = document.querySelector(
          "multi-shipping-settings",
        );
      if (!multiShippingSettings)
        return (this.showError("配送設定が見つかりません"), null);
      const deliveryGroups =
        multiShippingSettings.querySelectorAll("delivery-group");
      for (const part of parts) {
        const [groupIndexStr, destIndexStr] = part.split("-"),
          groupIndex = parseInt(groupIndexStr),
          destIndex = parseInt(destIndexStr),
          groupElement = Array.from(deliveryGroups).find(
            (_, idx) => idx + 1 === groupIndex,
          );
        if (!groupElement)
          return (
            this.showError(`お申し込み ${groupIndex} は存在しません`),
            null
          );
        const groupId =
          groupElement.getAttribute("data-delivery-group-id") || "",
          destinations = groupElement.querySelectorAll("destination-selector"),
          destElement = Array.from(destinations).find(
            (_, idx) => idx + 1 === destIndex,
          );
        if (!destElement)
          return (
            this.showError(
              `お申し込み ${groupIndex} のお届け先 ${destIndex} は存在しません`,
            ),
            null
          );
        const destinationId =
          destElement.getAttribute("data-destination-id") || "",
          managementNumber = `${groupIndex}-${destIndex}`;
        if (
          groupId === this.sourceData.groupId &&
          destinationId === this.sourceData.destinationId
        )
          return (
            this.showError(
              `複写元と同じお届け先には複写できません（${managementNumber}）`,
            ),
            null
          );
        if (targets.some((t) => t.managementNumber === managementNumber))
          return (
            this.showError(`複写先 ${managementNumber} が重複しています`),
            null
          );
        targets.push({
          groupId: groupId,
          groupIndex: groupIndex,
          destinationId: destinationId,
          destinationIndex: destIndex,
          isNewDestination: !1,
          managementNumber: managementNumber,
        });
      }
      return targets;
    }
    showError(message) {
      ((this.errorMessage.textContent = message),
        this.errorContainer.classList.remove("hidden"));
    }
    handleSubmit() {
      const inputValue = this.input.value.trim();
      if (!inputValue) return void this.showError("複写先が入力されていません");
      const targets = this.parseInput(inputValue);
      if (!targets || 0 === targets.length) return;
      const inventoryError = this.checkInventoryShortage(targets);
      if (inventoryError) return void this.showError(inventoryError);
      this.errorContainer.classList.add("hidden");
      const event = new CustomEvent("copy-destination-execute", {
        bubbles: !0,
        detail: { source: this.sourceData, targets: targets },
      });
      (this.dispatchEvent(event), this.close());
    }
    checkInventoryShortage(targets) {
      var _a, _b, _c, _d;
      if (!this.sourceData) return null;
      const projectedTotals = getCurrentTotalsByVariant(),
        sourceItems =
          (null == (_a = this.sourceData.destinationData)
            ? void 0
            : _a.items) || [],
        multiShippingSettings = document.querySelector(
          "multi-shipping-settings",
        ),
        settings =
          (null == multiShippingSettings
            ? void 0
            : multiShippingSettings.settings) ??
          (null ==
            (_b =
              null == multiShippingSettings
                ? void 0
                : multiShippingSettings.getSettings)
            ? void 0
            : _b.call(multiShippingSettings)) ??
          null;
      for (const target of targets) {
        if (!target.isNewDestination && target.destinationId) {
          const group =
            null == (_c = null == settings ? void 0 : settings.deliveryGroups)
              ? void 0
              : _c.find((g) => g.groupId === target.groupId),
            dest =
              null == (_d = null == group ? void 0 : group.destinations)
                ? void 0
                : _d.find((d) => d.destinationId === target.destinationId);
          for (const item of (null == dest ? void 0 : dest.items) || []) {
            const vid = normalizeVariantId(
              (null == item ? void 0 : item.variantId) ??
              (null == item ? void 0 : item.variant_id) ??
              "",
            );
            if (!vid) continue;
            const qty = Number(item.quantity) || 0;
            projectedTotals[vid] = Math.max(
              0,
              (projectedTotals[vid] || 0) - qty,
            );
          }
        }
        for (const item of sourceItems) {
          const vid = normalizeVariantId(
            (null == item ? void 0 : item.variantId) ?? "",
          );
          if (!vid) continue;
          const qty = Number(item.quantity) || 0;
          projectedTotals[vid] = (projectedTotals[vid] || 0) + qty;
        }
      }
      const shortages = findInventoryShortages(projectedTotals);
      if (0 === shortages.length) return null;
      const messages = formatShortageMessage(shortages);
      return ["お届け先を複写すると在庫を超えます。"]
        .concat(messages)
        .join(" ");
    }
  }
  customElements.define("copy-destination-dialog", CopyDestinationDialog);
}
if (!customElements.get("copy-products-dialog")) {
  const selectors = {
    modal: "#copy-products-modal",
    form: "#wl-copy-products-form",
    cancelButton: "#wl-copy-products-actions-cancel",
    submitButton: "#wl-copy-products-actions-submit",
    errorContainer: ".js-copy-products-error",
    errorMessage: ".js-copy-products-error-message",
    sourceGroup: ".js-copy-products-source-group",
    sourceDestination: ".js-copy-products-source-destination",
    sourceName: ".js-copy-products-source-name",
    productsList: ".js-copy-products-list",
    input: ".js-copy-products-destination-input",
  };
  class CopyProductsDialog extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "modal"),
        __publicField(this, "form"),
        __publicField(this, "cancelButton"),
        __publicField(this, "submitButton"),
        __publicField(this, "errorContainer"),
        __publicField(this, "errorMessage"),
        __publicField(this, "sourceGroup"),
        __publicField(this, "sourceDestination"),
        __publicField(this, "sourceName"),
        __publicField(this, "productsList"),
        __publicField(this, "input"),
        __publicField(this, "sourceData", null),
        __publicField(this, "selectedProductIds", new Set()),
        __publicField(this, "__cancelListener"),
        __publicField(this, "__submitListener"),
        __publicField(this, "__inputListener"));
    }
    connectedCallback() {
      ((this.modal = document.querySelector(selectors.modal)),
        (this.form = document.querySelector(selectors.form)),
        (this.cancelButton = document.querySelector(selectors.cancelButton)),
        (this.submitButton = document.querySelector(selectors.submitButton)),
        (this.errorContainer = document.querySelector(
          selectors.errorContainer,
        )),
        (this.errorMessage = document.querySelector(selectors.errorMessage)),
        (this.sourceGroup = document.querySelector(selectors.sourceGroup)),
        (this.sourceDestination = document.querySelector(
          selectors.sourceDestination,
        )),
        (this.sourceName = document.querySelector(selectors.sourceName)),
        (this.productsList = document.querySelector(selectors.productsList)),
        (this.input = document.querySelector(selectors.input)),
        (this.__cancelListener = this.close.bind(this)),
        (this.__submitListener = this.handleSubmit.bind(this)),
        (this.__inputListener = this.handleInputChange.bind(this)),
        this.cancelButton.addEventListener("click", this.__cancelListener),
        this.submitButton.addEventListener("click", this.__submitListener),
        this.input.addEventListener("input", this.__inputListener));
    }
    disconnectedCallback() {
      (this.cancelButton.removeEventListener("click", this.__cancelListener),
        this.submitButton.removeEventListener("click", this.__submitListener),
        this.input.removeEventListener("input", this.__inputListener));
    }
    open(source) {
      ((this.sourceData = source),
        this.selectedProductIds.clear(),
        this.renderSourceInfo(),
        this.renderProductsList(),
        (this.input.value = ""),
        this.errorContainer.classList.add("hidden"),
        (this.submitButton.disabled = !0),
        this.modal.showModal());
    }
    close() {
      (this.modal.close(),
        (this.sourceData = null),
        this.selectedProductIds.clear(),
        (this.input.value = ""),
        (this.productsList.innerHTML = ""));
    }
    renderSourceInfo() {
      if (!this.sourceData) return;
      const {
        groupIndex: groupIndex,
        destinationIndex: destinationIndex,
        destinationData: destinationData,
      } = this.sourceData;
      ((this.sourceGroup.textContent = `お申し込み ${groupIndex}`),
        (this.sourceDestination.textContent = `お届け先 ${destinationIndex}`));
      const name =
        destinationData.name ||
        `${destinationData.lastName || ""} ${destinationData.firstName || ""}`.trim() ||
        "-";
      this.sourceName.textContent = name;
    }
    renderProductsList() {
      if (!this.sourceData || !this.sourceData.destinationData.items)
        return void (this.productsList.innerHTML =
          '<p style="padding: 12px; color: #666;">商品がありません</p>');
      const items = this.sourceData.destinationData.items;
      if (0 === items.length)
        return void (this.productsList.innerHTML =
          '<p style="padding: 12px; color: #666;">商品がありません</p>');
      const itemsHTML = items
        .map((item, index) => {
          var _a, _b, _c, _d;
          const productId = item.productId || `product-${index}`,
            variantTitle = item.variantTitle ? ` - ${item.variantTitle}` : "",
            quantity = item.quantity || 1,
            price = (item.price || 0) / 100,
            hasGiftSettings =
              item.giftSettings &&
              (item.giftSettings.isGift ||
                item.giftSettings.noshi ||
                item.giftSettings.messageCard),
            hasDeliveryInfo =
              item.deliveryDate || item.deliveryTime || item.deliveryPeriod,
            details = [];
          (hasGiftSettings &&
            ((null ==
              (_b = null == (_a = item.giftSettings) ? void 0 : _a.noshi)
              ? void 0
              : _b.type) &&
              details.push(`のし: ${item.giftSettings.noshi.type}`),
              (null ==
                (_d = null == (_c = item.giftSettings) ? void 0 : _c.messageCard)
                ? void 0
                : _d.template) && details.push("メッセージカードあり")),
            hasDeliveryInfo &&
            (item.deliveryDate &&
              details.push(`配送日: ${item.deliveryDate}`),
              item.deliveryPeriod &&
              details.push(`配送期間: ${item.deliveryPeriod}`)));
          const detailsHTML =
            details.length > 0
              ? details
                .map((d) => `<p class="wl-copy-product-detail">${d}</p>`)
                .join("")
              : "";
          return `\n          <div class="wl-copy-product-item" data-product-id="${productId}">\n            <input type="checkbox" class="wl-copy-product-checkbox" data-product-id="${productId}">\n            <div class="wl-copy-product-info">\n              <p class="wl-copy-product-title">${item.productTitle}${variantTitle}</p>\n              <div class="wl-copy-product-details">\n                <p class="wl-copy-product-detail">数量: ${quantity} / 価格: ¥${price.toLocaleString()}</p>\n                ${detailsHTML}\n              </div>\n            </div>\n          </div>\n        `;
        })
        .join("");
      this.productsList.innerHTML = itemsHTML;
      const checkboxes = undefined;
      this.productsList
        .querySelectorAll(".wl-copy-product-checkbox")
        .forEach((checkbox) => {
          checkbox.addEventListener(
            "change",
            this.handleProductCheckboxChange.bind(this),
          );
        });
      const items_elements = undefined;
      this.productsList
        .querySelectorAll(".wl-copy-product-item")
        .forEach((item) => {
          item.addEventListener("click", (e) => {
            if (e.target.classList.contains("wl-copy-product-checkbox")) return;
            const productId = item.getAttribute("data-product-id") || "",
              checkbox = item.querySelector(".wl-copy-product-checkbox");
            checkbox &&
              ((checkbox.checked = !checkbox.checked),
                this.handleProductCheckboxChange.call(this, {
                  target: checkbox,
                }));
          });
        });
    }
    handleProductCheckboxChange(e) {
      const checkbox = e.target,
        productId = checkbox.getAttribute("data-product-id") || "",
        item = checkbox.closest(".wl-copy-product-item");
      (checkbox.checked
        ? (this.selectedProductIds.add(productId),
          null == item || item.classList.add("selected"))
        : (this.selectedProductIds.delete(productId),
          null == item || item.classList.remove("selected")),
        this.updateSubmitButton());
    }
    handleInputChange() {
      this.updateSubmitButton();
    }
    updateSubmitButton() {
      const hasSelectedProducts = this.selectedProductIds.size > 0,
        hasInputValue = this.input.value.trim().length > 0;
      ((this.submitButton.disabled = !(hasSelectedProducts && hasInputValue)),
        hasSelectedProducts &&
        hasInputValue &&
        this.errorContainer.classList.add("hidden"));
    }
    normalizeInput(input) {
      return input
        .replace(/[０-９]/g, (s) =>
          String.fromCharCode(s.charCodeAt(0) - 65248),
        )
        .replace(/[ー−]/g, "-")
        .replace(/\s/g, "");
    }
    parseInput(input) {
      const normalized = this.normalizeInput(input),
        formatRegex = /^\d+-\d+$/;
      if (!normalized.match(formatRegex))
        return (
          this.showError("複写先のフォーマットが正しくありません（例: 1-2）"),
          null
        );
      const [groupIndexStr, destIndexStr] = normalized.split("-"),
        groupIndex = parseInt(groupIndexStr),
        destIndex = parseInt(destIndexStr),
        multiShippingSettings = document.querySelector(
          "multi-shipping-settings",
        );
      if (!multiShippingSettings)
        return (this.showError("配送設定が見つかりません"), null);
      const deliveryGroups =
        multiShippingSettings.querySelectorAll("delivery-group"),
        groupElement = Array.from(deliveryGroups).find(
          (_, idx) => idx + 1 === groupIndex,
        );
      if (!groupElement)
        return (
          this.showError(`お申し込み ${groupIndex} は存在しません`),
          null
        );
      const groupId = groupElement.getAttribute("data-delivery-group-id") || "",
        destinations = groupElement.querySelectorAll("destination-selector"),
        destElement = Array.from(destinations).find(
          (_, idx) => idx + 1 === destIndex,
        );
      if (!destElement)
        return (
          this.showError(
            `お申し込み ${groupIndex} のお届け先 ${destIndex} は存在しません`,
          ),
          null
        );
      const destinationId =
        destElement.getAttribute("data-destination-id") || "",
        managementNumber = `${groupIndex}-${destIndex}`;
      return groupId === this.sourceData.groupId &&
        destinationId === this.sourceData.destinationId
        ? (this.showError(
          `複写元と同じお届け先には複写できません（${managementNumber}）`,
        ),
          null)
        : {
          groupId: groupId,
          groupIndex: groupIndex,
          destinationId: destinationId,
          destinationIndex: destIndex,
          managementNumber: managementNumber,
        };
    }
    showError(message) {
      ((this.errorMessage.textContent = message),
        this.errorContainer.classList.remove("hidden"));
    }
    handleSubmit() {
      if (0 === this.selectedProductIds.size)
        return void this.showError("複写する商品を選択してください");
      const inputValue = this.input.value.trim();
      if (!inputValue) return void this.showError("複写先が入力されていません");
      const target = this.parseInput(inputValue);
      if (!target) return;
      const selectedItems = [];
      if (this.sourceData && this.sourceData.destinationData.items)
        for (const item of this.sourceData.destinationData.items)
          this.selectedProductIds.has(item.productId || "") &&
            selectedItems.push(item);
      const inventoryError = this.checkInventoryShortage(selectedItems);
      if (inventoryError) return void this.showError(inventoryError);
      this.errorContainer.classList.add("hidden");
      const event = new CustomEvent("copy-products-execute", {
        bubbles: !0,
        detail: {
          source: this.sourceData,
          target: target,
          products: {
            productIds: Array.from(this.selectedProductIds),
            items: selectedItems,
          },
        },
      });
      (this.dispatchEvent(event), this.close());
    }
    checkInventoryShortage(itemsToCopy) {
      const projectedTotals = getCurrentTotalsByVariant();
      for (const item of itemsToCopy) {
        const vid = normalizeVariantId(
          (null == item ? void 0 : item.variantId) ?? "",
        );
        if (!vid) continue;
        const qty = Number(item.quantity) || 0;
        projectedTotals[vid] = (projectedTotals[vid] || 0) + qty;
      }
      const shortages = findInventoryShortages(projectedTotals);
      if (0 === shortages.length) return null;
      const messages = formatShortageMessage(shortages);
      return ["商品を複写すると在庫を超えます。"].concat(messages).join(" ");
    }
  }
  customElements.define("copy-products-dialog", CopyProductsDialog);
}
if (!customElements.get("customer-registration")) {
  class CustomerRegistration extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "modal"),
        __publicField(this, "formElement"),
        __publicField(this, "cancelButton"),
        __publicField(this, "submitButton"),
        __publicField(this, "editButton"),
        __publicField(this, "config"),
        __publicField(this, "phone1Input"),
        __publicField(this, "phone2Input"),
        __publicField(this, "phone3Input"),
        __publicField(this, "phoneHiddenInput"),
        __publicField(this, "provinceSelect"),
        __publicField(this, "__isIntentionalClose", !1),
        __publicField(this, "__editButtonListener"),
        __publicField(this, "restoredCustomerData", null),
        __publicField(this, "currentCustomer", null),
        __publicField(this, "emailValue", ""),
        __publicField(this, "ERROR_MESSAGES", {
          REQUIRED: "※ このフィールドは必須です。",
          FULL_WIDTH: "※ 全角文字を入力してください。",
          FULL_WIDTH_KANA: "※ 全角カタカナを入力してください。",
          HALF_WIDTH: "※ 半角文字を入力してください。",
          EMAIL: "※ 有効なメールアドレスを入力してください。",
          INT: "※ 半角数字を入力してください。",
        }));
    }
    connectedCallback() {
      ((this.editButton = this.querySelector(".edit-button")),
        this.querySelector("#customer-registration-modal")
          ? ((this.modal = this.querySelector("#customer-registration-modal")),
            (this.formElement = this.querySelector(
              "#customer-registration-form",
            )),
            (this.cancelButton = this.querySelector("#wlModalButtonCancel")),
            (this.submitButton = this.querySelector("#wlModalButtonSubmit")),
            (this.phone1Input = this.querySelector("#phone_1")),
            (this.phone2Input = this.querySelector("#phone_2")),
            (this.phone3Input = this.querySelector("#phone_3")),
            (this.phoneHiddenInput = this.querySelector("#phone")),
            (this.provinceSelect = this.querySelector("#province")))
          : this.render(),
        this.initializeCustomer(),
        this.attachEventListeners());
    }
    disconnectedCallback() {
      this.removeEventListeners();
    }
    open(config) {
      ((this.config = config),
        this.modal || (this.render(), this.attachEventListeners()),
        (this.formElement && this.cancelButton && this.submitButton) ||
        ((this.formElement = this.querySelector(
          "#customer-registration-form",
        )),
          (this.cancelButton = this.querySelector("#wlModalButtonCancel")),
          (this.submitButton = this.querySelector("#wlModalButtonSubmit")),
          (this.phone1Input = this.querySelector("#phone_1")),
          (this.phone2Input = this.querySelector("#phone_2")),
          (this.phone3Input = this.querySelector("#phone_3")),
          (this.phoneHiddenInput = this.querySelector("#phone")),
          (this.provinceSelect = this.querySelector("#province")),
          this.attachEventListeners()));
      const titleElement = this.modal.querySelector(".modal-title");
      (titleElement &&
        ("edit" === config.type
          ? (titleElement.textContent = "注文者の編集")
          : (titleElement.textContent = "注文者の追加・編集")),
        "edit" === config.type && config.address
          ? this.fillForm(config.address)
          : this.clearForm(),
        this.modal.showModal());
    }
    close() {
      ((this.__isIntentionalClose = !0),
        this.modal.close(),
        setTimeout(() => {
          this.__isIntentionalClose = !1;
        }, 100));
    }
    render() {
      var _a, _b, _c;
      if (this.querySelector("#customer-registration-modal")) return;
      const isJapanese =
        "ja" === document.documentElement.lang ||
        "ja" ===
        (null == (_b = null == (_a = window.Shopify) ? void 0 : _a.locale)
          ? void 0
          : _b.primaryLocale),
        modalHTML = document.createElement("template");
      ((modalHTML.innerHTML = `\n        <dialog class="address-modal" id="customer-registration-modal">\n          <div class="address-modal-content">\n            <div class="address-modal-header">\n              <h2 class="modal-title">\n                ${"edit" === (null == (_c = this.config) ? void 0 : _c.type) ? "注文者の編集" : "注文者の追加・編集"}\n              </h2>\n            </div>\n\n            <div class="address-modal-body">\n              <div class="new-address-form">\n                <form class="address-form" id="customer-registration-form">\n                  <div class="form-row">\n                    <div class="form-group">\n                      <label for="first-name" class="form-label required">姓</label>\n                      <input \n                        type="text" \n                        id="first-name" \n                        name="${isJapanese ? "last_name" : "first_name"}" \n                        class="form-input" \n                        placeholder="${isJapanese ? "田中" : "たなか"}" \n                        data-cf-field="${isJapanese ? "last_name" : "first_name"}"\n                        data-cf-validation="full-width"\n                        data-cf-required="true"\n                        required\n                      >\n                      <div data-cf-field="${isJapanese ? "last_name" : "first_name"}" class="cf-error form-field-error" style="display: none;"></div>\n                    </div>\n                    <div class="form-group">\n                      <label for="last-name" class="form-label required">名</label>\n                      <input \n                        type="text" \n                        id="last-name" \n                        name="${isJapanese ? "first_name" : "last_name"}" \n                        class="form-input" \n                        placeholder="${isJapanese ? "たなか" : "田中"}" \n                        data-cf-field="${isJapanese ? "first_name" : "last_name"}"\n                        data-cf-validation="full-width"\n                        data-cf-required="true"\n                        required\n                      >\n                      <div data-cf-field="${isJapanese ? "first_name" : "last_name"}" class="cf-error form-field-error" style="display: none;"></div>\n                    </div>\n                  </div>\n\n                  <div class="form-row">\n                    <div class="form-group">\n                      <label for="last-name-kana" class="form-label required">氏名カナ（セイ）</label>\n                      <input\n                        type="text"\n                        id="last-name-kana"\n                        name="last_name_kana"\n                        class="form-input"\n                        placeholder="タナカ"\n                        maxlength="10"\n                        data-cf-field="last_name_kana"\n                        data-cf-validation="full-width-kana"\n                        data-cf-required="true"\n                        required\n                      >\n                      <div data-cf-field="last_name_kana" class="cf-error form-field-error" style="display: none;"></div>\n                    </div>\n                    <div class="form-group">\n                      <label for="first-name-kana" class="form-label required">氏名カナ（メイ）</label>\n                      <input\n                        type="text"\n                        id="first-name-kana"\n                        name="first_name_kana"\n                        class="form-input"\n                        placeholder="タロウ"\n                        maxlength="10"\n                        data-cf-field="first_name_kana"\n                        data-cf-validation="full-width-kana"\n                        data-cf-required="true"\n                        required\n                      >\n                      <div data-cf-field="first_name_kana" class="cf-error form-field-error" style="display: none;"></div>\n                    </div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="email" class="form-label required">メールアドレス</label>\n                    <input \n                      type="email" \n                      id="email" \n                      name="email" \n                      class="form-input" \n                      placeholder="example@email.com" \n                      data-cf-field="email"\n                      data-cf-validation="email"\n                      data-cf-required="true"\n                      required\n                    >\n                    <div data-cf-field="email" class="cf-error form-field-error" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="zip" class="form-label required">郵便番号</label>\n                    <div class="form-input-with-button">\n                      <input\n                        type="text"\n                        id="zip"\n                        name="zip"\n                        class="form-input"\n                        placeholder="4500002"\n                        data-cf-field="zip"\n                        data-cf-validation="zip"\n                        data-cf-required="true"\n                        required\n                      >\n                      <button type="button" class="postal-code-lookup-btn" id="customer-postal-code-lookup-btn">住所検索</button>\n                    </div>\n                    <div data-cf-field="zip" class="cf-error form-field-error" style="display: none;"></div>\n                    <div class="postal-code-lookup-message" id="customer-postal-code-lookup-message" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="province" class="form-label required">都道府県</label>\n                    <select \n                      id="province" \n                      name="province" \n                      class="form-select" \n                      data-cf-field="province"\n                      data-cf-required="true"\n                      required\n                    >\n                      <option value="">選択してください</option>\n                      <option value="北海道">北海道</option>\n                      <option value="青森県">青森県</option>\n                      <option value="岩手県">岩手県</option>\n                      <option value="宮城県">宮城県</option>\n                      <option value="秋田県">秋田県</option>\n                      <option value="山形県">山形県</option>\n                      <option value="福島県">福島県</option>\n                      <option value="茨城県">茨城県</option>\n                      <option value="栃木県">栃木県</option>\n                      <option value="群馬県">群馬県</option>\n                      <option value="埼玉県">埼玉県</option>\n                      <option value="千葉県">千葉県</option>\n                      <option value="東京都">東京都</option>\n                      <option value="神奈川県">神奈川県</option>\n                      <option value="新潟県">新潟県</option>\n                      <option value="富山県">富山県</option>\n                      <option value="石川県">石川県</option>\n                      <option value="福井県">福井県</option>\n                      <option value="山梨県">山梨県</option>\n                      <option value="長野県">長野県</option>\n                      <option value="岐阜県">岐阜県</option>\n                      <option value="静岡県">静岡県</option>\n                      <option value="愛知県">愛知県</option>\n                      <option value="三重県">三重県</option>\n                      <option value="滋賀県">滋賀県</option>\n                      <option value="京都府">京都府</option>\n                      <option value="大阪府">大阪府</option>\n                      <option value="兵庫県">兵庫県</option>\n                      <option value="奈良県">奈良県</option>\n                      <option value="和歌山県">和歌山県</option>\n                      <option value="鳥取県">鳥取県</option>\n                      <option value="島根県">島根県</option>\n                      <option value="岡山県">岡山県</option>\n                      <option value="広島県">広島県</option>\n                      <option value="山口県">山口県</option>\n                      <option value="徳島県">徳島県</option>\n                      <option value="香川県">香川県</option>\n                      <option value="愛媛県">愛媛県</option>\n                      <option value="高知県">高知県</option>\n                      <option value="福岡県">福岡県</option>\n                      <option value="佐賀県">佐賀県</option>\n                      <option value="長崎県">長崎県</option>\n                      <option value="熊本県">熊本県</option>\n                      <option value="大分県">大分県</option>\n                      <option value="宮崎県">宮崎県</option>\n                      <option value="鹿児島県">鹿児島県</option>\n                      <option value="沖縄県">沖縄県</option>\n                    </select>\n                    <div data-cf-field="province" class="cf-error form-field-error" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="city" class="form-label required">市区町村</label>\n                    <input \n                      type="text" \n                      id="city" \n                      name="city" \n                      class="form-input" \n                      placeholder="中村区名駅" \n                      data-cf-field="city"\n                      data-cf-validation="full-width"\n                      data-cf-required="true"\n                      required\n                    >\n                    <div data-cf-field="city" class="cf-error form-field-error" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="town" class="form-label">町域</label>\n                    <input \n                      type="text" \n                      id="town" \n                      name="town" \n                      class="form-input" \n                      placeholder="町域"\n                      data-cf-field="town"\n                      data-cf-validation="full-width"\n                    >\n                    <div data-cf-field="town" class="cf-error form-field-error" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="address1" class="form-label required">番地以降</label>\n                    <input \n                      type="text" \n                      id="address1" \n                      name="address1" \n                      class="form-input" \n                      placeholder="２−８−１"\n                      data-cf-field="address1"\n                      data-cf-validation="full-width"\n                      data-cf-required="true"\n                      required\n                    >\n                    <div data-cf-field="address1" class="cf-error form-field-error" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="address2" class="form-label">建物名、部屋番号等</label>\n                    <input \n                      type="text" \n                      id="address2" \n                      name="address2" \n                      class="form-input" \n                      placeholder="例：◯ビル６ー６階（必ず全角入力してください）"\n                      data-cf-field="address2"\n                    >\n                    <div data-cf-field="address2" class="cf-error form-field-error" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="phone" class="form-label required">電話番号</label>\n                    <div class="phone-inputs-wrapper">\n                      <input \n                        type="tel" \n                        id="phone_1" \n                        name="phone_1" \n                        class="form-input phone-input" \n                        placeholder="090" \n                        maxlength="5" \n                        style="width: 30%;"\n                        data-cf-field="phone_1"\n                        data-cf-validation="int"\n                      >\n                      <span style="margin: 0 8px;">-</span>\n                      <input \n                        type="tel" \n                        id="phone_2" \n                        name="phone_2" \n                        class="form-input phone-input" \n                        placeholder="1234" \n                        maxlength="4" \n                        style="width: 30%;"\n                        data-cf-field="phone_2"\n                        data-cf-validation="int"\n                      >\n                      <span style="margin: 0 8px;">-</span>\n                      <input \n                        type="tel" \n                        id="phone_3" \n                        name="phone_3" \n                        class="form-input phone-input" \n                        placeholder="5678" \n                        maxlength="4" \n                        style="width: 30%;"\n                        data-cf-field="phone_3"\n                        data-cf-validation="int"\n                      >\n                      <input type="hidden" id="phone" name="address[phone]" data-cf-field="phone" data-cf-required="true">\n                    </div>\n                    <div data-cf-field="phone" class="cf-error form-field-error" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="company" class="form-label">会社名</label>\n                    <input \n                      type="text" \n                      id="company" \n                      name="company" \n                      class="form-input" \n                      placeholder="株式会社名鉄生活創研"\n                      data-cf-field="company"\n                    >\n                    <div data-cf-field="company" class="cf-error form-field-error" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-group">\n                    <label for="department" class="form-label">部署・役職</label>\n                    <input \n                      type="text" \n                      id="department" \n                      name="department" \n                      class="form-input" \n                      placeholder="名鉄百貨店事業部"\n                      data-cf-field="department"\n                    >\n                    <div data-cf-field="department" class="cf-error form-field-error" style="display: none;"></div>\n                  </div>\n\n                  <div class="form-error-message" style="display: none;"></div>\n                </form>\n              </div>\n            </div>\n\n            <div class="address-modal-footer">\n              <button type="button" class="modal-cancel-btn" id="wlModalButtonCancel">キャンセル</button>\n              <button type="button" class="modal-confirm-btn" id="wlModalButtonSubmit">登録</button>\n            </div>\n          </div>\n        </dialog>\n      `),
        this.appendChild(modalHTML.content.cloneNode(!0)),
        (this.modal = this.querySelector("#customer-registration-modal")),
        (this.formElement = this.querySelector("#customer-registration-form")),
        (this.cancelButton = this.querySelector("#wlModalButtonCancel")),
        (this.submitButton = this.querySelector("#wlModalButtonSubmit")),
        (this.phone1Input = this.querySelector("#phone_1")),
        (this.phone2Input = this.querySelector("#phone_2")),
        (this.phone3Input = this.querySelector("#phone_3")),
        (this.phoneHiddenInput = this.querySelector("#phone")),
        (this.provinceSelect = this.querySelector("#province")));
    }
    attachEventListeners() {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
      const updatePhone = () => {
        var _a2, _b2, _c2;
        const phone1 = undefined,
          phone2 = undefined,
          phone3 = undefined,
          fullPhone = `${(null == (_a2 = this.phone1Input) ? void 0 : _a2.value) || ""}-${(null == (_b2 = this.phone2Input) ? void 0 : _b2.value) || ""}-${(null == (_c2 = this.phone3Input) ? void 0 : _c2.value) || ""}`;
        this.phoneHiddenInput && (this.phoneHiddenInput.value = fullPhone);
      },
        validatePhone = () => {
          var _a2, _b2, _c2;
          const phone1 = undefined,
            phone2 = undefined,
            phone3 = undefined,
            fullPhone = `${(null == (_a2 = this.phone1Input) ? void 0 : _a2.value) || ""}${(null == (_b2 = this.phone2Input) ? void 0 : _b2.value) || ""}${(null == (_c2 = this.phone3Input) ? void 0 : _c2.value) || ""}`;
          this.phoneHiddenInput &&
            (!fullPhone || fullPhone.length < 10 || fullPhone.length > 11
              ? this.showAlert(
                this.phoneHiddenInput,
                "電話番号は10〜11桁の数字で入力してください",
              )
              : (this.hideAlert(
                this.phoneHiddenInput,
                "電話番号は10〜11桁の数字で入力してください",
              ),
                this.hideAlert(
                  this.phoneHiddenInput,
                  this.ERROR_MESSAGES.REQUIRED,
                )));
        };
      (null == (_a = this.phone1Input) ||
        _a.addEventListener("input", () => {
          (updatePhone(), validatePhone());
        }),
        null == (_b = this.phone2Input) ||
        _b.addEventListener("input", () => {
          (updatePhone(), validatePhone());
        }),
        null == (_c = this.phone3Input) ||
        _c.addEventListener("input", () => {
          (updatePhone(), validatePhone());
        }),
        null == (_d = this.phone1Input) ||
        _d.addEventListener("blur", validatePhone),
        null == (_e = this.phone2Input) ||
        _e.addEventListener("blur", validatePhone),
        null == (_f = this.phone3Input) ||
        _f.addEventListener("blur", validatePhone));
      const emailInput = this.formElement.querySelector("#email");
      (emailInput &&
        (emailInput.addEventListener("input", (event) => {
          this.validateEmail({ target: event.target });
        }),
          emailInput.addEventListener("blur", (event) => {
            this.validateEmail({ target: event.target });
          })),
        this.setupValidationFromAttributes(),
        (this.__editButtonListener = this.handleEditButtonClick.bind(this)),
        null == (_g = this.editButton) ||
        _g.addEventListener("click", this.__editButtonListener),
        null == (_h = this.cancelButton) ||
        _h.addEventListener("click", () => {
          (this.close(), this.config.onCancel && this.config.onCancel());
        }),
        null == (_i = this.submitButton) ||
        _i.addEventListener("click", () => {
          this.handleSubmit();
        }),
        null == (_j = this.modal) || _j.addEventListener("close", () => { }),
        null == (_k = this.modal) ||
        _k.addEventListener("cancel", (e) => {
          e.preventDefault();
        }));
      const postalCodeLookupBtn =
        null == (_l = this.modal)
          ? void 0
          : _l.querySelector("#customer-postal-code-lookup-btn");
      if (postalCodeLookupBtn) {
        const handleLookup = async () => {
          var _a2;
          const zipInput =
            null == (_a2 = this.formElement)
              ? void 0
              : _a2.querySelector("#zip");
          zipInput && (await this.handlePostalCodeLookup(zipInput.value));
        };
        (postalCodeLookupBtn.addEventListener("click", handleLookup),
          (postalCodeLookupBtn._handlePostalCodeLookup = handleLookup));
      }
    }
    removeEventListeners() {
      this.editButton &&
        this.__editButtonListener &&
        this.editButton.removeEventListener("click", this.__editButtonListener);
    }
    handleEditButtonClick() {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
      let addressData;
      if (this.currentCustomer)
        addressData = this.convertCustomerToRegistrationData(
          this.currentCustomer,
        );
      else if (this.restoredCustomerData)
        addressData = this.restoredCustomerData;
      else {
        const customer = CustomerService.getCustomerFromGlobal();
        if (customer) {
          const defaultAddress =
            customer.default_address ||
            (null == (_a = customer.addresses) ? void 0 : _a[0]);
          if (defaultAddress) {
            const addressCustomData =
              (null ==
                (_e =
                  null ==
                    (_d =
                      null ==
                        (_c =
                          null == (_b = window.BINDEC_SHIPPING_SETTINGS)
                            ? void 0
                            : _b.addressCustomData)
                        ? void 0
                        : _c.addresses)
                    ? void 0
                    : _d.find((item) => "customer" === item.id))
                ? void 0
                : _e.data) || {},
              phone = defaultAddress.phone || "";
            let phone_1 = "",
              phone_2 = "",
              phone_3 = "";
            if (phone) {
              const phoneClean = phone.replace(/-/g, "").replace(/\s/g, "");
              11 === phoneClean.length && phoneClean.startsWith("0")
                ? ((phone_1 = phoneClean.substring(0, 3)),
                  (phone_2 = phoneClean.substring(3, 7)),
                  (phone_3 = phoneClean.substring(7, 11)))
                : 10 === phoneClean.length && phoneClean.startsWith("0")
                  ? ((phone_1 = phoneClean.substring(0, 2)),
                    (phone_2 = phoneClean.substring(2, 6)),
                    (phone_3 = phoneClean.substring(6, 10)))
                  : ((phone_1 = phoneClean.substring(
                    0,
                    Math.min(5, phoneClean.length - 8),
                  )),
                    (phone_2 = phoneClean.substring(
                      phone_1.length,
                      phone_1.length + 4,
                    )),
                    (phone_3 = phoneClean.substring(phone_1.length + 4)));
            }
            const settingsCustomer =
              null == (_f = window.BINDEC_SHIPPING_SETTINGS)
                ? void 0
                : _f.customer,
              townFromSettings = undefined,
              town =
                (null ==
                  (_g =
                    null == settingsCustomer ? void 0 : settingsCustomer.address)
                  ? void 0
                  : _g.town) ||
                addressCustomData.town ||
                "",
              companyFromSettings =
                null == settingsCustomer ? void 0 : settingsCustomer.company,
              departmentFromSettings =
                null == settingsCustomer ? void 0 : settingsCustomer.department,
              company = companyFromSettings || defaultAddress.company || "",
              jobTitle =
                departmentFromSettings || addressCustomData.job_title || "";
            addressData = {
              first_name: defaultAddress.first_name || "",
              last_name: defaultAddress.last_name || "",
              first_name_kana: addressCustomData.first_name_kana || "",
              last_name_kana: addressCustomData.last_name_kana || "",
              email: customer.email || "",
              zip:
                (null == (_h = defaultAddress.zip)
                  ? void 0
                  : _h.replace(/-/g, "")) || "",
              province: defaultAddress.province || "",
              city: defaultAddress.city || "",
              town: town || "",
              address1: defaultAddress.address1 || "",
              address2: defaultAddress.address2 || "",
              phone: phone,
              phone_1: phone_1,
              phone_2: phone_2,
              phone_3: phone_3,
              company: company,
              jobTitle: jobTitle,
            };
          }
        }
      }
      this.open({
        type: addressData ? "edit" : "add",
        addressId:
          null ==
            (_j = null == (_i = this.currentCustomer) ? void 0 : _i.customerId)
            ? void 0
            : _j.toString(),
        address: this.restoredCustomerData || addressData,
        onSuccess: (data) => {
          const departmentElement = this.querySelector(
            ".js-selected-department",
          ),
            nameElement = this.querySelector(".js-selected-customer"),
            addressElement = this.querySelector(
              ".js-selected-customer-address",
            ),
            phoneElement = this.querySelector(".js-selected-customer-phone");
          (departmentElement &&
            (departmentElement.textContent = `${data.company || ""} ${data.jobTitle || ""}`),
            nameElement &&
            (nameElement.textContent = `${data.last_name} ${data.first_name}`));
          const nameKanaElement = this.querySelector(
            ".js-selected-customer-kana",
          );
          if (nameKanaElement) {
            const kana =
              `${data.last_name_kana || ""} ${data.first_name_kana || ""}`.trim();
            nameKanaElement.textContent = kana || "";
          }
          (addressElement &&
            (addressElement.textContent = `住所：〒 ${data.zip} ${convertProvinceToJapanese(data.province)} ${data.city} ${data.address1}${data.address2 ? ` ${data.address2}` : ""}`),
            phoneElement &&
            (phoneElement.textContent = `電話番号：${getDisplayPhone(data)}`));
        },
        onCancel: () => { },
      });
    }
    parseErrorMessage(error, defaultMessage) {
      return error.includes(":") ? error.split(":")[1].trim() : defaultMessage;
    }
    mapErrorToField(error, isJapanese) {
      const getFieldByName = (name) =>
        this.formElement.elements.namedItem(name),
        getFieldByDataAttribute = (fieldName) =>
          this.formElement.querySelector(`[data-cf-field="${fieldName}"]`),
        fieldMappings = [
          {
            keywords: ["姓"],
            getField: () => {
              const fieldName = isJapanese ? "last_name" : "first_name";
              return (
                getFieldByDataAttribute(fieldName) || getFieldByName(fieldName)
              );
            },
            defaultMessage: this.ERROR_MESSAGES.REQUIRED,
          },
          {
            keywords: ["名"],
            getField: () => {
              if (error.includes("姓")) return null;
              const fieldName = isJapanese ? "first_name" : "last_name";
              return (
                getFieldByDataAttribute(fieldName) || getFieldByName(fieldName)
              );
            },
            defaultMessage: this.ERROR_MESSAGES.REQUIRED,
          },
          {
            keywords: ["メールアドレス", "EMAIL"],
            getField: () =>
              getFieldByDataAttribute("email") || getFieldByName("email"),
            defaultMessage: this.ERROR_MESSAGES.EMAIL,
          },
          {
            keywords: ["郵便番号"],
            getField: () =>
              getFieldByDataAttribute("zip") || getFieldByName("zip"),
            defaultMessage: error,
          },
          {
            keywords: ["都道府県"],
            getField: () =>
              getFieldByDataAttribute("province") || getFieldByName("province"),
            defaultMessage: this.ERROR_MESSAGES.REQUIRED,
          },
          {
            keywords: ["市区町村"],
            getField: () =>
              getFieldByDataAttribute("city") || getFieldByName("city"),
            defaultMessage: this.ERROR_MESSAGES.REQUIRED,
          },
          {
            keywords: ["町域"],
            getField: () =>
              getFieldByDataAttribute("town") || getFieldByName("town"),
            defaultMessage: error,
          },
          {
            keywords: ["番地以降"],
            getField: () =>
              getFieldByDataAttribute("address1") || getFieldByName("address1"),
            defaultMessage: this.ERROR_MESSAGES.REQUIRED,
          },
          {
            keywords: ["建物名"],
            getField: () =>
              getFieldByDataAttribute("address2") || getFieldByName("address2"),
            defaultMessage: error,
          },
          {
            keywords: ["電話番号"],
            getField: () => {
              const phoneField = getFieldByDataAttribute("phone");
              return phoneField || this.phone1Input;
            },
            defaultMessage: error,
          },
        ];
      for (const mapping of fieldMappings)
        if (mapping.keywords.some((keyword) => error.includes(keyword))) {
          const input = mapping.getField();
          if (input)
            return {
              input: input,
              message: this.parseErrorMessage(error, mapping.defaultMessage),
            };
        }
      return { input: null, message: error };
    }
    async handleSubmit() {
      var _a, _b;
      const validation = this.validateForm();
      if (!validation.isValid) {
        const isJapanese =
          "ja" === document.documentElement.lang ||
          "ja" ===
          (null == (_b = null == (_a = window.Shopify) ? void 0 : _a.locale)
            ? void 0
            : _b.primaryLocale);
        for (const error of validation.errors) {
          const { input: input, message: message } = this.mapErrorToField(
            error,
            isJapanese,
          );
          if (input) this.showAlert(input, message);
          else {
            const firstInvalid = this.formElement.querySelector(
              "input:invalid, select:invalid",
            );
            firstInvalid && this.showAlert(firstInvalid, error);
          }
        }
        const firstError = this.formElement.querySelector(".form-field-error");
        return void (
          firstError &&
          firstError.scrollIntoView({ behavior: "smooth", block: "center" })
        );
      }
      const formData = this.getFormData();
      formData
        ? await this.saveAddress(formData)
        : alert("データの取得に失敗しました");
    }
    getFormData() {
      var _a, _b;
      if (!this.formElement) return null;
      const formData = new FormData(this.formElement),
        isJapanese =
          "ja" === document.documentElement.lang ||
          "ja" ===
          (null == (_b = null == (_a = window.Shopify) ? void 0 : _a.locale)
            ? void 0
            : _b.primaryLocale),
        phone1 = formData.get("phone_1") || "",
        phone2 = formData.get("phone_2") || "",
        phone3 = formData.get("phone_3") || "",
        phone = `${phone1}-${phone2}-${phone3}`,
        firstNameField = isJapanese ? "first_name" : "first_name",
        lastNameField = isJapanese ? "last_name" : "last_name";
      return {
        first_name: formData.get(firstNameField),
        last_name: formData.get(lastNameField),
        first_name_kana: formData.get("first_name_kana") || "",
        last_name_kana: formData.get("last_name_kana") || "",
        email: formData.get("email"),
        zip: formData.get("zip"),
        province: formData.get("province"),
        city: formData.get("city"),
        town: formData.get("town") || void 0,
        address1: formData.get("address1"),
        address2: formData.get("address2") || void 0,
        phone: phone,
        phone_1: phone1,
        phone_2: phone2,
        phone_3: phone3,
        company: formData.get("company") || void 0,
        jobTitle: formData.get("department") || void 0,
      };
    }
    validateForm() {
      var _a, _b, _c, _d, _e, _f, _g;
      const errors = [];
      if (!this.formElement)
        return (
          errors.push("フォームが見つかりません"),
          { isValid: !1, errors: errors }
        );
      if (!this.formElement.checkValidity()) {
        const isJapanese2 =
          "ja" === document.documentElement.lang ||
          "ja" ===
          (null == (_b = null == (_a = window.Shopify) ? void 0 : _a.locale)
            ? void 0
            : _b.primaryLocale),
          firstNameField2 = undefined,
          lastNameField2 = undefined,
          requiredFields = undefined;
        [
          { name: isJapanese2 ? "last_name" : "first_name", label: "姓" },
          { name: isJapanese2 ? "first_name" : "last_name", label: "名" },
          { name: "last_name_kana", label: "氏名カナ（セイ）" },
          { name: "first_name_kana", label: "氏名カナ（メイ）" },
          { name: "email", label: "メールアドレス" },
          { name: "zip", label: "郵便番号" },
          { name: "province", label: "都道府県" },
          { name: "city", label: "市区町村" },
          { name: "address1", label: "番地以降" },
        ].forEach((field) => {
          const input = this.formElement.elements.namedItem(field.name);
          input &&
            !input.value.trim() &&
            errors.push(`${field.label}を入力してください`);
        });
      }
      const zipInput = this.formElement.elements.namedItem("zip");
      if (zipInput && zipInput.value) {
        const zipValue = zipInput.value.replace(/-/g, "");
        /^[0-9]*$/.test(zipValue)
          ? 7 !== zipValue.length &&
          errors.push("郵便番号は7桁の数字で入力してください")
          : errors.push(`郵便番号: ${this.ERROR_MESSAGES.INT}`);
      }
      const phone1 =
        (null == (_c = this.phone1Input) ? void 0 : _c.value) || "",
        phone2 = (null == (_d = this.phone2Input) ? void 0 : _d.value) || "",
        phone3 = (null == (_e = this.phone3Input) ? void 0 : _e.value) || "",
        fullPhone = `${phone1}${phone2}${phone3}`,
        allPhoneParts = undefined;
      ([phone1, phone2, phone3]
        .filter((p) => p)
        .forEach((part) => {
          part &&
            !/^[0-9]*$/.test(part) &&
            errors.push(`電話番号: ${this.ERROR_MESSAGES.INT}`);
        }),
        (!fullPhone || fullPhone.length < 10 || fullPhone.length > 11) &&
        errors.push("電話番号は10〜11桁の数字で入力してください"));
      const emailInput = this.formElement.elements.namedItem("email");
      if (emailInput && emailInput.value) {
        const email = emailInput.value.trim();
        /^[^\s@]+@[^\s@]+\.[^\s@]+(\.[^\s@]+)*$/.test(email) ||
          errors.push(this.ERROR_MESSAGES.EMAIL);
      }
      const isJapanese =
        "ja" === document.documentElement.lang ||
        "ja" ===
        (null == (_g = null == (_f = window.Shopify) ? void 0 : _f.locale)
          ? void 0
          : _g.primaryLocale),
        firstNameField = undefined,
        lastNameField = undefined,
        fullWidthFields = undefined,
        fullWidthPattern =
          /^[\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\uFF01-\uFF60\uFFA0-\uFFEF\u4E00-\u9FAF\u3400-\u4DBF\uF900-\uFAFF]+$/;
      [
        { name: isJapanese ? "last_name" : "first_name", label: "姓" },
        { name: isJapanese ? "first_name" : "last_name", label: "名" },
        { name: "city", label: "市区町村" },
        { name: "town", label: "町域" },
        { name: "address1", label: "番地以降" },
      ].forEach((field) => {
        const input = this.formElement.elements.namedItem(field.name);
        input &&
          input.value &&
          !fullWidthPattern.test(input.value) &&
          errors.push(`${field.label}: ${this.ERROR_MESSAGES.FULL_WIDTH}`);
      });
      const kanaPattern = /^[ァ-ンヴー]*$/,
        kanaFields = undefined;
      return (
        [
          { name: "last_name_kana", label: "氏名カナ（セイ）" },
          { name: "first_name_kana", label: "氏名カナ（メイ）" },
        ].forEach((field) => {
          const input = this.formElement.elements.namedItem(field.name);
          input &&
            input.value &&
            !kanaPattern.test(input.value) &&
            errors.push(
              `${field.label}: ${this.ERROR_MESSAGES.FULL_WIDTH_KANA}`,
            );
        }),
        { isValid: 0 === errors.length, errors: errors }
      );
    }
    validateEmail(event) {
      const input = event.target,
        email = input.value.trim();
      ((this.emailValue = email),
        "" === email || /^[^\s@]+@[^\s@]+\.[^\s@]+(\.[^\s@]+)*$/.test(email)
          ? this.hideAlert(input, this.ERROR_MESSAGES.EMAIL)
          : this.showAlert(input, this.ERROR_MESSAGES.EMAIL));
    }
    showAlert(input, message) {
      var _a, _b;
      const fieldName =
        input.getAttribute("data-cf-field") || input.getAttribute("name");
      if (!fieldName) return;
      let errorContainer = this.formElement.querySelector(
        `.cf-error[data-cf-field="${fieldName}"]`,
      );
      (errorContainer ||
        (errorContainer =
          null == (_a = input.parentElement)
            ? void 0
            : _a.querySelector(`.cf-error[data-cf-field="${fieldName}"]`)),
        errorContainer ||
        ((errorContainer = document.createElement("div")),
          (errorContainer.className = "cf-error form-field-error"),
          errorContainer.setAttribute("data-cf-field", fieldName),
          (errorContainer.style.display = "none"),
          null == (_b = input.parentElement) ||
          _b.appendChild(errorContainer)));
      const existingMessages =
        errorContainer.querySelectorAll(".cf-error-message"),
        messageExists = undefined;
      if (
        !Array.from(existingMessages).some((msg) => msg.textContent === message)
      ) {
        const messageElement = document.createElement("div");
        ((messageElement.className = "cf-error-message"),
          (messageElement.textContent = message),
          errorContainer.appendChild(messageElement));
      }
      ((errorContainer.style.display = "block"),
        input.setAttribute("aria-invalid", "true"));
    }
    hideAlert(input, message) {
      var _a;
      const fieldName =
        input.getAttribute("data-cf-field") || input.getAttribute("name");
      if (!fieldName) return;
      let errorContainer = this.formElement.querySelector(
        `.cf-error[data-cf-field="${fieldName}"]`,
      );
      if (
        (errorContainer ||
          (errorContainer =
            null == (_a = input.parentElement)
              ? void 0
              : _a.querySelector(`.cf-error[data-cf-field="${fieldName}"]`)),
          errorContainer)
      ) {
        if (message) {
          const messages = undefined;
          errorContainer
            .querySelectorAll(".cf-error-message")
            .forEach((msg) => {
              msg.textContent === message && msg.remove();
            });
        } else errorContainer.innerHTML = "";
        0 === errorContainer.children.length &&
          (errorContainer.style.display = "none");
      }
      input.removeAttribute("aria-invalid");
    }
    validateRequired(event) {
      const input = event.target,
        value = "checkbox" === input.type ? input.checked : input.value;
      input.disabled ||
        ("" === value || !1 === value
          ? this.showAlert(input, this.ERROR_MESSAGES.REQUIRED)
          : this.hideAlert(input, this.ERROR_MESSAGES.REQUIRED));
    }
    validateInt(event) {
      const input = event.target,
        value = input.value;
      "" === value || /^[0-9]*$/.test(value)
        ? this.hideAlert(input, this.ERROR_MESSAGES.INT)
        : this.showAlert(input, this.ERROR_MESSAGES.INT);
    }
    validateZip(event) {
      const input = event.target,
        value = undefined,
        zipValue = input.value.replace(/-/g, "");
      if ("" === zipValue)
        return (
          this.hideAlert(input, this.ERROR_MESSAGES.INT),
          void this.hideAlert(input, "郵便番号は7桁の数字で入力してください")
        );
      /^[0-9]*$/.test(zipValue)
        ? (this.hideAlert(input, this.ERROR_MESSAGES.INT),
          7 !== zipValue.length
            ? this.showAlert(input, "郵便番号は7桁の数字で入力してください")
            : (this.hideAlert(input, "郵便番号は7桁の数字で入力してください"),
              this.hideAlert(input, this.ERROR_MESSAGES.REQUIRED),
              this.hideAlert(input, "郵便番号を入力してください"),
              this.validateRequired({ target: input })))
        : (this.showAlert(input, this.ERROR_MESSAGES.INT),
          this.hideAlert(input, "郵便番号は7桁の数字で入力してください"));
    }
    validateHalfWidth(event) {
      const input = event.target,
        value = input.value;
      "" === value || /^[\x01-\x7E\uFF61-\uFF9F]*$/.test(value)
        ? this.hideAlert(input, this.ERROR_MESSAGES.HALF_WIDTH)
        : this.showAlert(input, this.ERROR_MESSAGES.HALF_WIDTH);
    }
    setupValidationFromAttributes() {
      const inputsWithValidation = undefined;
      this.formElement
        .querySelectorAll(
          "input[data-cf-validation], select[data-cf-validation]",
        )
        .forEach((input) => {
          const validationType = input.getAttribute("data-cf-validation");
          input.addEventListener("blur", (event) => {
            const target = event.target;
            switch (validationType) {
              case "full-width":
                this.validateFullWidth({ target: target });
                break;
              case "full-width-kana":
                this.validateFullWidthKana({ target: target });
                break;
              case "half-width":
                this.validateHalfWidth({ target: target });
                break;
              case "int":
                this.validateInt({ target: target });
                break;
              case "zip":
                this.validateZip({ target: target });
                break;
              case "email":
                this.validateEmail({ target: target });
            }
          });
        });
      const requiredInputs = undefined;
      this.formElement
        .querySelectorAll(
          "input[data-cf-required='true'], select[data-cf-required='true']",
        )
        .forEach((input) => {
          input.addEventListener("blur", (event) => {
            this.validateRequired({ target: event.target });
          });
        });
    }
    validateFullWidth(event) {
      const input = event.target,
        value = input.value,
        fullWidthPattern = undefined;
      "" !== value &&
        (/^[\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\uFF01-\uFF60\uFFA0-\uFFEF\u4E00-\u9FAF\u3400-\u4DBF\uF900-\uFAFF]+$/.test(
          value,
        )
          ? this.hideAlert(input, this.ERROR_MESSAGES.FULL_WIDTH)
          : this.showAlert(input, this.ERROR_MESSAGES.FULL_WIDTH));
    }
    validateFullWidthKana(event) {
      const input = event.target,
        value = input.value;
      "" !== value &&
        (/^[ァ-ンヴー]*$/.test(value)
          ? this.hideAlert(input, this.ERROR_MESSAGES.FULL_WIDTH_KANA)
          : this.showAlert(input, this.ERROR_MESSAGES.FULL_WIDTH_KANA));
    }
    async handlePostalCodeLookup(postalCode) {
      var _a, _b, _c, _d;
      const messageEl =
        null == (_a = this.modal)
          ? void 0
          : _a.querySelector("#customer-postal-code-lookup-message"),
        lookupBtn =
          null == (_b = this.modal)
            ? void 0
            : _b.querySelector("#customer-postal-code-lookup-btn");
      if (!postalCode || 7 !== postalCode.replace(/-/g, "").length)
        return void (
          messageEl &&
          ((messageEl.textContent = "郵便番号は7桁の数字で入力してください"),
            (messageEl.style.display = "block"),
            (messageEl.className = "postal-code-lookup-message error"))
        );
      (lookupBtn &&
        ((lookupBtn.disabled = !0), (lookupBtn.textContent = "検索中...")),
        messageEl && (messageEl.style.display = "none"));
      const result = await lookupPostalCode(postalCode);
      if (
        (lookupBtn &&
          ((lookupBtn.disabled = !1), (lookupBtn.textContent = "住所検索")),
          result)
      ) {
        this.provinceSelect && (this.provinceSelect.value = result.pref);
        const cityInput =
          null == (_c = this.formElement)
            ? void 0
            : _c.querySelector("#city"),
          townInput =
            null == (_d = this.formElement)
              ? void 0
              : _d.querySelector("#town");
        (cityInput && (cityInput.value = result.city),
          townInput && (townInput.value = result.town),
          messageEl && (messageEl.style.display = "none"));
      } else
        messageEl &&
          ((messageEl.textContent = "該当する住所が見つかりませんでした"),
            (messageEl.style.display = "block"),
            (messageEl.className = "postal-code-lookup-message error"));
    }
    fillForm(data) {
      var _a, _b;
      if (!this.formElement) return;
      const isJapanese =
        "ja" === document.documentElement.lang ||
        "ja" ===
        (null == (_b = null == (_a = window.Shopify) ? void 0 : _a.locale)
          ? void 0
          : _b.primaryLocale),
        firstNameField = isJapanese ? "last_name" : "first_name",
        lastNameField = isJapanese ? "first_name" : "last_name",
        firstNameInput = this.formElement.querySelector("#first-name"),
        lastNameInput = this.formElement.querySelector("#last-name"),
        lastNameKanaInput = this.formElement.querySelector("#last-name-kana"),
        firstNameKanaInput = this.formElement.querySelector("#first-name-kana"),
        emailInput = this.formElement.querySelector("#email"),
        zipInput = this.formElement.querySelector("#zip"),
        cityInput = this.formElement.querySelector("#city"),
        townInput = this.formElement.querySelector("#town"),
        address1Input = this.formElement.querySelector("#address1"),
        address2Input = this.formElement.querySelector("#address2"),
        companyInput = this.formElement.querySelector("#company"),
        departmentInput = this.formElement.querySelector("#department");
      if (
        (firstNameInput &&
          (firstNameInput.value = isJapanese
            ? data.last_name || ""
            : data.first_name || ""),
          lastNameInput &&
          (lastNameInput.value = isJapanese
            ? data.first_name || ""
            : data.last_name || ""),
          lastNameKanaInput &&
          (lastNameKanaInput.value = data.last_name_kana || ""),
          firstNameKanaInput &&
          (firstNameKanaInput.value = data.first_name_kana || ""),
          emailInput && (emailInput.value = data.email || ""),
          zipInput && (zipInput.value = data.zip || ""),
          this.provinceSelect &&
          (this.provinceSelect.value = data.province || ""),
          cityInput && (cityInput.value = data.city || ""),
          townInput && (townInput.value = data.town || ""),
          address1Input && (address1Input.value = data.address1 || ""),
          address2Input && (address2Input.value = data.address2 || ""),
          companyInput && (companyInput.value = data.company || ""),
          departmentInput && (departmentInput.value = data.jobTitle || ""),
          data.phone)
      ) {
        const phone = data.phone.replace(/-/g, "").replace(/\s/g, "");
        phone.length >= 10 &&
          (11 === phone.length && phone.startsWith("0")
            ? (this.phone1Input &&
              (this.phone1Input.value = phone.substring(0, 3)),
              this.phone2Input &&
              (this.phone2Input.value = phone.substring(3, 7)),
              this.phone3Input &&
              (this.phone3Input.value = phone.substring(7, 11)))
            : 10 === phone.length && phone.startsWith("0")
              ? (this.phone1Input &&
                (this.phone1Input.value = phone.substring(0, 2)),
                this.phone2Input &&
                (this.phone2Input.value = phone.substring(2, 6)),
                this.phone3Input &&
                (this.phone3Input.value = phone.substring(6, 10)))
              : (this.phone1Input &&
                (this.phone1Input.value = phone.substring(
                  0,
                  Math.min(5, phone.length - 8),
                )),
                this.phone2Input &&
                (this.phone2Input.value = phone.substring(
                  this.phone1Input.value.length,
                  this.phone1Input.value.length + 4,
                )),
                this.phone3Input &&
                (this.phone3Input.value = phone.substring(
                  this.phone1Input.value.length + 4,
                ))));
      } else
        (data.phone_1 || data.phone_2 || data.phone_3) &&
          (this.phone1Input && (this.phone1Input.value = data.phone_1 || ""),
            this.phone2Input && (this.phone2Input.value = data.phone_2 || ""),
            this.phone3Input && (this.phone3Input.value = data.phone_3 || ""));
      if (
        this.phone1Input &&
        this.phone2Input &&
        this.phone3Input &&
        this.phoneHiddenInput
      ) {
        const phone1 = this.phone1Input.value || "",
          phone2 = this.phone2Input.value || "",
          phone3 = this.phone3Input.value || "";
        this.phoneHiddenInput.value = `${phone1}-${phone2}-${phone3}`;
      }
    }
    clearForm() {
      if (this.formElement) {
        this.formElement.reset();
        const existingError = this.formElement.querySelector(
          ".form-error-message",
        );
        existingError && existingError.remove();
      }
    }
    async saveAddress(data) {
      try {
        const multiShippingSettings = document.querySelector(
          "multi-shipping-settings",
        );
        if (!multiShippingSettings)
          throw new Error("multi-shipping-settings要素が見つかりません");
        const customerInfo = CustomerService.getCustomerBasicInfo(),
          customerId = CustomerService.getCustomerId() || "guest",
          shopifyCustomerId = CustomerService.getShopifyCustomerId() || "",
          existingCustomer = this.currentCustomer || {},
          customer = {
            customerId: existingCustomer.customerId || customerId || "guest",
            shopifyCustomerId:
              existingCustomer.shopifyCustomerId || shopifyCustomerId || "",
            name: `${data.last_name} ${data.first_name}`.trim(),
            firstName: data.first_name,
            lastName: data.last_name,
            firstNameKana: data.first_name_kana || null,
            lastNameKana: data.last_name_kana || null,
            email:
              data.email || existingCustomer.email || customerInfo.email || "",
            phone: data.phone,
            address: {
              zipcode: data.zip,
              prefecture: data.province,
              city: data.city,
              town: data.town || null,
              address1: data.address1,
              address2: data.address2 || null,
            },
            company: data.company || null,
            department: data.jobTitle || null,
            memberRank: existingCustomer.memberRank || null,
            ritsCardNumber: existingCustomer.ritsCardNumber || null,
            purpose: existingCustomer.purpose || null,
            orderFormPrintStatus: existingCustomer.orderFormPrintStatus || null,
            receiptPrintStatus: existingCustomer.receiptPrintStatus || null,
            purchaseDetailPrintStatus:
              existingCustomer.purchaseDetailPrintStatus || null,
            defaultAddressId: existingCustomer.defaultAddressId || null,
          };
        if ("function" == typeof multiShippingSettings.updateCustomer) {
          multiShippingSettings.updateCustomer(customer);
          const updatedSettings = multiShippingSettings.getSettings();
        } else {
          const currentSettings = multiShippingSettings.getSettings() || {};
          multiShippingSettings.settings = {
            ...currentSettings,
            customer: customer,
          };
        }
        if (window.BINDEC_SHIPPING_SETTINGS) {
          window.BINDEC_SHIPPING_SETTINGS.addressCustomData ||
            (window.BINDEC_SHIPPING_SETTINGS.addressCustomData = {
              addresses: [],
            });
          const customData = {
            town: data.town,
            phone_1: data.phone_1,
            phone_2: data.phone_2,
            phone_3: data.phone_3,
            job_title: data.jobTitle,
            first_name_kana: data.first_name_kana || "",
            last_name_kana: data.last_name_kana || "",
          },
            customerCustomDataId = "customer",
            tempAddressId = Date.now(),
            existingIndex =
              window.BINDEC_SHIPPING_SETTINGS.addressCustomData.addresses.findIndex(
                (item) => item.id === customerCustomDataId,
              );
          -1 !== existingIndex
            ? (window.BINDEC_SHIPPING_SETTINGS.addressCustomData.addresses[
              existingIndex
            ].data = customData)
            : window.BINDEC_SHIPPING_SETTINGS.addressCustomData.addresses.push({
              id: customerCustomDataId,
              data: customData,
            });
        }
        ((this.currentCustomer = customer),
          this.config.onSuccess && this.config.onSuccess(data),
          this.close());
      } catch (error) {
        alert("住所の保存に失敗しました。もう一度お試しください。");
      }
    }
    restoreCustomer(customer) {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
      if (!customer) return;
      this.currentCustomer = customer;
      const departmentElement = this.querySelector(".js-selected-department"),
        nameElement = this.querySelector(".js-selected-customer"),
        addressElement = this.querySelector(".js-selected-customer-address"),
        phoneElement = this.querySelector(".js-selected-customer-phone");
      (departmentElement &&
        (departmentElement.textContent = `${customer.company || ""} ${customer.department || ""}`),
        nameElement &&
        (nameElement.textContent =
          `${customer.lastName || ""} ${customer.firstName || ""}`.trim() ||
          "注文者情報を登録してください"));
      const nameKanaElement = this.querySelector(".js-selected-customer-kana");
      if (nameKanaElement) {
        const kana =
          `${customer.lastNameKana || ""} ${customer.firstNameKana || ""}`.trim();
        nameKanaElement.textContent = kana || "";
      }
      (addressElement &&
        customer.address &&
        (addressElement.textContent = `住所：〒 ${customer.address.zipcode || ""} ${convertProvinceToJapanese(customer.address.prefecture || "")} ${customer.address.city || ""} ${customer.address.address1 || ""} ${customer.address.address2 || ""}`),
        phoneElement &&
        (phoneElement.textContent = `電話番号：${getDisplayPhone(customer)}`));
      const isAuthenticated = undefined;
      if (!CustomerService.isAuthenticated() && customer) {
        const phone = customer.phone || "";
        let phone_1 = "",
          phone_2 = "",
          phone_3 = "";
        if (phone) {
          const phoneClean = phone.replace(/-/g, "").replace(/\s/g, "");
          11 === phoneClean.length && phoneClean.startsWith("0")
            ? ((phone_1 = phoneClean.substring(0, 3)),
              (phone_2 = phoneClean.substring(3, 7)),
              (phone_3 = phoneClean.substring(7, 11)))
            : 10 === phoneClean.length && phoneClean.startsWith("0")
              ? ((phone_1 = phoneClean.substring(0, 2)),
                (phone_2 = phoneClean.substring(2, 6)),
                (phone_3 = phoneClean.substring(6, 10)))
              : ((phone_1 = phoneClean.substring(
                0,
                Math.min(5, phoneClean.length - 8),
              )),
                (phone_2 = phoneClean.substring(
                  phone_1.length,
                  phone_1.length + 4,
                )),
                (phone_3 = phoneClean.substring(phone_1.length + 4)));
        }
        const addressCustomData =
          (null ==
            (_d =
              null ==
                (_c =
                  null ==
                    (_b =
                      null == (_a = window.BINDEC_SHIPPING_SETTINGS)
                        ? void 0
                        : _a.addressCustomData)
                    ? void 0
                    : _b.addresses)
                ? void 0
                : _c.find((item) => "customer" === item.id))
            ? void 0
            : _d.data) || {},
          town =
            (null == (_e = customer.address) ? void 0 : _e.town) ||
            addressCustomData.town ||
            "";
        this.restoredCustomerData = {
          first_name: customer.firstName || "",
          last_name: customer.lastName || "",
          first_name_kana: customer.firstNameKana || "",
          last_name_kana: customer.lastNameKana || "",
          email: customer.email || "",
          zip: (null == (_f = customer.address) ? void 0 : _f.zipcode) || "",
          province:
            (null == (_g = customer.address) ? void 0 : _g.prefecture) || "",
          city: (null == (_h = customer.address) ? void 0 : _h.city) || "",
          town: town,
          address1:
            (null == (_i = customer.address) ? void 0 : _i.address1) || "",
          address2:
            (null == (_j = customer.address) ? void 0 : _j.address2) || "",
          phone: phone,
          phone_1: phone_1,
          phone_2: phone_2,
          phone_3: phone_3,
          company: customer.company || "",
          jobTitle: customer.department || "",
        };
      } else this.restoredCustomerData = null;
    }
    convertCustomerToRegistrationData(customer) {
      var _a, _b, _c, _d, _e;
      if (!customer || !customer.address) return;
      const phone = customer.phone || "";
      let phone_1 = "",
        phone_2 = "",
        phone_3 = "";
      if (phone) {
        const phoneClean = phone.replace(/-/g, "").replace(/\s/g, "");
        11 === phoneClean.length && phoneClean.startsWith("0")
          ? ((phone_1 = phoneClean.substring(0, 3)),
            (phone_2 = phoneClean.substring(3, 7)),
            (phone_3 = phoneClean.substring(7, 11)))
          : 10 === phoneClean.length && phoneClean.startsWith("0")
            ? ((phone_1 = phoneClean.substring(0, 2)),
              (phone_2 = phoneClean.substring(2, 6)),
              (phone_3 = phoneClean.substring(6, 10)))
            : ((phone_1 = phoneClean.substring(
              0,
              Math.min(5, phoneClean.length - 8),
            )),
              (phone_2 = phoneClean.substring(
                phone_1.length,
                phone_1.length + 4,
              )),
              (phone_3 = phoneClean.substring(phone_1.length + 4)));
      }
      const addressCustomData =
        (null ==
          (_d =
            null ==
              (_c =
                null ==
                  (_b =
                    null == (_a = window.BINDEC_SHIPPING_SETTINGS)
                      ? void 0
                      : _a.addressCustomData)
                  ? void 0
                  : _b.addresses)
              ? void 0
              : _c.find((item) => "customer" === item.id))
          ? void 0
          : _d.data) || {},
        town =
          (null == (_e = customer.address) ? void 0 : _e.town) ||
          addressCustomData.town ||
          "";
      return {
        first_name: customer.firstName || "",
        last_name: customer.lastName || "",
        first_name_kana:
          customer.firstNameKana || addressCustomData.first_name_kana || "",
        last_name_kana:
          customer.lastNameKana || addressCustomData.last_name_kana || "",
        email: customer.email || "",
        zip: customer.address.zipcode || "",
        province: customer.address.prefecture || "",
        city: customer.address.city || "",
        town: town,
        address1: customer.address.address1 || "",
        address2: customer.address.address2 || "",
        phone: phone,
        phone_1: phone_1,
        phone_2: phone_2,
        phone_3: phone_3,
        company: customer.company || "",
        jobTitle: customer.department || addressCustomData.job_title || "",
      };
    }
    getCurrentCustomer() {
      return this.currentCustomer;
    }
    initializeCustomer() {
      setTimeout(() => {
        var _a, _b, _c;
        let customer = null;
        if (
          null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.customer
        )
          customer = window.BINDEC_SHIPPING_SETTINGS.customer;
        else {
          const multiShippingSettings = document.querySelector(
            "multi-shipping-settings",
          );
          if (
            multiShippingSettings &&
            "function" == typeof multiShippingSettings.getSettings
          )
            try {
              const settings = multiShippingSettings.getSettings();
              customer = null == settings ? void 0 : settings.customer;
            } catch (error) { }
        }
        customer &&
          (customer.firstName ||
            customer.lastName ||
            (null == (_b = customer.address) ? void 0 : _b.zipcode) ||
            (null == (_c = customer.address) ? void 0 : _c.address1)) &&
          this.restoreCustomer(customer);
      }, 0);
    }
  }
  customElements.define("customer-registration", CustomerRegistration);
}
if (!customElements.get("multi-shipping-settings")) {
  var selectors = {
    purposeSelect: "input[name='purpose']:checked",
    deliveryGroupsContainer: ".js-delivery-groups-container",
    addDeliveryGroupButton: ".js-add-delivery-group",
    newGroupTemplate: "template#new-group-template",
    homeDeliveryContainer: ".js-home-delivery-container",
    addHomeDestinationButton: ".js-add-home-destination",
    newDestinationTemplate: "template#new-destination-template",
  };
  const _MultiShippingSettings = class _MultiShippingSettings extends HTMLElement {
    constructor() {
      (super(),
        __publicField(this, "purpose"),
        __publicField(this, "settings"),
        __publicField(this, "purposeSelect"),
        __publicField(this, "addDeliveryGroupButton"),
        __publicField(this, "deliveryGroupsContainer"),
        __publicField(this, "newGroupTemplate"),
        __publicField(this, "homeDeliveryContainer"),
        __publicField(this, "addHomeDestinationButton"),
        __publicField(this, "newDestinationTemplate"),
        __publicField(this, "__addDeliveryGroupListener"),
        __publicField(this, "__changeListener"),
        __publicField(this, "__addDestinationListener"),
        __publicField(this, "__purposeChangeListener"),
        __publicField(this, "__addHomeDestinationListener"),
        __publicField(this, "__tabSwitchListener"),
        __publicField(this, "__copyDestinationExecuteListener"),
        __publicField(this, "__copyProductsExecuteListener"),
        __publicField(this, "__pageshowListener"),
        __publicField(this, "__pagehideListener"),
        __publicField(this, "__cartItemRemovedListener"),
        __publicField(this, "__cartItemUpdatedListener"),
        __publicField(this, "isRestoring", !1),
        __publicField(this, "isSyncing", !1),
        __publicField(this, "syncDebounceTimer", null),
        __publicField(this, "SYNC_DEBOUNCE_MS", 500),
        __publicField(this, "homeDestinations", []),
        __publicField(this, "currentOrderType", "gift"),
        (this.settings = { deliveryGroups: [] }),
        (this.addDeliveryGroupButton = this.querySelector(
          selectors.addDeliveryGroupButton,
        )),
        (this.deliveryGroupsContainer = this.querySelector(
          selectors.deliveryGroupsContainer,
        )),
        (this.newGroupTemplate = document.querySelector(
          selectors.newGroupTemplate,
        )),
        (this.purposeSelect = this.querySelector(selectors.purposeSelect)),
        (this.purpose = ""),
        (this.homeDeliveryContainer = this.querySelector(
          selectors.homeDeliveryContainer,
        )),
        (this.addHomeDestinationButton = this.querySelector(
          selectors.addHomeDestinationButton,
        )),
        (this.newDestinationTemplate = document.querySelector(
          selectors.newDestinationTemplate,
        )));
    }
    async connectedCallback() {
      const lastInitTime = sessionStorage.getItem("_multi_shipping_last_init"),
        lastCartUpdateTime = sessionStorage.getItem("_cart_last_updated"),
        now = Date.now(),
        DEBOUNCE_MS = 3e3,
        CART_UPDATE_GRACE_MS = 1e3;
      let currentCart,
        shouldInitialize = !0;
      if (lastInitTime) {
        const timeSinceLastInit = undefined;
        now - parseInt(lastInitTime, 10) < 3e3 && (shouldInitialize = !1);
      }
      if (lastCartUpdateTime && shouldInitialize) {
        const timeSinceCartUpdate = undefined;
        now - parseInt(lastCartUpdateTime, 10) < 1e3 && (shouldInitialize = !1);
      }
      (shouldInitialize
        ? ((currentCart = await cartCache.get(!0)),
          hasCartLevelDiscounts(currentCart) &&
          window.BINDEC_SHIPPING_SETTINGS &&
          (window.BINDEC_SHIPPING_SETTINGS.cart = currentCart),
          (currentCart = await this.unifyTokensInCart(currentCart)),
          (currentCart =
            await this._cleanupUnusedPropertiesFromCart(currentCart)),
          sessionStorage.setItem("_multi_shipping_last_init", now.toString()))
        : (currentCart = await cartCache.get()),
        await this.restoreFromSession());

      if(shouldInitialize) {
        try {
          updateCartAttributes({
            'deliveryDate_timeEntered': new Date().toLocaleString('ja-JP', {
              timeZone: 'Asia/Tokyo',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            }),
          }, false);

          console.log('日時モーダルからの属性保存に成功しました：　' + new Date().toLocaleString('ja-JP', {
            timeZone: 'Asia/Tokyo',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }));
          
          } catch(e) {
            console.error('日時モーダルからの属性保存に失敗しました：' + e);
          }
      }
      
      try {
        cartRecoveryService.isRecoveryInProgress() ||
          (currentCart.item_count > 0 &&
            this.purpose &&
            this._saveCartToSession(currentCart, this.purpose));
      } catch (error) { }
      ((this.__addDeliveryGroupListener = this.addDeliveryGroup.bind(this)),
        this.addDeliveryGroupButton.addEventListener(
          "click",
          this.__addDeliveryGroupListener,
        ),
        (this.__addHomeDestinationListener =
          this.addHomeDestination.bind(this)),
        this.addHomeDestinationButton.addEventListener(
          "click",
          this.__addHomeDestinationListener,
        ),
        (this.__addDestinationListener = this.addGroupDestination.bind(this)),
        (this.__purposeChangeListener = this.purposeChange.bind(this)),
        (this.__changeListener = this.change.bind(this)),
        this.addEventListener("settings-change", this.__changeListener),
        this.addEventListener("add-destination", this.__addDestinationListener),
        this.addEventListener("delete-group", this.deleteGroup.bind(this)),
        this.addEventListener(
          "delete-destination",
          this.deleteDestination.bind(this),
        ),
        (this.__copyDestinationExecuteListener =
          this.handleCopyDestinationExecute.bind(this)),
        document.addEventListener(
          "copy-destination-execute",
          this.__copyDestinationExecuteListener,
        ),
        (this.__copyProductsExecuteListener =
          this.handleCopyProductsExecute.bind(this)),
        document.addEventListener(
          "copy-products-execute",
          this.__copyProductsExecuteListener,
        ),
        this.addEventListener(
          "change",
          (e) => {
             if(e.target && e.target.classList.contains("purpose-radio-input")) {
                 this.__purposeChangeListener(e);
             }
          }
        ),
        (this.__tabSwitchListener = this.handleTabSwitch.bind(this)),
        document.addEventListener("tab-switched", this.__tabSwitchListener),
        sessionManager.enableAutoSave(2e3),
        (this.__pageshowListener = this.handlePageShow.bind(this)),
        (this.__pagehideListener = this.handlePageHide.bind(this)),
        window.addEventListener("pageshow", this.__pageshowListener),
        window.addEventListener("pagehide", this.__pagehideListener),
        (this.__cartItemRemovedListener =
          this.handleCartItemRemoved.bind(this)),
        document.addEventListener(
          "cart:item:removed",
          this.__cartItemRemovedListener,
        ),
        (this.__cartItemUpdatedListener =
          this.handleCartItemUpdated.bind(this)),
        document.addEventListener(
          "cart:item:updated",
          this.__cartItemUpdatedListener,
        ),
        window.subscribe("cart-update", async (data) => {
          try {
            if (cartRecoveryService.isRecoveryInProgress()) return;
            const cart2 = await cartCache.get(!0);
            cart2.item_count > 0 &&
              this.purpose &&
              this._saveCartToSession(cart2, this.purpose);
          } catch (error) { }
        }),
        this.syncCustomerFromShopify(),
        document.addEventListener(
          "cart:updated",
          this.handleCartUpdatedEvent.bind(this),
        ));
    }
    handleCartUpdatedEvent(event) {
      var _a, _b;
      const customEvent = event,
        source = null == (_a = customEvent.detail) ? void 0 : _a.source,
        updatedCart = null == (_b = customEvent.detail) ? void 0 : _b.cart;
      if ("multi-shipping-settings" !== source) {
        if (updatedCart && window.BINDEC_SHIPPING_SETTINGS) {
          const previousCart = window.BINDEC_SHIPPING_SETTINGS.cart,
            previousDiscount = undefined,
            currentDiscount = undefined,
            discountChanged =
              ((null == previousCart ? void 0 : previousCart.total_discount) ||
                0) !== (updatedCart.total_discount || 0);
          if (
            ((discountChanged && hasCartLevelDiscounts(updatedCart)) ||
              hasCartLevelDiscounts(updatedCart),
              (window.BINDEC_SHIPPING_SETTINGS.cart = updatedCart),
              discountChanged)
          ) {
            const senderSelectors = undefined;
            document.querySelectorAll("sender-selector").forEach((selector) => {
              selector.recalculateShippingUnits &&
                selector.recalculateShippingUnits();
            });
          }
        }
        ("cart-items" !== source && "cart-helper" !== source) ||
          (sessionStorage.setItem("_cart_last_updated", Date.now().toString()),
            this._updateTotalShippingCostDisplay());
      }
    }
    async handlePageShow(event) {
      var _a;
      if (event.persisted) {
        try {
          const cart2 = undefined,
            token =
              null == (_a = (await cartCache.get(!0)).attributes)
                ? void 0
                : _a._multi_shipping_session_token;
          token &&
            (sessionManager.setToken(token),
              await this.reapplyTokenToCartItems(token));
        } catch (error) { }
        ((this.deliveryGroupsContainer.innerHTML = ""),
          (this.homeDeliveryContainer.innerHTML = ""),
          await this.restoreFromSession());
        document.querySelectorAll("checkout-button").forEach((checkoutButton) => {
          typeof checkoutButton.setLoading === "function" && checkoutButton.setLoading(!1);
        });
      }
    }
    handlePageHide(event) {
      var _a;
      this.syncDebounceTimer &&
        (clearTimeout(this.syncDebounceTimer), (this.syncDebounceTimer = null));
      const data = this.getSettings();
      try {
        const backupData = {
          data: data,
          timestamp: Date.now(),
          token: sessionManager.getToken(),
        },
          jsonString = JSON.stringify(backupData),
          sizeInBytes = new Blob([jsonString]).size,
          sizeInMB = (sizeInBytes / 1048576).toFixed(2);
        if (sizeInBytes > 1887436.8) {
          const destinationCount =
            (null == (_a = data.deliveryGroups)
              ? void 0
              : _a.reduce((sum, g) => {
                var _a2;
                return (
                  sum +
                  ((null == (_a2 = g.destinations) ? void 0 : _a2.length) ||
                    0)
                );
              }, 0)) || 0;
        } else
          sizeInBytes > 4718592 ||
            sessionStorage.setItem("_multi_shipping_backup", jsonString);
      } catch (err) {
        if (err instanceof Error && "QuotaExceededError" === err.name)
          try {
            sessionStorage.removeItem("_multi_shipping_backup");
          } catch (cleanupErr) { }
      }
      sessionManager.saveImmediately(data);
    }
    async reapplyTokenToCartItems(token) {
      try {
        const cart2 = await cartCache.get();
        if (!cart2) return;
        const updates = [];
        for (let i = 0; i < cart2.items.length; i++) {
          const item = cart2.items[i],
            currentProperties = item.properties || {};
          if (0 === i) {
            if (currentProperties._multi_shipping_token === token) continue;
            updates.push({
              id: item.key,
              quantity: item.quantity,
              properties: {
                ...currentProperties,
                _multi_shipping_token: token,
              },
            });
          } else if (currentProperties._multi_shipping_token) {
            const newProperties = { ...currentProperties };
            (delete newProperties._multi_shipping_token,
              updates.push({
                id: item.key,
                quantity: item.quantity,
                properties: newProperties,
              }));
          }
        }
        updates.length > 0 && (await updateCartItemsBatch(updates, !0));
      } catch (error) { }
    }
    async unifyTokensInCart(initialCart) {
      var _a, _b;
      if (window._unifyTokensInCartRunning)
        return initialCart || (await cartCache.get());
      const lastRun = sessionStorage.getItem("_unifyTokensInCart_lastRun");
      if (lastRun) {
        const timeSinceLastRun = undefined;
        if (Date.now() - parseInt(lastRun) < 5e3)
          return initialCart || (await cartCache.get());
      }
      try {
        ((window._unifyTokensInCartRunning = !0),
          sessionStorage.setItem(
            "_unifyTokensInCart_lastRun",
            Date.now().toString(),
          ));
        const cart2 = initialCart || (await cartCache.get());
        if (!cart2) return ((window._unifyTokensInCartRunning = !1), null);
        const token =
          null == (_a = cart2.attributes)
            ? void 0
            : _a._multi_shipping_session_token;
        if (!token) return ((window._unifyTokensInCartRunning = !1), cart2);
        const variantGroups = new Map();
        for (const item of cart2.items) {
          const variantId = item.variant_id;
          (variantGroups.has(variantId) || variantGroups.set(variantId, []),
            variantGroups.get(variantId).push(item));
        }
        const mergeUpdates = [];
        for (const [variantId, items] of variantGroups.entries())
          if (items.length > 1) {
            const mainItem = items[0],
              totalQuantity = items.reduce(
                (sum, item) => sum + item.quantity,
                0,
              );
            for (let i = 1; i < items.length; i++) {
              const itemToDelete = items[i];
              mergeUpdates.push({ id: itemToDelete.key, quantity: 0 });
            }
            mergeUpdates.push({
              id: mainItem.key,
              quantity: totalQuantity,
              properties: { ...(mainItem.properties || {}) },
            });
          }
        if (mergeUpdates.length > 0)
          try {
            return (
              await updateCartItemsBatch(mergeUpdates, !1),
              void window.location.reload()
            );
          } catch (error) { }
        let updatedCart = await cartCache.get(!0);
        if (!updatedCart) return void (window._unifyTokensInCartRunning = !1);
        const UNUSED_PROPERTIES = ["__total_shipping_cost", "_order_date"];
        let needsUpdate = !1;
        const itemsToUpdate = [];
        for (let i = 0; i < updatedCart.items.length; i++) {
          const item = updatedCart.items[i],
            currentToken =
              null == (_b = item.properties)
                ? void 0
                : _b._multi_shipping_token,
            hasUnusedProps =
              item.properties &&
              UNUSED_PROPERTIES.some((prop) => prop in item.properties);
          0 === i
            ? (currentToken !== token || hasUnusedProps) &&
            itemsToUpdate.push({ item: item, shouldHaveToken: !0 })
            : (currentToken || hasUnusedProps) &&
            itemsToUpdate.push({ item: item, shouldHaveToken: !1 });
        }
        if (itemsToUpdate.length > 0) {
          needsUpdate = !0;
          const updates = itemsToUpdate.map(
            ({ item: item, shouldHaveToken: shouldHaveToken }) => {
              const cleanProperties = { ...(item.properties || {}) };
              return (
                UNUSED_PROPERTIES.forEach((prop) => {
                  delete cleanProperties[prop];
                }),
                shouldHaveToken
                  ? (cleanProperties._multi_shipping_token = token)
                  : delete cleanProperties._multi_shipping_token,
                {
                  id: item.key,
                  quantity: item.quantity,
                  properties: cleanProperties,
                }
              );
            },
          );
          try {
            const responseData = await updateCartItemsBatch(updates, !0);
            ((updatedCart = responseData),
              document.dispatchEvent(
                new CustomEvent("cart:updated", {
                  detail: {
                    cart: responseData,
                    source: "multi-shipping-settings",
                  },
                }),
              ));
          } catch (error) {
            window.location.reload();
          }
        }
        return updatedCart;
      } catch (error) {
        return cart;
      } finally {
        window._unifyTokensInCartRunning = !1;
      }
    }
    async handleCartItemRemoved() {
      await this.cleanupRemovedItems();
    }
    async handleCartItemUpdated(event) {
      const detail = event.detail;
      (this.syncDebounceTimer && clearTimeout(this.syncDebounceTimer),
        (this.syncDebounceTimer = window.setTimeout(async () => {
          (await this.syncCartWithSettings(), (this.syncDebounceTimer = null));
        }, this.SYNC_DEBOUNCE_MS)));
    }
    async syncCartWithSettings(options) {
      const forceSync = !0 === (null == options ? void 0 : options.forceSync);
      if (!window._syncCartWithSettingsRunning) {
        if (!forceSync) {
          const lastRun = sessionStorage.getItem(
            "_syncCartWithSettings_lastRun",
          );
          if (lastRun) {
            const timeSinceLastRun = undefined;
            if (Date.now() - parseInt(lastRun) < 3e3) return;
          }
        }
        if (!this.isSyncing) {
          ((this.isSyncing = !0),
            (window._syncCartWithSettingsRunning = !0),
            sessionStorage.setItem(
              "_syncCartWithSettings_lastRun",
              Date.now().toString(),
            ));
          try {
            const cart2 = await cartCache.get(),
              cartVariantIds = new Set(
                cart2.items.map((item) => String(item.variant_id)),
              ),
              settings = this.getSettings(),
              missingVariants = undefined;
            (this.checkForRemovedItems(settings, cartVariantIds).length > 0
              ? await this.cleanupRemovedItems()
              : "home" === this.currentOrderType &&
              (await this.regenerateHomeDestinationProducts(cart2, settings)),
              await this.unifyTokensInCart(),
              await this.recalculateShippingCostDisplay());
          } catch (error) {
            this.showErrorNotification(
              "カート情報の同期に失敗しました。ページを更新してください。",
            );
          } finally {
            ((this.isSyncing = !1), (window._syncCartWithSettingsRunning = !1));
          }
        }
      }
    }
    async updateCartSections(skipWidget = !1, skipFooter = !1) {
      try {
        const skipMessages = [];
        (skipWidget && skipMessages.push("ウィジェット"),
          skipFooter && skipMessages.push("フッター"));
        const skipMessage =
          skipMessages.length > 0
            ? `(${skipMessages.join(", ")}はスキップ)`
            : "",
          sectionIdsToUpdate = [],
          cartItemsElement = document.querySelector("cart-items");
        if (cartItemsElement) {
          const cartItemsSection =
            cartItemsElement.closest(".shopify-section") ||
            cartItemsElement.closest('[id^="shopify-section-"]');
          if (cartItemsSection && cartItemsSection.id) {
            const cartSectionId = cartItemsSection.id.replace(
              "shopify-section-",
              "",
            );
            cartSectionId && sectionIdsToUpdate.push(cartSectionId);
          }
        }
        if (!skipWidget) {
          const multiShippingBlock = document.querySelector(
            '[data-block-handle="multi-shipping-settings"]',
          );
          if (multiShippingBlock) {
            const widgetSection =
              multiShippingBlock.closest(".shopify-section") ||
              multiShippingBlock.closest('[id^="shopify-section-"]');
            if (widgetSection && widgetSection.id) {
              const widgetSectionId = widgetSection.id.replace(
                "shopify-section-",
                "",
              );
              widgetSectionId &&
                !sectionIdsToUpdate.includes(widgetSectionId) &&
                sectionIdsToUpdate.push(widgetSectionId);
            }
          }
        }
        if (!skipFooter) {
          const cartFooterElement = document.querySelector(
            ".wl-main-cart-footer",
          );
          if (cartFooterElement) {
            const footerSection =
              cartFooterElement.closest(".shopify-section") ||
              cartFooterElement.closest('[id^="shopify-section-"]');
            if (footerSection && footerSection.id) {
              const footerSectionId = footerSection.id.replace(
                "shopify-section-",
                "",
              );
              footerSectionId &&
                !sectionIdsToUpdate.includes(footerSectionId) &&
                sectionIdsToUpdate.push(footerSectionId);
            }
          }
        }
        if (0 === sectionIdsToUpdate.length) return;
        const sectionResponse = await fetch(
          `/?sections=${sectionIdsToUpdate.join(",")}`,
        );
        if (!sectionResponse.ok) return;
        const sections = await sectionResponse.json();
        let updatedCount = 0;
        for (const sectionId of sectionIdsToUpdate) {
          const sectionHtml = sections[sectionId];
          if (!sectionHtml) continue;
          const parser = undefined,
            doc = new DOMParser().parseFromString(sectionHtml, "text/html"),
            oldSection = document.getElementById(
              `shopify-section-${sectionId}`,
            );
          if (!oldSection) continue;
          const newSection =
            doc.querySelector(".shopify-section") ||
            doc.querySelector(`[id*="${sectionId}"]`);
          newSection && (oldSection.replaceWith(newSection), updatedCount++);
        }
      } catch (error) { }
    }
    checkForRemovedItems(settings, cartVariantIds) {
      var _a, _b;
      const missingVariants = [];
      return (
        null == (_a = settings.deliveryGroups) ||
        _a.forEach((group) => {
          var _a2;
          null == (_a2 = group.destinations) ||
            _a2.forEach((destination) => {
              var _a3;
              null == (_a3 = destination.items) ||
                _a3.forEach((item) => {
                  const variantId = item.variantId.replace(
                    "gid://shopify/ProductVariant/",
                    "",
                  );
                  cartVariantIds.has(variantId) ||
                    missingVariants.push(variantId);
                });
            });
        }),
        null == (_b = settings.homeDestinations) ||
        _b.forEach((destination) => {
          var _a2;
          null == (_a2 = destination.items) ||
            _a2.forEach((item) => {
              const variantId = item.variantId.replace(
                "gid://shopify/ProductVariant/",
                "",
              );
              cartVariantIds.has(variantId) ||
                missingVariants.push(variantId);
            });
        }),
        missingVariants
      );
    }
    async regenerateHomeDestinationProducts(cart2, settings) {
      var _a, _b, _c, _d;
      try {
        if (
          !settings.homeDestinations ||
          0 === settings.homeDestinations.length
        )
          return;
        const destination = settings.homeDestinations[0],
          cartItemsWithPurposes =
            (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
              ? void 0
              : _a.cartItemsWithPurposes) || [],
          productsWithoutHomePurpose = PurposeService.getProductsWithoutPurpose(
            cartItemsWithPurposes,
            "自宅用",
          ),
          excludedVariantIds = new Set(
            productsWithoutHomePurpose.map((p) => String(p.variant_id || p.id)),
          ),
          nonGiftItems = cart2.items.filter((item) => {
            const sku = item.sku || "";
            return (
              !(
                sku.includes("GIFT-NOSHI") ||
                sku.includes("GIFT-WRAPPING") ||
                sku.includes("GIFT-CARD")
              ) && !excludedVariantIds.has(String(item.variant_id))
            );
          });
        if (productsWithoutHomePurpose.length > 0) {
          const purposeHomeAlertModal = document.querySelector(
            "wl-purpose-home-alert-modal",
          );
          (purposeHomeAlertModal &&
            purposeHomeAlertModal.open(productsWithoutHomePurpose),
            this.resetAllSettings());
          const tabsElement = document.querySelector("wl-tabs");
          return (
            tabsElement && tabsElement.resetTabs(),
            void (this.currentOrderType = "")
          );
        }
        ((destination.items = nonGiftItems.map((cartItem) => {
          var _a2;
          return {
            variantId: `gid://shopify/ProductVariant/${cartItem.variant_id}`,
            productId: `gid://shopify/Product/${cartItem.product_id}`,
            productTitle: cartItem.product_title,
            variantTitle: cartItem.variant_title || "",
            sku: cartItem.sku || "",
            quantity: cartItem.quantity,
            price: cartItem.price,
            original_price: cartItem.original_price || cartItem.price,
            original_line_price:
              cartItem.original_line_price ||
              cartItem.price * cartItem.quantity,
            final_price: cartItem.final_price || cartItem.price,
            final_line_price:
              cartItem.final_line_price ||
              cartItem.final_price * cartItem.quantity,
            totalPrice:
              cartItem.original_line_price ||
              cartItem.price * cartItem.quantity,
            finalPrice: cartItem.final_price || cartItem.price,
            finalLinePrice:
              cartItem.final_line_price ||
              cartItem.final_price * cartItem.quantity,
            image:
              cartItem.image ||
              (null == (_a2 = cartItem.featured_image) ? void 0 : _a2.url) ||
              "",
          };
        })),
          (destination.shippingUnits = {}));
        const homeGroup =
          null == (_b = settings.deliveryGroups)
            ? void 0
            : _b.find((g) => "自宅用" === g.purpose);
        if (
          homeGroup &&
          homeGroup.destinations &&
          homeGroup.destinations.length > 0
        ) {
          const firstDest = homeGroup.destinations[0];
          ((firstDest.items = [...destination.items]),
            (firstDest.shippingUnits = {}));
        }
        ((this.settings = settings),
          (this.homeDestinations = settings.homeDestinations));
        const destinationSelectors = document.querySelectorAll(
          "destination-selector",
        );
        for (const selector of Array.from(destinationSelectors)) {
          const destinationId = undefined;
          if (
            selector.getAttribute("data-destination-id") ===
            destination.destinationId
          ) {
            ((destination.deliveryMethod || destination.firstName) &&
              (null == (_c = selector.restoreAddress) ||
                _c.call(selector, destination)),
              await (null == (_d = selector.restoreProducts)
                ? void 0
                : _d.call(selector, destination)),
              await selector.recalculateShipping());
            break;
          }
        }
        (await this.updateGiftItemsInCart(settings),
          await sessionManager.update(settings));
      } catch (error) { }
    }
    async recalculateShippingUnits(cart2) {
      var _a, _b, _c;
      try {
        const cartQuantities = new Map();
        cart2.items.forEach((item) => {
          const variantId = String(item.variant_id);
          cartQuantities.set(variantId, item.quantity);
        });
        const settings = this.getSettings();
        let hasChanges = !1;
        if (
          (settings.deliveryGroups &&
            settings.deliveryGroups.forEach((group) => {
              var _a2;
              null == (_a2 = group.destinations) ||
                _a2.forEach((destination) => {
                  var _a3;
                  (null == (_a3 = destination.items) ||
                    _a3.forEach((item) => {
                      const variantId = item.variantId.replace(
                        "gid://shopify/ProductVariant/",
                        "",
                      ),
                        cartQuantity = cartQuantities.get(variantId);
                      void 0 !== cartQuantity &&
                        cartQuantity !== item.quantity &&
                        ((item.quantity = cartQuantity), (hasChanges = !0));
                    }),
                    destination.shippingUnits &&
                    Object.values(destination.shippingUnits).forEach(
                      (unit) => {
                        var _a4;
                        null == (_a4 = unit.items) ||
                          _a4.forEach((item) => {
                            const variantId = String(item.variant_id),
                              cartQuantity = cartQuantities.get(variantId);
                            void 0 !== cartQuantity &&
                              cartQuantity !== item.quantity &&
                              ((item.quantity = cartQuantity),
                                (hasChanges = !0));
                          });
                      },
                    ));
                });
            }),
            settings.homeDestinations &&
            settings.homeDestinations.forEach((destination) => {
              var _a2;
              (null == (_a2 = destination.items) ||
                _a2.forEach((item) => {
                  const variantId = item.variantId.replace(
                    "gid://shopify/ProductVariant/",
                    "",
                  ),
                    cartQuantity = cartQuantities.get(variantId);
                  void 0 !== cartQuantity &&
                    cartQuantity !== item.quantity &&
                    ((item.quantity = cartQuantity), (hasChanges = !0));
                }),
                destination.shippingUnits &&
                Object.values(destination.shippingUnits).forEach((unit) => {
                  var _a3;
                  null == (_a3 = unit.items) ||
                    _a3.forEach((item) => {
                      const variantId = String(item.variant_id),
                        cartQuantity = cartQuantities.get(variantId);
                      void 0 !== cartQuantity &&
                        cartQuantity !== item.quantity &&
                        ((item.quantity = cartQuantity), (hasChanges = !0));
                    });
                }));
            }),
            hasChanges)
        ) {
          const destinationSelectors = document.querySelectorAll(
            "destination-selector",
          );
          for (const selector of Array.from(destinationSelectors)) {
            const destinationId = selector.getAttribute("data-destination-id");
            if (!destinationId) continue;
            let destination = null;
            if (settings.deliveryGroups)
              for (const group of settings.deliveryGroups)
                if (
                  ((destination =
                    null == (_a = group.destinations)
                      ? void 0
                      : _a.find((d) => d.destinationId === destinationId)),
                    destination)
                )
                  break;
            if (
              (!destination &&
                settings.homeDestinations &&
                (destination = settings.homeDestinations.find(
                  (d) => d.destinationId === destinationId,
                )),
                destination)
            )
              try {
                ((destination.deliveryMethod || destination.firstName) &&
                  (null == (_b = selector.restoreAddress) ||
                    _b.call(selector, destination)),
                  await (null == (_c = selector.restoreProducts)
                    ? void 0
                    : _c.call(selector, destination)),
                  await selector.recalculateShipping());
              } catch (error) { }
          }
          (await this.updateGiftItemsInCart(settings),
            await sessionManager.update(settings));
        }
      } catch (error) { }
    }
    async recalculateShippingCostDisplay() {
      try {
        await this.updateCartFooterTotal();
      } catch (error) { }
    }
    async updateCartFooterTotal() {
      try {
        let totalShippingCost = 0;
        if (
          (window.BINDEC_SHIPPING_UNIT_SETTINGS &&
            Object.values(window.BINDEC_SHIPPING_UNIT_SETTINGS).forEach(
              (unit) => {
                unit.shippingCost && (totalShippingCost += unit.shippingCost);
              },
            ),
            0 === totalShippingCost)
        ) {
          const allItems = undefined;
          document
            .querySelectorAll(".cart__footer-total-content-item")
            .forEach((item, index) => {
              var _a, _b, _c, _d;
              const label =
                null ==
                  (_b =
                    null ==
                      (_a = item.querySelector(
                        ".cart__footer-total-content-item-label",
                      ))
                      ? void 0
                      : _a.textContent)
                  ? void 0
                  : _b.trim(),
                value =
                  null ==
                    (_d =
                      null ==
                        (_c = item.querySelector(
                          ".cart__footer-total-content-item-value",
                        ))
                        ? void 0
                        : _c.textContent)
                    ? void 0
                    : _d.trim();
              if (null == label ? void 0 : label.includes("送料")) {
                const text = undefined,
                  match = (value || "").match(/[¥￥]?([\d,]+)/);
                if (match) {
                  const shippingValue = parseInt(
                    match[1].replace(/,/g, ""),
                    10,
                  );
                  isNaN(shippingValue) || (totalShippingCost += shippingValue);
                }
              }
            });
        }
        const cart2 = await cartCache.get(!0),
          cartTotal = undefined,
          finalTotal = cart2.total_price / 100 + totalShippingCost,
          totalElement = document.querySelector(
            '[data-cart-item="total"] .cart__footer-total-content-item-value strong',
          );
        totalElement &&
          (totalElement.textContent = `¥${finalTotal.toLocaleString()}`);
      } catch (error) { }
    }
    showErrorNotification(message) {
      const errorContainer = document.createElement("div");
      ((errorContainer.className = "multi-shipping-error-notification"),
        (errorContainer.style.cssText =
          "\n        position: fixed;\n        top: 20px;\n        right: 20px;\n        padding: 16px;\n        background-color: #f8d7da;\n        border: 1px solid #f5c6cb;\n        border-radius: 4px;\n        color: #721c24;\n        z-index: 9999;\n        box-shadow: 0 2px 8px rgba(0,0,0,0.15);\n        max-width: 400px;\n        animation: slideInRight 0.3s ease-out;\n      "),
        (errorContainer.innerHTML = `\n        <style>\n          @keyframes slideInRight {\n            from {\n              transform: translateX(100%);\n              opacity: 0;\n            }\n            to {\n              transform: translateX(0);\n              opacity: 1;\n            }\n          }\n        </style>\n        <div style="display: flex; align-items: start; gap: 12px;">\n          <span style="font-size: 20px; flex-shrink: 0;">⚠️</span>\n          <div style="flex: 1;">\n            <div style="font-weight: bold; margin-bottom: 4px;">エラー</div>\n            <div style="font-size: 14px;">${message}</div>\n          </div>\n          <button\n            style="\n              margin-left: auto;\n              border: none;\n              background: none;\n              cursor: pointer;\n              font-size: 20px;\n              padding: 0;\n              width: 24px;\n              height: 24px;\n              display: flex;\n              align-items: center;\n              justify-content: center;\n              flex-shrink: 0;\n            "\n            onclick="this.parentElement.parentElement.remove()"\n            aria-label="閉じる"\n          >×</button>\n        </div>\n      `),
        document.body.appendChild(errorContainer),
        setTimeout(() => {
          errorContainer.parentElement && errorContainer.remove();
        }, 5e3));
    }
    async cleanupRemovedItems() {
      var _a;
      try {
        const cart2 = await cartCache.get(!0);
        if (!cart2) return;
        const cartVariantIds = new Set(
          cart2.items.map((item) => String(item.variant_id)),
        ),
          toNumericVariantId = (v) => {
            if (null == v) return "";
            const s = String(v);
            return (s.startsWith("gid://") && s.split("/").pop()) || s;
          },
          hasNonGiftItems = this.hasNonGiftItems(cart2);
        if (0 === cart2.items.length || !hasNonGiftItems) {
          const emptySettings = {
            deliveryGroups: [],
            homeDestinations: [],
            notes: {},
            orderType: this.currentOrderType,
          };
          (await this.updateGiftItemsInCart(emptySettings),
            (this.deliveryGroupsContainer.innerHTML = ""),
            (this.homeDeliveryContainer.innerHTML = ""),
            await sessionManager.delete(),
            sessionStorage.removeItem("_multi_shipping_backup"),
            sessionStorage.removeItem("_cart_restored_after_back"),
            sessionStorage.removeItem("_checkout_purchased_variants"),
            sessionStorage.removeItem("_checkout_products_deleted"),
            sessionStorage.removeItem("bindec_preserved_cart_items"));
          try {
            await fetch("/cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                attributes: {
                  _multi_shipping_session_token: "",
                  _bindec_shipping_enabled: "",
                  _multi_shipping_enabled: "",
                },
              }),
            });
          } catch (error) { }
          return void window.location.reload();
        }
        const settings = this.getSettings();
        let hasChanges = !1;
        (settings.deliveryGroups &&
          (settings.deliveryGroups = settings.deliveryGroups.filter(
            (group) =>
              !group.destinations ||
              ((group.destinations = group.destinations.filter(
                (destination) => {
                  if (!destination.items) return !0;
                  const originalItemCount = destination.items.length;
                  return (
                    (destination.items = destination.items.filter((item) =>
                      cartVariantIds.has(toNumericVariantId(item.variantId)),
                    )),
                    destination.items.length < originalItemCount &&
                    (hasChanges = !0),
                    destination.shippingUnits &&
                    Object.keys(destination.shippingUnits).forEach(
                      (unitKey) => {
                        const unit = destination.shippingUnits[unitKey];
                        if (!unit.items) return;
                        const originalUnitItemCount = unit.items.length;
                        ((unit.items = unit.items.filter((item) =>
                          cartVariantIds.has(String(item.variant_id)),
                        )),
                          0 === unit.items.length
                            ? (delete destination.shippingUnits[unitKey],
                              (hasChanges = !0))
                            : unit.items.length < originalUnitItemCount &&
                            (hasChanges = !0));
                      },
                    ),
                    "自宅用" === group.purpose || destination.items.length > 0
                  );
                },
              )),
                0 !== group.destinations.length ||
                "自宅用" === group.purpose ||
                ((hasChanges = !0), !1)),
          )),
          settings.homeDestinations &&
          (settings.homeDestinations = settings.homeDestinations.filter(
            (destination) => {
              if (!destination.items) return !0;
              const originalItemCount = destination.items.length;
              return (
                (destination.items = destination.items.filter((item) =>
                  cartVariantIds.has(toNumericVariantId(item.variantId)),
                )),
                destination.items.length < originalItemCount &&
                (hasChanges = !0),
                destination.shippingUnits &&
                Object.keys(destination.shippingUnits).forEach(
                  (unitKey) => {
                    const unit = destination.shippingUnits[unitKey];
                    if (!unit.items) return;
                    const originalUnitItemCount = unit.items.length;
                    ((unit.items = unit.items.filter((item) =>
                      cartVariantIds.has(String(item.variant_id)),
                    )),
                      0 === unit.items.length
                        ? (delete destination.shippingUnits[unitKey],
                          (hasChanges = !0))
                        : unit.items.length < originalUnitItemCount &&
                        (hasChanges = !0));
                  },
                ),
                !0
              );
            },
          )));
        const shippingSettings = window.BINDEC_SHIPPING_SETTINGS;
        if (
          null == shippingSettings
            ? void 0
            : shippingSettings.cartItemsWithPurposes
        ) {
          const originalCount = shippingSettings.cartItemsWithPurposes.length;
          shippingSettings.cartItemsWithPurposes =
            shippingSettings.cartItemsWithPurposes.filter((item) =>
              cartVariantIds.has(String(item.variant_id)),
            );
          const removedCount = undefined;
          originalCount - shippingSettings.cartItemsWithPurposes.length > 0 &&
            (hasChanges = !0);
        }
        if (hasChanges) {
          ((this.settings = settings),
            (this.homeDestinations = settings.homeDestinations || []),
            (this.deliveryGroupsContainer.innerHTML = ""),
            (this.homeDeliveryContainer.innerHTML = ""),
            this.renderFromSavedData());
          const purposeSelector = document.querySelector("purpose-selector");
          purposeSelector &&
            purposeSelector.dispatchEvent(new CustomEvent("purpose:reload"));
        }
        if ("home" === this.currentOrderType && cart2.items.length > 0) {
          const hasNonGiftCartItems = undefined;
          if (
            this.hasNonGiftItems(cart2) &&
            settings.homeDestinations &&
            settings.homeDestinations.length > 0
          ) {
            const destination = settings.homeDestinations[0];
            if (!destination.items || 0 === destination.items.length) {
              const nonGiftItems = cart2.items.filter((item) => {
                const sku = item.sku || "";
                return (
                  !sku.includes("GIFT-NOSHI") &&
                  !sku.includes("GIFT-WRAPPING") &&
                  !sku.includes("GIFT-CARD")
                );
              });
              destination.items = nonGiftItems.map((cartItem) => {
                var _a2;
                return {
                  variantId: `gid://shopify/ProductVariant/${cartItem.variant_id}`,
                  productId: `gid://shopify/Product/${cartItem.product_id}`,
                  productTitle: cartItem.product_title,
                  variantTitle: cartItem.variant_title || "",
                  sku: cartItem.sku || "",
                  quantity: cartItem.quantity,
                  price: cartItem.price,
                  original_price: cartItem.original_price || cartItem.price,
                  original_line_price:
                    cartItem.original_line_price ||
                    cartItem.price * cartItem.quantity,
                  final_price: cartItem.final_price || cartItem.price,
                  final_line_price:
                    cartItem.final_line_price ||
                    cartItem.final_price * cartItem.quantity,
                  totalPrice:
                    cartItem.original_line_price ||
                    cartItem.price * cartItem.quantity,
                  finalPrice: cartItem.final_price || cartItem.price,
                  finalLinePrice:
                    cartItem.final_line_price ||
                    cartItem.final_price * cartItem.quantity,
                  image:
                    cartItem.image ||
                    (null == (_a2 = cartItem.featured_image)
                      ? void 0
                      : _a2.url) ||
                    "",
                };
              });
              const homeGroup =
                null == (_a = settings.deliveryGroups)
                  ? void 0
                  : _a.find((g) => "自宅用" === g.purpose);
              (homeGroup &&
                homeGroup.destinations &&
                homeGroup.destinations.length > 0 &&
                (homeGroup.destinations[0].items = [...destination.items]),
                (hasChanges = !0),
                (this.settings = settings),
                (this.homeDestinations = settings.homeDestinations || []),
                (this.deliveryGroupsContainer.innerHTML = ""),
                (this.homeDeliveryContainer.innerHTML = ""),
                this.renderFromSavedData());
            }
          }
        }
        (await this.updateGiftItemsInCart(settings),
          hasChanges && (await sessionManager.update(settings)));
      } catch (error) { }
    }
    async updateGiftItemsInCart(settings) {
      try {
        const cart2 = await cartCache.get(!0);
        if (!cart2) throw new Error("カート情報の取得に失敗しました");
        const giftVariantIds = this.getAllGiftVariantIds(),
          requiredQuantities = settings
            ? this.calculateRequiredGiftQuantitiesFromSettings(settings)
            : this.calculateRequiredGiftQuantities(),
          currentQuantities = new Map();
        cart2.items.forEach((item) => {
          var _a;
          const variantId = String(item.variant_id);
          giftVariantIds.has(variantId) &&
            currentQuantities.set(variantId, {
              quantity:
                ((null == (_a = currentQuantities.get(variantId))
                  ? void 0
                  : _a.quantity) || 0) + item.quantity,
              key: item.key,
            });
        });
        const updates = {};
        if (
          (requiredQuantities.forEach((requiredQty, variantId) => {
            const current = currentQuantities.get(variantId),
              currentQty = undefined;
            ((null == current ? void 0 : current.quantity) || 0) !==
              requiredQty &&
              ((null == current ? void 0 : current.key)
                ? (updates[current.key] = requiredQty)
                : (updates[variantId] = requiredQty));
          }),
            currentQuantities.forEach((current, variantId) => {
              requiredQuantities.has(variantId) || (updates[current.key] = 0);
            }),
            Object.keys(updates).length > 0)
        ) {
          const updateResponse = await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ updates: updates }),
          });
          if (!updateResponse.ok) {
            const errorText = await updateResponse.text();
            throw new Error(
              `カートの更新に失敗しました: ${updateResponse.statusText}`,
            );
          }
        }
      } catch (error) {
        throw error;
      }
    }
    getAllGiftVariantIds() {
      var _a, _b;
      const giftVariantIds = new Set(),
        cart2 =
          null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.cart,
        productMetafields =
          null == (_b = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _b.productMetafields;
      return (null == cart2 ? void 0 : cart2.items) &&
        0 !== cart2.items.length &&
        productMetafields
        ? (cart2.items.forEach((item) => {
          const variantId = String(item.variant_id),
            metafield = productMetafields[variantId],
            giftCategoryValue = undefined,
            isGift = undefined;
          !0 === (null == metafield ? void 0 : metafield.gift_category) &&
            giftVariantIds.add(variantId);
        }),
          giftVariantIds)
        : giftVariantIds;
    }
    hasNonGiftItems(cart2) {
      var _a;
      if (!(null == cart2 ? void 0 : cart2.items) || 0 === cart2.items.length)
        return !1;
      const productMetafields =
        null == (_a = window.BINDEC_SHIPPING_SETTINGS)
          ? void 0
          : _a.productMetafields;
      return (
        !productMetafields ||
        cart2.items.some((item) => {
          const variantId = String(item.variant_id),
            metafield = productMetafields[variantId],
            giftCategoryValue = undefined;
          return !0 !== (null == metafield ? void 0 : metafield.gift_category);
        })
      );
    }
    calculateRequiredGiftQuantitiesFromSettings(settings) {
      const quantityMap = new Map();
      return (
        settings.deliveryGroups &&
        settings.deliveryGroups.forEach((group) => {
          group.destinations &&
            group.destinations.forEach((destination) => {
              destination.shippingUnits &&
                Object.values(destination.shippingUnits).forEach((unit) => {
                  const giftSettings = unit.giftSettings;
                  if (giftSettings) {
                    if (
                      giftSettings.noshiProductId &&
                      "" !== giftSettings.noshiProductId
                    ) {
                      const variantId = String(giftSettings.noshiProductId);
                      quantityMap.set(
                        variantId,
                        (quantityMap.get(variantId) || 0) + 1,
                      );
                    }
                    if (
                      giftSettings.messageCardProductId &&
                      "" !== giftSettings.messageCardProductId
                    ) {
                      const variantId = String(
                        giftSettings.messageCardProductId,
                      );
                      quantityMap.set(
                        variantId,
                        (quantityMap.get(variantId) || 0) + 1,
                      );
                    }
                    if (
                      giftSettings.wrappingProductId &&
                      "" !== giftSettings.wrappingProductId
                    ) {
                      const variantId = String(
                        giftSettings.wrappingProductId,
                      );
                      quantityMap.set(
                        variantId,
                        (quantityMap.get(variantId) || 0) + 1,
                      );
                    }
                  }
                });
            });
        }),
        settings.homeDestinations &&
        settings.homeDestinations.forEach((destination) => {
          destination.shippingUnits &&
            Object.values(destination.shippingUnits).forEach((unit) => {
              const giftSettings = unit.giftSettings;
              if (giftSettings) {
                if (
                  giftSettings.noshiProductId &&
                  "" !== giftSettings.noshiProductId
                ) {
                  const variantId = String(giftSettings.noshiProductId);
                  quantityMap.set(
                    variantId,
                    (quantityMap.get(variantId) || 0) + 1,
                  );
                }
                if (
                  giftSettings.messageCardProductId &&
                  "" !== giftSettings.messageCardProductId
                ) {
                  const variantId = String(giftSettings.messageCardProductId);
                  quantityMap.set(
                    variantId,
                    (quantityMap.get(variantId) || 0) + 1,
                  );
                }
                if (
                  giftSettings.wrappingProductId &&
                  "" !== giftSettings.wrappingProductId
                ) {
                  const variantId = String(giftSettings.wrappingProductId);
                  quantityMap.set(
                    variantId,
                    (quantityMap.get(variantId) || 0) + 1,
                  );
                }
              }
            });
        }),
        quantityMap
      );
    }
    calculateRequiredGiftQuantities() {
      const quantityMap = new Map();
      return window.BINDEC_SHIPPING_UNIT_SETTINGS
        ? (Object.values(window.BINDEC_SHIPPING_UNIT_SETTINGS).forEach(
          (unit) => {
            const giftSettings = unit.giftSettings;
            if (giftSettings) {
              if (
                giftSettings.noshiProductId &&
                "" !== giftSettings.noshiProductId
              ) {
                const variantId = String(giftSettings.noshiProductId);
                quantityMap.set(
                  variantId,
                  (quantityMap.get(variantId) || 0) + 1,
                );
              }
              if (
                giftSettings.messageCardProductId &&
                "" !== giftSettings.messageCardProductId
              ) {
                const variantId = String(giftSettings.messageCardProductId);
                quantityMap.set(
                  variantId,
                  (quantityMap.get(variantId) || 0) + 1,
                );
              }
              if (
                giftSettings.wrappingProductId &&
                "" !== giftSettings.wrappingProductId
              ) {
                const variantId = String(giftSettings.wrappingProductId);
                quantityMap.set(
                  variantId,
                  (quantityMap.get(variantId) || 0) + 1,
                );
              }
            }
          },
        ),
          quantityMap)
        : quantityMap;
    }
    disconnectedCallback() {
      (this.addDeliveryGroupButton.removeEventListener(
        "click",
        this.__addDeliveryGroupListener,
      ),
        this.addHomeDestinationButton.removeEventListener(
          "click",
          this.__addHomeDestinationListener,
        ),
        this.removeEventListener("settings-change", this.__changeListener),
        this.removeEventListener(
          "add-destination",
          this.__addDestinationListener,
        ),
        this.purposeSelect.removeEventListener(
          "change",
          this.__purposeChangeListener,
        ),
        document.removeEventListener("tab-switched", this.__tabSwitchListener),
        document.removeEventListener(
          "copy-destination-execute",
          this.__copyDestinationExecuteListener,
        ),
        document.removeEventListener(
          "copy-products-execute",
          this.__copyProductsExecuteListener,
        ),
        window.removeEventListener("pageshow", this.__pageshowListener),
        window.removeEventListener("pagehide", this.__pagehideListener),
        document.removeEventListener(
          "cart:item:removed",
          this.__cartItemRemovedListener,
        ),
        document.removeEventListener(
          "cart:item:updated",
          this.__cartItemUpdatedListener,
        ));
    }
    async purposeChange(event) {
      const { value: value } = event.target;
      
      if (value) {
        this.purpose = value;
        
        // グローバル設定の用途を更新
        if (window.BINDEC_SHIPPING_SETTINGS) {
          window.BINDEC_SHIPPING_SETTINGS.currentPurpose = value;
        }
        
        // 既存の配送グループ（ギフトなど）の用途を更新
        if (this.settings.deliveryGroups) {
          this.settings.deliveryGroups.forEach((group) => {
            group.purpose = value;
            if (group.destinations) {
              group.destinations.forEach((dest) => {
                dest.purpose = value;
              });
            }
          });
        }
        
        // 自宅用の配送先の用途を更新
        if (this.homeDestinations) {
          this.homeDestinations.forEach((dest) => {
            dest.purpose = value;
          });
        }
        
        // 用途の変更により配送日時や送料が変わる可能性があるため再計算を実行
        const destinationSelectors = document.querySelectorAll("destination-selector");
        for (const selector of Array.from(destinationSelectors)) {
          if (typeof selector.recalculateShippingFee === 'function') {
            await selector.recalculateShippingFee();
          }
        }
        
        // 設定内容をセッションに保存
        if (typeof sessionManager !== 'undefined' && sessionManager.scheduleAutoSave) {
          sessionManager.scheduleAutoSave(this.getSettings());
        }

      } else {
        // 値が空（未選択）になった場合は、リセットする
        this.deliveryGroupsContainer.innerHTML = "";
        this.settings.deliveryGroups = [];
        this.purpose = value;
        if (window.BINDEC_SHIPPING_UNIT_SETTINGS) {
          Object.keys(window.BINDEC_SHIPPING_UNIT_SETTINGS).forEach((key) => {
            delete window.BINDEC_SHIPPING_UNIT_SETTINGS[key];
          });
        }
        this._clearTotalShippingCost();
      }
    }
    handleTabSwitch(event) {
      const { orderType: orderType } = event.detail;
      ((this.currentOrderType = orderType),
        this.isRestoring || this.resetAllSettings(),
        this._clearTotalShippingCost(),
        "home" === orderType 
          ? ((function(scope){ 
              const r = scope.querySelector("input[name='purpose'][value='自宅用']");
              if(r) r.checked = true;
            })
            (this),
            (this.purpose = "自宅用"),
            this.addHomeDestinationButton.classList.add("hidden"),
            this.addDeliveryGroupButton.classList.add("hidden"),
            this.isRestoring || this.initializeHomeRoute())
          : (this.addHomeDestinationButton.classList.remove("hidden"),
            this.addDeliveryGroupButton.classList.remove("hidden")));
    }
    initializeHomeRoute() {
      var _a, _b, _c, _d;
      const existingDestinations = this.homeDeliveryContainer.querySelectorAll(
        "destination-selector",
      ),
        homeGroup =
          null == (_a = this.settings.deliveryGroups)
            ? void 0
            : _a.find((g) => "自宅用" === g.purpose);
      if (
        0 === existingDestinations.length &&
        0 === this.homeDestinations.length &&
        !homeGroup
      ) {
        const groupId = this.generateGroupId(),
          destinationId = this.generateDestinationId(),
          destinationIndex = 1,
          _selectors = {
            destinationSelector: "destination-selector",
            destinationTitle: ".js-destination-title",
          },
          destinationElement =
            this.newDestinationTemplate.content.cloneNode(!0),
          destinationSelector = destinationElement.querySelector(
            _selectors.destinationSelector,
          );
        (null == destinationSelector ||
          destinationSelector.setAttribute(
            "data-destination-id",
            destinationId,
          ),
          null == destinationSelector ||
          destinationSelector.setAttribute(
            "data-destination-index",
            destinationIndex.toString(),
          ),
          (destinationElement.querySelector(
            _selectors.destinationTitle,
          ).textContent = `お届け先 ${destinationIndex}`),
          this.homeDeliveryContainer.appendChild(destinationElement));
        const allCartItemsRaw =
          (null ==
            (_c =
              null == (_b = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _b.cart)
            ? void 0
            : _c.items) || [],
          cartItemsWithPurposesForHome =
            (null == (_d = window.BINDEC_SHIPPING_SETTINGS)
              ? void 0
              : _d.cartItemsWithPurposes) || [],
          productsNotForHome = PurposeService.getProductsWithoutPurpose(
            cartItemsWithPurposesForHome,
            "自宅用",
          ),
          excludedVariantIdsForHome = new Set(
            productsNotForHome.map((p) => String(p.variant_id || p.id)),
          ),
          cartItems = allCartItemsRaw.filter((item) => {
            const sku = item.sku || "";
            return (
              !(
                sku.includes("GIFT-NOSHI") ||
                sku.includes("GIFT-WRAPPING") ||
                sku.includes("GIFT-CARD")
              ) && !excludedVariantIdsForHome.has(String(item.variant_id))
            );
          });
        if (productsNotForHome.length > 0) {
          const purposeHomeAlertModal = document.querySelector(
            "wl-purpose-home-alert-modal",
          );
          purposeHomeAlertModal &&
            purposeHomeAlertModal.open(productsNotForHome);
        }
        const allProducts = cartItems.map((item) => ({
          variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
          productId: `gid://shopify/Product/${item.product_id}`,
          quantity: item.quantity,
          price: item.final_price,
          original_price: item.original_price || item.price,
          original_line_price:
            item.original_line_price || item.price * item.quantity,
          final_price: item.final_price || item.price,
          final_line_price:
            item.final_line_price || item.final_price * item.quantity,
          totalPrice: item.original_line_price || item.price * item.quantity,
          finalPrice: item.final_price || item.price,
          finalLinePrice:
            item.final_line_price || item.final_price * item.quantity,
          productTitle: item.product_title,
          variantTitle: item.variant_title,
          sku: item.sku,
          productProperties: Object.entries(item.properties || {}).map(
            ([name, value]) => ({ name: name, value: value }),
          ),
          weight: item.grams,
          purpose: "自宅用",
        })),
          homeDestination = {
            destinationId: destinationId,
            purpose: "自宅用",
            items: allProducts,
          };
        this.homeDestinations.push(homeDestination);
        const customerSender = this.getCustomerAsSender(),
          senderList = undefined,
          homeDeliveryGroup = {
            groupId: groupId,
            purpose: "自宅用",
            sender: customerSender ? [customerSender] : [],
            destinations: [homeDestination],
          };
        (this.settings.deliveryGroups || (this.settings.deliveryGroups = []),
          this.settings.deliveryGroups.push(homeDeliveryGroup),
          setTimeout(async () => {
            const latestCart = await cartCache.get(!0);
            latestCart &&
              window.BINDEC_SHIPPING_SETTINGS &&
              (window.BINDEC_SHIPPING_SETTINGS.cart = latestCart);
            const latestCartItems =
              (null == latestCart ? void 0 : latestCart.items) || [],
              addedDestinationElement =
                this.homeDeliveryContainer.querySelector(
                  `destination-selector[data-destination-id="${destinationId}"]`,
                );
            if (addedDestinationElement) {
              const checkboxes = addedDestinationElement.querySelectorAll(
                ".js-product-checkbox",
              ),
                quantityInputs = addedDestinationElement.querySelectorAll(
                  ".js-product-quantity",
                );
              (checkboxes.forEach((checkbox, index) => {
                checkbox.checked = !0;
                const quantityInput = quantityInputs[index];
                if (quantityInput) {
                  quantityInput.disabled = !1;
                  const variantId = checkbox.dataset.variantId,
                    latestCartItem = latestCartItems.find(
                      (item) => item.variant_id.toString() === variantId,
                    );
                  latestCartItem &&
                    ((quantityInput.value = latestCartItem.quantity.toString()),
                      (checkbox.dataset.key = latestCartItem.key));
                }
              }),
                addedDestinationElement.submitModal &&
                addedDestinationElement.submitModal("products"));
            }
          }, 300));
      }
    }
    addDeliveryGroup(event) {
      var _a, _b, _c, _d, _e, _f, _g;
      try {
        const _selectors = {
          deliveryGroup: "delivery-group",
          destinationSelector: "destination-selector",
          groupTitle: ".js-group-title",
        },
          groupId = this.generateGroupId(),
          newGroup = {
            groupId: groupId,
            purpose: this.purpose,
            sender: [],
            destinations: [],
          },
          newGroupElement = this.newGroupTemplate.content.cloneNode(!0),
          groupIndex =
            ((null == (_a = this.settings.deliveryGroups)
              ? void 0
              : _a.length) || 0) + 1;
        (null ==
          (_b = newGroupElement.querySelector(_selectors.deliveryGroup)) ||
          _b.setAttribute("data-delivery-group-id", groupId),
          null ==
          (_c = newGroupElement.querySelector(_selectors.deliveryGroup)) ||
          _c.setAttribute(
            "data-delivery-group-index",
            groupIndex.toString(),
          ));
        const groupTitleElement = newGroupElement.querySelector(
          _selectors.groupTitle,
        );
        groupTitleElement &&
          (groupTitleElement.textContent = `お申込 ${groupIndex}`);
        const firstDestinationId = this.generateDestinationId(),
          firstDestinationSelector = newGroupElement.querySelector(
            _selectors.destinationSelector,
          );
        (firstDestinationSelector &&
          firstDestinationSelector.setAttribute(
            "data-destination-id",
            firstDestinationId,
          ),
          null == (_d = this.settings.deliveryGroups) || _d.push(newGroup),
          this.deliveryGroupsContainer.appendChild(newGroupElement));
        const newDestination = {
          destinationId: firstDestinationId,
          purpose: this.purpose,
          items: [],
        };
        (null ==
          (_g =
            null ==
              (_f =
                null == (_e = this.settings.deliveryGroups)
                  ? void 0
                  : _e.find((g) => g.groupId === groupId))
              ? void 0
              : _f.destinations) || _g.push(newDestination),
          this.validateStateConsistency(),
          this.updateDeleteButtonsVisibility());
      } catch (error) {
        alert("配送グループの追加に失敗しました。もう一度お試しください。");
      }
    }
    addGroupDestination(event) {
      var _a, _b, _c, _d, _e, _f, _g, _h;
      const { groupId: groupId } = event.detail,
        _selectors_destinationSelector = "destination-selector",
        _selectors_destinationTemplate = "template#new-destination-template",
        _selectors_deliverySectionContainer = ".js-delivery-section-container",
        _selectors_destinationTitle = ".js-destination-title",
        destinationId = this.generateDestinationId(),
        template = document.querySelector(_selectors_destinationTemplate),
        destinationIndex =
          ((null ==
            (_c =
              null ==
                (_b =
                  null == (_a = this.settings.deliveryGroups)
                    ? void 0
                    : _a.find((group) => group.groupId === groupId))
                ? void 0
                : _b.destinations)
            ? void 0
            : _c.length) || 0) + 1,
        destinationElement = template.content.cloneNode(!0),
        destinationSelector = destinationElement.querySelector(
          _selectors_destinationSelector,
        );
      (null == destinationSelector ||
        destinationSelector.setAttribute("data-destination-id", destinationId),
        null == destinationSelector ||
        destinationSelector.setAttribute(
          "data-destination-index",
          destinationIndex.toString(),
        ),
        (destinationElement.querySelector(
          _selectors_destinationTitle,
        ).textContent = `お届け先 ${destinationIndex}`),
        null ==
        (_e =
          null ==
            (_d = this.querySelector(
              `delivery-group[data-delivery-group-id="${groupId}"]`,
            ))
            ? void 0
            : _d.querySelector(_selectors_deliverySectionContainer)) ||
        _e.appendChild(destinationElement));
      const destination = {
        destinationId: destinationId,
        purpose: this.purpose,
        items: [],
      };
      (null ==
        (_h =
          null ==
            (_g =
              null == (_f = this.settings.deliveryGroups)
                ? void 0
                : _f.find((group) => group.groupId === groupId))
            ? void 0
            : _g.destinations) || _h.push(destination),
        this.updateDeleteButtonsVisibility());
    }
    addHomeDestination(_event) {
      var _a;
      const _selectors_destinationSelector = "destination-selector",
        _selectors_destinationTitle = ".js-destination-title",
        destinationId = this.generateDestinationId(),
        destinationIndex = this.homeDestinations.length + 1,
        destinationElement = this.newDestinationTemplate.content.cloneNode(!0),
        destinationSelector = destinationElement.querySelector(
          _selectors_destinationSelector,
        );
      (null == destinationSelector ||
        destinationSelector.setAttribute("data-destination-id", destinationId),
        null == destinationSelector ||
        destinationSelector.setAttribute(
          "data-destination-index",
          destinationIndex.toString(),
        ),
        (destinationElement.querySelector(
          _selectors_destinationTitle,
        ).textContent = `お届け先 ${destinationIndex}`),
        this.homeDeliveryContainer.appendChild(destinationElement));
      const homeDestination = {
        destinationId: destinationId,
        purpose: this.purpose,
        items: [],
      };
      this.homeDestinations.push(homeDestination);
      let homeGroup =
        null == (_a = this.settings.deliveryGroups)
          ? void 0
          : _a.find((g) => "自宅用" === g.purpose);
      if (!homeGroup) {
        const customerSender = this.getCustomerAsSender(),
          senderList = customerSender ? [customerSender] : [],
          groupId = undefined;
        ((homeGroup = {
          groupId: this.generateGroupId(),
          purpose: "自宅用",
          sender: senderList,
          destinations: [],
        }),
          this.settings.deliveryGroups || (this.settings.deliveryGroups = []),
          this.settings.deliveryGroups.push(homeGroup));
      }
      homeGroup.destinations.push(homeDestination);
    }
    deleteGroup(event) {
      var _a;
      const { groupId: groupId } = event.detail,
        groupElement = this.querySelector(
          `delivery-group[data-delivery-group-id="${groupId}"]`,
        );
      (groupElement && groupElement.remove(),
        (this.settings = {
          ...this.settings,
          deliveryGroups:
            (null == (_a = this.settings.deliveryGroups)
              ? void 0
              : _a.filter((g) => g.groupId !== groupId)) || [],
        }),
        this.updateGroupNumbers(),
        this.updateDeleteButtonsVisibility());
      this._updateTotalShippingCostDisplay();
    }
    deleteDestination(event) {
      var _a;
      const { groupId: groupId, destinationId: destinationId } = event.detail,
        groupElement = this.querySelector(
          `delivery-group[data-delivery-group-id="${groupId}"]`,
        );
      if (groupElement) {
        const destinationElement = groupElement.querySelector(
          `destination-selector[data-destination-id="${destinationId}"]`,
        );
        if (destinationElement) {
          const deliveryCard = destinationElement.closest(".delivery-card");
          deliveryCard && deliveryCard.remove();
        }
      }
      ((this.settings = {
        ...this.settings,
        deliveryGroups:
          (null == (_a = this.settings.deliveryGroups)
            ? void 0
            : _a.map((group) =>
              group.groupId === groupId
                ? {
                  ...group,
                  destinations: group.destinations.filter(
                    (d) => d.destinationId !== destinationId,
                  ),
                }
                : group,
            )) || [],
      }),
        this.updateDestinationNumbers(groupId),
        this.updateDeleteButtonsVisibility());
      this._updateTotalShippingCostDisplay();
    }
    handleCopyDestinationExecute(event) {
      var _a;
      const { source: source, targets: targets } = event.detail;
      if (!source || !targets || 0 === targets.length) return;
      const sourceItems =
        (null == (_a = source.destinationData) ? void 0 : _a.items) || [],
        projectedTotals =
          this.getTotalQuantitiesByVariantFromDeliverySettings();
      targets.forEach((target) => {
        var _a2, _b;
        if (!target.isNewDestination) {
          const group =
            null == (_a2 = this.settings.deliveryGroups)
              ? void 0
              : _a2.find((g) => g.groupId === target.groupId),
            dest =
              null == (_b = null == group ? void 0 : group.destinations)
                ? void 0
                : _b.find((d) => d.destinationId === target.destinationId);
          for (const item of (null == dest ? void 0 : dest.items) || []) {
            const vid = this.normalizeVariantIdToNumericString(item.variantId);
            if (!vid) continue;
            const qty = Number(item.quantity) || 0;
            projectedTotals[vid] = Math.max(
              0,
              (projectedTotals[vid] || 0) - qty,
            );
          }
        }
        for (const item of sourceItems) {
          const vid = this.normalizeVariantIdToNumericString(item.variantId);
          if (!vid) continue;
          const qty = Number(item.quantity) || 0;
          projectedTotals[vid] = (projectedTotals[vid] || 0) + qty;
        }
      });
      const shortages = undefined;
      findInventoryShortages(projectedTotals).length > 0 ||
        (targets.forEach((target) => {
          const clonedDestination = this.cloneDestinationData(
            source.destinationData,
          );
          if (target.isNewDestination) {
            const addEvent = new CustomEvent("add-destination", {
              bubbles: !0,
              detail: { groupId: target.groupId },
            });
            (this.dispatchEvent(addEvent),
              setTimeout(() => {
                (this.addDestinationToGroup(target.groupId, clonedDestination),
                  setTimeout(() => {
                    var _a2;
                    const groupElement = this.querySelector(
                      `delivery-group[data-delivery-group-id="${target.groupId}"]`,
                    );
                    if (groupElement) {
                      const destinations = groupElement.querySelectorAll(
                        "destination-selector",
                      ),
                        targetDestElement =
                          destinations[destinations.length - 1];
                      targetDestElement &&
                        (null == (_a2 = targetDestElement.restoreAddress) ||
                          _a2.call(targetDestElement, clonedDestination),
                          clonedDestination.items &&
                          clonedDestination.items.length > 0 &&
                          setTimeout(() => {
                            var _a3;
                            (null ==
                              (_a3 = targetDestElement.restoreProducts) ||
                              _a3.call(targetDestElement, clonedDestination),
                              setTimeout(() => {
                                var _a4;
                                null ==
                                  (_a4 =
                                    targetDestElement.recalculateShippingFee) ||
                                  _a4.call(targetDestElement);
                              }, 200));
                          }, 200));
                    }
                  }, 300));
              }, 100));
          } else
            ((clonedDestination.destinationId = target.destinationId),
              this.addDestinationToGroup(target.groupId, clonedDestination),
              setTimeout(() => {
                var _a2;
                const targetDestElement = this.querySelector(
                  `destination-selector[data-destination-id="${target.destinationId}"]`,
                );
                targetDestElement &&
                  (null == (_a2 = targetDestElement.restoreAddress) ||
                    _a2.call(targetDestElement, clonedDestination),
                    clonedDestination.items &&
                    clonedDestination.items.length > 0 &&
                    setTimeout(() => {
                      var _a3;
                      (null == (_a3 = targetDestElement.restoreProducts) ||
                        _a3.call(targetDestElement, clonedDestination),
                        setTimeout(() => {
                          var _a4;
                          null ==
                            (_a4 = targetDestElement.recalculateShippingFee) ||
                            _a4.call(targetDestElement);
                        }, 200));
                    }, 200));
              }, 200));
        }),
          setTimeout(() => {
            (targets.forEach((target) => {
              this.updateDestinationNumbers(target.groupId);
            }),
              this.updateDeleteButtonsVisibility());
          }, 500));
    }
    normalizeVariantIdToNumericString(variantId) {
      if (null == variantId) return null;
      let s = String(variantId).trim();
      return s
        ? (s.includes("/") && (s = s.split("/").pop() || s), s || null)
        : null;
    }
    getTotalQuantitiesByVariantFromDeliverySettings() {
      const totals = {},
        accumulate = (items) => {
          for (const item of items || []) {
            const vid = this.normalizeVariantIdToNumericString(item.variantId);
            if (!vid) continue;
            const qty = Number(item.quantity) || 0;
            qty <= 0 || (totals[vid] = (totals[vid] || 0) + qty);
          }
        };
      for (const group of this.settings.deliveryGroups || [])
        for (const dest of group.destinations || []) accumulate(dest.items);
      const homeDestinations = this.settings.homeDestinations;
      for (const dest of homeDestinations || []) accumulate(dest.items);
      return totals;
    }
    async syncCartQuantitiesWithDeliverySettings() {
      var _a, _b, _c;
      const cartRoot =
        null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.cart;
      if (
        !(null == (_b = null == cartRoot ? void 0 : cartRoot.items)
          ? void 0
          : _b.length)
      )
        return;
      const totalQuantities =
        this.getTotalQuantitiesByVariantFromDeliverySettings(),
        shortages = findInventoryShortages(totalQuantities);
      if (shortages.length > 0) {
        const messages = formatShortageMessage(shortages);
        throw new Error(messages.join("\n"));
      }
      const cartUpdates = [];
      if (
        (Object.entries(totalQuantities).forEach(
          ([variantId, totalQuantity]) => {
            const item = cartRoot.items.find(
              (i) => i.variant_id.toString() === variantId,
            );
            item &&
              item.quantity !== totalQuantity &&
              cartUpdates.push({
                key: item.key,
                variantId: variantId,
                quantity: totalQuantity,
              });
          },
        ),
          0 === cartUpdates.length)
      )
        return;
      const cart2 = await cartCache.get(!0);
      if (!cart2) throw new Error("カート情報の取得に失敗しました");
      const token =
        null == (_c = cart2.attributes)
          ? void 0
          : _c._multi_shipping_session_token,
        batchUpdates = [];
      (cartUpdates.forEach((update) => {
        var _a2;
        const item =
          cart2.items.find((ci) =>
            update.key
              ? ci.key === update.key
              : ci.variant_id.toString() === update.variantId,
          ) ||
          cart2.items.find(
            (ci) => ci.variant_id.toString() === update.variantId,
          );
        if (!item) return;
        const existingProperties = undefined,
          properties = { ...(item.properties || {}) },
          isFirstItem =
            (null == (_a2 = cart2.items[0]) ? void 0 : _a2.key) === item.key;
        (token && isFirstItem && (properties._multi_shipping_token = token),
          batchUpdates.push({
            id: item.key,
            quantity: update.quantity,
            properties: properties,
          }));
      }),
        batchUpdates.length > 0 &&
        (await updateCartItemsBatch(batchUpdates, !0)));
    }
    async handleCopyProductsExecute(event) {
      var _a;
      const {
        source: source,
        target: target,
        products: products,
      } = event.detail;
      if (
        !(
          source &&
          target &&
          products &&
          products.items &&
          0 !== products.items.length
        )
      )
        return;
      const targetDestElement = this.querySelector(
        `destination-selector[data-destination-id="${target.destinationId}"]`,
      );
      if (!targetDestElement)
        return void alert("複写先のお届け先が見つかりません。");
      const clonedItems = products.items.map((item) => ({
        ...item,
        giftSettings: item.giftSettings ? { ...item.giftSettings } : void 0,
      })),
        group =
          null == (_a = this.settings.deliveryGroups)
            ? void 0
            : _a.find((g) => g.groupId === target.groupId);
      if (!group) return;
      const destination = group.destinations.find(
        (d) => d.destinationId === target.destinationId,
      );
      if (!destination) return;
      destination.items || (destination.items = []);
      const destinationItemsBefore = JSON.parse(
        JSON.stringify(destination.items),
      );
      for (const clonedItem of clonedItems) {
        const existingItem = destination.items.find(
          (item) => item.variantId === clonedItem.variantId,
        );
        existingItem
          ? (existingItem.quantity += clonedItem.quantity)
          : destination.items.push(clonedItem);
      }
      try {
        await this.syncCartQuantitiesWithDeliverySettings();
      } catch (error) {
        destination.items = destinationItemsBefore;
        const inventoryMessage =
          error instanceof Error && /在庫が不足/.test(error.message)
            ? error.message
            : null;
        return void alert(
          inventoryMessage || formatCartUpdateErrorForAlert(error),
        );
      }
      setTimeout(() => {
        var _a2;
        (null == (_a2 = targetDestElement.restoreProducts) ||
          _a2.call(targetDestElement, destination),
          setTimeout(() => {
            var _a3;
            null == (_a3 = targetDestElement.recalculateShippingFee) ||
              _a3.call(targetDestElement);
          }, 200));
      }, 200);
    }
    cloneDestinationData(source) {
      const newDestinationId = this.generateDestinationId(),
        clonedItems = source.items
          ? source.items.map((item) => ({
            ...item,
            giftSettings: item.giftSettings
              ? { ...item.giftSettings }
              : void 0,
          }))
          : [],
        clonedAddress = source.address ? { ...source.address } : void 0,
        clonedGiftSettings = source.giftSettings
          ? { ...source.giftSettings }
          : void 0,
        clonedStoreAddress = source.storeAddress
          ? { ...source.storeAddress }
          : void 0,
        clonedDestination = undefined;
      return {
        destinationId: newDestinationId,
        purpose: source.purpose || this.querySelector("input[name='purpose']:checked")?.value,
        deliveryMethod: source.deliveryMethod,
        name: source.name,
        firstName: source.firstName,
        lastName: source.lastName,
        firstNameKana: source.firstNameKana,
        lastNameKana: source.lastNameKana,
        honorific: source.honorific,
        email: source.email,
        phone: source.phone,
        company: source.company,
        department: source.department,
        address: clonedAddress,
        shopifyAddressId: source.shopifyAddressId,
        storeId: source.storeId,
        storeName: source.storeName,
        storeAddress: clonedStoreAddress,
        storeMetaobjectId: source.storeMetaobjectId,
        items: clonedItems,
        giftSettings: clonedGiftSettings,
        deliveryDate: source.deliveryDate,
        deliveryTime: source.deliveryTime,
        deliveryPeriod: source.deliveryPeriod,
      };
    }
    updateGroupNumbers() {
      const groups = undefined;
      this.querySelectorAll("delivery-group").forEach((group, index) => {
        const groupTitle = group.querySelector(".js-group-title");
        (groupTitle && (groupTitle.textContent = `お申込 ${index + 1}`),
          group.setAttribute(
            "data-delivery-group-index",
            (index + 1).toString(),
          ));
      });
    }
    updateDestinationNumbers(groupId) {
      const groupElement = this.querySelector(
        `delivery-group[data-delivery-group-id="${groupId}"]`,
      );
      if (groupElement) {
        const destinations = undefined;
        groupElement
          .querySelectorAll("destination-selector")
          .forEach((dest, index) => {
            var _a;
            const destTitle =
              null == (_a = dest.closest(".delivery-card"))
                ? void 0
                : _a.querySelector(".js-destination-title");
            (destTitle && (destTitle.textContent = `お届け先 ${index + 1}`),
              dest.setAttribute(
                "data-destination-index",
                (index + 1).toString(),
              ));
          });
      }
    }
    updateDeleteButtonsVisibility() {
      const groups = this.querySelectorAll("delivery-group");
      (groups.forEach((group) => {
        const deleteButton = group.querySelector(".js-btn-delete-group"),
          headerWithDelete = group.querySelector(".header-with-delete");
        deleteButton &&
          headerWithDelete &&
          (groups.length > 1
            ? (deleteButton.classList.remove("hidden"),
              (deleteButton.style.opacity = "1"))
            : deleteButton.classList.add("hidden"));
      }),
        groups.forEach((group) => {
          const destinations = group.querySelectorAll("destination-selector");
          destinations.forEach((dest) => {
            const deliveryCard = dest.closest(".delivery-card"),
              deleteButton =
                null == deliveryCard
                  ? void 0
                  : deliveryCard.querySelector(".js-btn-delete-destination"),
              headerWithDelete =
                null == deliveryCard
                  ? void 0
                  : deliveryCard.querySelector(".delivery-header-with-delete");
            deleteButton &&
              headerWithDelete &&
              (destinations.length > 1
                ? (deleteButton.classList.remove("hidden"),
                  (deleteButton.style.opacity = "1"))
                : deleteButton.classList.add("hidden"));
          });
        }));
    }
    async change(event) {
      var _a;
      if (!this.isRestoring)
        try {
          const { type: type, data: data } = event.detail,
            { groupId: groupId } = data,
            isHomeRoute = !groupId || "" === groupId;
          switch (type) {
            case "sender-selected":
              const { sender: sender } = data;
              isHomeRoute || this.addSenderToGroup(groupId, sender);
              break;
            case "destination-delivery-address-selected":
              const { destination: destination } = data;
              isHomeRoute
                ? this.updateHomeDestination(destination)
                : this.addDestinationToGroup(groupId, destination);
              break;
            case "destination-products-selected":
              const { destination: destinationWithProducts } = data;
              isHomeRoute
                ? this.updateHomeDestination(destinationWithProducts)
                : this.addDestinationToGroup(groupId, destinationWithProducts);
              break;
            case "shipping-unit-gift-selected":
              const {
                destinationId: destinationId,
                shippingUnitId: shippingUnitId,
                giftSettings: giftSettings,
              } = data;
              this.addGiftSettingsToShippingUnit(
                groupId,
                destinationId,
                shippingUnitId,
                giftSettings,
              );
              break;
            case "shipping-unit-datetime-selected":
              const { shippingUnit: shippingUnit } = data;
              this.addDatetimeToShippingUnit(
                groupId,
                shippingUnit.destinationId,
                shippingUnit.shippingUnitId,
                {
                  deliveryDate: shippingUnit.deliveryDate,
                  deliveryTime: shippingUnit.deliveryTime,
                  deliveryPeriod: shippingUnit.deliveryPeriod,
                },
              );
              break;
            case "destination-shipping-fee-updated":
              const { destination: destinationWithShippingFee } = data;
              isHomeRoute
                ? this.updateHomeDestination(destinationWithShippingFee)
                : this.updateDestinationShippingFee(
                  groupId,
                  destinationWithShippingFee,
                );
              break;
            case "destination-store-selected":
              const { destination: destinationWithStore } = data;
              this.addDestinationToGroup(groupId, destinationWithStore);
          }
          await this._updateTotalShippingCostDisplay();
          sessionManager.scheduleAutoSave(this.getSettings());
        } catch (error) { }
    }
    async _updateTotalShippingCostDisplay() {
      var _a;
      const settingsService = new SettingsService(),
        allDestinations =
          (null == (_a = this.settings.deliveryGroups)
            ? void 0
            : _a
              .map((group) =>
                group.destinations.map((destination) => destination),
              )
              .flat()) || [];
      if (0 === allDestinations.length)
        return void this._clearTotalShippingCost();
      const totalShippingCost =
        settingsService.calculateShippingCostForAllDestinations(
          allDestinations,
        ),
        originalTotalShippingCost = allDestinations.reduce(
          (sum, destination) =>
            sum +
            (destination.originalShippingFee || destination.shippingFee || 0),
          0,
        );
      try {
        const hasFreeShippingDiscount =
          await this._hasFreeShippingDiscountCode(),
          displayShippingCost = hasFreeShippingDiscount
            ? 0
            : totalShippingCost;
        await this._renderTotalShippingCost(
          displayShippingCost,
          originalTotalShippingCost,
          hasFreeShippingDiscount,
        );
      } catch (error) { }
    }
    updateHomeDestination(destination) {
      var _a;
      const existingIndex = this.homeDestinations.findIndex(
        (dest) => dest.destinationId === destination.destinationId,
      );
      if (-1 !== existingIndex) {
        const existingDest = this.homeDestinations[existingIndex];
        let mergedShippingUnits = destination.shippingUnits;
        (existingDest.shippingUnits && destination.shippingUnits
          ? ((mergedShippingUnits = {}),
            Object.keys(destination.shippingUnits).forEach((unitKey) => {
              var _a2, _b, _c, _d, _e, _f, _g, _h, _i, _j;
              mergedShippingUnits[unitKey] = {
                ...destination.shippingUnits[unitKey],
                deliveryDate:
                  (null == (_a2 = existingDest.shippingUnits[unitKey])
                    ? void 0
                    : _a2.deliveryDate) ||
                  (null == (_b = destination.shippingUnits[unitKey])
                    ? void 0
                    : _b.deliveryDate),
                deliveryTime:
                  (null == (_c = existingDest.shippingUnits[unitKey])
                    ? void 0
                    : _c.deliveryTime) ||
                  (null == (_d = destination.shippingUnits[unitKey])
                    ? void 0
                    : _d.deliveryTime),
                deliveryPeriod:
                  (null == (_e = existingDest.shippingUnits[unitKey])
                    ? void 0
                    : _e.deliveryPeriod) ||
                  (null == (_f = destination.shippingUnits[unitKey])
                    ? void 0
                    : _f.deliveryPeriod),
                expectedShipmentDate:
                  (null == (_g = existingDest.shippingUnits[unitKey])
                    ? void 0
                    : _g.expectedShipmentDate) ||
                  (null == (_h = destination.shippingUnits[unitKey])
                    ? void 0
                    : _h.expectedShipmentDate),
                giftSettings:
                  (null == (_i = existingDest.shippingUnits[unitKey])
                    ? void 0
                    : _i.giftSettings) ||
                  (null == (_j = destination.shippingUnits[unitKey])
                    ? void 0
                    : _j.giftSettings),
              };
            }))
          : existingDest.shippingUnits &&
          !destination.shippingUnits &&
          (mergedShippingUnits = existingDest.shippingUnits),
          (this.homeDestinations[existingIndex] = {
            ...existingDest,
            ...destination,
            shippingUnits: mergedShippingUnits,
          }));
      } else this.homeDestinations.push(destination);
      let homeGroup =
        null == (_a = this.settings.deliveryGroups)
          ? void 0
          : _a.find((g) => "自宅用" === g.purpose);
      if (!homeGroup) {
        const groupId = undefined;
        ((homeGroup = {
          groupId: this.generateGroupId(),
          purpose: "自宅用",
          sender: [],
          destinations: [],
        }),
          this.settings.deliveryGroups || (this.settings.deliveryGroups = []),
          this.settings.deliveryGroups.push(homeGroup));
      }
      if (!homeGroup.sender || 0 === homeGroup.sender.length) {
        const customerSender = this.getCustomerAsSender();
        customerSender && (homeGroup.sender = [customerSender]);
      }
      const groupDestIndex = homeGroup.destinations.findIndex(
        (dest) => dest.destinationId === destination.destinationId,
      );
      if (-1 !== groupDestIndex) {
        const existingDest = homeGroup.destinations[groupDestIndex];
        let mergedShippingUnits = destination.shippingUnits;
        (existingDest.shippingUnits && destination.shippingUnits
          ? ((mergedShippingUnits = {}),
            Object.keys(destination.shippingUnits).forEach((unitKey) => {
              var _a2, _b, _c, _d, _e, _f, _g, _h, _i, _j;
              mergedShippingUnits[unitKey] = {
                ...destination.shippingUnits[unitKey],
                deliveryDate:
                  (null == (_a2 = existingDest.shippingUnits[unitKey])
                    ? void 0
                    : _a2.deliveryDate) ||
                  (null == (_b = destination.shippingUnits[unitKey])
                    ? void 0
                    : _b.deliveryDate),
                deliveryTime:
                  (null == (_c = existingDest.shippingUnits[unitKey])
                    ? void 0
                    : _c.deliveryTime) ||
                  (null == (_d = destination.shippingUnits[unitKey])
                    ? void 0
                    : _d.deliveryTime),
                deliveryPeriod:
                  (null == (_e = existingDest.shippingUnits[unitKey])
                    ? void 0
                    : _e.deliveryPeriod) ||
                  (null == (_f = destination.shippingUnits[unitKey])
                    ? void 0
                    : _f.deliveryPeriod),
                expectedShipmentDate:
                  (null == (_g = existingDest.shippingUnits[unitKey])
                    ? void 0
                    : _g.expectedShipmentDate) ||
                  (null == (_h = destination.shippingUnits[unitKey])
                    ? void 0
                    : _h.expectedShipmentDate),
                giftSettings:
                  (null == (_i = existingDest.shippingUnits[unitKey])
                    ? void 0
                    : _i.giftSettings) ||
                  (null == (_j = destination.shippingUnits[unitKey])
                    ? void 0
                    : _j.giftSettings),
              };
            }))
          : existingDest.shippingUnits &&
          !destination.shippingUnits &&
          (mergedShippingUnits = existingDest.shippingUnits),
          (homeGroup.destinations[groupDestIndex] = {
            ...existingDest,
            ...destination,
            shippingUnits: mergedShippingUnits,
          }));
      } else homeGroup.destinations.push(destination);
    }
    _getJobTitleFromAddressCustomData(bindecSettings, addressId) {
      var _a, _b;
      if (null == addressId) return null;
      const addresses =
        null ==
          (_a =
            null == bindecSettings ? void 0 : bindecSettings.addressCustomData)
          ? void 0
          : _a.addresses;
      if (!Array.isArray(addresses)) return null;
      const item = addresses.find(
        (a) => null != a.id && String(a.id) === String(addressId),
      ),
        jobTitle =
          null == (_b = null == item ? void 0 : item.data)
            ? void 0
            : _b.job_title;
      return null != jobTitle && "" !== jobTitle ? String(jobTitle) : null;
    }
    _getCustomerFieldsFromProfileAddress(bindecSettings) {
      const pa =
        null == bindecSettings ? void 0 : bindecSettings.profileAddress;
      if (!pa) return null;
      const department =
        pa.job_title ??
        this._getJobTitleFromAddressCustomData(bindecSettings, pa.id) ??
        null;
      return {
        firstName: pa.first_name ?? "",
        lastName: pa.last_name ?? "",
        name:
          pa.name ??
          (pa.last_name && pa.first_name
            ? `${pa.last_name} ${pa.first_name}`
            : ""),
        phone: pa.phone ?? "",
        company: pa.company ?? null,
        department: department,
        address: {
          zipcode: pa.zip ?? "",
          prefecture: pa.province ?? "",
          city: pa.city ?? "",
          town: pa.town ?? null,
          address1: pa.address1 ?? "",
          address2: pa.address2 ?? null,
        },
      };
    }
    syncCustomerFromShopify() {
      var _a, _b, _c, _d, _e, _f;
      if (!CustomerService.isAuthenticated()) return;
      const bindecSettings = window.BINDEC_SHIPPING_SETTINGS,
        shopifyCustomer =
          null == bindecSettings ? void 0 : bindecSettings.customer;
      if (!shopifyCustomer) return;
      const ritsData = null == bindecSettings ? void 0 : bindecSettings.RITS,
        currentCustomerId =
          null == (_a = this.settings.customer) ? void 0 : _a.customerId,
        shopifyCustomerId =
          null == (_b = shopifyCustomer.id) ? void 0 : _b.toString();
      if (currentCustomerId === shopifyCustomerId) return;
      const profileFields =
        this._getCustomerFieldsFromProfileAddress(bindecSettings),
        defaultAddress = shopifyCustomer.default_address,
        originalDefaultAddressId =
          ((null ==
            (_c = null == bindecSettings ? void 0 : bindecSettings.profileAddress)
            ? void 0
            : _c.id) ??
            (null == bindecSettings
              ? void 0
              : bindecSettings.defaultAddressId)) ||
          null,
        profileAddressId =
          (null ==
            (_d = null == bindecSettings ? void 0 : bindecSettings.profileAddress)
            ? void 0
            : _d.id) ??
          (null == defaultAddress ? void 0 : defaultAddress.id) ??
          originalDefaultAddressId,
        existingCustomer = this.settings.customer || {
          customerId: "guest",
          shopifyCustomerId: "",
          name: "",
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          address: {
            zipcode: "",
            prefecture: "",
            city: "",
            address1: "",
            address2: null,
          },
          defaultAddressId: null,
        },
        baseFromShopify = {
          customerId: shopifyCustomerId,
          shopifyCustomerId: `gid://shopify/Customer/${shopifyCustomer.id}`,
          name:
            shopifyCustomer.last_name && shopifyCustomer.first_name
              ? `${shopifyCustomer.last_name} ${shopifyCustomer.first_name}`
              : "",
          firstName: shopifyCustomer.first_name || "",
          lastName: shopifyCustomer.last_name || "",
          email: shopifyCustomer.email || "",
          phone:
            shopifyCustomer.phone ||
            (null == defaultAddress ? void 0 : defaultAddress.phone) ||
            "",
          address: defaultAddress
            ? {
              zipcode: defaultAddress.zip || "",
              prefecture: defaultAddress.province || "",
              city: defaultAddress.city || "",
              town: null,
              address1: defaultAddress.address1 || "",
              address2: defaultAddress.address2 || null,
            }
            : existingCustomer.address,
          defaultAddressId: originalDefaultAddressId,
          memberRank: existingCustomer.memberRank || null,
          ritsCardNumber: (null == ritsData ? void 0 : ritsData.cartNo) || null,
          purpose: existingCustomer.purpose || null,
          orderFormPrintStatus: existingCustomer.orderFormPrintStatus || null,
          receiptPrintStatus: existingCustomer.receiptPrintStatus || null,
          purchaseDetailPrintStatus:
            existingCustomer.purchaseDetailPrintStatus || null,
          honorific: existingCustomer.honorific || null,
          company:
            (null == defaultAddress ? void 0 : defaultAddress.company) ??
            existingCustomer.company ??
            null,
          firstNameKana:
            existingCustomer.firstNameKana ||
            (null ==
              (_e = this._getKanaFromAddressCustomData(
                bindecSettings,
                profileAddressId,
              ))
              ? void 0
              : _e.first_name_kana) ||
            null,
          lastNameKana:
            existingCustomer.lastNameKana ||
            (null ==
              (_f = this._getKanaFromAddressCustomData(
                bindecSettings,
                profileAddressId,
              ))
              ? void 0
              : _f.last_name_kana) ||
            null,
          department:
            existingCustomer.department ||
            this._getJobTitleFromAddressCustomData(
              bindecSettings,
              profileAddressId,
            ) ||
            null,
        },
        shopifyCustomerData = profileFields
          ? { ...baseFromShopify, ...profileFields }
          : baseFromShopify;
      this.settings = {
        ...this.settings,
        customer: {
          ...existingCustomer,
          ...shopifyCustomerData,
          address: {
            ...existingCustomer.address,
            ...(shopifyCustomerData.address || {}),
            town: null,
          },
        },
      };
    }
    updateCustomer(customerData) {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
      const existingCustomer = this.settings.customer || {
        customerId: "guest",
        shopifyCustomerId: "",
        name: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: {
          zipcode: "",
          prefecture: "",
          city: "",
          address1: "",
          address2: null,
        },
        defaultAddressId: null,
      };
      let shopifyCustomerData = {};
      if (CustomerService.isAuthenticated()) {
        const bindecSettings = window.BINDEC_SHIPPING_SETTINGS,
          shopifyCustomer =
            null == bindecSettings ? void 0 : bindecSettings.customer;
        if (shopifyCustomer) {
          const defaultAddress = shopifyCustomer.default_address,
            originalDefaultAddressId =
              ((null ==
                (_a =
                  null == bindecSettings ? void 0 : bindecSettings.profileAddress)
                ? void 0
                : _a.id) ??
                (null == bindecSettings
                  ? void 0
                  : bindecSettings.defaultAddressId)) ||
              null,
            profileAddressId =
              (null ==
                (_b =
                  null == bindecSettings ? void 0 : bindecSettings.profileAddress)
                ? void 0
                : _b.id) ??
              (null == defaultAddress ? void 0 : defaultAddress.id) ??
              originalDefaultAddressId,
            ritsData = null == bindecSettings ? void 0 : bindecSettings.RITS,
            customerId = undefined,
            shopifyCustomerId = undefined,
            baseFromShopify = {
              customerId:
                (null == (_c = shopifyCustomer.id) ? void 0 : _c.toString()) ||
                "guest",
              shopifyCustomerId: `gid://shopify/Customer/${shopifyCustomer.id}`,
              name:
                shopifyCustomer.last_name && shopifyCustomer.first_name
                  ? `${shopifyCustomer.last_name} ${shopifyCustomer.first_name}`
                  : "",
              firstName: shopifyCustomer.first_name || "",
              lastName: shopifyCustomer.last_name || "",
              email: shopifyCustomer.email || "",
              phone:
                shopifyCustomer.phone ||
                (null == defaultAddress ? void 0 : defaultAddress.phone) ||
                "",
              address: defaultAddress
                ? {
                  zipcode: defaultAddress.zip || "",
                  prefecture: defaultAddress.province || "",
                  city: defaultAddress.city || "",
                  town: null,
                  address1: defaultAddress.address1 || "",
                  address2: defaultAddress.address2 || null,
                }
                : existingCustomer.address,
              defaultAddressId: originalDefaultAddressId,
              memberRank: existingCustomer.memberRank || null,
              ritsCardNumber:
                (null == ritsData ? void 0 : ritsData.cartNo) || null,
              purpose: existingCustomer.purpose || null,
              orderFormPrintStatus:
                existingCustomer.orderFormPrintStatus || null,
              receiptPrintStatus: existingCustomer.receiptPrintStatus || null,
              purchaseDetailPrintStatus:
                existingCustomer.purchaseDetailPrintStatus || null,
              honorific: existingCustomer.honorific || null,
              firstNameKana:
                existingCustomer.firstNameKana ||
                (null ==
                  (_d = this._getKanaFromAddressCustomData(
                    bindecSettings,
                    profileAddressId,
                  ))
                  ? void 0
                  : _d.first_name_kana) ||
                null,
              lastNameKana:
                existingCustomer.lastNameKana ||
                (null ==
                  (_e = this._getKanaFromAddressCustomData(
                    bindecSettings,
                    profileAddressId,
                  ))
                  ? void 0
                  : _e.last_name_kana) ||
                null,
              company:
                (null == defaultAddress ? void 0 : defaultAddress.company) ||
                existingCustomer.company ||
                null,
              department:
                existingCustomer.department ||
                this._getJobTitleFromAddressCustomData(
                  bindecSettings,
                  profileAddressId,
                ) ||
                null,
            },
            profileFields =
              this._getCustomerFieldsFromProfileAddress(bindecSettings);
          shopifyCustomerData = profileFields
            ? { ...baseFromShopify, ...profileFields }
            : baseFromShopify;
        } else {
          const customerInfo = CustomerService.getCustomerBasicInfo(),
            defaultAddress = CustomerService.getDefaultAddress(),
            originalDefaultAddressId =
              ((null ==
                (_f =
                  null == bindecSettings ? void 0 : bindecSettings.profileAddress)
                ? void 0
                : _f.id) ??
                (null == bindecSettings
                  ? void 0
                  : bindecSettings.defaultAddressId)) ||
              null;
          let shopifyCustomerId = customerInfo.customerId;
          if (!shopifyCustomerId && customerInfo.shopifyCustomerId) {
            const match =
              customerInfo.shopifyCustomerId.match(/\/Customer\/(\d+)$/);
            match && (shopifyCustomerId = match[1]);
          }
          const fallbackBase = {
            customerId: shopifyCustomerId || "guest",
            shopifyCustomerId: customerInfo.shopifyCustomerId || "",
            name: customerInfo.name || "",
            firstName: customerInfo.firstName || "",
            lastName: customerInfo.lastName || "",
            email: customerInfo.email || "",
            phone:
              customerInfo.phone ||
              (null == defaultAddress ? void 0 : defaultAddress.phone) ||
              "",
            address: defaultAddress
              ? {
                zipcode: defaultAddress.zip || "",
                prefecture: defaultAddress.province || "",
                city: defaultAddress.city || "",
                town: null,
                address1: defaultAddress.address1 || "",
                address2: defaultAddress.address2 || null,
              }
              : existingCustomer.address,
            defaultAddressId: originalDefaultAddressId,
            memberRank: existingCustomer.memberRank || null,
            ritsCardNumber: existingCustomer.ritsCardNumber || null,
            purpose: existingCustomer.purpose || null,
            orderFormPrintStatus:
              existingCustomer.orderFormPrintStatus || null,
            receiptPrintStatus: existingCustomer.receiptPrintStatus || null,
            purchaseDetailPrintStatus:
              existingCustomer.purchaseDetailPrintStatus || null,
            honorific: existingCustomer.honorific || null,
            firstNameKana:
              existingCustomer.firstNameKana ||
              (null ==
                (_h = this._getKanaFromAddressCustomData(
                  bindecSettings,
                  (null ==
                    (_g =
                      null == bindecSettings
                        ? void 0
                        : bindecSettings.profileAddress)
                    ? void 0
                    : _g.id) ??
                  (null == bindecSettings
                    ? void 0
                    : bindecSettings.defaultAddressId),
                ))
                ? void 0
                : _h.first_name_kana) ||
              null,
            lastNameKana:
              existingCustomer.lastNameKana ||
              (null ==
                (_j = this._getKanaFromAddressCustomData(
                  bindecSettings,
                  (null ==
                    (_i =
                      null == bindecSettings
                        ? void 0
                        : bindecSettings.profileAddress)
                    ? void 0
                    : _i.id) ??
                  (null == bindecSettings
                    ? void 0
                    : bindecSettings.defaultAddressId),
                ))
                ? void 0
                : _j.last_name_kana) ||
              null,
            company: existingCustomer.company || null,
            department:
              existingCustomer.department ||
              this._getJobTitleFromAddressCustomData(
                bindecSettings,
                (null ==
                  (_k =
                    null == bindecSettings
                      ? void 0
                      : bindecSettings.profileAddress)
                  ? void 0
                  : _k.id) ??
                (null == bindecSettings
                  ? void 0
                  : bindecSettings.defaultAddressId),
              ) ||
              null,
          },
            profileFields =
              this._getCustomerFieldsFromProfileAddress(bindecSettings);
          shopifyCustomerData = profileFields
            ? { ...fallbackBase, ...profileFields }
            : fallbackBase;
        }
      }
      const finalCustomerId =
        shopifyCustomerData.customerId ||
        customerData.customerId ||
        existingCustomer.customerId ||
        "guest";
      ((this.settings = {
        ...this.settings,
        customer: {
          ...existingCustomer,
          ...(CustomerService.isAuthenticated()
            ? shopifyCustomerData
            : customerData),
          customerId: finalCustomerId,
          address: {
            ...existingCustomer.address,
            ...(CustomerService.isAuthenticated()
              ? { ...(shopifyCustomerData.address || {}), town: null }
              : customerData.address || {}),
          },
        },
      }),
        CustomerService.isAuthenticated());
    }
    addSenderToGroup(groupId, sender) {
      var _a;
      this.settings = {
        ...this.settings,
        deliveryGroups:
          null == (_a = this.settings.deliveryGroups)
            ? void 0
            : _a.map((group) =>
              group.groupId === groupId
                ? { ...group, sender: [sender] }
                : group,
            ),
      };
    }
    addDestinationToGroup(groupId, destination) {
      var _a;
      this.settings = {
        ...this.settings,
        deliveryGroups:
          null == (_a = this.settings.deliveryGroups)
            ? void 0
            : _a.map((group) => {
              if (group.groupId === groupId) {
                const existingDestinationIndex = group.destinations.findIndex(
                  (dest) => dest.destinationId === destination.destinationId,
                );
                if (-1 !== existingDestinationIndex) {
                  const updatedDestinations = [...group.destinations],
                    existingDest =
                      updatedDestinations[existingDestinationIndex];
                  let mergedShippingUnits = destination.shippingUnits;
                  return (
                    existingDest.shippingUnits && destination.shippingUnits
                      ? ((mergedShippingUnits = {}),
                        Object.keys(destination.shippingUnits).forEach(
                          (unitKey) => {
                            var _a2, _b, _c, _d, _e, _f, _g, _h;
                            mergedShippingUnits[unitKey] = {
                              ...destination.shippingUnits[unitKey],
                              deliveryDate:
                                (null ==
                                  (_a2 = existingDest.shippingUnits[unitKey])
                                  ? void 0
                                  : _a2.deliveryDate) ||
                                (null ==
                                  (_b = destination.shippingUnits[unitKey])
                                  ? void 0
                                  : _b.deliveryDate),
                              deliveryTime:
                                (null ==
                                  (_c = existingDest.shippingUnits[unitKey])
                                  ? void 0
                                  : _c.deliveryTime) ||
                                (null ==
                                  (_d = destination.shippingUnits[unitKey])
                                  ? void 0
                                  : _d.deliveryTime),
                              deliveryPeriod:
                                (null ==
                                  (_e = existingDest.shippingUnits[unitKey])
                                  ? void 0
                                  : _e.deliveryPeriod) ||
                                (null ==
                                  (_f = destination.shippingUnits[unitKey])
                                  ? void 0
                                  : _f.deliveryPeriod),
                              expectedShipmentDate:
                                (null ==
                                  (_g = existingDest.shippingUnits[unitKey])
                                  ? void 0
                                  : _g.expectedShipmentDate) ||
                                (null ==
                                  (_h = destination.shippingUnits[unitKey])
                                  ? void 0
                                  : _h.expectedShipmentDate),
                            };
                          },
                        ))
                      : existingDest.shippingUnits &&
                      !destination.shippingUnits &&
                      (mergedShippingUnits = existingDest.shippingUnits),
                    (updatedDestinations[existingDestinationIndex] = {
                      ...existingDest,
                      ...destination,
                      shippingUnits: mergedShippingUnits,
                    }),
                    { ...group, destinations: updatedDestinations }
                  );
                }
                return {
                  ...group,
                  destinations: [...group.destinations, destination],
                };
              }
              return group;
            }),
      };
    }
    addGiftSettingsToShippingUnit(
      groupId,
      destinationId,
      shippingUnitId,
      giftSettings,
    ) {
      var _a;
      const fullShippingUnitKey = `${destinationId}_${shippingUnitId}`,
        shippingUnit =
          window.BINDEC_SHIPPING_UNIT_SETTINGS[fullShippingUnitKey];
      if (!shippingUnit) return;
      const group =
        null == (_a = this.settings.deliveryGroups)
          ? void 0
          : _a.find((group2) => group2.groupId === groupId);
      if (!group) return;
      const destination = group.destinations.find(
        (destination2) => destination2.destinationId === destinationId,
      );
      if (!destination) return;
      const shippingUnitVariantIds = shippingUnit.items.map(
        (item) => `gid://shopify/ProductVariant/${item.variant_id}`,
      );
      (destination.items.forEach((item) => {
        shippingUnitVariantIds.includes(item.variantId) &&
          (item.giftSettings = giftSettings);
      }),
        destination.shippingUnits || (destination.shippingUnits = {}),
        destination.shippingUnits[fullShippingUnitKey] ||
        (destination.shippingUnits[fullShippingUnitKey] = shippingUnit),
        (destination.shippingUnits[fullShippingUnitKey].giftSettings =
          giftSettings),
        sessionManager.scheduleAutoSave(this.getSettings()));
    }
    addDatetimeToShippingUnit(
      groupId,
      destinationId,
      shippingUnitId,
      datetimeSettings,
    ) {
      var _a;
      const fullShippingUnitKey = `${destinationId}_${shippingUnitId}`,
        shippingUnit =
          window.BINDEC_SHIPPING_UNIT_SETTINGS[fullShippingUnitKey];
      if (!shippingUnit) return;
      let destination = null;
      const isHomeRoute = undefined;
      if ("home" === this.currentOrderType) {
        if (
          ((destination = this.homeDestinations.find(
            (dest) => dest.destinationId === destinationId,
          )),
            !destination)
        )
          return;
      } else {
        const group =
          null == (_a = this.settings.deliveryGroups)
            ? void 0
            : _a.find((group2) => group2.groupId === groupId);
        if (!group) return;
        if (
          ((destination = group.destinations.find(
            (destination2) => destination2.destinationId === destinationId,
          )),
            !destination)
        )
          return;
      }
      const shippingUnitVariantIds = shippingUnit.items.map(
        (item) => `gid://shopify/ProductVariant/${item.variant_id}`,
      );
      let expectedShipmentDate;
      if (datetimeSettings.deliveryDate) {
        const variantIds = shippingUnit.items.map((item) =>
          String(item.variant_id),
        ),
          largestLeadTime = Math.max(
            ...variantIds.map(
              (variantId) =>
                window.BINDEC_SHIPPING_SETTINGS.leadTimes[variantId] || 0,
            ),
          ),
          deliveryDate = new Date(datetimeSettings.deliveryDate),
          shipmentDate = new Date(deliveryDate);
        (shipmentDate.setDate(shipmentDate.getDate() - largestLeadTime),
          (expectedShipmentDate = shipmentDate.toISOString().split("T")[0]));
      }
      (destination.items.forEach((item) => {
        shippingUnitVariantIds.includes(item.variantId) &&
          ((item.deliveryDate = datetimeSettings.deliveryDate),
            (item.deliveryTime = datetimeSettings.deliveryTime),
            (item.deliveryPeriod = datetimeSettings.deliveryPeriod));
      }),
        destination.shippingUnits || (destination.shippingUnits = {}),
        destination.shippingUnits[fullShippingUnitKey] ||
        (destination.shippingUnits[fullShippingUnitKey] = shippingUnit),
        (destination.shippingUnits[fullShippingUnitKey].deliveryDate =
          datetimeSettings.deliveryDate),
        (destination.shippingUnits[fullShippingUnitKey].deliveryTime =
          datetimeSettings.deliveryTime),
        (destination.shippingUnits[fullShippingUnitKey].deliveryPeriod =
          datetimeSettings.deliveryPeriod),
        (destination.shippingUnits[fullShippingUnitKey].expectedShipmentDate =
          expectedShipmentDate),
        sessionManager.scheduleAutoSave(this.getSettings()));
    }
    updateDestinationShippingFee(groupId, destinationUpdate) {
      var _a;
      this.settings = {
        ...this.settings,
        deliveryGroups:
          null == (_a = this.settings.deliveryGroups)
            ? void 0
            : _a.map((group) => {
              if (group.groupId === groupId) {
                const updatedDestinations = group.destinations.map((dest) => {
                  if (
                    dest.destinationId === destinationUpdate.destinationId
                  ) {
                    let mergedShippingUnits = destinationUpdate.shippingUnits;
                    return (
                      dest.shippingUnits &&
                      destinationUpdate.shippingUnits &&
                      ((mergedShippingUnits = {}),
                        Object.keys(destinationUpdate.shippingUnits).forEach(
                          (unitKey) => {
                            var _a2, _b, _c, _d, _e, _f, _g, _h, _i, _j;
                            mergedShippingUnits[unitKey] = {
                              ...destinationUpdate.shippingUnits[unitKey],
                              deliveryDate:
                                (null == (_a2 = dest.shippingUnits[unitKey])
                                  ? void 0
                                  : _a2.deliveryDate) ||
                                (null ==
                                  (_b =
                                    destinationUpdate.shippingUnits[unitKey])
                                  ? void 0
                                  : _b.deliveryDate),
                              deliveryTime:
                                (null == (_c = dest.shippingUnits[unitKey])
                                  ? void 0
                                  : _c.deliveryTime) ||
                                (null ==
                                  (_d =
                                    destinationUpdate.shippingUnits[unitKey])
                                  ? void 0
                                  : _d.deliveryTime),
                              deliveryPeriod:
                                (null == (_e = dest.shippingUnits[unitKey])
                                  ? void 0
                                  : _e.deliveryPeriod) ||
                                (null ==
                                  (_f =
                                    destinationUpdate.shippingUnits[unitKey])
                                  ? void 0
                                  : _f.deliveryPeriod),
                              expectedShipmentDate:
                                (null == (_g = dest.shippingUnits[unitKey])
                                  ? void 0
                                  : _g.expectedShipmentDate) ||
                                (null ==
                                  (_h =
                                    destinationUpdate.shippingUnits[unitKey])
                                  ? void 0
                                  : _h.expectedShipmentDate),
                              giftSettings:
                                (null == (_i = dest.shippingUnits[unitKey])
                                  ? void 0
                                  : _i.giftSettings) ||
                                (null ==
                                  (_j =
                                    destinationUpdate.shippingUnits[unitKey])
                                  ? void 0
                                  : _j.giftSettings),
                            };
                          },
                        )),
                      {
                        ...dest,
                        shippingFee: destinationUpdate.shippingFee,
                        originalShippingFee:
                          destinationUpdate.originalShippingFee,
                        totalWeight: destinationUpdate.totalWeight,
                        shippingUnits: mergedShippingUnits,
                        hasUndeliverableDestination:
                          destinationUpdate.hasUndeliverableDestination,
                        undeliverableErrorMessage:
                          destinationUpdate.undeliverableErrorMessage,
                      }
                    );
                  }
                  return dest;
                });
                return { ...group, destinations: updatedDestinations };
              }
              return group;
            }),
      };
    }
    generateGroupId() {
      return `req_${Date.now()}`;
    }
    generateDestinationId() {
      return `dest_${Date.now()}`;
    }
    getCustomerAsSender() {
      const customerInfo = CustomerService.getCustomerBasicInfo(),
        defaultAddress = CustomerService.getDefaultAddress();
      if (!customerInfo.customerId || !defaultAddress) return null;
      const bindecSettings = window.BINDEC_SHIPPING_SETTINGS,
        profileAddress =
          null == bindecSettings ? void 0 : bindecSettings.profileAddress,
        addr = profileAddress || defaultAddress,
        profileAddressId =
          (null == profileAddress ? void 0 : profileAddress.id) ??
          (null != defaultAddress.id ? defaultAddress.id : null),
        honorific = this._getHonorificFromAddressCustomData(
          bindecSettings,
          profileAddressId,
        ),
        department =
          this._getJobTitleFromAddressCustomData(
            bindecSettings,
            profileAddressId,
          ) ?? null,
        kanaData = this._getKanaFromAddressCustomData(
          bindecSettings,
          profileAddressId,
        );
      return {
        name:
          `${addr.last_name || ""} ${addr.first_name || ""}`.trim() ||
          customerInfo.name ||
          "",
        firstName: addr.first_name || customerInfo.firstName || "",
        lastName: addr.last_name || customerInfo.lastName || "",
        firstNameKana:
          (null == kanaData ? void 0 : kanaData.first_name_kana) ?? null,
        lastNameKana:
          (null == kanaData ? void 0 : kanaData.last_name_kana) ?? null,
        honorific: honorific ?? null,
        shopifyAddressId:
          null != addr.id ? `gid://shopify/MailingAddress/${addr.id}` : null,
        company: addr.company || null,
        department: department,
        email: customerInfo.email || null,
        phone: addr.phone || customerInfo.phone || null,
        address: {
          zipcode: addr.zip || "",
          prefecture: addr.province || "",
          city: addr.city || "",
          address1: addr.address1 || "",
          address2: addr.address2 || null,
        },
      };
    }
    _getHonorificFromAddressCustomData(bindecSettings, addressId) {
      var _a, _b;
      if (null == addressId) return null;
      const addresses =
        null ==
          (_a =
            null == bindecSettings ? void 0 : bindecSettings.addressCustomData)
          ? void 0
          : _a.addresses;
      if (!Array.isArray(addresses)) return null;
      const item = addresses.find(
        (a) => null != a.id && String(a.id) === String(addressId),
      ),
        value =
          null == (_b = null == item ? void 0 : item.data)
            ? void 0
            : _b.honorific;
      return null != value && "" !== value ? String(value) : null;
    }
    _getKanaFromAddressCustomData(bindecSettings, addressId) {
      var _a, _b, _c;
      if (null == addressId) return null;
      const addresses =
        null ==
          (_a =
            null == bindecSettings ? void 0 : bindecSettings.addressCustomData)
          ? void 0
          : _a.addresses;
      if (!Array.isArray(addresses)) return null;
      const item = addresses.find(
        (a) => null != a.id && String(a.id) === String(addressId),
      ),
        first_name_kana =
          null == (_b = null == item ? void 0 : item.data)
            ? void 0
            : _b.first_name_kana,
        last_name_kana =
          null == (_c = null == item ? void 0 : item.data)
            ? void 0
            : _c.last_name_kana;
      return (null != first_name_kana && "" !== first_name_kana) ||
        (null != last_name_kana && "" !== last_name_kana)
        ? {
          first_name_kana: String(first_name_kana || ""),
          last_name_kana: String(last_name_kana || ""),
        }
        : null;
    }
    validateStateConsistency() {
      var _a, _b;
      try {
        const domGroups = this.querySelectorAll("delivery-group"),
          settingsGroups = this.settings.deliveryGroups || [];
        if (domGroups.length !== settingsGroups.length) return !1;
        for (let i = 0; i < settingsGroups.length; i++) {
          const group = settingsGroups[i],
            domGroup = undefined,
            domDestinations = domGroups[i].querySelectorAll(
              "destination-selector",
            ),
            settingsDestinations = group.destinations || [];
          if (domDestinations.length !== settingsDestinations.length) return !1;
          for (let j = 0; j < settingsDestinations.length; j++) {
            const domDestId =
              null == (_a = domDestinations[j])
                ? void 0
                : _a.getAttribute("data-destination-id"),
              settingsDestId = undefined;
            if (
              domDestId !==
              (null == (_b = settingsDestinations[j])
                ? void 0
                : _b.destinationId)
            )
              return !1;
          }
        }
        return !0;
      } catch (error) {
        return !1;
      }
    }
    _getProductsWithoutPurpose(currentPurpose) {
      var _a, _b;
      const allCartItems =
        (null == (_a = window.BINDEC_SHIPPING_SETTINGS)
          ? void 0
          : _a.cartItemsWithPurposes) || [],
        purposeMaster =
          (null == (_b = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _b.purposeMaster) || [],
        invalidProductIds = [];
      if (!allCartItems || 0 === allCartItems.length) return invalidProductIds;
      const cartItemsWithPurposes = undefined;
      return (
        filterOutAccessoryItems(allCartItems).forEach((item) => {
          var _a2, _b2;
          if (item.purposes && 0 !== item.purposes.length) {
            const hasPurpose = undefined;
            (item.purposes || []).some((purpose) => {
              const isActive =
                void 0 !== purpose.is_published
                  ? purpose.is_published
                  : purpose._isActive,
                purposeName = purpose.name || purpose._title;
              return (
                purpose &&
                isActive &&
                (purposeName === currentPurpose ||
                  purpose.id === currentPurpose)
              );
            }) ||
              invalidProductIds.push(
                null == (_b2 = item.product_id) ? void 0 : _b2.toString(),
              );
          } else
            invalidProductIds.push(
              null == (_a2 = item.product_id) ? void 0 : _a2.toString(),
            );
        }),
        invalidProductIds
      );
    }
    _removeAllProductsWithoutPurpose(invalidProductIds, newPurpose) {
      const removedItems = window.BINDEC_SHIPPING_SETTINGS.cart.items.reduce(
        (acc, item) => (
          invalidProductIds.includes(item.product_id.toString()) &&
          (acc = { ...acc, [item.variant_id]: 0 }),
          acc
        ),
        {},
      );
      Object.keys(removedItems).length > 0 &&
        fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            updates: removedItems,
            sections: this._getSectionsToRender().map(
              (section) => section.section,
            ),
          }),
        })
          .then((response) => response.text())
          .then(async (state) => {
            const parsedState = JSON.parse(state);
            (this._getSectionsToRender().forEach((section) => {
              var _a;
              const elementToReplace =
                (null == (_a = document.getElementById(section.id))
                  ? void 0
                  : _a.querySelector(section.selector)) ||
                document.getElementById(section.id);
              elementToReplace &&
                ((elementToReplace.innerHTML = this._getSectionInnerHTML(
                  parsedState.sections[section.section],
                  section.selector,
                )),
                  this._executeScripts(elementToReplace));
            }),
              window.publish("cart-update", {
                source: "cart-items",
                cartData: parsedState,
              }));
            const pendingPurpose = this._pendingPurposeRestore;
            pendingPurpose &&
              (delete this._pendingPurposeRestore,
                (async () => {
                  const restored = undefined;
                  (await this._restoreCartFromSession(pendingPurpose, !1)) &&
                    setTimeout(() => {
                      window.location.reload();
                    }, 500);
                })());
          })
          .catch((error) => { });
    }
    async _cleanupUnusedPropertiesFromCart(initialCart) {
      try {
        const UNUSED_PROPERTIES = ["__total_shipping_cost", "_order_date"],
          cart2 = initialCart || (await cartCache.get(!0));
        if (!cart2) return null;
        if (!cart2.items || 0 === cart2.items.length)
          return (
            sessionStorage.removeItem("_cleanup_unused_properties_done"),
            cart2
          );
        const hasUnusedProperties = undefined;
        if (
          !cart2.items.some(
            (item) =>
              item.properties &&
              UNUSED_PROPERTIES.some((prop) => prop in item.properties),
          )
        )
          return cart2;
        const cleanupDone = undefined;
        if (
          "true" === sessionStorage.getItem("_cleanup_unused_properties_done")
        )
          return cart2;
        let cleanedCount = 0;
        const itemsToClean = cart2.items.filter(
          (item) =>
            !!item.properties &&
            UNUSED_PROPERTIES.some((prop) => prop in item.properties),
        );
        if (itemsToClean.length > 0) {
          const updates = itemsToClean.map((item) => {
            const cleanProperties = { ...item.properties };
            return (
              UNUSED_PROPERTIES.forEach((prop) => {
                delete cleanProperties[prop];
              }),
              {
                id: item.key,
                quantity: item.quantity,
                properties: cleanProperties,
              }
            );
          });
          try {
            const responseData = await updateCartItemsBatch(updates, !0);
            ((cleanedCount = itemsToClean.length),
              document.dispatchEvent(
                new CustomEvent("cart:updated", {
                  detail: {
                    cart: responseData,
                    source: "multi-shipping-settings",
                  },
                }),
              ));
          } catch (error) {
            window.location.reload();
          }
        }
        return cleanedCount > 0
          ? (sessionStorage.setItem("_cleanup_unused_properties_done", "true"),
            await cartCache.get(!0))
          : cart2;
      } catch (error) {
        return cart;
      }
    }
    _updateCartSectionsFromResponse(sections) {
      try {
        if (!sections || "object" != typeof sections)
          return void window.location.reload();
        let updated = !1;
        for (const [sectionId, html] of Object.entries(sections)) {
          if (!html) continue;
          const parser = undefined,
            doc = new DOMParser().parseFromString(html, "text/html"),
            bodyContent = doc.body.innerHTML,
            sectionSelectors = [
              `#shopify-section-${sectionId}`,
              `[data-section-id="${sectionId}"]`,
              `.shopify-section-${sectionId}`,
              `[id*="${sectionId}"]`,
            ];
          for (const selector of sectionSelectors) {
            const newSection = doc.querySelector(selector),
              oldSection = document.querySelector(selector);
            if (newSection && oldSection) {
              (oldSection.replaceWith(newSection), (updated = !0));
              break;
            }
          }
          if (updated) break;
          const commonCartSelectors = [
            'form[action="/cart"]',
            "cart-items",
            "#cart-items",
            ".cart-items",
            "[data-cart-items]",
            "cart-drawer",
            "#cart-drawer",
            ".cart-drawer",
            "main-cart-items",
            "#main-cart-items",
            ".main-cart-items",
            "#MainContent",
            "main#MainContent",
          ];
          for (const selector of commonCartSelectors) {
            const newElement = doc.querySelector(selector),
              oldElement = document.querySelector(selector);
            if (newElement && oldElement) {
              (oldElement.replaceWith(newElement), (updated = !0));
              break;
            }
          }
          if (updated) break;
        }
        if (updated)
          return void document.dispatchEvent(
            new CustomEvent("cart:updated", {
              detail: { source: "multi-shipping-settings" },
            }),
          );
        const firstSectionHtml = Object.values(sections)[0];
        if (firstSectionHtml) {
          const parser = new DOMParser(),
            doc = undefined,
            allElements = parser
              .parseFromString(firstSectionHtml, "text/html")
              .body.querySelectorAll("*"),
            cartRelatedElements = document.querySelectorAll(
              '[id*="cart"], [class*="cart"], cart-items, cart-drawer',
            );
        }
        window.location.reload();
      } catch (error) {
        window.location.reload();
      }
    }
    async restoreFromSession() {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q;
      this.isRestoring = !0;
      try {
        try {
          const backup = sessionStorage.getItem("_multi_shipping_backup");
          if (backup) {
            const {
              data: data,
              timestamp: timestamp,
              token: token,
            } = JSON.parse(backup),
              cartItemCount2 = undefined;
            if (
              0 ===
              ((null ==
                (_c =
                  null ==
                    (_b =
                      null == (_a = window.BINDEC_SHIPPING_SETTINGS)
                        ? void 0
                        : _a.cart)
                    ? void 0
                    : _b.items)
                ? void 0
                : _c.length) || 0)
            )
              return (
                sessionStorage.removeItem("_multi_shipping_backup"),
                await sessionManager.delete(),
                void (this.isRestoring = !1)
              );
            let cart3 =
              null == (_d = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _d.cart;
            if (
              (null == cart3 ? void 0 : cart3.items) &&
              cart3.items.length > 0
            ) {
              const hasNonGiftItems = undefined;
              if (!this.hasNonGiftItems(cart3)) {
                sessionStorage.removeItem("_multi_shipping_backup");
                const emptySettings = {
                  deliveryGroups: [],
                  homeDestinations: [],
                  notes: {},
                  orderType: this.currentOrderType,
                };
                return (
                  await this.updateGiftItemsInCart(emptySettings),
                  await sessionManager.delete(),
                  void window.location.reload()
                );
              }
            }
            const attrToken2 =
              null == (_e = null == cart3 ? void 0 : cart3.attributes)
                ? void 0
                : _e._multi_shipping_session_token,
              validAttrToken2 =
                attrToken2 && "" !== attrToken2 ? attrToken2 : null;
            let itemToken2 = null;
            if (
              (null == cart3 ? void 0 : cart3.items) &&
              cart3.items.length > 0
            )
              for (const item of cart3.items) {
                const token2 =
                  null == (_f = item.properties)
                    ? void 0
                    : _f._multi_shipping_token;
                if (token2 && "" !== token2) {
                  itemToken2 = token2;
                  break;
                }
              }
            const currentToken2 = validAttrToken2 || itemToken2;
            if (!currentToken2 || (token && currentToken2 !== token))
              return (
                sessionStorage.removeItem("_multi_shipping_backup"),
                void (this.isRestoring = !1)
              );
            if (Date.now() - timestamp < 3e5) {
              (token && sessionManager.setToken(token),
                sessionStorage.removeItem("_multi_shipping_backup"));
              const hasDeliveryGroups2 =
                data && data.deliveryGroups && data.deliveryGroups.length > 0,
                hasHomeDestinations2 =
                  data &&
                  data.homeDestinations &&
                  data.homeDestinations.length > 0;
              if (hasDeliveryGroups2 || hasHomeDestinations2) {
                this.settings = data;
                const homeGroupsInDeliveryGroups2 =
                  (null == (_g = data.deliveryGroups)
                    ? void 0
                    : _g.filter((g) => "自宅用" === g.purpose)) || [];
                homeGroupsInDeliveryGroups2.length > 0
                  ? (this.homeDestinations =
                    homeGroupsInDeliveryGroups2.flatMap(
                      (group) => group.destinations || [],
                    ))
                  : hasHomeDestinations2 &&
                  (this.homeDestinations = data.homeDestinations || []);
                const purpose = null == (_h = data.notes) ? void 0 : _h.purpose,
                  orderType = data.orderType,
                  tabsElement = document.querySelector("wl-tabs");
                if (tabsElement) {
                  let tabType = "gift";
                  (orderType
                    ? (tabType = orderType)
                    : purpose &&
                    (tabType = "自宅用" === purpose ? "home" : "gift"),
                    tabsElement.switchTab(tabType, !0),
                    (this.currentOrderType = tabType));
                }
                return (
                  purpose &&
                  ((this.purpose = purpose),
                    window.BINDEC_SHIPPING_SETTINGS &&
                    (window.BINDEC_SHIPPING_SETTINGS.currentPurpose =
                      purpose)),
                  void setTimeout(async () => {
                    try {
                      (this.renderFromSavedData(),
                        purpose &&
                          "自宅用" !== purpose &&
                          (function(scope){ const r = scope.querySelector("input[name='purpose'][value='"+purpose+"']"); if(r) r.checked = true; })(this),
                        (this.isRestoring = !1),
                        await this.syncCartWithSettings(),
                        purpose &&
                          "自宅用" !== purpose &&
                          (function(scope){ const r = scope.querySelector("input[name='purpose'][value='"+purpose+"']"); if(r) r.checked = true; })(this),
                        purpose && document.dispatchEvent(new CustomEvent('wl:purpose-restored', { detail: { purpose } })),
                        document.dispatchEvent(new CustomEvent('wl:restore-complete')));
                    } catch (error) {
                      this.isRestoring = !1;
                      document.dispatchEvent(new CustomEvent('wl:restore-complete'));
                    }
                  }, 100)
                );
              }
            } else sessionStorage.removeItem("_multi_shipping_backup");
          }
        } catch (err) { }
        await sessionManager.ensureInitialized();
        const cartItemCount = undefined;
        if (
          0 ===
          ((null ==
            (_k =
              null ==
                (_j =
                  null == (_i = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _i.cart)
                ? void 0
                : _j.items)
            ? void 0
            : _k.length) || 0)
        )
          return (await sessionManager.delete(), void (this.isRestoring = !1));
        let cart2 =
          null == (_l = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _l.cart;
        if ((null == cart2 ? void 0 : cart2.items) && cart2.items.length > 0) {
          const hasNonGiftItems = undefined;
          if (!this.hasNonGiftItems(cart2)) {
            const emptySettings = {
              deliveryGroups: [],
              homeDestinations: [],
              notes: {},
              orderType: this.currentOrderType,
            };
            return (
              await this.updateGiftItemsInCart(emptySettings),
              await sessionManager.delete(),
              void window.location.reload()
            );
          }
        }
        const attrToken =
          null == (_m = null == cart2 ? void 0 : cart2.attributes)
            ? void 0
            : _m._multi_shipping_session_token,
          validAttrToken = attrToken && "" !== attrToken ? attrToken : null;
        let itemToken = null;
        if ((null == cart2 ? void 0 : cart2.items) && cart2.items.length > 0)
          for (const item of cart2.items) {
            const token =
              null == (_n = item.properties)
                ? void 0
                : _n._multi_shipping_token;
            if (token && "" !== token) {
              itemToken = token;
              break;
            }
          }
        const currentToken = validAttrToken || itemToken;
        if (!currentToken) return void (this.isRestoring = !1);
        const sessionToken = sessionManager.getToken();
        if (sessionToken && currentToken !== sessionToken)
          return void (this.isRestoring = !1);
        const savedData = await sessionManager.load(),
          hasDeliveryGroups =
            savedData &&
            savedData.deliveryGroups &&
            savedData.deliveryGroups.length > 0,
          hasHomeDestinations =
            savedData &&
            savedData.homeDestinations &&
            savedData.homeDestinations.length > 0,
          homeGroupsInDeliveryGroups =
            (null ==
              (_o = null == savedData ? void 0 : savedData.deliveryGroups)
              ? void 0
              : _o.filter((g) => "自宅用" === g.purpose)) || [],
          hasHomeGroupsInDeliveryGroups = homeGroupsInDeliveryGroups.length > 0;
        if (hasDeliveryGroups || hasHomeDestinations) {
          if (((this.settings = savedData), hasHomeGroupsInDeliveryGroups))
            this.homeDestinations = homeGroupsInDeliveryGroups.flatMap(
              (group) => group.destinations || [],
            );
          else if (hasHomeDestinations) {
            if (
              ((this.homeDestinations = savedData.homeDestinations || []),
                this.homeDestinations.length > 0)
            ) {
              const customerSender = this.getCustomerAsSender(),
                senderList = customerSender ? [customerSender] : [],
                groupId = undefined,
                homeGroup = {
                  groupId: this.generateGroupId(),
                  purpose: "自宅用",
                  sender: senderList,
                  destinations: this.homeDestinations,
                };
              (this.settings.deliveryGroups ||
                (this.settings.deliveryGroups = []),
                this.settings.deliveryGroups.push(homeGroup));
            }
          } else if (hasDeliveryGroups) {
            const homeGroups = [],
              giftGroups = [];
            (null == (_p = savedData.deliveryGroups) ||
              _p.forEach((group) => {
                var _a2;
                const isHomeGroup = undefined;
                "自宅用" === group.purpose ||
                  (null == (_a2 = group.destinations)
                    ? void 0
                    : _a2.every((dest) => "自宅用" === dest.purpose))
                  ? homeGroups.push(group)
                  : giftGroups.push(group);
              }),
              homeGroups.length > 0 &&
              (this.homeDestinations = homeGroups.flatMap(
                (group) => group.destinations || [],
              )));
          }
          const purpose = null == (_q = savedData.notes) ? void 0 : _q.purpose,
            orderType = savedData.orderType,
            tabsElement = document.querySelector("wl-tabs");
          if (tabsElement) {
            let tabType = "gift";
            (orderType
              ? (tabType = orderType)
              : purpose && (tabType = "自宅用" === purpose ? "home" : "gift"),
              tabsElement.switchTab(tabType, !0),
              (this.currentOrderType = tabType));
          }
          (purpose &&
            ((this.purpose = purpose),
              window.BINDEC_SHIPPING_SETTINGS &&
              (window.BINDEC_SHIPPING_SETTINGS.currentPurpose = purpose)),
            setTimeout(async () => {
              try {
                (this.renderFromSavedData(),
                  purpose &&
                    "自宅用" !== purpose &&
                    (function(scope){ const r = scope.querySelector("input[name='purpose'][value='"+purpose+"']"); if(r) r.checked = true; })(this),
                  (this.isRestoring = !1),
                  await this.syncCartWithSettings(),
                  purpose &&
                    "自宅用" !== purpose &&
                    (function(scope){ const r = scope.querySelector("input[name='purpose'][value='"+purpose+"']"); if(r) r.checked = true; })(this),
                  purpose && document.dispatchEvent(new CustomEvent('wl:purpose-restored', { detail: { purpose } })),
                  document.dispatchEvent(new CustomEvent('wl:restore-complete')));
              } catch (error) {
                this.isRestoring = !1;
                document.dispatchEvent(new CustomEvent('wl:restore-complete'));
              }
            }, 100));
        } else {
          this.isRestoring = !1;
          document.dispatchEvent(new CustomEvent('wl:restore-complete'));
        }
      } catch (error) {
        this.isRestoring = !1;
        document.dispatchEvent(new CustomEvent('wl:restore-complete'));
      }
    }
    renderFromSavedData() {
      const _selectors_deliveryGroup = "delivery-group",
        _selectors_destinationSelector = "destination-selector",
        _selectors_groupTitle = ".js-group-title",
        _selectors_destinationTemplate = "template#new-destination-template",
        _selectors_deliverySectionContainer = ".js-delivery-section-container",
        _selectors_destinationTitle = ".js-destination-title";
      if (
        (this.homeDestinations &&
          this.homeDestinations.length > 0 &&
          this.homeDestinations.forEach((destination, destIdx) => {
            var _a;
            const destinationElement =
              this.newDestinationTemplate.content.cloneNode(!0),
              destinationSelector = destinationElement.querySelector(
                _selectors_destinationSelector,
              );
            (null == destinationSelector ||
              destinationSelector.setAttribute(
                "data-destination-id",
                destination.destinationId || "",
              ),
              null == destinationSelector ||
              destinationSelector.setAttribute(
                "data-destination-index",
                (destIdx + 1).toString(),
              ),
              (destinationElement.querySelector(
                _selectors_destinationTitle,
              ).textContent = `お届け先 ${destIdx + 1}`),
              this.homeDeliveryContainer.appendChild(destinationElement));
            const destElement = this.homeDeliveryContainer.querySelectorAll(
              "destination-selector",
            )[destIdx];
            destElement &&
              ((destination.deliveryMethod || destination.firstName) &&
                (null == (_a = destElement.restoreAddress) ||
                  _a.call(destElement, destination)),
                setTimeout(() => {
                  var _a2;
                  null == (_a2 = destElement.restoreProducts) ||
                    _a2.call(destElement, destination);
                }, 100));
          }),
          !this.settings.deliveryGroups ||
          0 === this.settings.deliveryGroups.length)
      )
        return;
      const giftGroups = undefined;
      if (
        (this.settings.deliveryGroups
          .filter((g) => "自宅用" !== g.purpose)
          .forEach((group, groupIdx) => {
            var _a, _b;
            const newGroupElement = this.newGroupTemplate.content.cloneNode(!0),
              groupIndex = groupIdx + 1;
            (null ==
              (_a = newGroupElement.querySelector(_selectors_deliveryGroup)) ||
              _a.setAttribute("data-delivery-group-id", group.groupId || ""),
              null ==
              (_b = newGroupElement.querySelector(
                _selectors_deliveryGroup,
              )) ||
              _b.setAttribute(
                "data-delivery-group-index",
                groupIndex.toString(),
              ),
              (newGroupElement.querySelector(
                _selectors_groupTitle,
              ).textContent = `お申込 ${groupIndex}`));
            const firstDestination = group.destinations[0];
            if (firstDestination) {
              const firstDestSelector = newGroupElement.querySelector(
                _selectors_destinationSelector,
              );
              (null == firstDestSelector ||
                firstDestSelector.setAttribute(
                  "data-destination-id",
                  firstDestination.destinationId || "",
                ),
                null == firstDestSelector ||
                firstDestSelector.setAttribute(
                  "data-destination-index",
                  "1",
                ));
            }
            this.deliveryGroupsContainer.appendChild(newGroupElement);
            const groupElement = this.querySelector(
              `delivery-group[data-delivery-group-id="${group.groupId}"]`,
            );
            if (groupElement) {
              if (group.sender && group.sender.length > 0) {
                const senderSelector =
                  groupElement.querySelector("sender-selector");
                senderSelector &&
                  senderSelector.restoreSender &&
                  senderSelector.restoreSender(group.sender[0]);
              }
              group.destinations.forEach((destination, destIdx) => {
                var _a2, _b2;
                let destElement;
                if (0 === destIdx)
                  destElement = groupElement.querySelector(
                    "destination-selector",
                  );
                else {
                  const template = undefined,
                    destinationElement = document
                      .querySelector(_selectors_destinationTemplate)
                      .content.cloneNode(!0),
                    destinationSelector = destinationElement.querySelector(
                      _selectors_destinationSelector,
                    );
                  (null == destinationSelector ||
                    destinationSelector.setAttribute(
                      "data-destination-id",
                      destination.destinationId || "",
                    ),
                    null == destinationSelector ||
                    destinationSelector.setAttribute(
                      "data-destination-index",
                      (destIdx + 1).toString(),
                    ),
                    (destinationElement.querySelector(
                      _selectors_destinationTitle,
                    ).textContent = `お届け先 ${destIdx + 1}`),
                    null ==
                    (_a2 = groupElement.querySelector(
                      _selectors_deliverySectionContainer,
                    )) || _a2.appendChild(destinationElement),
                    (destElement = groupElement.querySelectorAll(
                      "destination-selector",
                    )[destIdx]));
                }
                destElement &&
                  ((destination.deliveryMethod || destination.firstName) &&
                    (null == (_b2 = destElement.restoreAddress) ||
                      _b2.call(destElement, destination)),
                    destination.items &&
                    destination.items.length > 0 &&
                    setTimeout(() => {
                      var _a3;
                      null == (_a3 = destElement.restoreProducts) ||
                        _a3.call(destElement, destination);
                    }, 100));
              });
            }
          }),
          this.settings.customer)
      ) {
        const customerRegistration = this.querySelector(
          "customer-registration",
        );
        customerRegistration &&
          customerRegistration.restoreCustomer &&
          customerRegistration.restoreCustomer(this.settings.customer);
      }
      setTimeout(() => {
        this._recalculateAllShippingFeesAfterRestore();
      }, 500);
    }
    async _recalculateAllShippingFeesAfterRestore(isRetry) {
      var _a;
      const footerContainer = document.querySelector(
        ".cart__footer-total-content",
      ),
        loadingClassName = "js-total-shipping-loading";
      let loadingElement = null;
      if (footerContainer) {
        const existingShippingCost = footerContainer.querySelector(
          ".js-total-shipping-cost",
        );
        existingShippingCost && existingShippingCost.remove();
        const existingLoading = footerContainer.querySelector(
          `.${loadingClassName}`,
        );
        (existingLoading && existingLoading.remove(),
          (loadingElement = document.createElement("div")),
          loadingElement.classList.add(
            loadingClassName,
            "cart__footer-total-content-item",
            "border-top",
          ),
          (loadingElement.innerHTML =
            '\n          <span class="cart__footer-total-content-item-label">送料（税込）</span>\n          <span class="cart__footer-total-content-item-value" style="color: #6b7280; font-size: 1.3rem;">送料を再計算中です...</span>\n        '));
        const taxRateElement = footerContainer.querySelector(
          '[data-cart-item="tax-10"]',
        );
        if (taxRateElement)
          footerContainer.insertBefore(loadingElement, taxRateElement);
        else {
          const totalElement = footerContainer.querySelector(
            '[data-cart-item="total"]',
          );
          totalElement
            ? footerContainer.insertBefore(loadingElement, totalElement)
            : footerContainer.appendChild(loadingElement);
        }
      }
      try {
        let selectors2 = "destination-selector";
        selectors2 =
          "home" === this.settings.orderType
            ? "#Home destination-selector"
            : "#Gift destination-selector";
        const allDestinationSelectors = this.querySelectorAll(selectors2),
          recalculatePromises = Array.from(allDestinationSelectors).map(
            async (destElement) => {
              destElement.recalculateShippingFee &&
                (await destElement.recalculateShippingFee());
            },
          );
        await Promise.all(recalculatePromises);
        const settingsService = new SettingsService(),
          allDestinations =
            (null == (_a = this.settings.deliveryGroups)
              ? void 0
              : _a
                .map((group) =>
                  group.destinations.map((destination) => destination),
                )
                .flat()) || [],
          totalShippingCost =
            settingsService.calculateShippingCostForAllDestinations(
              allDestinations,
            ),
          originalTotalShippingCost = allDestinations.reduce(
            (sum, destination) =>
              sum +
              (destination.originalShippingFee || destination.shippingFee || 0),
            0,
          ),
          hasFreeShippingDiscount = await this._hasFreeShippingDiscountCode(),
          displayShippingCost = hasFreeShippingDiscount ? 0 : totalShippingCost;
        await this._renderTotalShippingCost(
          displayShippingCost,
          originalTotalShippingCost,
          hasFreeShippingDiscount,
        );
      } catch (error) {
        if (!isRetry)
          return void (await this._recalculateAllShippingFeesAfterRestore(!0));
        const affectedDestinations =
          (null == (_a = this.settings.deliveryGroups)
            ? void 0
            : _a
              .map((group) =>
                group.destinations.map((destination) => destination),
              )
              .flat()) || [];
        affectedDestinations.forEach((destination) => {
          (destination.shippingFee = null), (destination.originalShippingFee = null);
        });
        await this._renderTotalShippingCost(
          0,
          0,
          !1,
          "送料を確認できませんでした",
          () => this._recalculateAllShippingFeesAfterRestore(),
        );
      } finally {
        if (footerContainer) {
          const loadingEl = footerContainer.querySelector(
            `.${loadingClassName}`,
          );
          loadingEl && loadingEl.remove();
        }
      }
    }
    async saveSession() {
      return await sessionManager.save(this.settings);
    }
    async clearSession() {
      return await sessionManager.delete();
    }
    resetAllSettings() {
      (this.settings.deliveryGroups &&
        this.settings.deliveryGroups.length > 0 &&
        ((this.settings.deliveryGroups = []),
          (this.deliveryGroupsContainer.innerHTML = "")),
        this.homeDestinations &&
        this.homeDestinations.length > 0 &&
        ((this.homeDestinations = []),
          (this.homeDeliveryContainer.innerHTML = "")),
        window.BINDEC_SHIPPING_UNIT_SETTINGS &&
        Object.keys(window.BINDEC_SHIPPING_UNIT_SETTINGS).forEach((key) => {
          delete window.BINDEC_SHIPPING_UNIT_SETTINGS[key];
        }),
        this.clearSession());
    }
    getSettings() {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
      const customerInfo = CustomerService.getCustomerBasicInfo();
      let defaultAddress = CustomerService.getDefaultAddress();
      if (
        !defaultAddress ||
        (!defaultAddress.zip && !defaultAddress.address1)
      ) {
        const bindecSettings2 = window.BINDEC_SHIPPING_SETTINGS,
          addresses =
            (null == bindecSettings2 ? void 0 : bindecSettings2.addresses) ||
            [],
          defaultAddressId =
            null == bindecSettings2 ? void 0 : bindecSettings2.defaultAddressId;
        (defaultAddressId &&
          addresses.length > 0 &&
          (defaultAddress = addresses.find(
            (addr) => addr.id === defaultAddressId,
          )),
          (!defaultAddress ||
            (!defaultAddress.zip && !defaultAddress.address1)) &&
          addresses.length > 0 &&
          (defaultAddress = addresses[0]));
      }
      let customerId = customerInfo.customerId;
      if (!customerId && customerInfo.shopifyCustomerId) {
        const match =
          customerInfo.shopifyCustomerId.match(/\/Customer\/(\d+)$/);
        match && (customerId = match[1]);
      }
      const bindecSettings = window.BINDEC_SHIPPING_SETTINGS,
        originalDefaultAddressId =
          ((null ==
            (_a = null == bindecSettings ? void 0 : bindecSettings.profileAddress)
            ? void 0
            : _a.id) ??
            (null == bindecSettings
              ? void 0
              : bindecSettings.defaultAddressId)) ||
          null;
      let customer;
      if (
        (CustomerService.isAuthenticated() && this.syncCustomerFromShopify(),
          this.settings.customer &&
          (this.settings.customer.firstName ||
            this.settings.customer.lastName ||
            (null == (_b = this.settings.customer.address)
              ? void 0
              : _b.zipcode) ||
            (null == (_c = this.settings.customer.address)
              ? void 0
              : _c.address1)))
      ) {
        customer = this.settings.customer;
        {
          const bs = window.BINDEC_SHIPPING_SETTINGS,
            pid =
              (null == (_d = null == bs ? void 0 : bs.profileAddress)
                ? void 0
                : _d.id) ??
              (null == defaultAddress ? void 0 : defaultAddress.id) ??
              originalDefaultAddressId,
            kana = this._getKanaFromAddressCustomData(bs, pid);
          kana &&
            ((customer.firstNameKana = kana.first_name_kana),
              (customer.lastNameKana = kana.last_name_kana));
        }
      } else {
        const bindecSettings2 = window.BINDEC_SHIPPING_SETTINGS,
          profileAddressId =
            (null ==
              (_e =
                null == bindecSettings2 ? void 0 : bindecSettings2.profileAddress)
              ? void 0
              : _e.id) ??
            (null == defaultAddress ? void 0 : defaultAddress.id) ??
            originalDefaultAddressId,
          kanaForCustomer = this._getKanaFromAddressCustomData(
            bindecSettings2,
            profileAddressId,
          ),
          defaultAddressCustomData =
            null ==
              (_h =
                null ==
                  (_g =
                    null ==
                      (_f =
                        null == bindecSettings2
                          ? void 0
                          : bindecSettings2.addressCustomData)
                      ? void 0
                      : _f.addresses)
                  ? void 0
                  : _g.find(
                    (item) =>
                      null != item.id &&
                      String(item.id) === String(profileAddressId),
                  ))
              ? void 0
              : _h.data;
        customer = customerId
          ? {
            customerId: customerId,
            shopifyCustomerId: customerInfo.shopifyCustomerId || "",
            name: customerInfo.name || "",
            firstName: customerInfo.firstName || "",
            lastName: customerInfo.lastName || "",
            firstNameKana:
              (null == kanaForCustomer
                ? void 0
                : kanaForCustomer.first_name_kana) || null,
            lastNameKana:
              (null == kanaForCustomer
                ? void 0
                : kanaForCustomer.last_name_kana) || null,
            email: customerInfo.email || "",
            phone:
              getDisplayPhone({
                phone_1:
                  null == defaultAddressCustomData
                    ? void 0
                    : defaultAddressCustomData.phone_1,
                phone_2:
                  null == defaultAddressCustomData
                    ? void 0
                    : defaultAddressCustomData.phone_2,
                phone_3:
                  null == defaultAddressCustomData
                    ? void 0
                    : defaultAddressCustomData.phone_3,
                phone:
                  customerInfo.phone ||
                  (null == defaultAddress ? void 0 : defaultAddress.phone),
              }) || "",
            address: {
              zipcode:
                (null == defaultAddress ? void 0 : defaultAddress.zip) || "",
              prefecture:
                (null == defaultAddress ? void 0 : defaultAddress.province) ||
                "",
              city:
                (null == defaultAddress ? void 0 : defaultAddress.city) || "",
              address1:
                (null == defaultAddress ? void 0 : defaultAddress.address1) ||
                "",
              address2:
                (null == defaultAddress ? void 0 : defaultAddress.address2) ||
                null,
            },
            defaultAddressId: originalDefaultAddressId,
          }
          : {
            customerId: "guest",
            shopifyCustomerId: "",
            name: "",
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            address: {
              zipcode: "",
              prefecture: "",
              city: "",
              address1: "",
              address2: null,
            },
            defaultAddressId: null,
          };
      }
      const profileAddress =
        null == bindecSettings ? void 0 : bindecSettings.profileAddress,
        ritsAddress =
          null == bindecSettings ? void 0 : bindecSettings.ritsAddress,
        ritsPhone = null == bindecSettings ? void 0 : bindecSettings.ritsPhone;
      if (
        customerId &&
        "guest" !== customerId &&
        (profileAddress || ritsAddress || ritsPhone)
      ) {
        if (((customer = { ...customer }), profileAddress)) {
          (null != profileAddress.first_name &&
            (customer.firstName = profileAddress.first_name),
            null != profileAddress.last_name &&
            (customer.lastName = profileAddress.last_name),
            (null == profileAddress.first_name &&
              null == profileAddress.last_name) ||
            (customer.name =
              `${profileAddress.last_name || ""} ${profileAddress.first_name || ""}`.trim()),
            null != profileAddress.company &&
            "" !== profileAddress.company &&
            (customer.company = profileAddress.company),
            null != profileAddress.job_title &&
            "" !== profileAddress.job_title &&
            (customer.department = profileAddress.job_title),
            null != profileAddress.id &&
            (customer.defaultAddressId = profileAddress.id));
          const pid =
            profileAddress.id ??
            (null == defaultAddress ? void 0 : defaultAddress.id) ??
            originalDefaultAddressId,
            kana = this._getKanaFromAddressCustomData(bindecSettings, pid);
          kana &&
            ((customer.firstNameKana = kana.first_name_kana),
              (customer.lastNameKana = kana.last_name_kana));
        }
        if (ritsAddress) {
          const town = ritsAddress.town ?? "",
            line1 = ritsAddress.line1 ?? "",
            ritsAddress1 = town && line1 ? `${town} ${line1}` : town || line1;
          customer.address = {
            zipcode:
              ritsAddress.zip ??
              (null == profileAddress ? void 0 : profileAddress.zip) ??
              customer.address.zipcode ??
              "",
            prefecture:
              ritsAddress.prefecture ??
              (null == profileAddress ? void 0 : profileAddress.province) ??
              customer.address.prefecture ??
              "",
            city:
              ritsAddress.city ??
              (null == profileAddress ? void 0 : profileAddress.city) ??
              customer.address.city ??
              "",
            address1:
              ritsAddress1 ||
              (null == profileAddress ? void 0 : profileAddress.address1) ||
              customer.address.address1 ||
              "",
            address2:
              (ritsAddress.line2 ??
                (null == profileAddress ? void 0 : profileAddress.address2) ??
                customer.address.address2) ||
              null,
          };
        }
        if (ritsPhone) {
          const parts = [
            ritsPhone.phone_1,
            ritsPhone.phone_2,
            ritsPhone.phone_3,
          ].filter((p) => null != p && "" !== p);
          if (parts.length > 0) customer.phone = parts.join("-");
          else if (null == profileAddress ? void 0 : profileAddress.phone) {
            const profileCustomData =
              null ==
                (_k =
                  null ==
                    (_j =
                      null ==
                        (_i =
                          null == bindecSettings
                            ? void 0
                            : bindecSettings.addressCustomData)
                        ? void 0
                        : _i.addresses)
                    ? void 0
                    : _j.find(
                      (item) =>
                        null != item.id &&
                        String(item.id) ===
                        String(
                          (null == profileAddress
                            ? void 0
                            : profileAddress.id) ??
                          (null == defaultAddress
                            ? void 0
                            : defaultAddress.id) ??
                          originalDefaultAddressId,
                        ),
                    ))
                ? void 0
                : _k.data;
            customer.phone = getDisplayPhone({
              phone_1:
                null == profileCustomData ? void 0 : profileCustomData.phone_1,
              phone_2:
                null == profileCustomData ? void 0 : profileCustomData.phone_2,
              phone_3:
                null == profileCustomData ? void 0 : profileCustomData.phone_3,
              phone: profileAddress.phone,
            });
          }
        }
      }
      const formattedDeliveryGroups = this.settings.deliveryGroups;
      return {
        ...this.settings,
        deliveryGroups: formattedDeliveryGroups,
        orderType: this.currentOrderType,
        homeDestinations: this.homeDestinations,
        customer: customer,
        notes: { ...this.settings.notes, purpose: this.purpose || null },
        metadata: { ...this.settings.metadata, multiShipping: !0 },
      };
    }
    async _hasFreeShippingDiscountCode() {
      try {
        const cart2 = await cartCache.get();
        if (!cart2) return !1;
        const discountCodes = undefined;
        return (cart2.discount_codes || []).some((codeEntry) =>
          "string" == typeof codeEntry
            ? codeEntry.includes("送料無料")
            : !("object" != typeof codeEntry || !codeEntry.code) &&
            codeEntry.code.includes("送料無料"),
        );
      } catch (error) {
        return !1;
      }
    }
    _clearTotalShippingCost() {
      const totalSelectors_container = ".cart__footer-total-content",
        totalSelectors_shippingCostItem = ".js-total-shipping-cost",
        container = document.querySelector(totalSelectors_container);
      if (!container) return;
      const existingElement = container.querySelector(
        totalSelectors_shippingCostItem,
      );
      existingElement && existingElement.remove();
    }
    async _renderTotalShippingCost(
      totalShippingCost,
      originalShippingCost,
      hasFreeShippingDiscount,
      errorMessage,
      onRetry,
    ) {
      const TAX_RATE = 0.1;
      let isFreeShipping = hasFreeShippingDiscount ?? !1,
        originalCost = originalShippingCost ?? totalShippingCost;
      !errorMessage &&
        void 0 === hasFreeShippingDiscount &&
        totalShippingCost > 0 &&
        ((isFreeShipping = await this._hasFreeShippingDiscountCode()),
          isFreeShipping &&
          ((originalCost = totalShippingCost), (totalShippingCost = 0)));
      const totalSelectors_container = ".cart__footer-total-content",
        totalSelectors_shippingCostItem = ".js-total-shipping-cost",
        container = document.querySelector(totalSelectors_container);
      if (!container) return;
      const existingElement = container.querySelector(
        totalSelectors_shippingCostItem,
      );
      existingElement && existingElement.remove();
      const div = document.createElement("div");
      (div.classList.add(
        "js-total-shipping-cost",
        "cart__footer-total-content-item",
        "border-top",
      ),
        (div.innerHTML = errorMessage
          ? `\n            <span class="cart__footer-total-content-item-label">送料（税込）</span>\n            <span class="cart__footer-total-content-item-value" style="flex-direction: column; align-items: flex-end; row-gap: 6px;">\n              <button type="button" class="js-total-shipping-cost-retry" style="display: flex; align-items: center; column-gap: 4px; border: 1px solid #bfbfbf; border-radius: 4px; background-color: #fff; color: #3b3834; padding: 0 1.2rem; height: 32px; font-size: 1.3rem; cursor: pointer;">\n                <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M13 8A5 5 0 1 1 11.5 4.5"/><path d="M13 2.5v3h-3"/></svg>\n                <span>再計算する</span>\n              </button>\n              <span style="color: red; font-size: 1.2rem;">${errorMessage}</span>\n            </span>\n          `
          : isFreeShipping
            ? originalCost > 0
              ? `\n            <span class="cart__footer-total-content-item-label">送料（税込）</span>\n            <span class="cart__footer-total-content-item-value">\n              <span style="text-decoration: line-through; color: #999; margin-right: 8px;">\n                ${new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(originalCost)}\n              </span>\n              <span>無料</span>\n            </span>\n          `
              : '\n            <span class="cart__footer-total-content-item-label">送料（税込）</span>\n            <span class="cart__footer-total-content-item-value"><span>無料</span></span>\n          '
            : `\n          <span class="cart__footer-total-content-item-label">送料（税込）</span>\n          <span class="cart__footer-total-content-item-value">\n            ${new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(totalShippingCost)}\n          </span>\n        `));
      const shippingDiscountElement = container.querySelector(
        '[data-cart-item="shipping-discount"]',
      ),
        couponElement = container.querySelector('[data-cart-item="coupon"]'),
        taxRate10Element = container.querySelector('[data-cart-item="tax-10"]'),
        taxRate8Element = container.querySelector('[data-cart-item="tax-8"]');
      if (shippingDiscountElement && shippingDiscountElement.nextElementSibling)
        container.insertBefore(div, shippingDiscountElement.nextElementSibling);
      else if (couponElement && couponElement.nextElementSibling)
        container.insertBefore(div, couponElement.nextElementSibling);
      else if (taxRate10Element) container.insertBefore(div, taxRate10Element);
      else if (taxRate8Element) container.insertBefore(div, taxRate8Element);
      else {
        const totalElement = container.querySelector(
          '[data-cart-item="total"]',
        );
        totalElement
          ? container.insertBefore(div, totalElement)
          : container.appendChild(div);
      }
      if (errorMessage) {
        const retryButton = div.querySelector(".js-total-shipping-cost-retry");
        retryButton &&
          retryButton.addEventListener("click", () => {
            (onRetry || (() => this._updateTotalShippingCostDisplay()))();
          });
        return;
      }
      (taxRate10Element && taxRate10Element.classList.remove("border-top"),
        taxRate8Element && taxRate8Element.classList.remove("border-top"));
      const shippingCostForTotal = isFreeShipping ? 0 : totalShippingCost;
      this._updateSubtotalAndTaxWithShipping(container, shippingCostForTotal);
    }
    async _updateSubtotalAndTaxWithShipping(container, totalShippingCost) {
      var _a, _b;
      const TAX_RATE = 0.1;
      try {
        const cart2 = await cartCache.get(!0);
        if (!cart2 || !cart2.items) return;
        const selectedVariantQuantities = {};
        if (
          this.settings.deliveryGroups &&
          this.settings.deliveryGroups.length > 0
        )
          for (const group of this.settings.deliveryGroups)
            for (const destination of group.destinations || [])
              for (const item of destination.items || []) {
                const rawId = String(item.variantId),
                  vid =
                    (rawId.includes("/") && rawId.split("/").pop()) || rawId;
                selectedVariantQuantities[vid] =
                  (selectedVariantQuantities[vid] || 0) + item.quantity;
              }
        const hasSelectedProducts =
          Object.keys(selectedVariantQuantities).length > 0,
          giftVariantIds = new Set();
        window.GIFT_DATA &&
          Object.values(window.GIFT_DATA).forEach((events) => {
            events.forEach((event) => {
              ["noshi", "messageCard", "wrapping"].forEach((type) => {
                var _a2;
                null == (_a2 = event.data[type]) ||
                  _a2.forEach((item) => {
                    item.variantId &&
                      giftVariantIds.add(String(item.variantId));
                  });
              });
            });
          });
        const cartItems = hasSelectedProducts
          ? cart2.items.filter(
            (item) =>
              selectedVariantQuantities[String(item.variant_id)] ||
              giftVariantIds.has(String(item.variant_id)),
          )
          : cart2.items;
        let originalSubtotal = 0,
          selectedOriginalTotal = 0;
        for (const item of cartItems)
          ((originalSubtotal +=
            (item.final_line_price || item.line_price) / 100),
            (selectedOriginalTotal += item.original_line_price / 100));
        if (hasSelectedProducts) {
          const totalPriceElement = container.querySelector(
            '[data-cart-item="total-price"] .cart__footer-total-content-item-value',
          );
          totalPriceElement &&
            (totalPriceElement.textContent = new Intl.NumberFormat("ja-JP", {
              style: "currency",
              currency: "JPY",
            }).format(selectedOriginalTotal));
        }
        const newSubtotalWithTax = originalSubtotal + totalShippingCost;
        let tax8TotalPrice = 0,
          tax10TotalPrice = 0;
        const productMetafields =
          (null == (_b = window.BINDEC_SHIPPING_SETTINGS)
            ? void 0
            : _b.productMetafields) || {};
        for (const item of cartItems) {
          const itemTotalPrice =
            (item.final_line_price || item.line_price) / 100,
            metafield = productMetafields[String(item.variant_id)],
            taxClsf = undefined;
          if (
            "3" === (null == metafield ? void 0 : metafield.tax_clsf) ||
            !1 === item.taxable
          )
            continue;
          const taxRate = (null == metafield ? void 0 : metafield.tax_rate)
            ? parseFloat(metafield.tax_rate)
            : 10;
          0 !== taxRate &&
            (8 === taxRate
              ? (tax8TotalPrice += itemTotalPrice)
              : (tax10TotalPrice += itemTotalPrice));
        }
        let cartLevelDiscountAmount = 0;
        const cartLevelDiscounts = cart2.cart_level_discount_applications || [];
        for (const discount of cartLevelDiscounts) {
          const discountType = discount.type,
            discountTitle = discount.title || "";
          if (discountTitle.includes("PT_")) {
            const parts = discountTitle.split("_");
            if (parts.length >= 2) {
              const pointValue = 100 * parseInt(parts[1], 10);
              isNaN(pointValue) || (cartLevelDiscountAmount += pointValue);
            }
          } else if (discountTitle.includes("GC_")) {
            const parts = discountTitle.split("_");
            if (parts.length >= 2) {
              const gcValue = 100 * parseInt(parts[1], 10);
              isNaN(gcValue) || (cartLevelDiscountAmount += gcValue);
            }
          } else
            "shipping_line" !== discount.target_type &&
              (cartLevelDiscountAmount += discount.total_allocated_amount || 0);
        }
        const cartLevelDiscountAmountInYen = cartLevelDiscountAmount / 100,
          taxableTotalTaxed = tax8TotalPrice + tax10TotalPrice;
        let discountedTax8Total = tax8TotalPrice,
          discountedTax10Total = tax10TotalPrice;
        if (cartLevelDiscountAmountInYen > 0 && taxableTotalTaxed > 0) {
          const discountFor8 =
            (cartLevelDiscountAmountInYen * tax8TotalPrice) /
            taxableTotalTaxed,
            discountFor10 = cartLevelDiscountAmountInYen - discountFor8;
          ((discountedTax8Total = Math.max(0, tax8TotalPrice - discountFor8)),
            (discountedTax10Total = Math.max(
              0,
              tax10TotalPrice - discountFor10,
            )));
        }
        ((tax8TotalPrice = discountedTax8Total),
          (tax10TotalPrice = discountedTax10Total));
        const tax10TotalWithShipping = tax10TotalPrice + totalShippingCost,
          tax8TotalWithTax = tax8TotalPrice,
          tax10ExcludingPrice = undefined,
          tax10BeforeRound =
            tax10TotalWithShipping - tax10TotalWithShipping / 1.1,
          tax8ExcludingPrice = undefined,
          tax8BeforeRound = tax8TotalPrice - tax8TotalPrice / 1.08,
          tax10Amount = Math.round(tax10BeforeRound),
          tax8Amount = Math.round(tax8BeforeRound),
          totalTax = tax10Amount + tax8Amount;
        let tax10Element = container.querySelector('[data-cart-item="tax-10"]');
        if (tax10Element) {
          const valueElement = tax10Element.querySelector(
            ".cart__footer-total-content-item-value",
          );
          valueElement &&
            (valueElement.textContent = new Intl.NumberFormat("ja-JP", {
              style: "currency",
              currency: "JPY",
            }).format(tax10Amount));
        } else if (totalShippingCost > 0 && tax10Amount > 0) {
          const div = document.createElement("div");
          ((div.className = "cart__footer-total-content-item"),
            div.setAttribute("data-cart-item", "tax-10"),
            (div.innerHTML = `\n            <span class="cart__footer-total-content-item-label">税率10%</span>\n            <span class="cart__footer-total-content-item-value">${new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(tax10Amount)}</span>\n          `));
          const shippingCostElement = container.querySelector(
            ".js-total-shipping-cost",
          ),
            tax8ElementForInsert = container.querySelector(
              '[data-cart-item="tax-8"]',
            ),
            totalTaxElement2 = container.querySelector(
              '[data-cart-item="total-tax"]',
            );
          if (shippingCostElement && shippingCostElement.nextElementSibling)
            container.insertBefore(div, shippingCostElement.nextElementSibling);
          else if (tax8ElementForInsert)
            container.insertBefore(div, tax8ElementForInsert);
          else if (totalTaxElement2)
            container.insertBefore(div, totalTaxElement2);
          else {
            const totalElement2 = container.querySelector(
              '[data-cart-item="total"]',
            );
            totalElement2 && container.insertBefore(div, totalElement2);
          }
          tax10Element = div;
        }
        const tax8Element = container.querySelector('[data-cart-item="tax-8"]');
        if (tax8Element) {
          const valueElement = tax8Element.querySelector(
            ".cart__footer-total-content-item-value",
          );
          valueElement &&
            (valueElement.textContent = new Intl.NumberFormat("ja-JP", {
              style: "currency",
              currency: "JPY",
            }).format(tax8Amount));
        }
        const totalTaxElement = container.querySelector(
          '[data-cart-item="total-tax"]',
        );
        if (totalTaxElement) {
          const valueElement = totalTaxElement.querySelector(
            ".cart__footer-total-content-item-value",
          );
          valueElement &&
            (valueElement.textContent = new Intl.NumberFormat("ja-JP", {
              style: "currency",
              currency: "JPY",
            }).format(totalTax));
        }
        const totalElement = container.querySelector(
          '[data-cart-item="total"]',
        );
        if (totalElement) {
          const valueElement = totalElement.querySelector(
            ".cart__footer-total-content-item-value strong",
          );
          if (valueElement) {
            const totalAmount = cart2.total_price / 100 + totalShippingCost;
            valueElement.textContent = new Intl.NumberFormat("ja-JP", {
              style: "currency",
              currency: "JPY",
            }).format(totalAmount);
          }
        }
      } catch (error) { }
    }
    _getSectionsToRender() {
      var _a, _b, _c;
      return [
        {
          id: "main-cart-items",
          section:
            (null == (_a = document.getElementById("main-cart-items"))
              ? void 0
              : _a.dataset.id) || "",
          selector: ".js-contents",
        },
        {
          id: "multiShippingData",
          section:
            (null == (_b = document.getElementById("main-cart-items"))
              ? void 0
              : _b.dataset.id) || "",
          selector: "#multiShippingData",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
        {
          id: "cart-live-region-text",
          section: "cart-live-region-text",
          selector: ".shopify-section",
        },
        {
          id: "main-cart-footer",
          section:
            (null == (_c = document.getElementById("main-cart-footer"))
              ? void 0
              : _c.dataset.id) || "",
          selector: ".js-contents",
        },
      ];
    }
    _saveCartToSession(cart2, purpose) {
      var _a;
      try {
        const cartData =
          cart2 ||
          (null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.cart),
          targetPurpose = purpose || this.purpose;
        if (!cartData || !cartData.items || 0 === cartData.items.length) return;
        if (!targetPurpose) return;
        const allSessions = this._getAllSessionData(),
          now = new Date().toISOString();
        ((allSessions[targetPurpose] = {
          items: cartData.items.map((item) => ({
            variantId: item.variant_id,
            quantity: item.quantity,
            properties: item.properties,
            productId: item.product_id,
            final_price: item.final_price,
            price: item.price,
          })),
          savedAt: now,
        }),
          sessionStorage.setItem(
            _MultiShippingSettings.PRESERVED_ITEMS_KEY,
            JSON.stringify(allSessions),
          ));
      } catch (error) { }
    }
    _getAllSessionData() {
      try {
        const data = sessionStorage.getItem(
          _MultiShippingSettings.PRESERVED_ITEMS_KEY,
        );
        if (data) return JSON.parse(data);
      } catch (error) { }
      return {};
    }
    _getCartFromSession(purpose) {
      try {
        const allSessions = undefined;
        return this._getAllSessionData()[purpose] || null;
      } catch (error) { }
      return null;
    }
    _clearCartFromSession(purpose) {
      try {
        if (purpose) {
          const allSessions = this._getAllSessionData();
          (delete allSessions[purpose],
            0 === Object.keys(allSessions).length
              ? sessionStorage.removeItem(
                _MultiShippingSettings.PRESERVED_ITEMS_KEY,
              )
              : sessionStorage.setItem(
                _MultiShippingSettings.PRESERVED_ITEMS_KEY,
                JSON.stringify(allSessions),
              ));
        } else
          sessionStorage.removeItem(_MultiShippingSettings.PRESERVED_ITEMS_KEY);
      } catch (error) { }
    }
    _isGiftItem(variantId) {
      var _a, _b, _c, _d, _e;
      const variantIdStr = String(variantId),
        item =
          null ==
            (_c =
              null ==
                (_b =
                  null == (_a = window.BINDEC_SHIPPING_SETTINGS) ? void 0 : _a.cart)
                ? void 0
                : _b.items)
            ? void 0
            : _c.find((item2) => String(item2.variant_id) === variantIdStr);
      if (!item) return !1;
      const giftProductTypes = [
        "gift-wrapping",
        "noshi",
        "message-card",
        "wrapping",
      ],
        productType =
          (null == (_d = item.product_type) ? void 0 : _d.toLowerCase()) || "";
      if (giftProductTypes.some((type) => productType.includes(type)))
        return !0;
      const title =
        (null == (_e = item.title) ? void 0 : _e.toLowerCase()) || "",
        giftKeywords = undefined;
      return [
        "ラッピング",
        "のし",
        "熨斗",
        "メッセージカード",
        "wrapping",
      ].some((keyword) => title.includes(keyword));
    }
    async _restoreCartFromSession(purpose, clearAfterRestore = !1) {
      try {
        const savedCart = this._getCartFromSession(purpose);
        if (!savedCart || !savedCart.items || 0 === savedCart.items.length)
          return !1;
        const savedAt = new Date(savedCart.savedAt).getTime(),
          age = undefined;
        if (
          Date.now() - savedAt >
          _MultiShippingSettings.SESSION_EXPIRATION_TIME
        )
          return (this._clearCartFromSession(purpose), !1);
        const currentCart = await cartCache.get(!0);
        if (!currentCart) return !1;
        const currentVariantIds = new Set(
          currentCart.items.map((item) => item.variant_id.toString()),
        ),
          purchasedVariantsStr = sessionStorage.getItem(
            "_checkout_purchased_variants",
          ),
          purchasedVariantIds = purchasedVariantsStr
            ? new Set(JSON.parse(purchasedVariantsStr))
            : new Set();
        let restoredCount = 0,
          skippedCount = 0;
        for (const item of savedCart.items)
          try {
            const variantId = item.variantId
              .toString()
              .replace("gid://shopify/ProductVariant/", "");
            if (currentVariantIds.has(variantId)) {
              skippedCount++;
              continue;
            }
            if (purchasedVariantIds.has(variantId)) {
              skippedCount++;
              continue;
            }
            const response = undefined;
            (
              await fetch("/cart/add.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  id: item.variantId,
                  quantity: item.quantity,
                  properties: item.properties || {},
                }),
              })
            ).ok && restoredCount++;
          } catch (error) { }
        return (
          clearAfterRestore && this._clearCartFromSession(purpose),
          restoredCount > 0
        );
      } catch (error) {
        return !1;
      }
    }
    _getSectionInnerHTML(html, selector) {
      var _a;
      return (
        (null ==
          (_a = new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector))
          ? void 0
          : _a.innerHTML) || ""
      );
    }
    _executeScripts(container) {
      const scripts = undefined;
      container.querySelectorAll("script").forEach((oldScript) => {
        var _a;
        const newScript = document.createElement("script");
        (Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        }),
          (newScript.textContent = oldScript.textContent),
          null == (_a = oldScript.parentNode) ||
          _a.replaceChild(newScript, oldScript));
      });
    }
  };
  (__publicField(
    _MultiShippingSettings,
    "PRESERVED_ITEMS_KEY",
    "bindec_preserved_cart_items",
  ),
    __publicField(_MultiShippingSettings, "SESSION_EXPIRATION_TIME", 36e5));
  let MultiShippingSettings = _MultiShippingSettings;
  (customElements.define("multi-shipping-settings", MultiShippingSettings),
    "loading" === document.readyState
      ? document.addEventListener("DOMContentLoaded", () => {
        sessionStorage.removeItem("_multi_shipping_last_init");
      })
      : sessionStorage.removeItem("_multi_shipping_last_init"));
}
document.addEventListener('input', function (e) { if (e.target && e.target.name === 'address2') { const maxLength = parseInt(e.target.getAttribute('maxlength') || 30, 10); if (e.target.value.length > maxLength) { e.target.value = e.target.value.slice(0, maxLength); } } });
